import React, { useState, useEffect, useCallback, useRef, useMemo, createContext, useContext } from "react";
import { useLocation } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { t, L } from "../lib/translations";
import { ISRAEL_TIMEZONE, formatIsraelDate, formatIsraelDateTime, formatIsraelTime } from "../lib/time";
import { toast } from "sonner";
import {
  BarChart3, BarChart2, Package, ShoppingBag, Users as UsersIcon, Plus, Trash2, Check, X,
  Settings, Save, Coins, MessageSquare, Send, Gift, Crown, Shield, User,
  TrendingUp, Clock, ChevronDown, ChevronUp, Target, Wallet, Zap,
  ImagePlus, Ticket, Megaphone, Percent, Power, HelpCircle, Calendar,
  RefreshCw, RotateCcw, ArrowUpRight, Search, Sparkles, Languages, PenSquare, Filter, History, ChevronLeft, ChevronRight,
  Instagram, Heart, Music4, Youtube, Twitter, Earth, ClipboardList, CalendarClock, ExternalLink, AlertTriangle,
  TrendingDown, Award, CalendarDays, Moon, Info, AlertCircle, Bell, Mail, CheckCircle2, XCircle, Globe, Lightbulb,
  Tag, Eye, Trophy, DollarSign, Lock, Link2, CheckCircle, Pencil, Menu
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, PieChart, Pie, Cell, Legend, LineChart, Line, ComposedChart, ReferenceLine } from "recharts";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import StepperInput from "../components/StepperInput";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import Lightbox from "../components/Lightbox";
import CRMTicketList from "../components/CRMTicketList";
import CRMChatPanel from "../components/CRMChatPanel";
import CRMUserProfilePanel from "../components/CRMUserProfilePanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ensureHttps = (url) => {
  if (url && !/^https?:\/\//i.test(url)) return "https://" + url;
  return url;
};

const ADMIN_TAB_SECTIONS = [
  { id: "overview",    emoji: "🔭", label: (lang) => t("Admin.overview", lang),          color: "#E1306C", tabs: ["dashboard", "analytics", "onboarding-analytics", "seo", "improvements"] },
  { id: "operations",  emoji: "⚙️",  label: (lang) => t("Admin.operations", lang),        color: "#F77737", tabs: ["orders", "products", "topups", "promos", "payouts"] },
  { id: "workspace",   emoji: "🖥️",  label: (lang) => t("Admin.workspace", lang),         color: "#833AB4", tabs: ["tasks", "users", "messages", "ig-locks", "emails"] },
  { id: "support",     emoji: "🛟",  label: (lang) => t("Admin.support", lang),            color: "#405DE6", tabs: ["tickets", "support-analytics", "ai-chats"] },
  { id: "system",      emoji: "🔧",  label: (lang) => t("Admin.system", lang),             color: "#9ca3af", tabs: ["settings", "roles"] },
];
// Tabs that only super_admin can see regardless of permissions
const SUPER_ADMIN_ONLY_TABS = new Set(["settings", "roles"]);
const ADMIN_VALID_TABS = ADMIN_TAB_SECTIONS.flatMap((section) => section.tabs);
const TASK_TYPES = [
  { value: "instagram_follow", icon: Instagram, label: "Instagram Follow", color: "#E1306C" },
  { value: "instagram_like", icon: Heart, label: "Instagram Like", color: "#F43F5E" },
  { value: "telegram", icon: Send, label: "Telegram", color: "#38BDF8" },
  { value: "tiktok", icon: Music4, label: "TikTok", color: "#F8FAFC" },
  { value: "youtube", icon: Youtube, label: "YouTube", color: "#FF3131" },
  { value: "twitter", icon: Twitter, label: "Twitter/X", color: "#9CA3AF" },
  { value: "website", icon: Earth, label: "Website", color: "#22C55E" },
  { value: "custom", icon: ClipboardList, label: "Custom", color: "#F77737" },
];
const TASK_TYPE_FALLBACK = TASK_TYPES[TASK_TYPES.length - 1];
const getTaskTypeMeta = (value) => TASK_TYPES.find((taskType) => taskType.value === value) || TASK_TYPE_FALLBACK;
const TaskTypeIcon = ({ type, className = "w-4 h-4" }) => {
  const meta = getTaskTypeMeta(type);
  const Icon = meta.icon;
  return <Icon className={className} style={{ color: meta.color }} />;
};
const formatProviderBalance = (providerBalance) => {
  if (!providerBalance || providerBalance.balance === undefined || providerBalance.balance === null || providerBalance.balance === "") {
    return null;
  }
  const numeric = Number(providerBalance.balance);
  const amount = Number.isFinite(numeric)
    ? numeric.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : String(providerBalance.balance);
  return `${amount}${providerBalance.currency ? ` ${providerBalance.currency}` : ""}`;
};
const formatProviderBalanceIls = (providerBalance) => {
  if (!providerBalance || providerBalance.balance_ils === undefined || providerBalance.balance_ils === null) {
    return null;
  }
  const numeric = Number(providerBalance.balance_ils);
  if (!Number.isFinite(numeric)) {
    return null;
  }
  return `≈ ${numeric.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₪`;
};
const formatProviderFxRate = (providerBalance) => {
  if (!providerBalance || providerBalance.fx_rate_to_ils === undefined || providerBalance.fx_rate_to_ils === null) {
    return null;
  }
  const numeric = Number(providerBalance.fx_rate_to_ils);
  if (!Number.isFinite(numeric)) {
    return null;
  }
  const baseCurrency = providerBalance.fx_base_currency || providerBalance.currency || "USD";
  return `1 ${baseCurrency} ≈ ${numeric.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })} ₪`;
};
const formatProviderIds = (providerIds) => providerIds?.length ? providerIds.join(", ") : null;
const formatProviderMetric = (value) => value === undefined || value === null || value === "" ? "—" : String(value);
class AdminErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(e) { return { error: e }; }
  render() {
    if (this.state.error) return (
      <div className="py-16 flex flex-col items-center justify-center gap-3 px-6">
        <div className="text-3xl">⚠️</div>
        <p className="text-sm font-semibold" style={{ color: "#ef4444" }}>Render error</p>
        <pre className="text-[11px] max-w-xl text-center break-all whitespace-pre-wrap" style={{ color: "var(--text-secondary)" }}>{this.state.error?.message}</pre>
      </div>
    );
    return this.props.children;
  }
}

const formatDateTime = (value) => {
  return formatIsraelDateTime(value);
};
// ── Emoji / Icon toggle system ──────────────────────────────────────────────
const NavStyleContext = createContext("emoji");
const useNavStyle = () => useContext(NavStyleContext);

const ADMIN_ICON_MAP = {
  "📅": Calendar,   "💸": TrendingDown,  "📈": TrendingUp,   "🛒": ShoppingBag,
  "👥": UsersIcon,  "📦": Package,       "⏳": Clock,         "✅": CheckCircle2,
  "💵": DollarSign, "⚡": Zap,           "🛡️": Shield,        "🪙": Coins,
  "🔄": RefreshCw,  "🎁": Gift,          "❤️": Heart,         "👤": User,
  "💡": Lightbulb,  "🎯": Target,        "💳": Wallet,        "🏷️": Tag,
  "📊": BarChart3,  "📉": TrendingDown,  "📋": ClipboardList, "🚫": XCircle,
  "⭐": Crown,       "🔍": Search,        "💎": Crown,         "🌟": Sparkles,
  "👑": Crown,       "🎫": Ticket,        "🏦": Wallet,        "🎧": HelpCircle,
  "💬": MessageSquare, "💰": Wallet,      "🎵": Music4,        "👁": Eye,
  "👁️": Eye,         "🔒": Lock,          "🔭": Globe,         "⚙️": Settings,
  "🖥️": UsersIcon,  "🛟": HelpCircle,    "🔧": Settings,      "🛍️": Package,
  "📧": Mail,        "🏆": Trophy,        "🎉": Sparkles,      "📝": PenSquare,
  "🔌": Zap,         "🔗": ExternalLink,  "🟢": CheckCircle2,  "🚀": Zap,
  "📘": Globe,       "📑": ClipboardList, "🎨": Sparkles,      "💻": BarChart3,
};

function AdminIcon({ emoji, className = "w-4 h-4" }) {
  const navStyle = useNavStyle();
  if (navStyle === "emoji") return <span className="leading-none select-none">{emoji}</span>;
  const IconComp = ADMIN_ICON_MAP[emoji];
  if (!IconComp) return <span className="leading-none select-none">{emoji}</span>;
  return <IconComp className={className} />;
}

const IP_HISTORY_BASE_CFG = {
  topup: { icon: Wallet, color: "#00C853" },
  topup_request: { icon: Clock, color: "#F59E0B" },
  order: { icon: Package, color: "#E1306C" },
  task: { icon: Target, color: "#405DE6" },
  refund: { icon: RefreshCw, color: "#22C55E" },
  daily: { icon: CalendarDays, color: "#833AB4" },
  referral: { icon: UsersIcon, color: "#405DE6" },
  promo: { icon: Percent, color: "#E1306C" },
  admin: { icon: Crown, color: "#F77737" },
  default: { icon: Coins, color: "#94A3B8" },
};
const getIpointsHistoryCfg = (entry) => {
  if ((entry?.source_type || "") === "topup_request") {
    if (entry?.status === "rejected") return { icon: XCircle, color: "#EF4444" };
    if (entry?.status === "draft") return { icon: PenSquare, color: "#94A3B8" };
    return IP_HISTORY_BASE_CFG.topup_request;
  }
  return IP_HISTORY_BASE_CFG[entry?.source_type] || IP_HISTORY_BASE_CFG.default;
};
const getIpointsHistoryStatusMeta = (lang, status) => {
  if (status === "approved") {
    return { label: t("order.approved", lang), color: "#22c55e", bg: "rgba(34,197,94,0.14)" };
  }
  if (status === "pending") {
    return { label: t("Admin.pending", lang), color: "#f59e0b", bg: "rgba(245,158,11,0.14)" };
  }
  if (status === "rejected") {
    return { label: t("order.rejected", lang), color: "#ef4444", bg: "rgba(239,68,68,0.14)" };
  }
  if (status === "draft") {
    return { label: t("Admin.draft", lang), color: "#94a3b8", bg: "var(--border-color)" };
  }
  return null;
};
function IpointsHistoryPanel({ lang, history = [], meta = null, balance = 0, compact = false }) {
  const totalEarned = history.filter((entry) => Number(entry?.delta || 0) > 0).reduce((sum, entry) => sum + Number(entry.delta || 0), 0);
  const totalSpent = history.filter((entry) => Number(entry?.delta || 0) < 0).reduce((sum, entry) => sum + Number(entry.delta || 0), 0);
  const gap = Number(meta?.history_gap_ipoints || 0);
  const containerMaxHeight = compact ? "max-h-64" : "max-h-72";
  return (
    <>
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <History className="w-4 h-4 text-[#F77737]" />
          <span className="font-semibold">{t("Admin.ipoints_history", lang)}</span>
          <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: "rgba(247,119,55,0.1)", color: "#F77737" }}>
            {history.length}
          </span>
          {!!meta?.reconstructed_count && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: "rgba(122,162,255,0.12)", color: "#7aa2ff" }}>
              {L(lang, `${meta.reconstructed_count} restored`, `${meta.reconstructed_count} восстановлено`, `${meta.reconstructed_count} שוחזרו`)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-[11px] flex-wrap">
          <span style={{ color: "#00C853" }}>+{totalEarned.toLocaleString()} iP</span>
          <span style={{ color: "#E1306C" }}>{totalSpent.toLocaleString()} iP</span>
          <span className="font-bold" style={{ color: "#F77737" }}>= {Number(balance || 0).toLocaleString()} iP</span>
        </div>
      </div>
      {gap !== 0 && (
        <div className="mb-3 rounded-2xl px-3 py-2.5 text-[11px] flex items-start gap-2" style={{ background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.18)", color: "#fbbf24" }}>
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <div>
            <span className="font-semibold">{t("Admin.historical_gap", lang)}:</span>{" "}
            <span>{gap > 0 ? "+" : ""}{gap.toLocaleString()} iP</span>
            <span style={{ color: "rgba(251,191,36,0.88)" }}>
              {" "}{t("Admin.older_manual_or_bot_actions_were_not_fully_logge", lang)}
            </span>
          </div>
        </div>
      )}
      {history.length === 0 ? (
        <div className="text-sm py-3" style={{ color: "var(--text-secondary)" }}>{t("Admin.no_history_yet", lang)}</div>
      ) : (
        <div className={`space-y-1.5 overflow-y-auto pe-1 ${containerMaxHeight}`}>
          {history.map((entry) => {
            const cfg = getIpointsHistoryCfg(entry);
            const Icon = cfg.icon;
            const isGain = Number(entry?.delta || 0) > 0;
            const isSpend = Number(entry?.delta || 0) < 0;
            const statusMeta = getIpointsHistoryStatusMeta(lang, entry?.status);
            const label = entry?.label?.[lang] || entry?.label?.en || entry?.source_type || "iPoints";
            const details = entry?.details || {};
            const summaryBits = [];
            if (["topup", "topup_request", "referral"].includes(entry?.source_type)) {
              if (details.amount_ils || details.ipoints_amount) {
                summaryBits.push(`₪${Number(details.amount_ils || 0).toLocaleString()} -> ${Number(details.ipoints_amount || Math.abs(entry?.delta || 0)).toLocaleString()} iP`);
              }
              if (entry?.source_type === "referral" && (details.user_name || details.user_email)) {
                summaryBits.push(L(lang, `from ${details.user_name || details.user_email}`, `от ${details.user_name || details.user_email}`, `מ-${details.user_name || details.user_email}`));
              }
              const bonusBits = [];
              if (details.large_topup_bonus) bonusBits.push(`large +${Number(details.large_topup_bonus).toLocaleString()}`);
              if (details.promo_bonus) bonusBits.push(`promo +${Number(details.promo_bonus).toLocaleString()}`);
              if (details.global_bonus) bonusBits.push(`global +${Number(details.global_bonus).toLocaleString()}`);
              if (bonusBits.length) summaryBits.push(bonusBits.join(" · "));
              if (details.promo_code) summaryBits.push(`promo ${details.promo_code}`);
            }
            if (["order", "refund"].includes(entry?.source_type)) {
              if (details.order_number || details.order_id) summaryBits.push(`#${details.order_number || details.order_id}`);
              if (details.status) summaryBits.push(details.status);
            }
            if (entry?.source_type === "promo" && details.code) {
              summaryBits.push(details.code);
            }
            if (entry?.source_type === "task") {
              const taskTitle = details.task_title?.[lang] || details.task_title?.en;
              if (taskTitle) summaryBits.push(taskTitle);
              if (details.bonus_given) summaryBits.push(`bonus +${Number(details.bonus_given).toLocaleString()} iP`);
            }
            if (Number(entry?.delta || 0) !== 0 && entry?.balance_before !== undefined && entry?.balance_after !== undefined) {
              summaryBits.push(`${Number(entry.balance_before || 0).toLocaleString()} -> ${Number(entry.balance_after || 0).toLocaleString()} iP`);
            }
            return (
              <div key={entry.history_id || entry.ledger_id} className={`rounded-xl border ${compact ? "px-2.5 py-2" : "px-3 py-2.5"}`} style={{ background: "var(--surface-1)", borderColor: "var(--surface-2)" }}>
                <div className="flex items-start gap-2.5">
                  <div className={`${compact ? "w-6 h-6" : "w-7 h-7"} rounded-lg flex-shrink-0 flex items-center justify-center`} style={{ background: `${cfg.color}18`, color: cfg.color }}>
                    <Icon className={compact ? "w-3 h-3" : "w-3.5 h-3.5"} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className={`${compact ? "text-xs" : "text-sm"} font-medium truncate`}>{label}</div>
                      {statusMeta && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: statusMeta.bg, color: statusMeta.color }}>
                          {statusMeta.label}
                        </span>
                      )}
                      {entry?.is_reconstructed && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium" style={{ background: "rgba(122,162,255,0.12)", color: "#7aa2ff" }}>
                          {t("Admin.restored", lang)}
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] mt-0.5" style={{ color: "var(--text-secondary)" }}>{formatIsraelDateTime(entry.created_at)}</div>
                    {summaryBits.length > 0 && (
                      <div className="text-[11px] mt-1 leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                        {summaryBits.join(" · ")}
                      </div>
                    )}
                    {details.admin_note && (
                      <div className="text-[11px] mt-1 leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                        {t("Admin.note", lang)}: {details.admin_note}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {isGain ? <TrendingUp className={`${compact ? "w-3 h-3" : "w-3.5 h-3.5"} text-[#00C853]`} /> : isSpend ? <TrendingDown className={`${compact ? "w-3 h-3" : "w-3.5 h-3.5"} text-[#E1306C]`} /> : null}
                    {Number(entry?.delta || 0) !== 0 ? (
                      <span className={`${compact ? "text-sm" : "text-base"} font-black font-outfit`} style={{ color: isGain ? "#00C853" : "#E1306C" }}>
                        {isGain ? "+" : ""}{Number(entry.delta || 0).toLocaleString()}
                      </span>
                    ) : (
                      <span className="text-[10px] font-semibold" style={{ color: "var(--text-secondary)" }}>0 iP</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
const resolveSupportAttachmentUrl = (url) => (
  !url ? "" : url.startsWith("http") ? url : `${BACKEND}${url}`
);
const canRetryProvider = (order) => (
  ["rejected", "cancelled_admin"].includes(order?.status)
  || ["error", "skipped", "pending", "not_configured", "rejected", "cancelled", "cancelled_admin", "partial"].includes(order?.provider_sync_status)
  || !(order?.provider_order_ids?.length)
  || (order?.provider_orders || []).some((entry) => ["error", "skipped"].includes(entry?.status))
  || (order?.provider_orders || []).some((entry) => ["rejected", "cancelled", "partial", "error"].includes(entry?.provider_status_bucket))
);
const canCancelProvider = (order) => (
  ["approved", "processing"].includes(order?.status)
  && (order?.provider_order_ids?.length || (order?.provider_orders || []).some((entry) => entry?.provider_order_id))
);
const canManualResolve = (order) => (
  ["approved", "processing"].includes(order?.status)
  && !order?.needs_admin_review
);

const compressImage = (file, maxPx = 1280, quality = 0.82) =>
  new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const s = Math.min(1, maxPx / Math.max(img.width, img.height, 1));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * s);
      canvas.height = Math.round(img.height * s);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(
        (blob) => resolve(new File([blob], file.name.replace(/\.[^.]+$/, ".jpg"), { type: "image/jpeg" })),
        "image/jpeg", quality
      );
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });

function SupportAttachmentGrid({ attachments, lang, onImageClick }) {
  if (!attachments?.length) return null;
  return (
    <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
      {attachments.map((attachment, index) => (
        <button
          key={`${attachment.url}-${index}`}
          type="button"
          onClick={() => onImageClick?.(resolveSupportAttachmentUrl(attachment.url))}
          className="group relative block overflow-hidden rounded-2xl border border-white/10 bg-black/20 cursor-zoom-in"
        >
          <img
            src={resolveSupportAttachmentUrl(attachment.url)}
            alt={attachment.filename || `attachment-${index + 1}`}
            className="h-24 w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
            loading="lazy"
          />
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-2 pb-2 pt-6 text-[11px] text-white/90">
            {attachment.filename || `${t("Admin.image", lang)} ${index + 1}`}
          </div>
        </button>
      ))}
    </div>
  );
}

const CRM_ORDER_STATUS_META = {
  approved: { color: "#7aa2ff", bg: "rgba(64,93,230,0.14)", border: "rgba(122,162,255,0.25)" },
  processing: { color: "#F77737", bg: "rgba(247,119,55,0.14)", border: "rgba(247,119,55,0.25)" },
  completed: { color: "#22c55e", bg: "rgba(34,197,94,0.14)", border: "rgba(34,197,94,0.25)" },
  rejected: { color: "#E1306C", bg: "rgba(225,48,108,0.14)", border: "rgba(225,48,108,0.25)" },
  cancelled_admin: { color: "#9ca3af", bg: "rgba(156,163,175,0.08)", border: "rgba(156,163,175,0.18)" },
};
const getCrmOrderSC = (status) => CRM_ORDER_STATUS_META[status] || { color: "#facc15", bg: "rgba(250,204,21,0.14)", border: "rgba(250,204,21,0.25)" };
const crmOrderStatusLabel = (lang, status) => ({
  draft: t("Admin.draft", lang),
  approved: t("Admin.sent", lang),
  processing: t("Admin.processing", lang),
  completed: t("Admin.completed", lang),
  rejected: t("Admin.rejected", lang),
  cancelled_admin: t("Admin.cancelled", lang),
}[status] || status || "—");

function AdminTicketsDialogs({
  lang,
  profileOpen,
  setProfileOpen,
  userDetailsLoading,
  selectedUserDetails,
  selectedRoleBadge,
  expandedOrderId,
  setExpandedOrderId,
  noRespModal,
  setNoRespModal,
  confirmNoResponse,
}) {
  return (
    <>
      <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
        <DialogContent className="border-white/10 max-w-3xl max-h-[88vh] overflow-y-auto" style={{ background: "var(--bg-primary)" }}>
          {userDetailsLoading ? (
            <div className="py-20 flex items-center justify-center">
              <div className="animate-spin h-8 w-8 border-2 border-[#405DE6] border-t-transparent rounded-full" />
            </div>
          ) : selectedUserDetails ? (
            <div className="space-y-4">
              <DialogHeader>
                <DialogTitle className="text-base">{t("Admin.user_profile", lang)}</DialogTitle>
              </DialogHeader>

              <div className="flex items-start gap-4 rounded-2xl p-4 border" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
                {selectedUserDetails.user?.picture
                  ? <img src={selectedUserDetails.user.picture} alt="" className="w-16 h-16 rounded-2xl object-cover flex-shrink-0" />
                  : <div className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0 font-black text-xl" style={{ background: "rgba(225,48,108,0.12)", color: "#E1306C" }}>{(selectedUserDetails.user?.name || selectedUserDetails.user?.email || "?")[0].toUpperCase()}</div>}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-lg font-black truncate">{selectedUserDetails.user?.name || selectedUserDetails.user?.email}</div>
                    <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: selectedRoleBadge.bg, color: selectedRoleBadge.color }}>{selectedRoleBadge.text}</span>
                    {selectedUserDetails.user?.is_blocked && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/12 text-red-400">{t("Admin.blocked", lang)}</span>}
                  </div>
                  <div className="text-sm mt-1 break-all" style={{ color: "var(--text-secondary)" }}>{selectedUserDetails.user?.email}</div>
                  <div className="text-[11px] mt-2 flex flex-wrap gap-x-3 gap-y-1" style={{ color: "var(--text-secondary)" }}>
                    <span>{t("Admin.joined", lang)}: {formatDateTime(selectedUserDetails.registration?.created_at)}</span>
                    <span>{t("Admin.last_login", lang)}: {selectedUserDetails.registration?.last_login_at ? formatDateTime(selectedUserDetails.registration.last_login_at) : "—"}</span>
                    <span>IP: {selectedUserDetails.registration?.ip || "—"}</span>
                    <span>{t("Admin.auth", lang)}: {selectedUserDetails.registration?.auth_provider || "—"}</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  { label: t("Admin.balance", lang), value: `${selectedUserDetails.user?.ipoints || 0} iP`, color: "#F77737" },
                  { label: t("admin.orders", lang), value: selectedUserDetails.order_stats?.total || 0, color: "#E1306C" },
                  { label: t("Admin.top_ups", lang), value: `${selectedUserDetails.topup_stats?.approved_amount_ils || 0} ₪`, color: "#00C853" },
                  { label: t("nav.tasks", lang), value: selectedUserDetails.task_stats?.approved || 0, color: "#833AB4" },
                  { label: t("Admin.tickets", lang), value: selectedUserDetails.ticket_stats?.total || 0, color: "#405DE6" },
                  { label: t("Admin.referrals", lang), value: selectedUserDetails.referral_stats?.referral_count || 0, color: "#c084fc" },
                  { label: t("Admin.daily_earned", lang), value: `${selectedUserDetails.user?.daily_reward_total || 0} iP`, color: "#22c55e" },
                  { label: t("Admin.login_streak", lang), value: `${selectedUserDetails.user?.login_streak || 0} ${t("Admin.d", lang)}`, color: "#F77737" },
                ].map((item) => (
                  <div key={item.label} className="rounded-2xl px-3 py-3 border" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
                    <div className="text-[11px]" style={{ color: "var(--text-secondary)" }}>{item.label}</div>
                    <div className="text-base font-black mt-1" style={{ color: item.color }}>{item.value}</div>
                  </div>
                ))}
              </div>

              {selectedUserDetails.recent_orders?.length > 0 && (
                <div className="rounded-2xl p-4 border" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
                  <div className="font-semibold mb-3">{t("admin.orders", lang)} <span className="text-xs font-normal ms-1" style={{ color: "var(--text-secondary)" }}>({selectedUserDetails.recent_orders.length})</span></div>
                  <div className="space-y-1.5 max-h-80 overflow-y-auto pe-1">
                    {selectedUserDetails.recent_orders.map((order) => {
                      const sc = getCrmOrderSC(order.status);
                      const isOpen = expandedOrderId === order.order_id;
                      return (
                        <div key={order.order_id} className="rounded-xl overflow-hidden border" style={{ borderColor: isOpen ? sc.border : "var(--border-color)", background: "var(--surface-2)" }}>
                          <button type="button" onClick={() => setExpandedOrderId(isOpen ? null : order.order_id)} className="w-full px-3 py-2 text-start flex items-center justify-between gap-2 hover:bg-black/5 dark:hover:bg-white/5 transition-colors">
                            <div className="flex items-center gap-2.5 min-w-0">
                              <span className="font-black flex-shrink-0" style={{ color: sc.color }}>#{order.order_number}</span>
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0" style={{ background: sc.bg, color: sc.color }}>{crmOrderStatusLabel(lang, order.status)}</span>
                              <span className="text-xs truncate" style={{ color: "var(--text-secondary)" }}>{(order.items || []).map((item) => `${item.category === "followers" ? "👥" : "❤️"} ${item.quantity?.toLocaleString?.()}`).join("·")}</span>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="text-[10px]" style={{ color: "var(--text-secondary)" }}>{formatDateTime(order.created_at)}</span>
                              {isOpen ? <ChevronUp className="w-3.5 h-3.5 opacity-40" /> : <ChevronDown className="w-3.5 h-3.5 opacity-40" />}
                            </div>
                          </button>
                          {isOpen && (
                            <div className="px-3 pb-3 border-t space-y-2" style={{ borderColor: "var(--border-color)" }}>
                              <div className="flex items-center justify-between pt-2 text-xs">
                                <span style={{ color: "var(--text-secondary)" }}>{order.is_free_gift ? "🎁" : `${order.total_ipoints} iP`} · {order.order_id}</span>
                                {order.provider_sync_status && <span className="text-[10px]" style={{ color: "var(--text-secondary)" }}>🔌 {order.provider_sync_status}</span>}
                              </div>
                              {(order.items || []).map((item, index) => (
                                <div key={index} className="rounded-lg p-2.5 border" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
                                  <div className="flex items-center justify-between gap-2 mb-1">
                                    <span className="text-sm font-semibold">{item.category === "followers" ? "👥" : "❤️"} {item.product_name?.[lang] || item.product_name?.en}</span>
                                    <span className="font-bold text-sm flex-shrink-0" style={{ color: item.category === "followers" ? "#A970FF" : "#E1306C" }}>×{item.quantity?.toLocaleString?.()}</span>
                                  </div>
                                  {item.instagram_link && <a href={ensureHttps(item.instagram_link)} target="_blank" rel="noopener noreferrer" className="text-xs hover:underline block truncate" style={{ color: "#7aa2ff" }}>🔗 {item.instagram_link}</a>}
                                  {item.instagram_link_2 && <a href={ensureHttps(item.instagram_link_2)} target="_blank" rel="noopener noreferrer" className="text-xs hover:underline block truncate" style={{ color: "#7aa2ff" }}>❤️ {item.instagram_link_2}</a>}
                                  {(item.instagram_links_extra || []).map((link, linkIndex) => <a key={linkIndex} href={ensureHttps(link)} target="_blank" rel="noopener noreferrer" className="text-xs hover:underline block truncate" style={{ color: "#7aa2ff" }}>❤️ {link}</a>)}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="grid gap-3 md:grid-cols-2">
                <div className="rounded-2xl p-4 border" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
                  <div className="font-semibold mb-3">{t("Admin.referral_info", lang)}</div>
                  <div className="text-sm">{t("Admin.code", lang)}: {selectedUserDetails.referral_stats?.referral_code || "—"}</div>
                  <div className="text-sm mt-1">{selectedUserDetails.referral_stats?.referred_by_user ? `${t("Admin.referred_by", lang)}: ${selectedUserDetails.referral_stats.referred_by_user.name || selectedUserDetails.referral_stats.referred_by_user.email}` : t("Admin.no_referrer", lang)}</div>
                  <div className="text-sm mt-1">{t("Admin.referrals_2", lang)}: {selectedUserDetails.referral_stats?.referral_count || 0}</div>
                </div>
                <div className="rounded-2xl p-4 border" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
                  <div className="font-semibold mb-3">{t("Admin.top_up_bonuses", lang)}</div>
                  <div className="text-sm">{t("Admin.large_top_up", lang)}: +{selectedUserDetails.topup_bonus_profile?.large_topup_bonus_pct || 0}% {t("Admin.from", lang)} {selectedUserDetails.topup_bonus_profile?.large_topup_bonus_threshold_ils || 0} ₪</div>
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <div className="rounded-2xl p-4 border" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
                  <div className="font-semibold mb-3">{t("Admin.recent_promo_uses", lang)}</div>
                  <div className="space-y-2 max-h-40 overflow-y-auto pe-1">
                    {selectedUserDetails.recent_promo_uses?.length ? selectedUserDetails.recent_promo_uses.map((promoUse, index) => (
                      <div key={`${promoUse.promo_id}-${index}`} className="rounded-xl px-3 py-2 border" style={{ background: "var(--surface-2)", borderColor: "var(--border-color)" }}>
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold">{promoUse.code || promoUse.promo_id || "—"}</span>
                          <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: "rgba(0,200,83,0.1)", color: "#22c55e" }}>+{promoUse.bonus || 0} iP</span>
                        </div>
                        <div className="text-[11px] mt-1" style={{ color: "var(--text-secondary)" }}>{formatDateTime(promoUse.created_at)}</div>
                      </div>
                    )) : <div className="text-sm" style={{ color: "var(--text-secondary)" }}>{t("Admin.no_promo_usage_yet", lang)}</div>}
                  </div>
                </div>
                <div className="rounded-2xl p-4 border" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
                  <div className="font-semibold mb-3">{t("Admin.achievements", lang)}</div>
                  <div className="space-y-2 max-h-40 overflow-y-auto pe-1">
                  </div>
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <div className="rounded-2xl p-4 border" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
                  <div className="font-semibold mb-3">{t("Admin.recent_task_submissions", lang)}</div>
                  <div className="space-y-2 max-h-40 overflow-y-auto pe-1">
                    {selectedUserDetails.recent_task_submissions?.length ? selectedUserDetails.recent_task_submissions.map((submission) => (
                      <div key={submission.submission_id} className="rounded-xl px-3 py-2 border" style={{ background: "var(--surface-2)", borderColor: "var(--border-color)" }}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0"><TaskTypeIcon type={submission.task_type} className="w-4 h-4 flex-shrink-0" /><span className="font-semibold truncate">{submission.task_title?.[lang] || submission.task_title?.en || submission.task_id}</span></div>
                          <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: submission.status === "approved" ? "rgba(34,197,94,0.12)" : submission.status === "rejected" ? "rgba(239,68,68,0.12)" : "rgba(250,204,21,0.1)", color: submission.status === "approved" ? "#22c55e" : submission.status === "rejected" ? "#f87171" : "#facc15" }}>{submission.status}</span>
                        </div>
                        <div className="text-[11px] mt-1" style={{ color: "var(--text-secondary)" }}>{formatDateTime(submission.created_at)}</div>
                      </div>
                    )) : <div className="text-sm" style={{ color: "var(--text-secondary)" }}>{t("Admin.no_task_submissions_yet", lang)}</div>}
                  </div>
                </div>
                <div className="rounded-2xl p-4 border" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
                  <div className="font-semibold mb-3">{t("Admin.recent_referrals", lang)}</div>
                  <div className="space-y-2 max-h-40 overflow-y-auto pe-1">
                    {selectedUserDetails.referral_stats?.recent_referrals?.length ? selectedUserDetails.referral_stats.recent_referrals.map((refUser) => (
                      <div key={refUser.user_id} className="rounded-xl px-3 py-2 border" style={{ background: "var(--surface-2)", borderColor: "var(--border-color)" }}>
                        <div className="font-semibold truncate">{refUser.name || refUser.email}</div>
                        <div className="text-[11px] truncate" style={{ color: "var(--text-secondary)" }}>{refUser.email}</div>
                        <div className="text-[11px] mt-1" style={{ color: "var(--text-secondary)" }}>{formatDateTime(refUser.created_at)}</div>
                      </div>
                    )) : <div className="text-sm" style={{ color: "var(--text-secondary)" }}>{t("Admin.no_referrals_yet", lang)}</div>}
                  </div>
                </div>
              </div>

              {(() => {
                const history = selectedUserDetails.ipoints_history || selectedUserDetails.ipoints_ledger || [];
                return (
                  <div className="rounded-2xl p-4 border" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
                    <IpointsHistoryPanel
                      lang={lang}
                      history={history}
                      meta={selectedUserDetails.ipoints_history_meta}
                      balance={selectedUserDetails.user?.ipoints || 0}
                      compact
                    />
                  </div>
                );
              })()}

              {/* Onboarding survey */}
              <div className="rounded-2xl p-4 border" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
                <div className="font-semibold mb-3 flex items-center gap-2">
                  📋 Анкета пользователя
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-medium"
                    style={{
                      background: selectedUserSurvey?.status === "completed" ? "rgba(34,197,94,0.12)" : selectedUserSurvey?.status === "skipped" ? "rgba(250,204,21,0.1)" : "rgba(148,163,184,0.12)",
                      color: selectedUserSurvey?.status === "completed" ? "#22c55e" : selectedUserSurvey?.status === "skipped" ? "#facc15" : "var(--text-secondary)",
                    }}>
                    {selectedUserSurvey?.status === "completed" ? "✓ Заполнена" : selectedUserSurvey?.status === "skipped" ? "⏭ Пропущена" : "Не заполнена"}
                  </span>
                </div>
                {selectedUserSurvey?.status === "completed" && selectedUserSurvey.answers ? (
                  <div className="space-y-2 text-sm">
                    {selectedUserSurvey.answers.heard_about_us && <div><span style={{ color: "var(--text-secondary)" }}>Откуда узнал: </span><span className="font-medium">{selectedUserSurvey.answers.heard_about_us}</span></div>}
                    {selectedUserSurvey.answers.invited_by && <div><span style={{ color: "var(--text-secondary)" }}>Пригласил: </span><span className="font-medium">{selectedUserSurvey.answers.invited_by}</span></div>}
                    {selectedUserSurvey.answers.platforms?.length > 0 && <div><span style={{ color: "var(--text-secondary)" }}>Платформы: </span><span className="font-medium">{selectedUserSurvey.answers.platforms.join(", ")}</span></div>}
                    {selectedUserSurvey.answers.age_category && <div><span style={{ color: "var(--text-secondary)" }}>Возраст: </span><span className="font-medium">{selectedUserSurvey.answers.age_category}</span></div>}
                    {selectedUserSurvey.answers.main_interest && <div><span style={{ color: "var(--text-secondary)" }}>Интерес: </span><span className="font-medium">{selectedUserSurvey.answers.main_interest}</span></div>}
                    {selectedUserSurvey.answers.optional_comment && <div><span style={{ color: "var(--text-secondary)" }}>Комментарий: </span><span className="font-medium">{selectedUserSurvey.answers.optional_comment}</span></div>}
                    {selectedUserSurvey.submitted_at && <div className="text-[11px] mt-2" style={{ color: "var(--text-secondary)" }}>{formatDateTime(selectedUserSurvey.submitted_at)}</div>}
                  </div>
                ) : (
                  <div className="text-sm" style={{ color: "var(--text-secondary)" }}>
                    {selectedUserSurvey?.status === "skipped" ? "Пользователь пропустил анкету" : "Пользователь ещё не заполнял анкету"}
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {noRespModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }} onClick={() => setNoRespModal(null)}>
          <div className="rounded-2xl p-5 w-full max-w-sm space-y-4 shadow-2xl" style={{ background: "var(--bg-primary)", border: "1px solid var(--border-color)" }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-bold text-sm" style={{ color: "var(--text-primary)" }}>{t("Admin.send_in_which_language", lang)}</div>
                <div className="text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>{t("Admin.the_message_will_be_sent_to_the_user_before_clos", lang)}</div>
              </div>
              <button onClick={() => setNoRespModal(null)} className="w-7 h-7 rounded-lg flex items-center justify-center transition-colors ms-3" style={{ color: "var(--text-secondary)" }}><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-2">
              {[{ code: "en", flag: "🇬🇧", label: "English", preview: "This ticket has been closed due to lack of response. If you need further help, please open a new chat." }, { code: "ru", flag: "🇷🇺", label: "Русский", preview: "Этот чат закрыт в связи с отсутствием ответа. Если вам нужна помощь — откройте новый чат." }, { code: "he", flag: "🇮🇱", label: "עברית", preview: "הכרטיס נסגר עקב חוסר מענה. אם תצטרך עזרה נוספת, ניתן לפתוח צ'אט חדש.", dir: "rtl" }].map(({ code, flag, label, preview, dir }) => (
                <button key={code} onClick={() => confirmNoResponse(noRespModal, code)} className="w-full rounded-xl px-3 py-3 text-start border transition-colors group hover:bg-black/5 dark:hover:bg-white/5" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-base">{flag}</span>
                    <span className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>{label}</span>
                    <span className="ms-auto text-[10px] px-2 py-0.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: "rgba(122,162,255,0.1)", color: "#7aa2ff" }}>{t("Admin.send", lang)}</span>
                  </div>
                  <div className="text-[11px] leading-relaxed" dir={dir || "ltr"} style={{ color: "var(--text-secondary)" }}>{preview}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function AdminTicketsWorkspaceV2({ lang, crm }) {
  const {
    onBack,
    onNavigate,
    lightboxUrl,
    setLightboxUrl,
    selectedId,
    setSelectedId,
    selected,
    selectedMessages,
    lastVisibleAdminMessage,
    selectedSla,
    selectedConversationPresence,
    selectedAdminTypers,
    selectedAdminViewers,
    visibleTickets,
    totalUnread,
    ticketStats,
    filter,
    setFilter,
    ticketSearch,
    setTicketSearch,
    FILTER_DEFS,
    statsPopup,
    setStatsPopup,
    presence,
    userPresence,
    scOf,
    stLabel,
    relT,
    pColor,
    pLabel,
    myQueueCount,
    snoozedCount,
    inboxSummary,
    selectTicket,
    refreshing,
    setRefreshing,
    fetch_,
    fetchStats,
    fetchSelectedFull,
    openMacrosPanel,
    openCustomerPanel,
    sidePanelOpen,
    setSidePanelOpen,
    cannedSettings,
    setCannedSettings,
    selectedUserDetails,
    openProfileModal,
    assignDropdown,
    setAssignDropdown,
    priorityDropdown,
    setPriorityDropdown,
    tagDropdown,
    setTagDropdown,
    adminList,
    assign,
    fetchUserDetails,
    closing,
    close,
    closeNoResponse,
    reopening,
    reopen,
    snoozeOpen,
    setSnoozeOpen,
    snoozeTicket,
    snoozeUntilTomorrow,
    snoozeCustom,
    setSnoozeCustom,
    unsnoozeTicket,
    editingSubject,
    setEditingSubject,
    subjectDraft,
    setSubjectDraft,
    patchMeta,
    toggleTag,
    msgRef,
    currentAdmin,
    renderChatText,
    userSeenAt,
    noteMode,
    setNoteMode,
    aiModal,
    setAiModal,
    aiMode,
    setAiMode,
    aiLang,
    setAiLang,
    aiPrompt,
    setAiPrompt,
    aiSuggest,
    aiSuggesting,
    navDropdown,
    setNavDropdown,
    cannedDropdown,
    setCannedDropdown,
    cannedMatches,
    cannedSearch,
    cannedSelectedIdx,
    setCannedSelectedIdx,
    insertCanned,
    insertNavShortcut,
    handleReplyChange,
    replyTextareaRef,
    selectedReplyValue,
    selectedReplyAttachments,
    setReplyAtts,
    reply,
    sendNote,
    sending,
    uploading,
    uploadFiles,
    renderMacrosSidebar,
    renderCustomerSidebar,
  } = crm;

  const refreshWorkspace = async () => {
    setRefreshing(true);
    await Promise.all([fetch_(), fetchStats(), selectedId ? fetchSelectedFull(selectedId) : Promise.resolve()]);
    setRefreshing(false);
  };

  return (
    <div className="animate-fade-in-up overflow-hidden flex flex-col h-full" style={{ background: "var(--bg-primary)" }}>
      {lightboxUrl && <Lightbox src={lightboxUrl} onClose={() => setLightboxUrl("")} />}

      <div className="flex flex-1 min-h-0 flex-col gap-3 p-3 md:p-4" style={{ background: "var(--bg-primary)" }}>
        <div className="rounded-2xl border px-4 py-4 md:px-5 md:py-4" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
          <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: "var(--surface-2)", color: "var(--text-primary)" }}>
                  <MessageSquare className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.18em]" style={{ color: "var(--text-secondary)" }}>CRM Workspace</div>
                  <div className="text-lg md:text-xl font-black truncate" style={{ color: "var(--text-primary)" }}>{t("Admin.support_operations", lang)}</div>
                </div>
              </div>
              <p className="text-[12px] mt-3 max-w-2xl" style={{ color: "var(--text-secondary)" }}>
                {t("Admin.inbox_triage_live_conversations_macros_and_custo", lang)}
              </p>
            </div>
            <div className="flex flex-col gap-3 xl:items-end">
              <div className="flex flex-wrap gap-2">
                {inboxSummary.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.label} className="min-w-[108px] rounded-2xl border px-3 py-2.5" style={{ background: "var(--surface-2)", borderColor: "var(--border-color)" }}>
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-xl flex items-center justify-center" style={{ background: `${item.color}18`, color: item.color }}>
                          <Icon className="w-3.5 h-3.5" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--text-secondary)" }}>{item.label}</div>
                          <div className="text-sm font-black" style={{ color: "var(--text-primary)" }}>{item.value}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={refreshWorkspace} className="rounded-xl h-8 px-3 text-[12px] font-semibold flex items-center gap-2 transition-all" style={{ background: "var(--surface-2)", color: "var(--text-secondary)", border: "1px solid var(--border-color)" }}>
                  <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`} />
                  {t("Admin.refresh", lang)}
                </button>
                <button onClick={openMacrosPanel} className="rounded-xl h-8 px-3 text-[12px] font-semibold flex items-center gap-2 transition-all" style={{ background: cannedSettings ? "rgba(139,92,246,0.12)" : "var(--surface-2)", color: cannedSettings ? "#8B5CF6" : "var(--text-secondary)", border: "1px solid var(--border-color)" }}>
                  <Settings className="w-3.5 h-3.5" />
                  {t("Admin.macros", lang)}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-1 min-h-0 gap-3">
          <div className={`${selectedId ? "hidden md:flex" : "flex"} w-full md:w-[330px] xl:w-[360px] flex-shrink-0 flex-col rounded-2xl border overflow-hidden`} style={{ background: "var(--bg-secondary)", borderColor: "var(--border-color)" }}>
            <div className="flex-shrink-0 px-4 py-4 border-b" style={{ borderColor: "var(--border-color)" }}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2.5 min-w-0">
                  {onBack && (
                    <button onClick={onBack} className="w-9 h-9 rounded-2xl flex items-center justify-center transition-all" style={{ background: "var(--surface-2)", color: "var(--text-secondary)" }}>
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                  )}
                  <div className="min-w-0">
                    <div className="text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: "#8B5CF6" }}>{t("Admin.inbox", lang)}</div>
                    <div className="text-sm font-semibold mt-0.5" style={{ color: "var(--text-primary)" }}>{t("Admin.live_support_queue", lang)}</div>
                    <div className="text-[11px] mt-1" style={{ color: "var(--text-secondary)" }}>{t("Admin.unread_urgent_and_assigned_conversations_stay_vi", lang)}</div>
                  </div>
                </div>
                <div className="rounded-2xl px-3 py-2 text-end" style={{ background: "rgba(225,48,108,0.08)", color: "#E1306C" }}>
                  <div className="text-[10px] uppercase tracking-[0.18em]">{t("Admin.unread", lang)}</div>
                  <div className="text-sm font-black">{totalUnread}</div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2 mt-4">
                {[
                  { label: t("Admin.mine", lang), value: myQueueCount, color: "#22c55e" },
                  { label: t("Admin.snoozed", lang), value: snoozedCount, color: "#8b5cf6" },
                  { label: "CSAT", value: ticketStats?.csat != null ? `${ticketStats.csat}%` : "—", color: "#F77737" },
                ].map((item) => (
                  <div key={item.label} className="rounded-2xl px-3 py-2.5 border" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
                    <div className="text-[10px]" style={{ color: "var(--text-secondary)" }}>{item.label}</div>
                    <div className="text-sm font-black mt-1" style={{ color: item.color }}>{item.value}</div>
                  </div>
                ))}
              </div>

              <div className="flex gap-1.5 overflow-x-auto scrollbar-none pb-1 mt-4">
                {FILTER_DEFS.map((item) => {
                  const active = filter === item.key;
                  return (
                    <button key={item.key} onClick={() => setFilter(item.key)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap transition-all" style={{ background: active ? "#8B5CF6" : "var(--surface-2)", color: active ? "#fff" : "var(--text-secondary)" }}>
                      <span>{item.label}</span>
                      {item.count > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full text-[9px] font-black flex items-center justify-center" style={{ background: active ? "rgba(255,255,255,0.18)" : "rgba(122,162,255,0.12)", color: active ? "#fff" : "#7aa2ff" }}>{item.count}</span>}
                    </button>
                  );
                })}
              </div>

              <div className="mt-3 flex items-center gap-2 px-3 py-2 rounded-2xl border" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
                <Search className="w-4 h-4 flex-shrink-0" style={{ color: "var(--text-secondary)" }} />
                <input value={ticketSearch} onChange={(e) => setTicketSearch(e.target.value)} placeholder={t("Admin.search_name_subject_or_assignee", lang)} className="flex-1 bg-transparent text-[12px] outline-none" style={{ color: "var(--text-primary)" }} />
                {ticketSearch && <button onClick={() => setTicketSearch("")} style={{ color: "var(--text-secondary)" }}><X className="w-3.5 h-3.5" /></button>}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-2">
              {visibleTickets.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full px-5 text-center">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ background: "var(--surface-2)", color: "var(--text-secondary)" }}>
                    <Ticket className="w-7 h-7" />
                  </div>
                  <div className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>{t("Admin.inbox_is_clear", lang)}</div>
                  <div className="text-[12px] mt-1" style={{ color: "var(--text-secondary)" }}>{t("Admin.no_conversations_match_the_current_filter", lang)}</div>
                </div>
              ) : (
                <div className="space-y-2">
                  {visibleTickets.map((ticket) => {
                    const statusMeta = scOf(ticket.status);
                    const unread = ticket.admin_unread_count || 0;
                    const isActive = selectedId === ticket.ticket_id;
                    const last = ticket.messages?.[ticket.messages.length - 1];
                    const preview = last?.content?.trim().slice(0, 88) || (last?.attachments?.length ? t("Admin.photo_attachment", lang) : "");
                    const isAdminMsg = last?.sender_type === "admin";
                    const ticketPresence = presence[ticket.ticket_id] || [];
                    const hasTyping = ticketPresence.some((entry) => entry.mode === "typing");
                    const ticketUserPresence = userPresence[ticket.ticket_id];
                    const userTyping = ticketUserPresence?.mode === "typing";
                    return (
                      <button key={ticket.ticket_id} onClick={() => selectTicket(ticket)} className="relative w-full text-start rounded-2xl border p-3 transition-all" style={{ background: isActive ? "rgba(139,92,246,0.06)" : "var(--surface-1)", borderColor: isActive ? "rgba(139,92,246,0.3)" : "var(--border-color)" }} data-testid={`admin-ticket-${ticket.ticket_id}`}>
                        <div className="flex items-start gap-3">
                          <div className="relative flex-shrink-0">
                            {ticket.user_picture
                              ? <img src={ticket.user_picture} alt="" className="w-10 h-10 rounded-2xl object-cover" />
                              : <div className="w-10 h-10 rounded-2xl flex items-center justify-center text-[12px] font-black" style={{ background: `${statusMeta.color}18`, color: statusMeta.color }}>{(ticket.user_name || ticket.user_email || "?")[0].toUpperCase()}</div>}
                            {unread > 0 && <span className="absolute -top-1 -end-1 min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center text-[9px] font-black text-white" style={{ background: "#E1306C" }}>{unread > 9 ? "9+" : unread}</span>}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start gap-2">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className={`text-[12px] truncate ${unread ? "font-black" : "font-semibold"}`} style={{ color: "var(--text-primary)" }}>{ticket.user_name || ticket.user_email}</span>
                                  {ticket.ticket_code && <span className="text-[9px] font-mono flex-shrink-0" style={{ color: "#7aa2ff" }}>{ticket.ticket_code}</span>}
                                </div>
                                <div className="text-[10px] mt-0.5 truncate" style={{ color: "var(--text-secondary)" }}>{ticket.subject || t("Admin.no_subject_yet", lang)}</div>
                              </div>
                              <button onClick={(e) => { e.stopPropagation(); setStatsPopup((current) => current === ticket.ticket_id ? "" : ticket.ticket_id); }} className="text-[10px] font-semibold flex-shrink-0" style={{ color: "var(--text-secondary)" }}>
                                {relT(ticket.updated_at)}
                              </button>
                            </div>
                            <div className="text-[10.5px] leading-relaxed mt-2 min-h-[32px]" style={{ color: userTyping ? "#E1306C" : hasTyping ? "#7aa2ff" : "var(--text-secondary)" }}>
                              {userTyping ? (
                                <span className="flex items-center gap-1.5">
                                  <span className="flex gap-[2px]">{[0, 1, 2].map((step) => <span key={step} className="w-1 h-1 rounded-full inline-block" style={{ background: "#E1306C", animation: `ticketTypingBounce 1s ${step * 0.18}s infinite` }} />)}</span>
                                  {t("Admin.customer_is_typing", lang)}
                                </span>
                              ) : hasTyping ? (
                                <span className="flex items-center gap-1.5">
                                  <span className="flex gap-[2px]">{[0, 1, 2].map((step) => <span key={step} className="w-1 h-1 rounded-full inline-block" style={{ background: "#7aa2ff", animation: `ticketTypingBounce 1s ${step * 0.18}s infinite` }} />)}</span>
                                  {t("Admin.teammate_is_typing", lang)}
                                </span>
                              ) : preview ? (
                                <span>{isAdminMsg && <span style={{ color: "rgba(122,162,255,0.8)" }}>↩ </span>}{preview}</span>
                              ) : "—"}
                            </div>
                            <div className="flex items-center gap-1.5 flex-wrap mt-2">
                              <span className="text-[9px] px-2 py-1 rounded-full font-semibold" style={{ background: statusMeta.bg, color: statusMeta.color }}>{ticket.status === "snoozed" && <Moon className="w-2.5 h-2.5 inline me-1" />}{stLabel(ticket.status)}</span>
                              {ticket.priority && <span className="text-[9px] px-2 py-1 rounded-full font-black" style={{ background: `${pColor(ticket.priority)}18`, color: pColor(ticket.priority) }}>{pLabel(ticket.priority, lang)}</span>}
                              {(ticket.tags || []).slice(0, 1).map((tag) => <span key={tag} className="text-[9px] px-2 py-1 rounded-full font-semibold" style={{ background: `${TAG_COLORS[tag] || "#94a3b8"}18`, color: TAG_COLORS[tag] || "#94a3b8" }}>{tag}</span>)}
                              {ticket.assigned_to && (
                                <span className="ms-auto flex-shrink-0">
                                  {ticket.assigned_to.picture ? <img src={ticket.assigned_to.picture} alt="" className="w-5 h-5 rounded-full object-cover" title={ticket.assigned_to.name} /> : <span className="w-5 h-5 rounded-full inline-flex items-center justify-center text-[9px] font-black" style={{ background: "rgba(122,162,255,0.15)", color: "#7aa2ff" }} title={ticket.assigned_to.name}>{ticket.assigned_to.initials}</span>}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {statsPopup === ticket.ticket_id && (
                          <div className="absolute top-14 z-30 rounded-2xl border p-3 text-start min-w-[200px] shadow-xl" style={{ insetInlineEnd: "0.75rem", background: "var(--bg-primary)", borderColor: "var(--border-color)" }} onClick={(e) => e.stopPropagation()}>
                            <div className="text-[10px] font-bold uppercase tracking-[0.18em] mb-2" style={{ color: "#7aa2ff" }}>{t("Admin.ticket_stats", lang)}</div>
                            <div className="space-y-1 text-[10px]" style={{ color: "var(--text-secondary)" }}>
                              <div>{t("Admin.created", lang)}: <span style={{ color: "var(--text-primary)" }}>{formatIsraelDate(ticket.created_at)}</span></div>
                              <div>{t("Admin.updated", lang)}: <span style={{ color: "var(--text-primary)" }}>{relT(ticket.updated_at)}</span></div>
                              {ticket.first_admin_reply_at && <div>FRT: <span style={{ color: "#22c55e" }}>{resolutionTime(ticket.created_at, ticket.first_admin_reply_at, lang)}</span></div>}
                              {ticket.closed_at && <div>{t("Admin.resolved", lang)}: <span style={{ color: "#f59e0b" }}>{resolutionTime(ticket.created_at, ticket.closed_at, lang)}</span></div>}
                              <div>{t("Admin.messages", lang)}: <span style={{ color: "var(--text-primary)" }}>{ticket.messages?.length || 0}</span></div>
                            </div>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className={`${!selectedId ? "hidden md:flex" : "flex"} flex-1 min-w-0 flex-col rounded-2xl border overflow-hidden`} style={{ background: "var(--bg-primary)", borderColor: "var(--border-color)" }}>
            {!selected ? (
              <div className="flex-1 flex items-center justify-center px-8">
                <div className="max-w-md text-center">
                  <div className="w-20 h-20 rounded-2xl mx-auto flex items-center justify-center mb-5" style={{ background: "var(--surface-2)" }}>
                    <MessageSquare className="w-8 h-8" style={{ color: "#7aa2ff" }} />
                  </div>
                  <div className="text-lg font-black" style={{ color: "var(--text-primary)" }}>{t("Admin.pick_a_conversation", lang)}</div>
                  <p className="text-[13px] mt-2" style={{ color: "var(--text-secondary)" }}>{t("Admin.choose_a_ticket_from_the_inbox_to_open_the_threa", lang)}</p>
                </div>
              </div>
            ) : (
              <>
                <div className="flex-shrink-0 border-b px-4 py-4 md:px-5 md:py-5" style={{ borderColor: "var(--border-color)", background: "var(--bg-secondary)" }}>
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
                      <div className="flex items-start gap-3 min-w-0">
                        <button onClick={() => setSelectedId("")} className="md:hidden w-9 h-9 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: "var(--surface-2)", color: "var(--text-secondary)" }}>
                          <ChevronLeft className="w-4 h-4" />
                        </button>
                        <button type="button" onClick={openProfileModal} className="flex-shrink-0 rounded-2xl overflow-hidden transition-opacity hover:opacity-85">
                          {selectedUserDetails?.user?.picture ? <img src={selectedUserDetails.user.picture} alt="" className="w-12 h-12 object-cover rounded-2xl" /> : <div className="w-12 h-12 rounded-2xl flex items-center justify-center font-black text-sm" style={{ background: `${scOf(selected.status).color}20`, color: scOf(selected.status).color }}>{(selected.user_name || selected.user_email || "?")[0].toUpperCase()}</div>}
                        </button>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap min-w-0">
                            <button type="button" onClick={openProfileModal} className="text-lg font-black truncate text-start hover:opacity-80 transition-opacity" style={{ color: "var(--text-primary)" }}>{selected.user_name || selected.user_email}</button>
                            <span className="text-[10px] px-2 py-1 rounded-full font-semibold" style={{ background: scOf(selected.status).bg, color: scOf(selected.status).color }}>{stLabel(selected.status)}</span>
                            {selected.priority && <span className="text-[10px] px-2 py-1 rounded-full font-black" style={{ background: `${pColor(selected.priority)}18`, color: pColor(selected.priority) }}>{pLabel(selected.priority, lang)}</span>}
                            {selected.rating && <span className="text-[10px] px-2 py-1 rounded-full" style={{ background: selected.rating === "up" ? "rgba(34,197,94,0.12)" : "rgba(239,68,68,0.12)", color: selected.rating === "up" ? "#22c55e" : "#f87171" }}>{selected.rating === "up" ? "👍 CSAT" : "👎 CSAT"}</span>}
                            {selectedConversationPresence?.mode === "typing" && <span className="text-[10px] px-2 py-1 rounded-full font-semibold" style={{ background: "rgba(225,48,108,0.12)", color: "#E1306C" }}>{t("Admin.typing_now", lang)}</span>}
                          </div>
                          <div className="text-[12px] mt-1" style={{ color: "var(--text-secondary)" }}>{selected.user_email}</div>
                          <div className="flex items-center gap-2 flex-wrap mt-2">
                            {selected.ticket_code && <span className="text-[10px] font-mono px-2 py-1 rounded-full" style={{ background: "rgba(122,162,255,0.12)", color: "#7aa2ff" }}>{selected.ticket_code}</span>}
                            <button onClick={() => { setSubjectDraft(selected.subject || ""); setEditingSubject(true); }} className="text-[11px] px-2.5 py-1 rounded-full border truncate max-w-[280px] text-start" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)", color: selected.subject ? "var(--text-secondary)" : "rgba(122,162,255,0.6)" }}>{selected.subject || t("Admin.add_subject", lang)}</button>
                            {selectedAdminViewers.length > 0 && <span className="text-[10px] px-2 py-1 rounded-full" style={{ background: "rgba(34,197,94,0.12)", color: "#22c55e" }}>{selectedAdminViewers.map((viewer) => viewer.name).join(", ")} {t("Admin.viewing", lang)}</span>}
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-2 xl:justify-end">
                        <button onClick={openCustomerPanel} className="xl:hidden rounded-2xl px-3 py-2 text-[11px] font-semibold flex items-center gap-2" style={{ background: "var(--surface-1)", color: "var(--text-secondary)", border: "1px solid var(--border-color)" }}>
                          <User className="w-3.5 h-3.5" />
                          {t("Admin.context", lang)}
                        </button>
                        <button onClick={openMacrosPanel} className="rounded-2xl px-3 py-2 text-[11px] font-semibold flex items-center gap-2" style={{ background: cannedSettings ? "rgba(122,162,255,0.14)" : "var(--surface-1)", color: cannedSettings ? "#7aa2ff" : "var(--text-secondary)", border: "1px solid var(--border-color)" }}>
                          <Settings className="w-3.5 h-3.5" />
                          {t("Admin.macros", lang)}
                        </button>

                        <div className="relative">
                          <button onClick={() => { setAssignDropdown((value) => !value); setPriorityDropdown(false); setTagDropdown(false); }} className="rounded-2xl px-3 py-2 text-[11px] font-semibold flex items-center gap-2" style={{ background: "var(--surface-1)", color: "var(--text-secondary)", border: "1px solid var(--border-color)" }}>
                            {selected.assigned_to?.picture ? <img src={selected.assigned_to.picture} alt="" className="w-4 h-4 rounded-full object-cover" /> : <User className="w-3.5 h-3.5" />}
                            <span>{selected.assigned_to?.initials || t("Admin.assign", lang)}</span>
                          </button>
                          {assignDropdown && (
                            <div className="absolute top-full mt-2 z-30 rounded-2xl border overflow-hidden w-44 shadow-xl" style={{ insetInlineEnd: 0, background: "var(--bg-primary)", borderColor: "var(--border-color)" }}>
                              <button onClick={() => { assign(selected.ticket_id, null); setAssignDropdown(false); }} className="w-full text-start px-3 py-2 text-xs transition-colors" style={{ color: "var(--text-secondary)" }}>{t("Admin.unassigned", lang)}</button>
                              {adminList.map((admin) => (
                                <button key={admin.user_id} onClick={() => { assign(selected.ticket_id, admin.user_id); setAssignDropdown(false); }} className="w-full text-start px-3 py-2 text-xs transition-colors flex items-center gap-2" style={{ color: selected.assigned_to?.user_id === admin.user_id ? "#7aa2ff" : "var(--text-primary)" }}>
                                  {admin.picture ? <img src={admin.picture} alt="" className="w-4 h-4 rounded-full object-cover flex-shrink-0" /> : <span className="w-4 h-4 rounded-full flex items-center justify-center text-[8px] font-bold flex-shrink-0" style={{ background: "rgba(122,162,255,0.15)", color: "#7aa2ff" }}>{admin.initials}</span>}
                                  <span className="truncate">{admin.name}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>

                        {selected.status !== "closed" && selected.status !== "snoozed" && (
                          <div className="relative">
                            <button onClick={() => setSnoozeOpen((value) => !value)} className="rounded-2xl px-3 py-2 text-[11px] font-semibold flex items-center gap-2" style={{ background: "rgba(139,92,246,0.08)", color: "#8b5cf6", border: "1px solid rgba(139,92,246,0.2)" }}>
                              <Moon className="w-3.5 h-3.5" />
                              {t("Admin.snooze", lang)}
                            </button>
                            {snoozeOpen && (
                              <div className="absolute top-full mt-2 end-0 z-30 rounded-2xl p-2 min-w-[190px] shadow-xl flex flex-col gap-1" style={{ background: "var(--bg-primary)", border: "1px solid var(--border-color)" }}>
                                {[{ label: t("Admin.1_hour", lang), min: 60 }, { label: t("Admin.4_hours", lang), min: 240 }, { label: t("Admin.8_hours", lang), min: 480 }].map((option) => <button key={option.min} onClick={() => snoozeTicket(option.min)} className="text-start px-3 py-2 rounded-xl text-xs transition-all" style={{ color: "#8b5cf6" }}>{option.label}</button>)}
                                <button onClick={snoozeUntilTomorrow} className="text-start px-3 py-2 rounded-xl text-xs transition-all" style={{ color: "#8b5cf6" }}>{t("Admin.tomorrow_9_00", lang)}</button>
                                <div className="border-t my-1" style={{ borderColor: "var(--border-color)" }} />
                                <div className="px-2 pb-1">
                                  <div className="relative">
                                    <CalendarClock className="absolute start-2 top-1/2 -translate-y-1/2 w-3 h-3 pointer-events-none" style={{ color: "#8B5CF6", opacity: 0.7 }} />
                                    <input type="datetime-local" value={snoozeCustom} onChange={(e) => setSnoozeCustom(e.target.value)} className="w-full text-xs rounded-xl ps-7 pe-2 py-1.5 outline-none border" style={{ background: "var(--surface-2)", color: "var(--text-primary)", borderColor: snoozeCustom ? "rgba(139,92,246,0.4)" : "var(--border-color)" }} />
                                  </div>
                                  {snoozeCustom && <button onClick={() => { snoozeTicket(Math.max(1, Math.round((new Date(snoozeCustom) - Date.now()) / 60000))); setSnoozeCustom(""); }} className="w-full mt-2 text-xs py-2 rounded-xl font-medium" style={{ background: "rgba(139,92,246,0.15)", color: "#8b5cf6" }}>{t("Admin.set", lang)}</button>}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        {selected.status === "snoozed" && (
                          <button onClick={unsnoozeTicket} className="rounded-2xl px-3 py-2 text-[11px] font-semibold flex items-center gap-2" style={{ background: "rgba(139,92,246,0.08)", color: "#8b5cf6", border: "1px solid rgba(139,92,246,0.2)" }}>
                            <Moon className="w-3.5 h-3.5" />
                            {t("Admin.wake_up", lang)}
                          </button>
                        )}
                        {selected.status !== "closed" && (
                          <button onClick={() => closeNoResponse(selected.ticket_id)} disabled={closing === selected.ticket_id} className="rounded-2xl px-3 py-2 text-[11px] font-semibold flex items-center gap-2 disabled:opacity-50" style={{ background: "rgba(250,204,21,0.10)", color: "#f59e0b", border: "1px solid rgba(250,204,21,0.22)" }}>
                            <Clock className="w-3.5 h-3.5" />
                            {t("Admin.no_reply_close", lang)}
                          </button>
                        )}
                        {selected.status !== "closed" ? (
                          <button onClick={() => close(selected.ticket_id)} disabled={closing === selected.ticket_id} className="rounded-2xl px-3.5 py-2 text-[11px] font-semibold flex items-center gap-2 disabled:opacity-50" style={{ background: "#22c55e", color: "#fff" }}>
                            {closing === selected.ticket_id ? <div className="w-3.5 h-3.5 border-2 border-white/35 border-t-white rounded-full animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                            {t("Admin.resolve", lang)}
                          </button>
                        ) : (
                          <button onClick={() => reopen(selected.ticket_id)} disabled={reopening === selected.ticket_id} className="rounded-2xl px-3.5 py-2 text-[11px] font-semibold flex items-center gap-2 disabled:opacity-50" style={{ background: "rgba(122,162,255,0.12)", color: "#7aa2ff", border: "1px solid rgba(122,162,255,0.2)" }}>
                            {reopening === selected.ticket_id ? <div className="w-3.5 h-3.5 border-2 border-[#7aa2ff]/30 border-t-[#7aa2ff] rounded-full animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                            {t("Admin.reopen", lang)}
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="grid gap-2 md:grid-cols-4">
                      {[
                        { label: selected.status === "closed" ? t("Admin.resolution", lang) : t("Admin.sla_clock", lang), value: selected.status === "closed" ? (resolutionTime(selected.created_at, selected.closed_at, lang) || "—") : (selectedSla?.text || "—"), color: selected.status === "closed" ? "#22c55e" : (selectedSla?.color || "#8B5CF6") },
                        { label: "FRT", value: selected.first_admin_reply_at ? resolutionTime(selected.created_at, selected.first_admin_reply_at, lang) : "—", color: "#F77737" },
                        { label: t("nav.messages", lang), value: selectedMessages.length, color: "#8B5CF6" },
                        { label: t("Admin.last_touch", lang), value: relT(selected.updated_at || selected.created_at), color: "var(--text-secondary)" },
                      ].map((item) => (
                        <div key={item.label} className="rounded-2xl border px-3 py-3" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
                          <div className="text-[10px]" style={{ color: "var(--text-secondary)" }}>{item.label}</div>
                          <div className="text-sm font-black mt-1" style={{ color: item.color }}>{item.value}</div>
                        </div>
                      ))}
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      {editingSubject ? (
                        <div className="flex items-center gap-2 flex-1 min-w-[240px]">
                          <input autoFocus value={subjectDraft} onChange={(e) => setSubjectDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { patchMeta(selected.ticket_id, { subject: subjectDraft }); setEditingSubject(false); } if (e.key === "Escape") setEditingSubject(false); }} className="flex-1 bg-transparent text-[12px] rounded-2xl border px-3 py-2 outline-none" style={{ borderColor: "rgba(122,162,255,0.26)", color: "var(--text-primary)" }} />
                          <button onClick={() => { patchMeta(selected.ticket_id, { subject: subjectDraft }); setEditingSubject(false); }} className="rounded-2xl px-3 py-2 text-[11px] font-semibold" style={{ background: "rgba(122,162,255,0.12)", color: "#7aa2ff" }}>✓</button>
                          <button onClick={() => setEditingSubject(false)} className="rounded-2xl px-3 py-2 text-[11px] font-semibold" style={{ background: "var(--surface-2)", color: "var(--text-secondary)" }}>✕</button>
                        </div>
                      ) : (
                        <button onClick={() => { setSubjectDraft(selected.subject || ""); setEditingSubject(true); }} className="rounded-2xl px-3 py-2 text-[11px] font-semibold text-start max-w-full truncate border" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)", color: selected.subject ? "var(--text-primary)" : "rgba(122,162,255,0.6)" }}>{selected.subject || t("Admin.add_subject_2", lang)}</button>
                      )}

                      <div className="relative">
                        <button onClick={() => { setPriorityDropdown((value) => !value); setTagDropdown(false); }} className="rounded-full px-3 py-2 text-[10px] font-semibold border" style={{ background: selected.priority ? `${pColor(selected.priority)}18` : "var(--surface-1)", color: selected.priority ? pColor(selected.priority) : "var(--text-secondary)", borderColor: selected.priority ? `${pColor(selected.priority)}40` : "var(--border-color)" }}>
                          {selected.priority ? pLabel(selected.priority, lang) : t("Admin.priority", lang)}
                        </button>
                        {priorityDropdown && (
                          <div className="absolute top-full mt-2 z-30 rounded-2xl border overflow-hidden w-32 shadow-xl" style={{ background: "var(--bg-primary)", borderColor: "var(--border-color)" }}>
                            {[null, "low", "medium", "high", "urgent"].map((priority) => (
                              <button key={priority || "none"} onClick={() => { patchMeta(selected.ticket_id, { priority: priority || "" }); setPriorityDropdown(false); }} className="w-full text-start px-3 py-2 text-xs" style={{ color: priority ? pColor(priority) : "var(--text-secondary)" }}>
                                {priority ? pLabel(priority, lang) : t("Admin.none", lang)}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="relative">
                        <button onClick={() => { setTagDropdown((value) => !value); setPriorityDropdown(false); }} className="rounded-full px-3 py-2 text-[10px] font-semibold border" style={{ background: "var(--surface-1)", color: "var(--text-secondary)", borderColor: "var(--border-color)" }}>
                          + {t("Admin.tags", lang)}
                        </button>
                        {tagDropdown && (
                          <div className="absolute top-full mt-2 z-30 rounded-2xl border p-2 w-52 shadow-xl" style={{ background: "var(--bg-primary)", borderColor: "var(--border-color)" }}>
                            <div className="flex flex-wrap gap-1.5">
                              {TICKET_TAGS.map((tag) => {
                                const active = (selected.tags || []).includes(tag);
                                return <button key={tag} onClick={() => toggleTag(selected.ticket_id, tag, selected.tags)} className="text-[10px] px-2 py-1 rounded-full border transition-all" style={{ background: active ? `${TAG_COLORS[tag] || "#94a3b8"}25` : "var(--surface-2)", color: active ? TAG_COLORS[tag] || "#e2e8f0" : "var(--text-secondary)", borderColor: active ? `${TAG_COLORS[tag] || "#94a3b8"}60` : "var(--border-color)", fontWeight: active ? 600 : 400 }}>{tag}</button>;
                              })}
                            </div>
                          </div>
                        )}
                      </div>

                      {(selected.tags || []).map((tag) => (
                        <span key={tag} className="text-[10px] px-2 py-1 rounded-full flex items-center gap-1 font-semibold" style={{ background: `${TAG_COLORS[tag] || "#94a3b8"}18`, color: TAG_COLORS[tag] || "#94a3b8" }}>
                          {tag}
                          <button onClick={() => toggleTag(selected.ticket_id, tag, selected.tags)} className="opacity-70 hover:opacity-100 text-[9px]">✕</button>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div ref={msgRef} className="flex-1 overflow-y-auto px-4 py-4 md:px-6 md:py-5 space-y-4" style={{ background: "var(--bg-primary)" }}>
                  <div className="flex items-center justify-center gap-3">
                    <div className="h-px flex-1" style={{ background: "var(--border-color)" }} />
                    <span className="text-[10px] px-3 py-1 rounded-full" style={{ background: "var(--surface-2)", color: "var(--text-secondary)" }}>{formatIsraelDate(selected.created_at)} · #{selected.ticket_id.slice(-8).toUpperCase()}</span>
                    <div className="h-px flex-1" style={{ background: "var(--border-color)" }} />
                  </div>

                  {(selected.linked_order || selected.linked_topup || selected.bot_selections) && (
                    <div className="grid gap-3 md:grid-cols-2">
                      {(selected.linked_order || selected.linked_topup) && (() => {
                        const isOrder = !!selected.linked_order;
                        const item = isOrder ? selected.linked_order : selected.linked_topup;
                        const statusColor = { processing: "#60a5fa", pending: "#facc15", completed: "#4ade80", cancelled: "#f87171", approved: "#4ade80", rejected: "#f87171", awaiting_approval: "#a78bfa" }[item.status] || "#9ca3af";
                        const postUrl = item.post_url || item.target_url;
                        const isPostType = item.order_type === "post" || (postUrl && postUrl.includes("/p/"));
                        return (
                          <div className="rounded-2xl border px-4 py-3" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
                            <div className="flex items-center gap-2 mb-2">
                              <AdminIcon emoji={isOrder ? "📦" : "💳"} className="w-4 h-4" />
                              <span className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: "var(--text-secondary)" }}>{isOrder ? t("Admin.linked_order", lang) : t("Admin.linked_top_up", lang)}</span>
                              <span className="ms-auto text-[10px] font-bold px-2 py-1 rounded-full" style={{ background: `${statusColor}18`, color: statusColor }}>{item.status}</span>
                            </div>
                            {isOrder ? (
                              <div className="grid grid-cols-2 gap-2 text-[11px]">
                                <div><span style={{ color: "var(--text-secondary)" }}>#{item.order_number}</span> <span className="font-semibold">{item.product_name?.[lang] || item.product_name?.en || item.product_name}</span></div>
                                {item.quantity && <div><span style={{ color: "var(--text-secondary)" }}>{t("Admin.qty", lang)}:</span> <span className="font-semibold">{item.quantity}</span></div>}
                                {item.price && <div><span style={{ color: "var(--text-secondary)" }}>{t("shop.price", lang)}:</span> <span className="font-semibold">₪{item.price}</span></div>}
                                {item.instagram_username && <div><span style={{ color: "var(--text-secondary)" }}>@</span><span className="font-semibold">{item.instagram_username}</span></div>}
                                {isPostType && postUrl && <div className="col-span-2"><a href={ensureHttps(postUrl)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[10px] font-semibold hover:underline" style={{ color: "#7aa2ff" }}><ExternalLink className="w-3 h-3" />{t("Admin.view_post", lang)}</a></div>}
                              </div>
                            ) : (
                              <div className="grid grid-cols-2 gap-2 text-[11px]">
                                <div><span style={{ color: "var(--text-secondary)" }}>{t("Admin.amount", lang)}:</span> <span className="font-semibold">{item.amount} {item.currency || "₪"}</span></div>
                                <div><span style={{ color: "var(--text-secondary)" }}>{t("Admin.method", lang)}:</span> <span className="font-semibold">{item.method}</span></div>
                                {item.created_at && <div className="col-span-2" style={{ color: "var(--text-secondary)" }}>{formatDateTime(item.created_at)}</div>}
                              </div>
                            )}
                            {onNavigate && <button onClick={() => onNavigate(isOrder ? "orders" : "topups")} className="mt-3 rounded-2xl px-3 py-1.5 text-[10px] font-bold" style={{ background: "rgba(122,162,255,0.12)", color: "#7aa2ff" }}>{t("Admin.open_in_admin", lang)}</button>}
                          </div>
                        );
                      })()}
                      {selected.bot_selections && (
                        <div className="rounded-2xl border px-4 py-3" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
                          <div className="text-[10px] font-bold uppercase tracking-[0.18em] mb-2" style={{ color: "var(--text-secondary)" }}>{t("Admin.issue_category", lang)}</div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-2xl" style={{ background: "rgba(122,162,255,0.12)", color: "#7aa2ff" }}>
                              <span>{selected.bot_selections.topic_emoji}</span>
                              {selected.bot_selections.topic_label?.[lang] || selected.bot_selections.topic_label?.en}
                            </span>
                            {selected.bot_selections.detail_label && <span className="text-[11px] px-2.5 py-1 rounded-2xl" style={{ background: "var(--surface-2)", color: "var(--text-secondary)" }}>{selected.bot_selections.detail_label?.[lang] || selected.bot_selections.detail_label?.en}</span>}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* AI conversation history */}
                  {selected.ai_conversation?.length > 0 && (
                    <div className="rounded-2xl border mb-4" style={{ borderColor: "rgba(139,92,246,0.25)", background: "rgba(139,92,246,0.04)" }}>
                      <div className="flex items-center gap-2 px-4 py-2.5 border-b" style={{ borderColor: "rgba(139,92,246,0.18)" }}>
                        <Sparkles className="w-3.5 h-3.5" style={{ color: "#a78bfa" }} />
                        <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#a78bfa" }}>
                          {t("Admin.ai_chat_before_ticket", lang)}
                        </span>
                        <span className="ms-auto text-[10px]" style={{ color: "var(--text-secondary)" }}>
                          {selected.user_name}
                        </span>
                      </div>
                      <div className="px-4 py-3 flex flex-col gap-2">
                        {selected.ai_conversation.map((m, i) => {
                          const isUser = m.role === "user";
                          return (
                            <div key={i} className={`flex gap-2 ${isUser ? "flex-row-reverse" : ""}`}>
                              <div className="w-6 h-6 rounded-xl flex-shrink-0 flex items-center justify-center text-[9px] font-black mt-0.5"
                                style={{ background: isUser ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.12)", color: "#a78bfa" }}>
                                {isUser ? (selected.user_name?.[0] || "U").toUpperCase() : <Sparkles className="w-3 h-3" />}
                              </div>
                              <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-[12px] leading-relaxed whitespace-pre-wrap break-words ${isUser ? "rounded-br-[4px]" : "rounded-bl-[4px]"}`}
                                style={{
                                  background: isUser ? "rgba(139,92,246,0.12)" : "rgba(139,92,246,0.06)",
                                  border: "1px solid rgba(139,92,246,0.18)",
                                  color: "var(--text-primary)",
                                }}>
                                {m.content}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {selectedMessages.map((msg, index) => {
                    const isNote = msg.sender_type === "note";
                    const isAdmin = msg.sender_type === "admin";
                    const text = msg.content || msg.message || "";

                    if (msg.sender_type === "system") {
                      return <div key={index} className="flex justify-center"><span className="text-[10px] px-3 py-1 rounded-full" style={{ background: "var(--surface-2)", color: "var(--text-secondary)", border: "1px solid var(--border-color)" }}>{text}</span></div>;
                    }

                    if (isNote) {
                      return (
                        <div key={index} className="flex justify-center">
                          <div className="max-w-[82%] rounded-2xl px-4 py-3 border" style={{ background: "rgba(250,204,21,0.07)", borderColor: "rgba(250,204,21,0.18)" }}>
                            <div className="flex items-center gap-2 mb-1.5">
                              <span className="text-[10px]">📝</span>
                              <span className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: "#facc15" }}>{t("Admin.internal_note", lang)}</span>
                              <span className="text-[10px] ms-auto" style={{ color: "rgba(250,204,21,0.75)" }}>{msg.sender_name} · {formatIsraelTime(msg.created_at)}</span>
                            </div>
                            <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: "#fde68a" }}>{text}</p>
                          </div>
                        </div>
                      );
                    }

                    const isDeleted = !!msg.deleted;
                    const adminInitial = (msg.sender_name || currentAdmin?.name || "S")[0].toUpperCase();
                    const seenAt = userSeenAt[selected.ticket_id] || selected.user_last_read_at;
                    const showSeen = isAdmin && seenAt && lastVisibleAdminMessage?.created_at === msg.created_at && msg.created_at <= seenAt;

                    return (
                      <div key={index} className={`flex gap-3 group ${isAdmin ? "flex-row-reverse" : "flex-row"}`}>
                        {isAdmin ? (
                          <div className="w-8 h-8 rounded-2xl flex-shrink-0 flex items-center justify-center text-[11px] font-black mt-0.5" style={{ background: "#8B5CF6", color: "#fff" }}>{adminInitial}</div>
                        ) : selectedUserDetails?.user?.picture ? (
                          <img src={selectedUserDetails.user.picture} alt="" className="w-8 h-8 rounded-2xl object-cover flex-shrink-0 mt-0.5" />
                        ) : (
                          <div className="w-8 h-8 rounded-2xl flex items-center justify-center text-[11px] font-black flex-shrink-0 mt-0.5" style={{ background: "rgba(225,48,108,0.15)", color: "#E1306C" }}>{(selected.user_display_name || selected.user_name || "?")[0].toUpperCase()}</div>
                        )}
                        <div className={`flex flex-col ${isAdmin ? "items-end" : "items-start"} max-w-[78%]`}>
                          <div className="relative group/bubble">
                            <div className="rounded-2xl px-4 py-3" style={{ background: isDeleted ? "rgba(239,68,68,0.06)" : isAdmin ? "rgba(139,92,246,0.85)" : "var(--surface-1)", border: isDeleted ? "1px solid rgba(239,68,68,0.18)" : isAdmin ? "1px solid rgba(139,92,246,0.35)" : "1px solid var(--border-color)" }}>
                              {text && <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: isDeleted ? "rgba(239,68,68,0.72)" : (isAdmin ? "#fff" : "var(--text-primary)") }}>{isDeleted ? text : renderChatText(text, lang)}</p>}
                              {!isDeleted && <SupportAttachmentGrid attachments={msg.attachments} lang={lang} onImageClick={setLightboxUrl} />}
                            </div>
                            {isAdmin && !isDeleted && <button onClick={async () => { await fetch(`${API}/admin/tickets/${selected.ticket_id}/messages`, { method: "DELETE", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ created_at: msg.created_at }) }); fetch_(); }} className="absolute -top-1.5 w-6 h-6 rounded-full flex items-center justify-center opacity-0 group-hover/bubble:opacity-100 transition-opacity" style={{ insetInlineEnd: "-0.25rem", background: "rgba(239,68,68,0.88)", color: "#fff" }}><X className="w-3 h-3" /></button>}
                          </div>
                          <div className={`flex items-center gap-1.5 mt-1 px-1 ${isAdmin ? "flex-row-reverse" : "flex-row"}`}>
                            <span className="text-[10px]" style={{ color: "var(--text-secondary)" }}>{msg.sender_name || (isAdmin ? "Support" : selected.user_name)} · {formatIsraelTime(msg.created_at)}</span>
                            {showSeen && <span className="flex items-center gap-0.5 text-[10px] font-semibold" style={{ color: "#22c55e" }}><svg width="15" height="10" viewBox="0 0 15 10" fill="none"><path d="M1 5L4 8L9 2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /><path d="M6 5L9 8L14 2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>{t("Admin.seen", lang)}</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {(selectedConversationPresence?.mode === "typing" || selectedAdminTypers.length > 0 || selectedAdminViewers.length > 0) && (
                  <div className="flex-shrink-0 px-4 md:px-5 py-2.5 flex items-center gap-3 flex-wrap border-t" style={{ borderColor: "var(--border-color)", background: "var(--bg-secondary)" }}>
                    {selectedConversationPresence?.mode === "typing" && <div className="flex items-center gap-2"><div className="flex gap-[3px]">{[0, 1, 2].map((step) => <span key={step} className="w-1.5 h-1.5 rounded-full" style={{ background: "#E1306C", animation: `ticketTypingBounce 1s ${step * 0.18}s infinite` }} />)}</div><span className="text-[11px] font-semibold" style={{ color: "#E1306C" }}>{selectedConversationPresence.name || t("Admin.user", lang)} {t("Admin.is_typing", lang)}</span></div>}
                    {selectedAdminTypers.length > 0 && <div className="flex items-center gap-2"><div className="flex gap-[3px]">{[0, 1, 2].map((step) => <span key={step} className="w-1.5 h-1.5 rounded-full" style={{ background: "#7aa2ff", animation: `ticketTypingBounce 1s ${step * 0.18}s infinite` }} />)}</div><span className="text-[11px] font-semibold" style={{ color: "#7aa2ff" }}>{selectedAdminTypers.map((entry) => entry.name).join(", ")} {t("Admin.typing", lang)}</span></div>}
                    {!selectedAdminTypers.length && selectedAdminViewers.length > 0 && <div className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /><span className="text-[11px]" style={{ color: "var(--text-secondary)" }}>{selectedAdminViewers.map((entry) => entry.name).join(", ")} {t("Admin.viewing", lang)}</span></div>}
                  </div>
                )}

                {selected.status !== "closed" ? (
                  <div className="flex-shrink-0 border-t" style={{ borderColor: "var(--border-color)", background: "var(--bg-secondary)" }}>
                    {aiModal && (
                      <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={() => setAiModal(false)}>
                        <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.5)" }} />
                        <div className="relative rounded-2xl p-5 w-[340px] max-w-[95vw]" style={{ background: "var(--bg-primary)", border: "1px solid var(--border-color)" }} onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2"><Sparkles className="w-4 h-4" style={{ color: "#c084fc" }} /><span className="font-bold text-sm">{t("Admin.ai_reply_generator", lang)}</span></div>
                            <button onClick={() => setAiModal(false)} className="w-7 h-7 rounded-xl flex items-center justify-center" style={{ color: "var(--text-secondary)" }}><X className="w-3.5 h-3.5" /></button>
                          </div>
                          <div className="h-px mb-4" style={{ background: "var(--border-color)" }} />
                          <div className="mb-3">
                            <div className="text-[11px] font-semibold mb-1.5" style={{ color: "var(--text-secondary)" }}>{t("Admin.mode", lang)}</div>
                            <div className="flex gap-1.5">
                              {[["auto", t("Admin.auto", lang)], ["custom", t("Admin.custom", lang)]].map(([mode, label]) => <button key={mode} onClick={() => setAiMode(mode)} className="flex-1 py-1.5 rounded-xl text-xs font-semibold transition-all" style={{ background: aiMode === mode ? "rgba(192,132,252,0.15)" : "var(--surface-2)", color: aiMode === mode ? "#c084fc" : "var(--text-secondary)", border: aiMode === mode ? "1px solid rgba(192,132,252,0.3)" : "1px solid var(--border-color)" }}>{label}{aiMode === mode ? " ✓" : ""}</button>)}
                            </div>
                          </div>
                          <div className="mb-3">
                            <div className="text-[11px] font-semibold mb-1.5" style={{ color: "var(--text-secondary)" }}>{t("Admin.language", lang)}</div>
                            <div className="flex gap-1.5">
                              {[["auto", "Auto"], ["en", "EN"], ["ru", "RU"], ["he", "HE"]].map(([language, label]) => <button key={language} onClick={() => setAiLang(language)} className="flex-1 py-1.5 rounded-xl text-xs font-semibold transition-all" style={{ background: aiLang === language ? "rgba(122,162,255,0.15)" : "var(--surface-2)", color: aiLang === language ? "#7aa2ff" : "var(--text-secondary)", border: aiLang === language ? "1px solid rgba(122,162,255,0.3)" : "1px solid var(--border-color)" }}>{label}</button>)}
                            </div>
                          </div>
                          {aiMode === "custom" && <textarea value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} placeholder={t("Admin.hint_or_context", lang)} className="w-full rounded-xl px-3 py-2.5 text-xs resize-none outline-none leading-relaxed mb-3" style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)", color: "var(--text-primary)", minHeight: 70 }} />}
                          <div className="h-px mb-4" style={{ background: "var(--border-color)" }} />
                          <button onClick={() => aiSuggest(selected.ticket_id, aiMode, aiLang, aiPrompt)} disabled={aiSuggesting === selected.ticket_id} className="w-full py-2.5 rounded-xl text-sm font-bold transition-all disabled:opacity-50" style={{ background: "#8B5CF6", color: "#fff" }}>
                            {aiSuggesting === selected.ticket_id ? <span className="flex items-center justify-center gap-2"><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />{t("Admin.thinking", lang)}</span> : <span className="flex items-center justify-center gap-2"><Sparkles className="w-4 h-4" />{t("Admin.generate", lang)}</span>}
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="relative px-4 py-4 md:px-5 md:py-4">
                      {cannedDropdown && !noteMode && cannedMatches.length > 0 && (
                        <div className="absolute bottom-full mb-2 z-30 rounded-2xl border overflow-hidden shadow-xl" style={{ insetInlineStart: "1rem", insetInlineEnd: "1rem", background: "var(--bg-primary)", borderColor: "rgba(122,162,255,0.35)" }}>
                          <div className="flex items-center gap-2 px-3 py-2 border-b" style={{ borderColor: "var(--border-color)" }}>
                            <span className="text-[10px] font-bold" style={{ color: "#7aa2ff" }}># {t("Admin.templates", lang)}</span>
                            <span className="text-[10px]" style={{ color: "var(--text-secondary)" }}>↑↓ {t("Admin.navigate", lang)} · Enter {t("Admin.insert", lang)}</span>
                          </div>
                          {cannedMatches.map((response, idx) => (
                            <button key={response.id} onClick={() => insertCanned(selected.ticket_id, response.text, selectedReplyValue)} onMouseEnter={() => setCannedSelectedIdx(idx)} className="w-full text-start px-3 py-2.5 text-xs transition-colors flex flex-col gap-0.5" style={{ background: cannedSelectedIdx === idx ? "rgba(122,162,255,0.08)" : "transparent", borderInlineStart: cannedSelectedIdx === idx ? "2px solid #7aa2ff" : "2px solid transparent" }}>
                              <span className="font-bold text-[11px]" style={{ color: "#7aa2ff" }}>#{response.title}</span>
                              <span className="text-[11px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>{response.text.slice(0, 160)}{response.text.length > 160 ? "…" : ""}</span>
                            </button>
                          ))}
                        </div>
                      )}
                      {cannedDropdown && !noteMode && cannedMatches.length === 0 && cannedSearch && <div className="absolute bottom-full mb-2 z-30 rounded-2xl border px-3 py-2 text-xs shadow-xl" style={{ insetInlineStart: "1rem", insetInlineEnd: "1rem", background: "var(--bg-primary)", borderColor: "var(--border-color)", color: "var(--text-secondary)" }}>{t("Admin.no_matching_templates", lang)} · <button onClick={openMacrosPanel} className="underline" style={{ color: "#7aa2ff" }}>{t("Admin.create_one", lang)}</button></div>}
                      {navDropdown && !noteMode && (
                        <div className="absolute bottom-full mb-2 z-30 rounded-2xl border overflow-hidden shadow-xl" style={{ insetInlineEnd: "1rem", minWidth: 220, background: "var(--bg-primary)", borderColor: "rgba(0,200,83,0.25)" }}>
                          <div className="flex items-center gap-2 px-3 py-2 border-b" style={{ borderColor: "var(--border-color)" }}><span className="text-[10px] font-bold" style={{ color: "#00C853" }}>{"{}"} {t("Admin.page_links", lang)}</span></div>
                          {NAV_SHORTCUTS.map((shortcut) => <button key={shortcut.key} onMouseDown={(e) => { e.preventDefault(); insertNavShortcut(selected.ticket_id, shortcut.key); }} className="w-full text-start px-3 py-2 text-xs flex items-center gap-2.5" style={{ color: "var(--text-primary)" }}><span className="text-base">{shortcut.icon}</span><span className="font-semibold">{shortcut.label[lang] || shortcut.label.en}</span><span className="ms-auto font-mono text-[10px] opacity-40">{`{${shortcut.key}}`}</span></button>)}
                        </div>
                      )}

                      <div className="rounded-2xl border overflow-hidden" style={{ borderColor: noteMode ? "rgba(250,204,21,0.24)" : "var(--border-color)", background: noteMode ? "rgba(250,204,21,0.04)" : "var(--surface-1)" }}>
                        <div className="flex items-center justify-between gap-3 px-4 pt-4">
                          <div className="flex items-center gap-2">
                            <button onClick={() => setNoteMode(false)} className="rounded-full px-3 py-1.5 text-[11px] font-semibold transition-all" style={{ background: !noteMode ? "rgba(122,162,255,0.14)" : "transparent", color: !noteMode ? "#7aa2ff" : "var(--text-secondary)" }}>{t("Admin.reply", lang)}</button>
                            <button onClick={() => setNoteMode(true)} className="rounded-full px-3 py-1.5 text-[11px] font-semibold transition-all" style={{ background: noteMode ? "rgba(250,204,21,0.16)" : "transparent", color: noteMode ? "#f59e0b" : "var(--text-secondary)" }}>{t("Admin.note_2", lang)}</button>
                          </div>
                          <div className="text-[10px]" style={{ color: "var(--text-secondary)" }}>{noteMode ? t("Admin.only_admins_can_see_this_message", lang) : t("Admin.macro_links_ai_available", lang)}</div>
                        </div>

                        {!noteMode && selectedReplyAttachments.length > 0 && (
                          <div className="flex flex-wrap gap-2 px-4 pt-3">
                            {selectedReplyAttachments.map((attachment, index) => (
                              <div key={index} className="relative w-14 h-14 rounded-2xl overflow-hidden border" style={{ borderColor: "var(--border-color)" }}>
                                <img src={resolveSupportAttachmentUrl(attachment.url)} alt="" className="w-full h-full object-cover" />
                                <button onClick={() => setReplyAtts((current) => ({ ...current, [selected.ticket_id]: (current[selected.ticket_id] || []).filter((_, attachmentIndex) => attachmentIndex !== index) }))} className="absolute top-1 w-4.5 h-4.5 rounded-full bg-black/70 text-white flex items-center justify-center" style={{ insetInlineEnd: "0.25rem" }}><X className="w-2.5 h-2.5" /></button>
                              </div>
                            ))}
                          </div>
                        )}

                        <textarea value={selectedReplyValue} onChange={(e) => handleReplyChange(e, selected.ticket_id)} onKeyDown={(e) => {
                          if (cannedDropdown && cannedMatches.length > 0) {
                            if (e.key === "ArrowDown") { e.preventDefault(); setCannedSelectedIdx((value) => Math.min(value + 1, cannedMatches.length - 1)); return; }
                            if (e.key === "ArrowUp") { e.preventDefault(); setCannedSelectedIdx((value) => Math.max(value - 1, 0)); return; }
                            if (e.key === "Tab" || (e.key === "Enter" && !e.shiftKey)) { e.preventDefault(); insertCanned(selected.ticket_id, cannedMatches[cannedSelectedIdx]?.text || cannedMatches[0].text, selectedReplyValue); return; }
                            if (e.key === "Escape") { setCannedDropdown(false); return; }
                          }
                          if (e.key === "Escape") { setCannedDropdown(false); setNavDropdown(false); return; }
                          if (e.key === "Enter" && !e.shiftKey && !cannedDropdown) { e.preventDefault(); noteMode ? sendNote(selected.ticket_id) : reply(selected.ticket_id); }
                        }} placeholder={noteMode ? t("Admin.internal_note_for_the_team", lang) : t("Admin.write_a_reply_macro_link_ai_draft", lang)} className="w-full bg-transparent px-4 pt-4 pb-2 text-sm resize-none outline-none leading-relaxed" style={{ color: noteMode ? "#fef08a" : "var(--text-primary)", minHeight: 110, maxHeight: 240, display: "block" }} ref={replyTextareaRef} />

                        <div className="flex items-center justify-between gap-3 px-4 py-3 border-t" style={{ borderColor: "var(--border-color)" }}>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {!noteMode && (
                              <>
                                <button onClick={openMacrosPanel} className="rounded-2xl px-3 py-2 text-[11px] font-semibold flex items-center gap-2" style={{ background: "rgba(122,162,255,0.12)", color: "#7aa2ff" }}><Settings className="w-3.5 h-3.5" />{t("Admin.macros", lang)}</button>
                                <label className="rounded-2xl px-3 py-2 text-[11px] font-semibold flex items-center gap-2 cursor-pointer" style={{ background: "var(--surface-2)", color: uploading === selected.ticket_id ? "#405DE6" : "var(--text-secondary)" }}><ImagePlus className="w-3.5 h-3.5" /><span>{uploading === selected.ticket_id ? t("Admin.uploading", lang) : t("Admin.image_2", lang)}</span><input type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadFiles(selected.ticket_id, e.target.files)} /></label>
                                <button onClick={() => { setAiMode("auto"); setAiLang(lang || "auto"); setAiPrompt(""); setAiModal(true); }} disabled={aiSuggesting === selected.ticket_id} className="rounded-2xl px-3 py-2 text-[11px] font-semibold flex items-center gap-2 disabled:opacity-50" style={{ background: "var(--surface-2)", color: aiSuggesting === selected.ticket_id ? "#E1306C" : "var(--text-secondary)" }}>{aiSuggesting === selected.ticket_id ? <div className="w-3.5 h-3.5 border-2 border-[#E1306C]/35 border-t-[#E1306C] rounded-full animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}AI</button>
                                <button onClick={() => setNavDropdown((value) => !value)} className="rounded-2xl px-3 py-2 text-[11px] font-semibold flex items-center gap-2" style={{ background: navDropdown ? "rgba(0,200,83,0.10)" : "var(--surface-2)", color: navDropdown ? "#00C853" : "var(--text-secondary)" }}><ExternalLink className="w-3.5 h-3.5" />{}</button>
                              </>
                            )}
                          </div>
                          {noteMode ? (
                            <button onClick={() => sendNote(selected.ticket_id)} disabled={sending === selected.ticket_id || !selectedReplyValue.trim()} className="rounded-2xl px-4 py-2.5 text-[11px] font-semibold flex items-center gap-2 disabled:opacity-40" style={{ background: selectedReplyValue.trim() ? "rgba(250,204,21,0.16)" : "var(--surface-3)", color: selectedReplyValue.trim() ? "#f59e0b" : "var(--text-secondary)", border: "1px solid rgba(250,204,21,0.18)" }}>{sending === selected.ticket_id ? <div className="w-3.5 h-3.5 border-2 border-yellow-300/30 border-t-yellow-300 rounded-full animate-spin" /> : <PenSquare className="w-3.5 h-3.5" />}{t("Admin.save_note", lang)}</button>
                          ) : (
                            <button onClick={() => reply(selected.ticket_id)} disabled={sending === selected.ticket_id || (!selectedReplyValue.trim() && !selectedReplyAttachments.length)} className="rounded-2xl px-4 py-2.5 text-[11px] font-semibold flex items-center gap-2 disabled:opacity-40" style={{ background: selectedReplyValue.trim() || selectedReplyAttachments.length ? "#8B5CF6" : "var(--surface-3)", color: selectedReplyValue.trim() || selectedReplyAttachments.length ? "#fff" : "var(--text-secondary)" }}>{sending === selected.ticket_id ? <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Send className="w-3.5 h-3.5" />}{sending === selected.ticket_id ? t("Admin.sending", lang) : t("Admin.send_reply", lang)}</button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="border-t px-4 py-4 md:px-5 flex-shrink-0 flex items-center justify-between gap-3" style={{ borderColor: "var(--border-color)", background: "var(--bg-secondary)" }}>
                    <div className="flex items-center gap-2 text-sm" style={{ color: "var(--text-secondary)" }}>
                      <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                      <span>{t("Admin.conversation_closed", lang)}</span>
                      {selected.closed_at && <span className="text-[11px]">· {resolutionTime(selected.created_at, selected.closed_at, lang)}</span>}
                    </div>
                    <button onClick={() => reopen(selected.ticket_id)} disabled={reopening === selected.ticket_id} className="rounded-2xl px-3.5 py-2 text-[11px] font-semibold flex items-center gap-2 disabled:opacity-40" style={{ background: "rgba(122,162,255,0.12)", color: "#7aa2ff", border: "1px solid rgba(122,162,255,0.2)" }}>
                      {reopening === selected.ticket_id ? <div className="w-3 h-3 border-2 border-[#7aa2ff]/30 border-t-[#7aa2ff] rounded-full animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                      {t("Admin.reopen_2", lang)}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {sidePanelOpen && <div className="fixed inset-0 z-[65] xl:hidden" style={{ background: "rgba(15,23,42,0.6)" }} onClick={() => setSidePanelOpen(false)} />}
          {(selected || cannedSettings) && (
            <div className={`${sidePanelOpen ? "fixed inset-0 z-[70] flex items-stretch justify-end p-3" : "hidden"} ${(selected || cannedSettings) ? "xl:flex" : "xl:hidden"} xl:static xl:z-auto xl:p-0`} onClick={() => setSidePanelOpen(false)}>
              <div className="w-full max-w-[390px] xl:max-w-none xl:w-[350px] 2xl:w-[380px] rounded-2xl border overflow-hidden flex flex-col" style={{ background: "var(--bg-secondary)", borderColor: "var(--border-color)" }} onClick={(e) => e.stopPropagation()}>
                <div className="px-4 py-4 border-b" style={{ borderColor: "var(--border-color)" }}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-[13px] font-black uppercase tracking-[0.18em]" style={{ color: cannedSettings ? "#7aa2ff" : "#F77737" }}>{cannedSettings ? t("Admin.macros", lang) : t("Admin.customer", lang)}</div>
                      <div className="text-sm font-semibold mt-0.5" style={{ color: "var(--text-primary)" }}>{cannedSettings ? t("Admin.response_library", lang) : t("Admin.context_and_account_snapshot", lang)}</div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {selected?.user_id && !cannedSettings && <button onClick={() => fetchUserDetails(selected.user_id)} className="w-9 h-9 rounded-2xl flex items-center justify-center" style={{ background: "var(--surface-1)", color: "var(--text-secondary)" }}><RefreshCw className="w-4 h-4" /></button>}
                      <button onClick={() => setSidePanelOpen(false)} className="xl:hidden w-9 h-9 rounded-2xl flex items-center justify-center" style={{ background: "var(--surface-1)", color: "var(--text-secondary)" }}><X className="w-4 h-4" /></button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-4">
                    <button onClick={() => { setCannedSettings(false); setSidePanelOpen(true); }} className="rounded-xl px-3 py-2 text-[11px] font-semibold transition-all" style={{ background: !cannedSettings ? "#E1306C" : "var(--surface-2)", color: !cannedSettings ? "#fff" : "var(--text-secondary)" }}>{t("Admin.customer", lang)}</button>
                    <button onClick={() => { setCannedSettings(true); setSidePanelOpen(true); }} className="rounded-xl px-3 py-2 text-[11px] font-semibold transition-all" style={{ background: cannedSettings ? "#8B5CF6" : "var(--surface-2)", color: cannedSettings ? "#fff" : "var(--text-secondary)" }}>{t("Admin.macros", lang)}</button>
                  </div>
                </div>
                {cannedSettings ? renderMacrosSidebar() : renderCustomerSidebar()}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MercOverlay({ imgSrc, onClose }) {
  const [imgOk, setImgOk] = useState(!!imgSrc);
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(0,0,0,0.92)",
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        cursor: "pointer",
      }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ position: "relative", textAlign: "center" }}>
        {imgOk && imgSrc ? (
          <img
            src={imgSrc}
            alt="Scania"
            onError={() => setImgOk(false)}
            style={{
              maxWidth: "92vw", maxHeight: "78vh",
              borderRadius: 16,
              boxShadow: "0 0 80px #00000099",
              display: "block",
            }}
          />
        ) : (
          <div style={{ fontSize: 160, lineHeight: 1 }}>🚛</div>
        )}
        <p style={{ color: "#fff", fontWeight: 900, fontSize: 28, marginTop: 20, letterSpacing: 2 }}>
          🚛 SCANIA V8 🚛
        </p>
        <p style={{ color: "#ffffff88", fontSize: 13, marginTop: 6 }}>
          Кликни в любое место чтобы закрыть
        </p>
      </div>
      <button
        onClick={onClose}
        style={{
          position: "absolute", top: 20, right: 24,
          background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)",
          color: "#fff", borderRadius: 8, padding: "6px 14px",
          cursor: "pointer", fontSize: 13, fontWeight: 600,
        }}
      >
        ✕ Закрыть
      </button>
    </div>
  );
}

export default function Admin() {
  const { lang } = useLanguage();
  const { user } = useAuth();
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mercVisible, setMercVisible] = useState(false);
  const [mercImgUrl, setMercImgUrl] = useState("");
  const [japAdminBalance, setJapAdminBalance] = useState(null);
  const [fsAdminBalance, setFsAdminBalance] = useState(null);
  const [navStyle, setNavStyle] = useState(() => localStorage.getItem("admin_nav_style") || "icons");
  const setAndSaveNavStyle = (style) => { setNavStyle(style); localStorage.setItem("admin_nav_style", style); };
  const [activeTab, setActiveTab] = useState(() => {
    const h = window.location.hash.slice(1);
    return ADMIN_VALID_TABS.includes(h) ? h : "dashboard";
  });
  const [deepLinkTicketId, setDeepLinkTicketId] = useState("");
  const [crmFullscreen, setCrmFullscreen] = useState(false);
  const [mobileSection, setMobileSection] = useState(() => {
    const h = window.location.hash.slice(1);
    const initialTab = ADMIN_VALID_TABS.includes(h) ? h : "dashboard";
    return ADMIN_TAB_SECTIONS.find(s => s.tabs.includes(initialTab))?.id || ADMIN_TAB_SECTIONS[0].id;
  });
  const [expandedSections, setExpandedSections] = useState(() => {
    const h = window.location.hash.slice(1);
    const initialTab = ADMIN_VALID_TABS.includes(h) ? h : "dashboard";
    const section = ADMIN_TAB_SECTIONS.find(s => s.tabs.includes(initialTab));
    return new Set(section ? [section.id] : [ADMIN_TAB_SECTIONS[0].id]);
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem("admin_sb_collapsed") === "true");
  const [showAppearance, setShowAppearance] = useState(false);
  const setSidebarCollapsedAndSave = (v) => { setSidebarCollapsed(v); localStorage.setItem("admin_sb_collapsed", String(v)); };

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/stats`, { credentials: "include" });
      if (res.ok) setStats(await res.json());
    } catch {} finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  // Fetch JAP provider balance separately
  useEffect(() => {
    fetch(`${API}/admin/provider/balance?provider=jap`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setJapAdminBalance(d); })
      .catch(() => {});
  }, []);

  // Fetch Follow.Sale provider balance separately
  useEffect(() => {
    fetch(`${API}/admin/provider/balance?provider=followsale`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setFsAdminBalance(d); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch(`${API}/admin/merc`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d) return;
        setMercImgUrl(d.merc_img_url || "");
        setMercVisible(!!d.merc_mode);
      })
      .catch(() => {});
  }, []);

  // Sync URL hash with active tab (persists on F5)
  useEffect(() => {
    window.location.hash = activeTab;
  }, [activeTab]);

  // React Router location change → sync activeTab (handles Link / navigate() / pushState)
  useEffect(() => {
    const hash = location.hash.slice(1);
    if (ADMIN_VALID_TABS.includes(hash) && hash !== activeTab) setActiveTab(hash);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash]);

  // Fallback: native hashchange + custom event (belt-and-suspenders)
  useEffect(() => {
    const applyTab = (tab) => {
      if (ADMIN_VALID_TABS.includes(tab)) setActiveTab(tab);
    };
    const onHashChange = () => applyTab(window.location.hash.slice(1));
    const onAdminNavigate = (event) => applyTab(event?.detail?.tab || "");
    window.addEventListener("hashchange", onHashChange);
    window.addEventListener("admin:navigate", onAdminNavigate);
    return () => {
      window.removeEventListener("hashchange", onHashChange);
      window.removeEventListener("admin:navigate", onAdminNavigate);
    };
  }, []);

  // Sync mobile section when active tab changes (e.g. deep links)
  useEffect(() => {
    const section = ADMIN_TAB_SECTIONS.find(s => s.tabs.includes(activeTab));
    if (section) setMobileSection(section.id);
  }, [activeTab]);

  // Auto-expand section containing active tab
  useEffect(() => {
    const section = ADMIN_TAB_SECTIONS.find(s => s.tabs.includes(activeTab));
    if (section) setExpandedSections(prev => new Set([...prev, section.id]));
  }, [activeTab]);

  // Hide accessibility widget when entering full-screen support CRM
  useEffect(() => {
    window.dispatchEvent(new CustomEvent("support-open-state", { detail: { open: activeTab === "tickets" } }));
  }, [activeTab]);

  if (loading) return <div className="min-h-screen pt-24 flex items-center justify-center"><div className="animate-spin h-8 w-8 border-2 border-[#E1306C] border-t-transparent rounded-full" /></div>;
  const providerFxRate = formatProviderFxRate(fsAdminBalance || stats?.provider_balance);

  const TABS = [
    { id: "dashboard",          emoji:"📊", icon: <BarChart3 className="w-4 h-4" />,     label: t("admin.dashboard", lang),                             color: "#E1306C" },
    { id: "analytics",              emoji:"📈", icon: <TrendingUp className="w-4 h-4" />,    label: t("Admin.analytics", lang),             color: "#00C853" },
    { id: "onboarding-analytics",   emoji:"📋", icon: <ClipboardList className="w-4 h-4" />, label: "Анкеты",                                   color: "#8B5CF6" },
    { id: "seo",                    emoji:"🌐", icon: <Globe className="w-4 h-4" />,          label: t("Admin.seo", lang),                       color: "#8B5CF6" },
    { id: "improvements",       emoji:"💡", icon: <Lightbulb className="w-4 h-4" />,     label: t("Admin.improvements", lang),           color: "#FACC15" },
    { id: "tasks",              emoji:"🎯", icon: <Target className="w-4 h-4" />,        label: t("nav.tasks", lang),                     color: "#F77737", badge: stats?.pending_submissions },
    { id: "topups",             emoji:"💳", icon: <Wallet className="w-4 h-4" />,        label: t("Admin.top_ups", lang),               color: "#00C853", badge: stats?.pending_topups },
    { id: "promos",             emoji:"🏷️", icon: <Percent className="w-4 h-4" />,       label: t("Admin.promo_codes", lang),           color: "#833AB4" },
    { id: "tickets",            emoji:"🎧", icon: <HelpCircle className="w-4 h-4" />,    label: t("Admin.support", lang),                 color: "#405DE6", badge: stats?.pending_tickets },
    { id: "support-analytics",  emoji:"📉", icon: <BarChart2 className="w-4 h-4" />,    label: t("Admin.support_stats", lang), color: "#405DE6" },
    { id: "ai-chats",           emoji:"🤖", icon: <Sparkles className="w-4 h-4" />,      label: t("Admin.ai_chats", lang),              color: "#8B5CF6" },
    { id: "orders",             emoji:"📦", icon: <ShoppingBag className="w-4 h-4" />,   label: t("admin.orders", lang),                               color: "#E1306C" },
    { id: "products",           emoji:"🛍️", icon: <Package className="w-4 h-4" />,       label: t("admin.products", lang),                             color: "#F77737" },
    { id: "users",              emoji:"👥", icon: <UsersIcon className="w-4 h-4" />,     label: t("admin.users", lang),                                color: "#833AB4" },
    { id: "messages",           emoji:"💬", icon: <MessageSquare className="w-4 h-4" />, label: t("nav.messages", lang),              color: "#405DE6" },
    { id: "ig-locks",           emoji:"🔒", icon: <Instagram className="w-4 h-4" />,     label: t("Admin.free_locks", lang),       color: "#E1306C" },
    { id: "emails",             emoji:"📧", icon: <Mail className="w-4 h-4" />,          label: t("Admin.emails", lang),                    color: "#06B6D4" },
    { id: "payouts",            emoji:"💰", icon: <Coins className="w-4 h-4" />,         label: t("Admin.payouts", lang),                 color: "#22c55e" },
    { id: "settings",           emoji:"⚙️", icon: <Settings className="w-4 h-4" />,      label: t("admin.settings", lang),                             color: "#9ca3af" },
    { id: "roles",              emoji:"🛡️", icon: <Shield className="w-4 h-4" />,        label: "Roles & Access",                                      color: "#8B5CF6" },
  ];
  const tabsById = Object.fromEntries(TABS.map((tab) => [tab.id, tab]));

  // Filter tabs by user's admin_permissions (null = super_admin = all)
  const isSuperAdmin = user?.role === "super_admin";
  const adminPerms = user?.admin_permissions; // null=all, []=none, [...]=specific

  const canAccessTab = (tabId) => {
    if (SUPER_ADMIN_ONLY_TABS.has(tabId)) return isSuperAdmin;
    if (isSuperAdmin || adminPerms === null || adminPerms === undefined) return true;
    return Array.isArray(adminPerms) && adminPerms.includes(tabId);
  };

  const groupedTabs = ADMIN_TAB_SECTIONS.map((section) => ({
    ...section,
    title: section.label(lang),
    tabs: section.tabs.map((tabId) => tabsById[tabId]).filter(t => t && canAccessTab(t.id)),
  })).filter(section => section.tabs.length > 0);

  function renderContent() {
    switch (activeTab) {
      case "dashboard": return <DashboardTab lang={lang} stats={stats} japBalance={japAdminBalance} fsBalance={fsAdminBalance} />;
      case "analytics":             return <AnalyticsTab lang={lang} />;
      case "onboarding-analytics":  return <OnboardingAnalyticsTab lang={lang} />;
      case "seo":                   return <SeoAnalyticsTab lang={lang} />;
      case "improvements": return <AdminImprovementsTab lang={lang} />;
      case "tasks":     return <AdminTasksTab lang={lang} />;
      case "topups":    return <AdminTopUpsTab lang={lang} />;
      case "promos":    return <AdminPromosTab lang={lang} />;
      case "tickets":   return <AdminTicketsTab lang={lang} initialTicketId={deepLinkTicketId} onClearDeepLink={() => setDeepLinkTicketId("")} />;
      case "orders":    return <OrdersTab lang={lang} onUpdate={fetchStats} />;
      case "products":  return <ProductsTab lang={lang} />;
      case "users":     return <UsersTab lang={lang} currentUser={user} onUpdate={fetchStats} onOpenTicket={(id) => { setDeepLinkTicketId(id); setActiveTab("tickets"); }} />;
      case "messages":  return <MessagesTab lang={lang} />;
      case "ig-locks":  return <InstagramLocksTab lang={lang} />;
      case "emails":    return <EmailsTab lang={lang} />;
      case "settings":  return <SettingsTab lang={lang} providerFxRate={providerFxRate} />;
      case "support-analytics": return <SupportAnalyticsTab lang={lang} />;
      case "ai-chats":          return <AiChatsTab lang={lang} onOpenTicket={(id) => { setDeepLinkTicketId(id); setActiveTab("tickets"); }} />;
      case "payouts":   return <PayoutsTab lang={lang} stats={stats} />;
      case "roles":     return <RolesTab lang={lang} />;
      default: return null;
    }
  }

  const isRtl = lang === "he";
  const sidebarW = sidebarCollapsed ? 60 : 260;
  const activeSection = groupedTabs.find(s => s.tabs.some(t => t.id === activeTab));
  const activeTabDef = groupedTabs.flatMap(s => s.tabs).find(t => t.id === activeTab);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
  };

  /* ─── Single nav item ─── */
  const NavItem = ({ tab, inMobile = false }) => {
    const isActive = activeTab === tab.id;
    const railMode = !inMobile && sidebarCollapsed;
    const iconEl = navStyle === "emoji"
      ? <span className="text-sm leading-none">{tab.emoji}</span>
      : tab.icon;
    return (
      <button
        onClick={() => { setActiveTab(tab.id); if (inMobile) setMobileOpen(false); }}
        data-testid={`admin-tab-${tab.id}`}
        title={railMode ? tab.label : undefined}
        className="relative w-full flex items-center transition-colors duration-100 text-start"
        style={{
          height: 36,
          paddingLeft: railMode ? 0 : (isRtl ? 10 : 12),
          paddingRight: railMode ? 0 : (isRtl ? 12 : 10),
          justifyContent: railMode ? "center" : undefined,
          background: isActive ? `${tab.color}1A` : "transparent",
          color: isActive ? tab.color : "var(--text-secondary)",
          borderLeft:  !isRtl ? (isActive ? `3px solid ${tab.color}` : "3px solid transparent") : undefined,
          borderRight:  isRtl ? (isActive ? `3px solid ${tab.color}` : "3px solid transparent") : undefined,
        }}
        onMouseEnter={e => { if (!isActive) { e.currentTarget.style.background = "var(--surface-2)"; e.currentTarget.style.color = "var(--text-primary)"; } }}
        onMouseLeave={e => { if (!isActive) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--text-secondary)"; } }}
      >
        <span className="w-5 h-5 flex items-center justify-center flex-shrink-0"
          style={{ color: isActive ? tab.color : "var(--text-secondary)", opacity: isActive ? 1 : 0.7 }}>
          {iconEl}
        </span>
        {!railMode && (
          <>
            <span className="flex-1 text-[13px] truncate"
              style={{ marginLeft: isRtl ? 0 : 9, marginRight: isRtl ? 9 : 0, fontWeight: isActive ? 600 : 450 }}>
              {tab.label}
            </span>
            {!!tab.badge && (
              <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[9px] font-bold text-white flex-shrink-0"
                style={{ background: tab.color, marginLeft: isRtl ? 0 : 3, marginRight: isRtl ? 3 : 0 }}>
                {tab.badge}
              </span>
            )}
          </>
        )}
        {railMode && !!tab.badge && (
          <span
            className="absolute w-3.5 h-3.5 rounded-full flex items-center justify-center text-[7px] font-bold text-white"
            style={{ background: tab.color, top: 3, [isRtl ? "left" : "right"]: 3 }}>
            {tab.badge > 9 ? "9+" : tab.badge}
          </span>
        )}
      </button>
    );
  };

  /* ─── Sidebar nav groups ─── */
  const SidebarNav = ({ inMobile = false }) => {
    const railMode = !inMobile && sidebarCollapsed;
    return (
      <div className="flex-1 overflow-y-auto py-2" style={{ scrollbarWidth: "thin" }}>
        {groupedTabs.map((section, si) => {
          const isExpanded = railMode || expandedSections.has(section.id);
          const isActiveSection = section.tabs.some(tab => tab.id === activeTab);
          const sectionBadge = section.tabs.reduce((sum, tab) => sum + (tab.badge || 0), 0);
          return (
            <div key={section.id} style={{ marginTop: si > 0 ? 2 : 0 }}>
              {/* Section label / divider */}
              {!railMode ? (
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full flex items-center gap-1 transition-opacity"
                  style={{ height: 28, paddingLeft: isRtl ? 10 : 12, paddingRight: isRtl ? 12 : 10 }}
                >
                  <span
                    className="flex-1 text-[10.5px] font-bold uppercase tracking-wider truncate text-start"
                    style={{ color: isActiveSection ? section.color : "var(--text-secondary)", opacity: isActiveSection ? 1 : 0.6 }}>
                    {section.title}
                  </span>
                  {sectionBadge > 0 && !isExpanded && (
                    <span className="inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 rounded-full text-[8px] font-bold text-white flex-shrink-0"
                      style={{ background: section.color }}>
                      {sectionBadge}
                    </span>
                  )}
                  <ChevronRight
                    className="w-3 h-3 transition-transform duration-150 flex-shrink-0"
                    style={{ color: isActiveSection ? section.color : "var(--text-secondary)", opacity: isActiveSection ? 0.7 : 0.45,
                      transform: isExpanded ? "rotate(90deg)" : (isRtl ? "rotate(180deg)" : "rotate(0deg)") }}
                  />
                </button>
              ) : (
                si > 0 && <div style={{ height: 1, background: "var(--border-color)", margin: "6px 12px" }} />
              )}
              {/* Tab items */}
              {isExpanded && section.tabs.map(tab => (
                <NavItem key={tab.id} tab={tab} inMobile={inMobile} />
              ))}
            </div>
          );
        })}
      </div>
    );
  };

  /* ─── Appearance settings popover ─── */
  const AppearancePanel = () => (
    <div
      className="absolute z-50 rounded-xl overflow-hidden"
      style={{
        bottom: "calc(100% + 6px)",
        [isRtl ? "right" : "left"]: 6,
        width: 228,
        background: "var(--bg-secondary)",
        border: "1px solid var(--border-color)",
        boxShadow: "0 -8px 32px rgba(0,0,0,0.14), 0 8px 32px rgba(0,0,0,0.14)",
      }}
    >
      <div className="flex items-center gap-2 px-3 py-2.5" style={{ borderBottom: "1px solid var(--border-color)" }}>
        <Sparkles className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "#6366f1" }} />
        <p className="text-[12px] font-bold" style={{ color: "var(--text-primary)" }}>
          {L(lang, "Panel Appearance", "Внешний вид", "מראה הפאנל")}
        </p>
      </div>
      <div className="p-3 space-y-4">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest mb-1.5" style={{ color: "var(--text-secondary)", opacity: 0.45 }}>
            {L(lang, "Nav icons", "Иконки навигации", "אייקוני ניווט")}
          </p>
          <div className="flex gap-1">
            {[{ v: "icons", l: L(lang, "Icons", "Иконки", "אייקונים") }, { v: "emoji", l: "Emoji" }].map(({ v, l }) => (
              <button key={v} onClick={() => setAndSaveNavStyle(v)}
                className="flex-1 py-1.5 rounded-lg text-[11px] font-semibold transition-all"
                style={{ background: navStyle === v ? "#6366f1" : "var(--surface-2)", color: navStyle === v ? "#fff" : "var(--text-secondary)" }}>
                {l}
              </button>
            ))}
          </div>
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest mb-1.5" style={{ color: "var(--text-secondary)", opacity: 0.45 }}>
            {L(lang, "Sidebar mode", "Режим сайдбара", "מצב סרגל")}
          </p>
          <div className="flex gap-1">
            {[{ v: false, l: L(lang, "Full", "Полный", "מלא") }, { v: true, l: L(lang, "Rail", "Компакт", "קומפקטי") }].map(({ v, l }) => (
              <button key={String(v)} onClick={() => setSidebarCollapsedAndSave(v)}
                className="flex-1 py-1.5 rounded-lg text-[11px] font-semibold transition-all"
                style={{ background: sidebarCollapsed === v ? "#6366f1" : "var(--surface-2)", color: sidebarCollapsed === v ? "#fff" : "var(--text-secondary)" }}>
                {l}
              </button>
            ))}
          </div>
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest mb-1.5" style={{ color: "var(--text-secondary)", opacity: 0.45 }}>
            {L(lang, "Sections", "Разделы", "קטגוריות")}
          </p>
          <div className="flex gap-1">
            <button onClick={() => setExpandedSections(new Set(groupedTabs.map(s => s.id)))}
              className="flex-1 py-1.5 rounded-lg text-[11px] font-semibold transition-all"
              style={{ background: "var(--surface-2)", color: "var(--text-secondary)" }}
              onMouseEnter={e => e.currentTarget.style.background = "var(--surface-3, var(--surface-2))"}
              onMouseLeave={e => e.currentTarget.style.background = "var(--surface-2)"}>
              {L(lang, "Open all", "Раскрыть", "פתח הכל")}
            </button>
            <button onClick={() => setExpandedSections(new Set())}
              className="flex-1 py-1.5 rounded-lg text-[11px] font-semibold transition-all"
              style={{ background: "var(--surface-2)", color: "var(--text-secondary)" }}
              onMouseEnter={e => e.currentTarget.style.background = "var(--surface-3, var(--surface-2))"}
              onMouseLeave={e => e.currentTarget.style.background = "var(--surface-2)"}>
              {L(lang, "Close all", "Свернуть", "סגור הכל")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <NavStyleContext.Provider value={navStyle}>
    <>
      {mercVisible && <MercOverlay imgSrc={mercImgUrl} onClose={() => setMercVisible(false)} />}

      {/* Appearance backdrop */}
      {showAppearance && (
        <div className="fixed inset-0 z-40" onClick={() => setShowAppearance(false)} />
      )}

      {/* ══════════ Full-screen CRM overlay ══════════ */}
      {activeTab === "tickets" && (
        <div
          className="fixed left-0 right-0 bottom-0 flex flex-col overflow-hidden"
          style={{ top: crmFullscreen ? 0 : "64px", zIndex: crmFullscreen ? 51 : 49, background: "var(--bg-secondary)" }}
          data-testid="admin-page"
        >
          <AdminTicketsTab
            lang={lang}
            initialTicketId={deepLinkTicketId}
            onClearDeepLink={() => setDeepLinkTicketId("")}
            onBack={() => setActiveTab("dashboard")}
            onNavigate={setActiveTab}
            fullscreen={crmFullscreen}
            onToggleFullscreen={() => setCrmFullscreen(f => !f)}
          />
        </div>
      )}

      {/* ══════════ Main admin shell ══════════ */}
      <div
        data-testid="admin-page"
        style={{
          display: activeTab === "tickets" ? "none" : "flex",
          flexDirection: isRtl ? "row-reverse" : "row",
          minHeight: "100vh",
          paddingTop: 64,
        }}
      >
        {/* ════════════════════════════════════════════
            DESKTOP SIDEBAR (sticky, full-height)
        ════════════════════════════════════════════ */}
        <aside
          className="hidden lg:flex flex-col flex-shrink-0 sticky top-[64px]"
          style={{
            width: sidebarW,
            height: "calc(100vh - 64px)",
            background: "var(--bg-secondary)",
            borderRight: !isRtl ? "1px solid var(--border-color)" : undefined,
            borderLeft:   isRtl ? "1px solid var(--border-color)" : undefined,
            transition: "width 0.2s cubic-bezier(0.4,0,0.2,1)",
            overflow: "hidden",
            zIndex: 45,
          }}
        >
          {/* Brand header */}
          <div
            className="flex items-center flex-shrink-0 relative"
            style={{
              height: 56,
              borderBottom: "1px solid var(--border-color)",
              paddingLeft: sidebarCollapsed ? 0 : (isRtl ? 12 : 16),
              paddingRight: sidebarCollapsed ? 0 : (isRtl ? 16 : 12),
              justifyContent: sidebarCollapsed ? "center" : "space-between",
              background: "linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(139,92,246,0.05) 100%)",
            }}
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: "linear-gradient(135deg,#6366f1,#8B5CF6)", boxShadow: "0 3px 10px rgba(99,102,241,0.45)" }}>
                <Shield className="w-[15px] h-[15px] text-white" />
              </div>
              {!sidebarCollapsed && (
                <div className="min-w-0">
                  <p className="text-[13px] font-bold leading-tight truncate" style={{ color: "var(--text-primary)" }}>
                    {t("Admin.admin_panel", lang)}
                  </p>
                  <p className="text-[10px] font-medium" style={{ color: "#6366f1", opacity: 0.8 }}>iFollowers</p>
                </div>
              )}
            </div>
            {!sidebarCollapsed && (
              <button
                onClick={() => setSidebarCollapsedAndSave(true)}
                className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors"
                style={{ color: "var(--text-secondary)" }}
                onMouseEnter={e => { e.currentTarget.style.background = "rgba(99,102,241,0.1)"; e.currentTarget.style.color = "#6366f1"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--text-secondary)"; }}
                title={L(lang, "Collapse", "Свернуть", "כווץ")}
              >
                {isRtl ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
              </button>
            )}
          </div>

          {/* Nav */}
          <SidebarNav />

          {/* Footer */}
          <div className="flex-shrink-0" style={{ borderTop: "1px solid var(--border-color)" }}>
            {/* Appearance settings button */}
            <div className="relative px-2 py-2">
              {showAppearance && !sidebarCollapsed && <AppearancePanel />}
              <button
                onClick={() => setShowAppearance(v => !v)}
                className="w-full flex items-center gap-2.5 rounded-lg transition-colors"
                style={{
                  height: 34,
                  paddingLeft: sidebarCollapsed ? 0 : (isRtl ? 8 : 10),
                  paddingRight: sidebarCollapsed ? 0 : (isRtl ? 10 : 8),
                  justifyContent: sidebarCollapsed ? "center" : undefined,
                  color: showAppearance ? "#6366f1" : "var(--text-secondary)",
                  background: showAppearance ? "rgba(99,102,241,0.08)" : "transparent",
                }}
                onMouseEnter={e => { if (!showAppearance) { e.currentTarget.style.background = "var(--surface-2)"; } }}
                onMouseLeave={e => { if (!showAppearance) { e.currentTarget.style.background = "transparent"; } }}
                title={sidebarCollapsed ? L(lang, "Appearance", "Внешний вид", "מראה") : undefined}
              >
                <Sparkles className="w-[15px] h-[15px] flex-shrink-0" />
                {!sidebarCollapsed && (
                  <span className="text-[12px] font-medium truncate">
                    {L(lang, "Appearance", "Внешний вид", "מראה")}
                  </span>
                )}
              </button>
            </div>

            {/* User card / expand toggle */}
            {!sidebarCollapsed ? (
              <div
                className="flex items-center gap-2.5"
                style={{ paddingLeft: isRtl ? 12 : 16, paddingRight: isRtl ? 16 : 12, paddingTop: 10, paddingBottom: 12 }}
              >
                <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-[11px] font-bold text-white"
                  style={{ background: "linear-gradient(135deg,#6366f1,#8B5CF6)", boxShadow: "0 2px 8px rgba(99,102,241,0.4)" }}>
                  {(user?.name || user?.email || "A")[0].toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[12px] font-semibold truncate" style={{ color: "var(--text-primary)" }}>
                    {user?.name || user?.email || "Admin"}
                  </div>
                  <div className="text-[10px] font-semibold flex items-center gap-1" style={{ color: "#6366f1" }}>
                    <Crown className="w-2.5 h-2.5 flex-shrink-0" />
                    {user?.role === "super_admin" ? "Super Admin" : "Admin"}
                  </div>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setSidebarCollapsedAndSave(false)}
                className="w-full h-11 flex items-center justify-center transition-all"
                style={{ color: "var(--text-secondary)" }}
                onMouseEnter={e => { e.currentTarget.style.color = "#6366f1"; e.currentTarget.style.background = "rgba(99,102,241,0.08)"; }}
                onMouseLeave={e => { e.currentTarget.style.color = "var(--text-secondary)"; e.currentTarget.style.background = "transparent"; }}
                title={L(lang, "Expand sidebar", "Развернуть", "הרחב")}
              >
                {isRtl ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </button>
            )}
          </div>
        </aside>

        {/* ════════════════════════════════════════════
            CONTENT COLUMN
        ════════════════════════════════════════════ */}
        <div className="flex-1 min-w-0 flex flex-col">

          {/* Mobile nav bar */}
          <div
            className="lg:hidden flex items-center gap-3 flex-shrink-0 sticky top-[64px] z-30"
            style={{
              height: 50,
              paddingLeft: isRtl ? 12 : 16,
              paddingRight: isRtl ? 16 : 12,
              background: "var(--bg-secondary)",
              borderBottom: "1px solid var(--border-color)",
            }}
          >
            <button
              onClick={() => setMobileOpen(true)}
              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors"
              style={{ background: "var(--surface-2)", color: "var(--text-primary)" }}>
              <Menu className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-1.5 flex-1 min-w-0">
              {activeSection && <span className="text-sm leading-none flex-shrink-0">{activeSection.emoji}</span>}
              {activeSection && activeTabDef && (
                <ChevronRight className="w-3 h-3 flex-shrink-0"
                  style={{ color: "var(--text-secondary)", opacity: 0.35, transform: isRtl ? "scaleX(-1)" : undefined }} />
              )}
              {activeTabDef && (
                <span className="text-[13px] font-semibold truncate" style={{ color: "var(--text-primary)" }}>
                  {activeTabDef.label}
                </span>
              )}
            </div>
            {activeTabDef?.badge > 0 && (
              <span className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white flex-shrink-0"
                style={{ background: activeTabDef.color }}>
                {activeTabDef.badge}
              </span>
            )}
          </div>

          {/* Desktop content topbar */}
          <div
            className="hidden lg:flex items-center gap-3 flex-shrink-0 sticky top-[64px] z-20 relative"
            style={{
              height: 50,
              paddingLeft: isRtl ? 20 : 24,
              paddingRight: isRtl ? 24 : 20,
              background: "var(--bg-primary)",
              borderBottom: "1px solid var(--border-color)",
            }}
          >
            {/* Colored accent line at bottom */}
            {activeTabDef && (
              <div className="absolute bottom-0 h-[2px] rounded-full"
                style={{ [isRtl ? "right" : "left"]: 24, width: 40, background: activeTabDef.color, opacity: 0.7 }} />
            )}
            <div className="flex items-center gap-1.5 text-[13px] min-w-0">
              {activeSection && (
                <span className="font-medium" style={{ color: "var(--text-secondary)", opacity: 0.65 }}>{activeSection.title}</span>
              )}
              {activeSection && activeTabDef && (
                <ChevronRight className="w-3.5 h-3.5 flex-shrink-0"
                  style={{ color: "var(--text-secondary)", opacity: 0.3, transform: isRtl ? "scaleX(-1)" : undefined }} />
              )}
              {activeTabDef && (
                <>
                  <span className="font-semibold truncate" style={{ color: "var(--text-primary)" }}>
                    {activeTabDef.label}
                  </span>
                  {activeTabDef.badge > 0 && (
                    <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[9px] font-bold text-white"
                      style={{ background: activeTabDef.color }}>
                      {activeTabDef.badge}
                    </span>
                  )}
                </>
              )}
            </div>
            <div className="flex-1" />
            <button
              onClick={fetchStats}
              className="w-8 h-8 rounded-lg flex items-center justify-center transition-all"
              style={{ color: "var(--text-secondary)" }}
              onMouseEnter={e => { e.currentTarget.style.background = "var(--surface-2)"; e.currentTarget.style.color = "var(--text-primary)"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--text-secondary)"; }}
              title={L(lang, "Refresh", "Обновить", "רענן")}
            >
              <RefreshCw className="w-[14px] h-[14px]" />
            </button>
          </div>

          {/* Tab content */}
          <div className="flex-1 p-4 lg:p-6">
            {renderContent()}
          </div>
        </div>

        {/* ════════════════════════════════════════════
            MOBILE DRAWER backdrop + panel
        ════════════════════════════════════════════ */}
        {mobileOpen && (
          <div
            className="lg:hidden fixed inset-0 z-40"
            style={{ background: "rgba(0,0,0,0.45)", backdropFilter: "blur(3px)" }}
            onClick={() => setMobileOpen(false)}
          />
        )}

        <aside
          className="lg:hidden fixed top-0 bottom-0 z-50 flex flex-col"
          style={{
            width: 280,
            [isRtl ? "right" : "left"]: 0,
            background: "var(--bg-secondary)",
            borderRight: !isRtl ? "1px solid var(--border-color)" : undefined,
            borderLeft:   isRtl ? "1px solid var(--border-color)" : undefined,
            boxShadow: "4px 0 24px rgba(0,0,0,0.18)",
            transform: mobileOpen ? "translateX(0)" : isRtl ? "translateX(100%)" : "translateX(-100%)",
            transition: "transform 0.26s cubic-bezier(0.4,0,0.2,1)",
          }}
        >
          {/* Drawer brand */}
          <div
            className="flex items-center justify-between flex-shrink-0"
            style={{ height: 60, paddingLeft: isRtl ? 12 : 16, paddingRight: isRtl ? 16 : 12, borderBottom: "1px solid var(--border-color)" }}
          >
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ background: "linear-gradient(135deg,#6366f1,#8B5CF6)" }}>
                <Shield className="w-[15px] h-[15px] text-white" />
              </div>
              <div>
                <p className="text-[13px] font-bold" style={{ color: "var(--text-primary)" }}>{t("Admin.admin_panel", lang)}</p>
                <p className="text-[10px]" style={{ color: "var(--text-secondary)" }}>iFollowers</p>
              </div>
            </div>
            <button
              onClick={() => setMobileOpen(false)}
              className="w-8 h-8 rounded-lg flex items-center justify-center transition-colors"
              style={{ background: "var(--surface-2)", color: "var(--text-secondary)" }}>
              <X className="w-4 h-4" />
            </button>
          </div>

          <SidebarNav inMobile={true} />

          {/* Mobile drawer footer */}
          <div
            className="flex-shrink-0 flex items-center gap-2.5"
            style={{ height: 56, paddingLeft: isRtl ? 12 : 16, paddingRight: isRtl ? 16 : 12, borderTop: "1px solid var(--border-color)" }}
          >
            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-[11px] font-bold text-white"
              style={{ background: "linear-gradient(135deg,#6366f1,#8B5CF6)" }}>
              {(user?.name || user?.email || "A")[0].toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[12px] font-semibold truncate" style={{ color: "var(--text-primary)" }}>
                {user?.name || user?.email || "Admin"}
              </div>
              <div className="text-[10px] flex items-center gap-1" style={{ color: "#6366f1" }}>
                <Crown className="w-2.5 h-2.5 flex-shrink-0" />
                {user?.role === "super_admin" ? "Super Admin" : "Admin"}
              </div>
            </div>
          </div>
        </aside>
      </div>
    </>
    </NavStyleContext.Provider>
  );
}

function AdminImprovementsTab({ lang }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyIds, setBusyIds] = useState(new Set());
  const [form, setForm] = useState({ title: "", description: "", priority: "medium" });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [expandedIds, setExpandedIds] = useState(new Set());
  // Drag state
  const [draggingId, setDraggingId] = useState(null);
  const [overColumn, setOverColumn] = useState(null);
  const [overItemId, setOverItemId] = useState(null);
  const dragItemRef = useRef(null);
  const dragSourceColumn = useRef(null);
  // dragCounters: tracks nested dragEnter/Leave per column to prevent flickering
  const dragCounters = useRef({ idea: 0, in_progress: 0, done: 0 });
  // itemsRef: stable reference so patchItem doesn't re-create on every items change
  const itemsRef = useRef([]);

  const PRIORITY_META = useMemo(() => ({
    urgent: { label: t("Admin.urgent", lang),    color:"#EF4444", bg:"rgba(239,68,68,0.12)",    order:0 },
    high:   { label: t("Admin.high", lang),   color:"#F97316", bg:"rgba(249,115,22,0.12)",   order:1 },
    medium: { label: t("Admin.medium", lang),  color:"#7aa2ff", bg:"rgba(122,162,255,0.12)",  order:2 },
    low:    { label: t("Admin.low", lang),   color:"#94A3B8", bg:"var(--border-color)",  order:3 },
  }), [lang]);

  const STATUS_META = useMemo(() => ({
    idea: {
      label: t("Admin.ideas", lang),
      color:"#FACC15", bg:"rgba(250,204,21,0.12)",
      icon: <Lightbulb className="w-3.5 h-3.5" />,
      empty: t("Admin.drop_cards_here", lang),
    },
    in_progress: {
      label: t("Admin.in_progress", lang),
      color:"#7aa2ff", bg:"rgba(122,162,255,0.12)",
      icon: <Zap className="w-3.5 h-3.5" />,
      empty: t("Admin.drop_cards_here", lang),
    },
    done: {
      label: t("Admin.done", lang),
      color:"#22C55E", bg:"rgba(34,197,94,0.12)",
      icon: <CheckCircle2 className="w-3.5 h-3.5" />,
      empty: t("Admin.drop_cards_here", lang),
    },
  }), [lang]);

  const fetchItems = useCallback(async () => {
    try {
      const response = await fetch(`${API}/admin/improvements`, { credentials: "include" });
      const data = await response.json().catch(() => ([]));
      if (!response.ok) throw new Error(data.detail || "Failed to load");
      setItems(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => { itemsRef.current = items; }, [items]);

  const createItem = async () => {
    if (!form.title.trim()) { toast.error(t("Admin.enter_a_title", lang)); return; }
    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/improvements`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: form.title.trim(), description: form.description.trim(), priority: form.priority }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || "Failed to create");
      setItems(cur => [data, ...cur]);
      setForm({ title: "", description: "", priority: "medium" });
      toast.success(t("Admin.idea_added", lang));
    } catch (error) {
      toast.error(error.message);
    } finally { setSaving(false); }
  };

  const patchItem = useCallback(async (id, patch, optimisticFn) => {
    setBusyIds(s => new Set([...s, id]));
    // Snapshot for rollback — read from ref so this callback stays stable
    const snapshot = itemsRef.current;
    if (optimisticFn) setItems(optimisticFn);
    try {
      const response = await fetch(`${API}/admin/improvements/${id}`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || "Failed to update");
      setItems(cur => cur.map(e => e.improvement_id === id ? data : e));
    } catch (error) {
      if (optimisticFn) setItems(snapshot); // rollback
      toast.error(error.message);
    } finally { setBusyIds(s => { const n = new Set(s); n.delete(id); return n; }); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveEdit = async (item) => {
    if (!editForm.title?.trim()) { toast.error(t("Admin.title_required", lang)); return; }
    const patch = { title: editForm.title.trim(), description: editForm.description?.trim() || "", priority: editForm.priority };
    setEditingId(null);
    await patchItem(item.improvement_id, patch,
      cur => cur.map(e => e.improvement_id === item.improvement_id ? { ...e, ...patch } : e));
  };

  const removeItem = async (item) => {
    if (!window.confirm(t("Admin.delete_this_idea", lang))) return;
    setBusyIds(s => new Set([...s, item.improvement_id]));
    try {
      const response = await fetch(`${API}/admin/improvements/${item.improvement_id}`, { method: "DELETE", credentials: "include" });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.detail || "Failed to delete");
      setItems(cur => cur.filter(e => e.improvement_id !== item.improvement_id));
      toast.success(t("Admin.deleted", lang));
    } catch (error) {
      toast.error(error.message);
    } finally { setBusyIds(s => { const n = new Set(s); n.delete(item.improvement_id); return n; }); }
  };

  // ── Drag & Drop handlers ──────────────────────────────────────────────────
  const onDragStart = useCallback((e, item) => {
    dragItemRef.current = item;
    dragSourceColumn.current = item.status;
    setDraggingId(item.improvement_id);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", item.improvement_id);
  }, []);

  const onDragEnd = useCallback(() => {
    // Reset all counters
    dragCounters.current = { idea: 0, in_progress: 0, done: 0 };
    setDraggingId(null);
    setOverColumn(null);
    setOverItemId(null);
    dragItemRef.current = null;
    dragSourceColumn.current = null;
  }, []);

  const onColumnDragEnter = useCallback((e, colStatus) => {
    e.preventDefault();
    dragCounters.current[colStatus] = (dragCounters.current[colStatus] || 0) + 1;
    setOverColumn(colStatus);
  }, []);

  const onColumnDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }, []);

  const onColumnDragLeave = useCallback((e, colStatus) => {
    dragCounters.current[colStatus] = Math.max(0, (dragCounters.current[colStatus] || 0) - 1);
    if (dragCounters.current[colStatus] === 0) {
      setOverColumn(prev => prev === colStatus ? null : prev);
    }
  }, []);

  const onColumnDrop = useCallback((e, targetStatus) => {
    e.preventDefault();
    dragCounters.current = { idea: 0, in_progress: 0, done: 0 };
    const item = dragItemRef.current;
    setOverColumn(null);
    setOverItemId(null);
    if (!item || item.status === targetStatus) return;
    patchItem(
      item.improvement_id,
      { status: targetStatus },
      cur => cur.map(e => e.improvement_id === item.improvement_id ? { ...e, status: targetStatus } : e)
    );
  }, [patchItem]);

  // ── Grouped + sorted items ────────────────────────────────────────────────
  const groupedItems = useMemo(() => {
    const sortByPriority = arr => [...arr].sort((a, b) => {
      const oa = PRIORITY_META[a.priority || "medium"]?.order ?? 2;
      const ob = PRIORITY_META[b.priority || "medium"]?.order ?? 2;
      return oa - ob;
    });
    return {
      idea:        sortByPriority(items.filter(i => i.status === "idea")),
      in_progress: sortByPriority(items.filter(i => i.status === "in_progress")),
      done:        sortByPriority(items.filter(i => i.status === "done")),
    };
  }, [items, PRIORITY_META]);

  const urgentCount = items.filter(i => i.priority === "urgent" && i.status !== "done").length;

  if (loading) return (
    <div className="rounded-2xl border p-8 flex items-center justify-center" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
      <div className="animate-spin h-7 w-7 border-2 border-[#FACC15] border-t-transparent rounded-full" />
    </div>
  );

  return (
    <div className="space-y-4 animate-fade-in-up">

      {/* ── Header ── */}
      <div className="rounded-2xl p-4 border" style={{ borderColor: "var(--border-color)", background: "var(--surface-1)" }}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <AdminIcon emoji="💡" className="w-5 h-5" />
            <div>
              <h2 className="text-base font-bold">{t("Admin.improvements_tracker", lang)}</h2>
              <p className="text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>{t("Admin.drag_cards_between_columns", lang)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {urgentCount > 0 && (
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold animate-pulse" style={{ background:"rgba(239,68,68,0.15)", color:"#EF4444" }}>
                <AlertCircle className="w-3 h-3" />
                {urgentCount} {t("Admin.urgent_2", lang)}
              </div>
            )}
            {Object.entries(STATUS_META).map(([status, meta]) => (
              <div key={status} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: meta.bg, color: meta.color }}>
                {meta.icon}
                <span className="tabular-nums">{(groupedItems[status] || []).length}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Add form ── */}
      <div className="rounded-2xl border p-4" style={{ borderColor: "var(--border-color)", background: "var(--surface-1)" }}>
        <div className="flex flex-col gap-3">
          <Input
            value={form.title}
            onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            onKeyDown={e => e.key === "Enter" && !e.shiftKey && createItem()}
            placeholder={t("Admin.new_idea_title", lang)}
            className="h-10 rounded-xl text-sm"
            style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }}
          />
          <Textarea
            value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            placeholder={t("Admin.details_scope_links_optional", lang)}
            className="min-h-[64px] resize-none text-sm rounded-xl"
            style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }}
          />
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[11px] font-medium" style={{ color:"var(--text-secondary)" }}>{t("Admin.priority_2", lang)}</span>
              {Object.entries(PRIORITY_META).map(([p, pm]) => (
                <button key={p} type="button" onClick={() => setForm(f => ({ ...f, priority: p }))}
                  className="px-2.5 py-1 rounded-full text-[11px] font-semibold transition-all"
                  style={{
                    background: form.priority === p ? pm.bg : "var(--surface-2)",
                    color: form.priority === p ? pm.color : "var(--text-secondary)",
                    border: `1.5px solid ${form.priority === p ? pm.color : "transparent"}`,
                  }}>
                  {pm.label}
                </button>
              ))}
            </div>
            <Button onClick={createItem} disabled={saving || !form.title.trim()} className="btn-brand h-9 px-5 text-xs">
              <Plus className="w-3.5 h-3.5 me-1.5" />
              {saving ? t("Admin.adding", lang) : t("Admin.add_idea", lang)}
            </Button>
          </div>
        </div>
      </div>

      {/* ── Kanban board ── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        {Object.entries(STATUS_META).map(([status, meta]) => {
          const colItems = groupedItems[status] || [];
          const isDragTarget = overColumn === status && draggingId && dragSourceColumn.current !== status;
          return (
            <div key={status}
              className="flex flex-col"
              onDragEnter={e => onColumnDragEnter(e, status)}
              onDragOver={onColumnDragOver}
              onDragLeave={e => onColumnDragLeave(e, status)}
              onDrop={e => onColumnDrop(e, status)}
            >
              {/* Column header */}
              <div className="flex items-center justify-between px-1 mb-2">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: meta.color }} />
                  <span className="text-xs font-bold uppercase tracking-wide" style={{ color: meta.color }}>{meta.label}</span>
                </div>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold tabular-nums"
                  style={{ background: meta.bg, color: meta.color }}>
                  {colItems.length}
                </span>
              </div>

              {/* Drop zone column body */}
              <div
                className="flex-1 flex flex-col gap-2 rounded-2xl p-2 min-h-[80px] transition-all"
                style={{
                  background: isDragTarget ? `${meta.color}10` : "transparent",
                  border: `2px dashed ${isDragTarget ? meta.color : "transparent"}`,
                  transition: "background 0.15s, border-color 0.15s",
                }}
              >
                {colItems.length === 0 && (
                  <div className="flex-1 rounded-xl border-2 border-dashed px-3 py-8 text-xs text-center flex flex-col items-center justify-center gap-1.5"
                    style={{ borderColor: isDragTarget ? meta.color : "var(--surface-3)", color: isDragTarget ? meta.color : "var(--text-secondary)" }}>
                    {isDragTarget
                      ? <><CheckCircle2 className="w-4 h-4 mb-0.5" />{t("Admin.release_to_move", lang)}</>
                      : meta.empty}
                  </div>
                )}

                {colItems.map(item => {
                  const pm = PRIORITY_META[item.priority || "medium"];
                  const isEditing = editingId === item.improvement_id;
                  const isDragging = draggingId === item.improvement_id;
                  const isBusy = busyIds.has(item.improvement_id);

                  return (
                    <div key={item.improvement_id}
                      draggable={!isEditing}
                      onDragStart={e => !isEditing && onDragStart(e, item)}
                      onDragEnd={onDragEnd}
                      className="rounded-2xl overflow-hidden select-none"
                      style={{
                        background: "var(--bg-secondary)",
                        border: "1px solid var(--border-color)",
                        borderInlineStart: `3px solid ${pm.color}`,
                        opacity: isDragging ? 0.4 : 1,
                        cursor: isEditing ? "default" : "grab",
                        transition: "opacity 0.1s",
                      }}>

                      {isEditing ? (
                        /* ── Edit mode ── */
                        <div className="p-3 flex flex-col gap-2">
                          <Input value={editForm.title}
                            onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))}
                            className="text-sm h-9 rounded-xl"
                            style={{ background: "var(--surface-2)", borderColor: "var(--border-color)" }}
                          />
                          <Textarea value={editForm.description}
                            onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                            className="min-h-[60px] resize-none text-xs rounded-xl"
                            style={{ background: "var(--surface-2)", borderColor: "var(--border-color)" }}
                          />
                          <div className="flex items-center gap-1 flex-wrap">
                            {Object.entries(PRIORITY_META).map(([p, em]) => (
                              <button key={p} type="button" onClick={() => setEditForm(f => ({ ...f, priority: p }))}
                                className="px-2 py-0.5 rounded-full text-[10px] font-semibold transition-all"
                                style={{
                                  background: editForm.priority === p ? em.bg : "var(--surface-2)",
                                  color: editForm.priority === p ? em.color : "var(--text-secondary)",
                                  border: `1.5px solid ${editForm.priority === p ? em.color : "transparent"}`,
                                }}>
                                {em.label}
                              </button>
                            ))}
                          </div>
                          <div className="flex items-center gap-2">
                            <Button onClick={() => saveEdit(item)} className="btn-brand h-7 px-3 text-xs flex-1">
                              <Save className="w-3 h-3 me-1" />{t("admin.save", lang)}
                            </Button>
                            <button type="button" onClick={() => setEditingId(null)}
                              className="h-7 px-3 rounded-xl text-xs font-medium transition-all"
                              style={{ color:"var(--text-secondary)", background:"var(--surface-2)" }}>
                              {t("Admin.cancel", lang)}
                            </button>
                          </div>
                        </div>
                      ) : (
                        /* ── View mode ── */
                        <div className="p-3">
                          {/* Title + action icons */}
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="min-w-0 flex-1">
                              <div className="text-[13px] font-bold leading-5" style={{ color:"var(--text-primary)" }}>{item.title}</div>
                              {item.description && (() => {
                                const expanded = expandedIds.has(item.improvement_id);
                                const isLong = item.description.length > 160 || item.description.split('\n').length > 3;
                                return (
                                  <div className="mt-1">
                                    <p className={`text-[11.5px] leading-4 ${!expanded && isLong ? "line-clamp-3" : ""}`} style={{ color:"var(--text-secondary)", whiteSpace:"pre-line" }}>{item.description}</p>
                                    {isLong && (
                                      <button type="button"
                                        onClick={() => setExpandedIds(s => { const n = new Set(s); expanded ? n.delete(item.improvement_id) : n.add(item.improvement_id); return n; })}
                                        className="text-[10px] mt-0.5 font-semibold hover:underline"
                                        style={{ color:"#7aa2ff" }}>
                                        {expanded ? t("Admin.less", lang) : t("Admin.more", lang)}
                                      </button>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <button type="button"
                                onClick={() => { setEditingId(item.improvement_id); setEditForm({ title: item.title, description: item.description || "", priority: item.priority || "medium" }); }}
                                className="w-6 h-6 rounded-lg flex items-center justify-center transition-all hover:bg-white/8"
                                style={{ color:"var(--text-secondary)" }}>
                                <PenSquare className="w-3 h-3" />
                              </button>
                              <button type="button" onClick={() => removeItem(item)}
                                disabled={isBusy}
                                className="w-6 h-6 rounded-lg flex items-center justify-center transition-all hover:bg-red-500/10 disabled:opacity-40"
                                style={{ color:"#f87171" }}>
                                {isBusy
                                  ? <div className="animate-spin h-3 w-3 border border-red-400 border-t-transparent rounded-full" />
                                  : <Trash2 className="w-3 h-3" />}
                              </button>
                            </div>
                          </div>

                          {/* Meta: priority badge + date */}
                          <div className="flex items-center gap-1.5 flex-wrap mb-2.5">
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold" style={{ background:pm.bg, color:pm.color }}>
                              {pm.label}
                            </span>
                            <span className="text-[10px] ms-auto tabular-nums" style={{ color:"var(--text-secondary)" }}>
                              {formatDateTime(item.updated_at)}
                            </span>
                          </div>

                          {/* Priority quick-change */}
                          <div className="flex items-center gap-1 mb-2.5">
                            {Object.entries(PRIORITY_META).map(([p, pm2]) => (
                              <button key={p} type="button"
                                onClick={() => patchItem(item.improvement_id, { priority: p },
                                  cur => cur.map(e => e.improvement_id === item.improvement_id ? { ...e, priority: p } : e))}
                                disabled={isBusy}
                                className="px-1.5 py-0.5 rounded-full text-[9px] font-semibold transition-all disabled:opacity-40"
                                style={{
                                  background: (item.priority || "medium") === p ? pm2.bg : "transparent",
                                  color: (item.priority || "medium") === p ? pm2.color : "var(--text-secondary)",
                                  border: `1px solid ${(item.priority || "medium") === p ? pm2.color : "var(--border-color)"}`,
                                }}>
                                {pm2.label}
                              </button>
                            ))}
                          </div>

                          {/* Move buttons (fallback for touch / non-drag) */}
                          <div className="flex flex-wrap gap-1.5">
                            {item.status !== "idea" && (
                              <button type="button"
                                onClick={() => patchItem(item.improvement_id, { status:"idea" },
                                  cur => cur.map(e => e.improvement_id === item.improvement_id ? { ...e, status:"idea" } : e))}
                                disabled={isBusy}
                                className="py-1.5 px-2.5 rounded-xl text-[10px] font-semibold transition-all disabled:opacity-50"
                                style={{ background:"rgba(250,204,21,0.1)", color:"#FACC15" }}>
                                <Lightbulb className="w-3 h-3 inline me-1" />{t("Admin.idea", lang)}
                              </button>
                            )}
                            {item.status !== "in_progress" && (
                              <button type="button"
                                onClick={() => patchItem(item.improvement_id, { status:"in_progress" },
                                  cur => cur.map(e => e.improvement_id === item.improvement_id ? { ...e, status:"in_progress" } : e))}
                                disabled={isBusy}
                                className="py-1.5 px-2.5 rounded-xl text-[10px] font-semibold transition-all disabled:opacity-50"
                                style={{ background:"rgba(122,162,255,0.1)", color:"#7aa2ff" }}>
                                <Zap className="w-3 h-3 inline me-1" />{t("Admin.in_progress_2", lang)}
                              </button>
                            )}
                            {item.status !== "done" && (
                              <button type="button"
                                onClick={() => patchItem(item.improvement_id, { status:"done" },
                                  cur => cur.map(e => e.improvement_id === item.improvement_id ? { ...e, status:"done" } : e))}
                                disabled={isBusy}
                                className="py-1.5 px-2.5 rounded-xl text-[10px] font-semibold transition-all disabled:opacity-50"
                                style={{ background:"rgba(34,197,94,0.1)", color:"#22C55E" }}>
                                <CheckCircle2 className="w-3 h-3 inline me-1" />{t("Admin.done_2", lang)}
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ===== DASHBOARD =====
// Reusable stat card for dashboard
function StatCard({ label, value, sub, color = "#8B5CF6", large = false, icon = null }) {
  return (
    <div className="rounded-2xl overflow-hidden" style={{ background:"var(--surface-1)", border:`1px solid ${color}28` }}>
      <div style={{ height: 3, background: `linear-gradient(90deg, ${color}, ${color}60)` }} />
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="text-[10px] font-bold uppercase tracking-wider leading-tight" style={{ color:"var(--text-secondary)" }}>{label}</div>
          {icon && (
            <div className="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center ms-2" style={{ background:`${color}18` }}>
              <span style={{ color, display:"flex" }}>{icon}</span>
            </div>
          )}
        </div>
        <div className={`font-black font-outfit leading-none ${large ? "text-[34px]" : "text-[26px]"}`} style={{ color, fontVariantNumeric:"tabular-nums" }}>{value}</div>
        {sub && <div className="text-[10px] mt-1.5" style={{ color:"var(--text-secondary)" }}>{sub}</div>}
      </div>
    </div>
  );
}

function DashboardTab({ lang, stats, japBalance, fsBalance }) {
  const [realtime, setRealtime] = useState(null);
  useEffect(() => {
    fetch(`${API}/admin/analytics/realtime`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setRealtime(d); })
      .catch(() => {});
  }, []);

  if (!stats) return null;
  const m          = stats.monthly || {};
  const fsData     = fsBalance || stats.provider_balance;
  const provBal    = formatProviderBalance(fsData);
  const provBIls   = formatProviderBalanceIls(fsData);
  const japBal     = formatProviderBalance(japBalance);
  const japBIls    = formatProviderBalanceIls(japBalance);
  const commission = Math.round((m.topup_ils||0) * 0.25 * 100) / 100;
  const now        = new Date();
  const monthName  = now.toLocaleString(lang === "he" ? "he-IL" : lang === "ru" ? "ru-RU" : "en-US", { month: "long", year: "numeric" });
  const N          = (v) => (v||0).toLocaleString();

  const SectionLabel = ({ icon: Icon, label, color = "#8B5CF6" }) => (
    <div className="flex items-center gap-2 mb-2">
      <div className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0" style={{ background:`${color}20` }}>
        <Icon className="w-3 h-3" style={{ color }} />
      </div>
      <span className="text-[11px] font-bold uppercase tracking-widest" style={{ color:"var(--text-secondary)" }}>{label}</span>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="monthly-stats">

      {/* ══ ROW 1 — 4 month KPIs ══ */}
      <div>
        <SectionLabel icon={CalendarDays} label={monthName} color="#22c55e" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard large label={t("Admin.revenue_this_month", lang)} value={`${N(m.topup_ils)} ₪`} sub={monthName} color="#22c55e" icon={<TrendingUp className="w-3.5 h-3.5"/>} />
          <StatCard large label={t("Admin.net_profit", lang)}          value={`${N(m.net_profit)} ₪`} sub={t("Admin.this_month", lang)} color="#F77737" icon={<DollarSign className="w-3.5 h-3.5"/>} />
          <StatCard large label={t("admin.orders", lang)}               value={N(m.orders)}             sub={t("Admin.this_month", lang)} color="#8B5CF6" icon={<Package className="w-3.5 h-3.5"/>} />
          <StatCard large label={t("Admin.new_users", lang)}            value={N(m.new_users)}           sub={t("Admin.this_month", lang)} color="#7aa2ff" icon={<UsersIcon className="w-3.5 h-3.5"/>} />
        </div>
      </div>

      {/* ══ ROW 2 — all-time + live ══ */}
      <div>
        <SectionLabel icon={BarChart3} label={L(lang,"Overview","Обзор","סקירה")} color="#8B5CF6" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">

          {/* All-time revenue card */}
          <div className="rounded-2xl overflow-hidden" style={{ background:"var(--surface-1)", border:"1px solid rgba(16,185,129,0.2)" }}>
            <div style={{ height:3, background:"linear-gradient(90deg,#10b981,#22c55e60)" }} />
            <div className="p-5">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background:"rgba(16,185,129,0.12)" }}>
                    <Wallet className="w-3.5 h-3.5" style={{ color:"#10b981" }} />
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color:"var(--text-secondary)" }}>{t("Admin.total_revenue", lang)}</span>
                </div>
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background:"rgba(16,185,129,0.12)", color:"#10b981" }}>
                  +{N(m.topup_ils)} ₪ {t("Admin.this_month_2", lang)} ↑
                </span>
              </div>
              <div className="text-[42px] font-black font-outfit leading-none tracking-tight mt-2" style={{ color:"var(--text-primary)", fontVariantNumeric:"tabular-nums" }}>
                {N(stats.total_topup_ils)}
                <span className="text-2xl font-bold ms-1.5" style={{ color:"#10b981" }}>₪</span>
              </div>
              <div className="text-[10px] mt-1.5 mb-4" style={{ color:"var(--text-secondary)" }}>{t("Admin.all_time", lang)}</div>
              <div style={{ height:1, background:"var(--border-color)" }} />
              <div className="pt-4 grid grid-cols-3 gap-3">
                {[
                  { label: t("Admin.total_users", lang),  value: N(stats.total_users),  color:"#8B5CF6", icon:<UsersIcon className="w-3 h-3"/> },
                  { label: t("Admin.total_orders", lang), value: N(stats.total_orders), color:"#7aa2ff", icon:<Package className="w-3 h-3"/> },
                  { label: t("Admin.commission", lang),   value: `${N(commission)} ₪`,  color:"#fb7185", icon:<Percent className="w-3 h-3"/> },
                ].map((s,i) => (
                  <div key={i}>
                    <div className="flex items-center gap-1 mb-1">
                      <span style={{ color:s.color, display:"flex" }}>{s.icon}</span>
                      <span className="text-[10px]" style={{ color:"var(--text-secondary)" }}>{s.label}</span>
                    </div>
                    <div className="text-[18px] font-black font-outfit leading-none" style={{ color:s.color, fontVariantNumeric:"tabular-nums" }}>{s.value}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Realtime + orders status */}
          <div className="grid grid-rows-2 gap-3">

            {/* Online / visitors */}
            <div className="rounded-2xl overflow-hidden" style={{ background:"var(--surface-1)", border:"1px solid rgba(34,197,94,0.2)" }}>
              <div style={{ height:3, background:"linear-gradient(90deg,#22c55e,#22c55e40)" }} />
              <div className="grid grid-cols-3 h-[calc(100%-3px)]">
                {[
                  { label: t("Admin.online_now", lang),    value: realtime?.online_count ?? "—",     color:"#22c55e", dot:true, icon:<Zap className="w-3 h-3"/> },
                  { label: t("Admin.today_visitors", lang), value: realtime?.today_visitors ?? "—",   color:"#8B5CF6", icon:<UsersIcon className="w-3 h-3"/> },
                  { label: t("Admin.today_views", lang),    value: realtime?.today_page_views ?? "—", color:"#0ea5e9", icon:<Eye className="w-3 h-3"/> },
                ].map((s,i) => (
                  <div key={i} className="px-4 py-3 border-s first:border-s-0" style={{ borderColor:"var(--border-color)" }}>
                    <div className="flex items-center gap-1.5 mb-1.5">
                      {s.dot && <span className="w-1.5 h-1.5 rounded-full animate-pulse flex-shrink-0" style={{ background:s.color }} />}
                      <span style={{ color:s.color, display:"flex" }}>{s.icon}</span>
                      <span className="text-[10px] font-bold uppercase tracking-wider leading-tight" style={{ color:"var(--text-secondary)" }}>{s.label}</span>
                    </div>
                    <div className="text-[22px] font-black font-outfit leading-none" style={{ color:s.color, fontVariantNumeric:"tabular-nums" }}>
                      {typeof s.value === "number" ? s.value.toLocaleString() : s.value}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Orders status */}
            <div className="rounded-2xl overflow-hidden" style={{ background:"var(--surface-1)", border:"1px solid rgba(139,92,246,0.2)" }}>
              <div style={{ height:3, background:"linear-gradient(90deg,#8B5CF6,#8B5CF640)" }} />
              <div className="grid grid-cols-3 h-[calc(100%-3px)]">
                {[
                  { label: t("Admin.in_progress_3", lang), value: N(stats.pending_orders),   color:"#F77737", icon:<Clock className="w-3 h-3"/> },
                  { label: t("Admin.completed_2", lang),   value: N(stats.completed_orders), color:"#22c55e", icon:<CheckCircle className="w-3 h-3"/> },
                  { label: "iP " + t("Admin.in_circulation", lang), value: N(stats.total_ipoints_in_circulation), color:"#a855f7", icon:<Coins className="w-3 h-3"/> },
                ].map((s,i) => (
                  <div key={i} className="px-4 py-3 border-s first:border-s-0" style={{ borderColor:"var(--border-color)" }}>
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <span style={{ color:s.color, display:"flex" }}>{s.icon}</span>
                      <span className="text-[10px] font-bold uppercase tracking-wider leading-tight" style={{ color:"var(--text-secondary)" }}>{s.label}</span>
                    </div>
                    <div className="text-[22px] font-black font-outfit leading-none" style={{ color:s.color, fontVariantNumeric:"tabular-nums" }}>{s.value}</div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ══ ROW 3 — provider balances + ipoints ══ */}
      <div>
        <SectionLabel icon={Globe} label={L(lang,"Providers & iPoints","Провайдеры и iPoints","ספקים ו-iPoints")} color="#0ea5e9" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { label:"Follow.Sale", tag:"FS",  color:"#a855f7", value: provBal||(fsData?.error?"Error":"—"),    sub: provBIls, icon:<Wallet className="w-3.5 h-3.5"/> },
            { label:"JustAnotherPanel", tag:"JAP", color:"#0ea5e9", value: japBal||(japBalance?.error?"Error":"—"), sub: japBIls, icon:<Zap className="w-3.5 h-3.5"/> },
            { label: t("Admin.ipoints_earned", lang),   value:`${N(stats.total_ipoints_earned)} iP`,         sub: t("Admin.all_time", lang),             color:"#f97316", icon:<TrendingUp className="w-3.5 h-3.5"/> },
            { label: t("Admin.in_circulation", lang),   value:`${N(stats.total_ipoints_in_circulation)} iP`, sub: t("Admin.active_user_balances", lang), color:"#a855f7", icon:<Coins className="w-3.5 h-3.5"/> },
          ].map((s,i) => (
            <div key={i} className="rounded-2xl overflow-hidden" style={{ background:"var(--surface-1)", border:`1px solid ${s.color}28` }}>
              <div style={{ height:3, background:`linear-gradient(90deg,${s.color},${s.color}50)` }} />
              <div className="p-4">
                <div className="flex items-center gap-1.5 mb-2.5">
                  {s.tag && <span className="text-[9px] font-black px-1.5 py-0.5 rounded-md" style={{ background:`${s.color}18`, color:s.color }}>{s.tag}</span>}
                  <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background:`${s.color}15` }}>
                    <span style={{ color:s.color, display:"flex" }}>{s.icon}</span>
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-wider truncate" style={{ color:"var(--text-secondary)" }}>{s.label}</span>
                </div>
                <div className="text-[22px] font-black font-outfit leading-none mb-1" style={{ color:"var(--text-primary)", fontVariantNumeric:"tabular-nums" }}>{s.value}</div>
                {s.sub && <div className="text-[10px]" style={{ color:`${s.color}90` }}>{s.sub}</div>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ══ ROW 4 — free gifts (conditional) ══ */}
      {stats.free_gifts && (
        <div>
          <SectionLabel icon={Gift} label={`🎁 ${t("Admin.free_bonus_claimed", lang)}`} color="#f43f5e" />
          <div className="rounded-2xl overflow-hidden" style={{ background:"var(--surface-1)", border:"1px solid rgba(244,63,94,0.2)" }}>
            <div style={{ height:3, background:"linear-gradient(90deg,#f43f5e,#a855f7)" }} />
            <div className="grid grid-cols-2 sm:grid-cols-4">
              {[
                { label: t("shop.likes", lang),     value: N(stats.free_gifts.likes_claimed),     sub:`${N(stats.free_gifts.total_likes_sent)} sent`,    color:"#f43f5e", icon:<Heart className="w-3.5 h-3.5"/> },
                { label: t("shop.followers", lang), value: N(stats.free_gifts.followers_claimed), sub:`${N(stats.free_gifts.total_followers_sent)} sent`, color:"#a855f7", icon:<UsersIcon className="w-3.5 h-3.5"/> },
                { label: t("Admin.cost_usd", lang), value:`$${(stats.free_gifts.provider_cost_usd||0).toFixed(4)}`, sub: t("Admin.provider_rates", lang), color:"#f97316", icon:<DollarSign className="w-3.5 h-3.5"/> },
                { label: t("Admin.cost_ils", lang), value:`₪${(stats.free_gifts.provider_cost_ils||0).toFixed(2)}`, sub: t("Admin.converted", lang),      color:"#10b981", icon:<TrendingDown className="w-3.5 h-3.5"/> },
              ].map((s,i) => (
                <div key={i} className="p-4 border-s first:border-s-0" style={{ borderColor:"var(--border-color)" }}>
                  <div className="flex items-center gap-1.5 mb-2">
                    <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0" style={{ background:`${s.color}15` }}>
                      <span style={{ color:s.color, display:"flex" }}>{s.icon}</span>
                    </div>
                    <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color:"var(--text-secondary)" }}>{s.label}</span>
                  </div>
                  <div className="text-[22px] font-black font-outfit leading-none" style={{ color:s.color, fontVariantNumeric:"tabular-nums" }}>{s.value}</div>
                  <div className="text-[10px] mt-1.5" style={{ color:"var(--text-secondary)" }}>{s.sub}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
// ===== SEO ANALYTICS TAB =====

const PIE_COLORS = ["#8B5CF6","#4F46E5","#E1306C","#F77737","#22c55e","#0ea5e9","#f59e0b","#ec4899","#14b8a6","#f43f5e"];

function SeoStatCard({ emoji, label, value, sub, color = "#8B5CF6" }) {
  return (
    <div className="rounded-2xl p-4 border" style={{ borderColor: "var(--border-color)", background: "var(--surface-1)" }}>
      <div className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: "var(--text-secondary)" }}>{label}</div>
      <div className="text-[28px] font-black font-outfit leading-none" style={{ color, fontVariantNumeric: "tabular-nums" }}>{value}</div>
      {sub && <div className="text-[10px] mt-1.5 font-medium" style={{ color: "var(--text-secondary)" }}>{sub}</div>}
    </div>
  );
}

function SeoAnalyticsTab({ lang }) {
  const [seo, setSeo] = useState(null);
  const [realtime, setRealtime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [showOnlineList, setShowOnlineList] = useState(false);

  const loadSeo = useCallback(async (d) => {
    setLoading(true);
    try {
      const [seoRes, rtRes] = await Promise.all([
        fetch(`${API}/admin/analytics/seo?days=${d}`, { credentials: "include" }),
        fetch(`${API}/admin/analytics/realtime`, { credentials: "include" }),
      ]);
      if (seoRes.ok) setSeo(await seoRes.json());
      if (rtRes.ok) setRealtime(await rtRes.json());
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { loadSeo(days); }, [loadSeo, days]);

  const refreshRealtime = async () => {
    try {
      const r = await fetch(`${API}/admin/analytics/realtime`, { credentials: "include" });
      if (r.ok) setRealtime(await r.json());
    } catch {}
  };

  const PAGE_LABELS = {
    "/": t("Admin.home", lang),
    "/shop": t("Admin.shop", lang),
    "/login": t("Admin.login", lang),
    "/dashboard": t("Admin.dashboard", lang),
    "/tasks": t("nav.tasks", lang),
    "/topup": t("Admin.top_up", lang),
    "/cart": t("nav.cart", lang),
    "/contact": t("Admin.contact", lang),
    "/faq": t("Admin.faq", lang),
    "/welcome": t("Admin.welcome", lang),
  };
  const fmtPage = (p) => PAGE_LABELS[p] || p || "/";

  const USER_AGE_LABELS = {
    today: t("Admin.today", lang),
    week: t("Admin.this_week", lang),
    month: t("Admin.this_month_3", lang),
    "3months": t("Admin.3_months", lang),
    year: t("Admin.this_year", lang),
    older: t("Admin.older", lang),
  };
  const AGE_ORDER = ["today","week","month","3months","year","older"];
  const AGE_COLORS = ["#22c55e","#0ea5e9","#8B5CF6","#F77737","#f59e0b","#9ca3af"];

  if (loading) return (
    <div className="flex items-center justify-center py-32">
      <div className="animate-spin h-8 w-8 border-2 border-[#8B5CF6] border-t-transparent rounded-full" />
    </div>
  );

  const devData = (seo?.devices || []).map(d => ({
    name: d.type === "mobile" ? t("Admin.mobile", lang)
        : d.type === "tablet" ? t("Admin.tablet", lang)
        : t("Admin.desktop", lang),
    value: d.count,
  }));
  const userTypeData = [
    { name: t("Admin.auth_2", lang), value: seo?.auth_views || 0 },
    { name: t("Admin.anonymous", lang), value: seo?.anon_views || 0 },
  ];
  const userAgeData = AGE_ORDER
    .filter(k => seo?.user_age?.[k])
    .map((k, i) => ({ name: USER_AGE_LABELS[k], value: seo.user_age[k], color: AGE_COLORS[i] }));

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="rounded-2xl border px-5 py-4" style={{ borderColor: "var(--border-color)", background: "var(--surface-1)" }}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Globe className="w-5 h-5" style={{ color: "#8B5CF6" }} />
            <div>
              <h2 className="text-lg font-bold" style={{ color: "var(--text-primary)" }}>
                {t("Admin.seo_site_analytics", lang)}
              </h2>
              <p className="text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>
                {t("Admin.visitors_pages_clicks_geography_more", lang)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {[7, 14, 30, 90].map(d => (
              <button key={d} onClick={() => setDays(d)}
                className="text-xs px-3 py-1.5 rounded-lg font-semibold transition-colors"
                style={{
                  background: days === d ? "#8B5CF6" : "var(--surface-2)",
                  color: days === d ? "#fff" : "var(--text-secondary)",
                }}>
                {d}{t("Admin.d_2", lang)}
              </button>
            ))}
            <button onClick={() => loadSeo(days)} className="p-2 rounded-xl hover:bg-white/5 transition-colors">
              <RefreshCw className="w-4 h-4" style={{ color: "var(--text-secondary)" }} />
            </button>
          </div>
        </div>
      </div>

      {/* Realtime strip */}
      <div className="rounded-2xl border overflow-hidden" style={{ borderColor: "var(--border-color)", background: "var(--surface-1)" }}>
        <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: "var(--border-color)" }}>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: "#22c55e" }} />
            <span className="text-sm font-bold" style={{ color: "var(--text-primary)" }}>
              {t("Admin.real_time", lang)}
            </span>
          </div>
          <button onClick={refreshRealtime} className="p-1.5 rounded-lg hover:bg-white/5 transition-colors">
            <RefreshCw className="w-3.5 h-3.5" style={{ color: "var(--text-secondary)" }} />
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 divide-x" style={{ borderColor: "var(--border-color)" }}>
          {[
            { label: t("Admin.online_now_2", lang), value: realtime?.online_count ?? "—", color: "#22c55e", clickable: true },
            { label: t("Admin.today_visitors_2", lang), value: realtime?.today_visitors ?? "—", color: "#8B5CF6" },
            { label: t("Admin.today_views", lang), value: realtime?.today_page_views ?? "—", color: "#0ea5e9" },
            { label: t("Admin.period_views", lang), value: (seo?.total_views ?? 0).toLocaleString(), color: "#F77737" },
          ].map((s, i) => (
            <div key={i} className={`px-5 py-4 ${s.clickable ? "cursor-pointer hover:bg-white/[0.02]" : ""}`}
              onClick={s.clickable ? () => setShowOnlineList(v => !v) : undefined}>
              <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: "var(--text-secondary)" }}>{s.label}</div>
              <div className="text-[26px] font-black font-outfit leading-none" style={{ color: s.color, fontVariantNumeric: "tabular-nums" }}>
                {typeof s.value === "number" ? s.value.toLocaleString() : s.value}
              </div>
              {s.clickable && <div className="text-[10px] mt-1" style={{ color: "var(--text-secondary)" }}>
                {t("Admin.click_to_expand", lang)}
              </div>}
            </div>
          ))}
        </div>
        {/* Online users list */}
        {showOnlineList && realtime?.online_list?.length > 0 && (
          <div className="border-t" style={{ borderColor: "var(--border-color)" }}>
            <div className="px-5 py-2 text-[10px] font-bold uppercase tracking-wider" style={{ color: "var(--text-secondary)" }}>
              {t("Admin.users_online_now", lang)}
            </div>
            <div className="divide-y" style={{ borderColor: "var(--border-color)" }}>
              {realtime.online_list.map((u) => (
                <div key={u.user_id} className="px-5 py-2.5 flex items-center gap-3">
                  <span className="w-2 h-2 flex-shrink-0 rounded-full" style={{ background: "#22c55e" }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold truncate" style={{ color: "var(--text-primary)" }}>{u.name || u.email}</div>
                    <div className="text-[10px] truncate" style={{ color: "var(--text-secondary)" }}>{u.last_page || "/"}</div>
                  </div>
                  <div className="text-[10px] shrink-0" style={{ color: "var(--text-secondary)" }}>
                    {u.last_active_at ? new Date(u.last_active_at).toLocaleTimeString() : ""}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {showOnlineList && (!realtime?.online_list?.length) && (
          <div className="px-5 py-4 border-t text-sm" style={{ borderColor: "var(--border-color)", color: "var(--text-secondary)" }}>
            {t("Admin.no_users_online_at_the_moment", lang)}
          </div>
        )}
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SeoStatCard emoji="👁️" label={t("Admin.total_views", lang)} value={(seo?.total_views||0).toLocaleString()} color="#8B5CF6" />
        <SeoStatCard emoji="👤" label={t("Admin.unique_visitors", lang)} value={(seo?.unique_visitors||0).toLocaleString()} color="#0ea5e9" />
        <SeoStatCard emoji="🔐" label={t("Admin.auth_views", lang)} value={(seo?.auth_views||0).toLocaleString()} color="#22c55e" />
        <SeoStatCard emoji="👻" label={t("Admin.anonymous_views", lang)} value={(seo?.anon_views||0).toLocaleString()} color="#F77737" />
      </div>

      {/* Daily views chart */}
      {seo?.daily_views?.length > 0 && (
        <div className="rounded-2xl border overflow-hidden" style={{ borderColor: "var(--border-color)", background: "var(--surface-1)" }}>
          <div className="px-5 py-3.5 border-b flex items-center gap-2" style={{ borderColor: "var(--border-color)" }}>
            <TrendingUp className="w-4 h-4" style={{ color: "#8B5CF6" }} />
            <span className="text-sm font-bold" style={{ color: "var(--text-primary)" }}>
              {t("Admin.daily_traffic", lang)}
            </span>
          </div>
          <div className="p-4">
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={seo.daily_views} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="gViews" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="gUnique" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="date" tick={{ fontSize: 9, fill: "var(--text-secondary)" }} tickLine={false}
                  tickFormatter={v => v?.slice(5)} />
                <YAxis tick={{ fontSize: 9, fill: "var(--text-secondary)" }} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background: "var(--bg-secondary)", border: "1px solid var(--border-color)", borderRadius: 8, fontSize: 12 }}
                  labelStyle={{ color: "var(--text-secondary)" }} />
                <Area type="monotone" dataKey="views" stroke="#8B5CF6" strokeWidth={2} fill="url(#gViews)"
                  name={t("Shop.views", lang)} />
                <Area type="monotone" dataKey="unique" stroke="#0ea5e9" strokeWidth={2} fill="url(#gUnique)"
                  name={t("Admin.unique", lang)} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Top pages + Countries side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Top pages */}
        <div className="rounded-2xl border overflow-hidden" style={{ borderColor: "var(--border-color)", background: "var(--surface-1)" }}>
          <div className="px-5 py-3.5 border-b flex items-center gap-2" style={{ borderColor: "var(--border-color)" }}>
            <Eye className="w-4 h-4" style={{ color: "#8B5CF6" }} />
            <span className="text-sm font-bold" style={{ color: "var(--text-primary)" }}>
              {t("Admin.top_pages", lang)}
            </span>
          </div>
          {!seo?.top_pages?.length ? (
            <div className="px-5 py-8 text-sm text-center" style={{ color: "var(--text-secondary)" }}>
              {t("Admin.no_data_yet", lang)}
            </div>
          ) : (
            <div className="divide-y" style={{ borderColor: "var(--border-color)" }}>
              {seo.top_pages.slice(0, 10).map((p, i) => {
                const maxV = seo.top_pages[0]?.views || 1;
                const pct = Math.round((p.views / maxV) * 100);
                return (
                  <div key={i} className="px-5 py-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-semibold truncate max-w-[65%]" style={{ color: "var(--text-primary)" }}>
                        {fmtPage(p.page)}
                      </span>
                      <div className="flex items-center gap-2 text-[10px]" style={{ color: "var(--text-secondary)" }}>
                        <span>{p.views} {t("Admin.views", lang)}</span>
                        <span style={{ color: "#8B5CF6" }}>{p.unique} {t("Admin.uniq", lang)}</span>
                      </div>
                    </div>
                    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "var(--surface-2)" }}>
                      <div className="h-1.5 rounded-full" style={{ width: `${pct}%`, background: "#8B5CF6" }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Countries */}
        <div className="rounded-2xl border overflow-hidden" style={{ borderColor: "var(--border-color)", background: "var(--surface-1)" }}>
          <div className="px-5 py-3.5 border-b flex items-center gap-2" style={{ borderColor: "var(--border-color)" }}>
            <Globe className="w-4 h-4" style={{ color: "#0ea5e9" }} />
            <span className="text-sm font-bold" style={{ color: "var(--text-primary)" }}>
              {t("Admin.geography", lang)}
            </span>
          </div>
          {!seo?.countries?.length ? (
            <div className="px-5 py-8 text-sm text-center" style={{ color: "var(--text-secondary)" }}>
              {t("Admin.no_data_yet", lang)}
            </div>
          ) : (
            <div className="divide-y" style={{ borderColor: "var(--border-color)" }}>
              {seo.countries.slice(0, 10).map((c, i) => {
                const maxV = seo.countries[0]?.count || 1;
                const pct = Math.round((c.count / maxV) * 100);
                return (
                  <div key={i} className="px-5 py-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-semibold" style={{ color: "var(--text-primary)" }}>{c.country}</span>
                      <span className="text-[10px]" style={{ color: "var(--text-secondary)" }}>{c.count.toLocaleString()}</span>
                    </div>
                    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "var(--surface-2)" }}>
                      <div className="h-1.5 rounded-full" style={{ width: `${pct}%`, background: PIE_COLORS[i % PIE_COLORS.length] }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Top clicks */}
      <div className="rounded-2xl border overflow-hidden" style={{ borderColor: "var(--border-color)", background: "var(--surface-1)" }}>
        <div className="px-5 py-3.5 border-b flex items-center gap-2" style={{ borderColor: "var(--border-color)" }}>
          <Target className="w-4 h-4" style={{ color: "#F77737" }} />
          <span className="text-sm font-bold" style={{ color: "var(--text-primary)" }}>
            {t("Admin.top_clicks", lang)}
          </span>
          <span className="text-[10px] ml-1" style={{ color: "var(--text-secondary)" }}>
            — {t("Admin.most_clicked_elements", lang)}
          </span>
        </div>
        {!seo?.top_clicks?.length ? (
          <div className="px-5 py-8 text-sm text-center" style={{ color: "var(--text-secondary)" }}>
            {t("Admin.no_click_data_yet_clicks_are_tracked_automatical", lang)}
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: "var(--border-color)" }}>
            <div className="grid grid-cols-[2fr_1fr_80px] gap-4 px-5 py-2 text-[10px] font-bold uppercase tracking-wider" style={{ color: "var(--text-secondary)" }}>
              <span>{t("Admin.element", lang)}</span>
              <span>{t("Admin.page", lang)}</span>
              <span>{t("Admin.clicks", lang)}</span>
            </div>
            {seo.top_clicks.map((c, i) => (
              <div key={i} className="grid grid-cols-[2fr_1fr_80px] gap-4 px-5 py-2.5 items-center hover:bg-white/[0.02] transition-colors">
                <span className="text-xs truncate font-medium" style={{ color: "var(--text-primary)" }}>{c.element}</span>
                <span className="text-xs truncate" style={{ color: "var(--text-secondary)" }}>{c.page || "/"}</span>
                <span className="text-sm font-black font-outfit" style={{ color: "#F77737", fontVariantNumeric: "tabular-nums" }}>{c.count}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ───── SEO SPECIFIC: Traffic sources (organic / social / direct) ───── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SeoStatCard emoji="🔍" label={L(lang,"Organic","Органика","אורגני")}
          value={(seo?.organic_total || 0).toLocaleString()} color="#22c55e" />
        <SeoStatCard emoji="💬" label={L(lang,"Social","Соцсети","רשתות חברתיות")}
          value={(seo?.social_total || 0).toLocaleString()} color="#E1306C" />
        <SeoStatCard emoji="➡️" label={L(lang,"Direct","Прямой","ישיר")}
          value={(seo?.direct_total || 0).toLocaleString()} color="#8B5CF6" />
        <SeoStatCard emoji="🗂️" label={L(lang,"Sitemap URLs","URL в sitemap","URLים ב-sitemap")}
          value={(seo?.extras?.sitemap_urls || 0).toLocaleString()} color="#F77737" />
      </div>

      {/* Search engines + Top search queries */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Search engines */}
        <div className="rounded-2xl border overflow-hidden" style={{ borderColor:"var(--border-color)", background:"var(--surface-1)" }}>
          <div className="px-5 py-3.5 border-b flex items-center gap-2" style={{ borderColor:"var(--border-color)" }}>
            <span className="text-sm font-bold" style={{ color:"var(--text-primary)" }}>
              🔎 {L(lang,"Search engines","Поисковики","מנועי חיפוש")}
            </span>
            <span className="text-[10px] ml-1" style={{ color:"var(--text-secondary)" }}>
              — {L(lang,"who sends us organic traffic","кто присылает органику","מי שולח תנועה אורגנית")}
            </span>
          </div>
          {!seo?.search_engines?.length ? (
            <div className="px-5 py-8 text-sm text-center" style={{ color:"var(--text-secondary)" }}>
              {L(lang,"No organic traffic tracked yet","Органический трафик пока не отслежен","לא נמצאה עדיין תנועה אורגנית")}
            </div>
          ) : (
            <div className="divide-y" style={{ borderColor:"var(--border-color)" }}>
              {seo.search_engines.map((e, i) => {
                const maxV = seo.search_engines[0]?.count || 1;
                const pct = Math.round((e.count / maxV) * 100);
                return (
                  <div key={i} className="px-5 py-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-semibold" style={{ color:"var(--text-primary)" }}>{e.engine}</span>
                      <span className="text-[10px]" style={{ color:"var(--text-secondary)" }}>{e.count.toLocaleString()}</span>
                    </div>
                    <div className="h-1.5 rounded-full overflow-hidden" style={{ background:"var(--surface-2)" }}>
                      <div className="h-1.5 rounded-full" style={{ width:`${pct}%`, background:"#22c55e" }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Top search queries */}
        <div className="rounded-2xl border overflow-hidden" style={{ borderColor:"var(--border-color)", background:"var(--surface-1)" }}>
          <div className="px-5 py-3.5 border-b flex items-center gap-2" style={{ borderColor:"var(--border-color)" }}>
            <span className="text-sm font-bold" style={{ color:"var(--text-primary)" }}>
              🔑 {L(lang,"Search queries","Поисковые запросы","שאילתות חיפוש")}
            </span>
            <span className="text-[10px] ml-1" style={{ color:"var(--text-secondary)" }}>
              — {L(lang,"what brought users here","что привело пользователей","מה הביא משתמשים")}
            </span>
          </div>
          {!seo?.top_search_queries?.length ? (
            <div className="px-5 py-8 text-sm text-center" style={{ color:"var(--text-secondary)" }}>
              {L(lang,"No indexed queries yet — wait for Google to crawl","Нет запросов — ждём индексации Google","אין עדיין שאילתות")}
            </div>
          ) : (
            <div className="divide-y max-h-[360px] overflow-y-auto" style={{ borderColor:"var(--border-color)" }}>
              {seo.top_search_queries.map((q, i) => (
                <div key={i} className="flex items-center justify-between px-5 py-2.5">
                  <span className="text-xs truncate font-medium" style={{ color:"var(--text-primary)" }}>{q.query}</span>
                  <span className="text-xs font-black font-outfit flex-shrink-0 ms-3" style={{ color:"#8B5CF6" }}>{q.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Social sources + Language mix */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border overflow-hidden" style={{ borderColor:"var(--border-color)", background:"var(--surface-1)" }}>
          <div className="px-5 py-3.5 border-b flex items-center gap-2" style={{ borderColor:"var(--border-color)" }}>
            <span className="text-sm font-bold" style={{ color:"var(--text-primary)" }}>
              💬 {L(lang,"Social referrers","Соцсети","רשתות חברתיות")}
            </span>
          </div>
          {!seo?.social_sources?.length ? (
            <div className="px-5 py-8 text-sm text-center" style={{ color:"var(--text-secondary)" }}>
              {L(lang,"No social traffic yet","Пока нет трафика из соцсетей","אין עדיין תנועה מרשתות חברתיות")}
            </div>
          ) : (
            <div className="divide-y" style={{ borderColor:"var(--border-color)" }}>
              {seo.social_sources.map((s, i) => {
                const maxV = seo.social_sources[0]?.count || 1;
                const pct = Math.round((s.count / maxV) * 100);
                return (
                  <div key={i} className="px-5 py-3">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-semibold" style={{ color:"var(--text-primary)" }}>{s.source}</span>
                      <span className="text-[10px]" style={{ color:"var(--text-secondary)" }}>{s.count.toLocaleString()}</span>
                    </div>
                    <div className="h-1.5 rounded-full overflow-hidden" style={{ background:"var(--surface-2)" }}>
                      <div className="h-1.5 rounded-full" style={{ width:`${pct}%`, background:"#E1306C" }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="rounded-2xl border overflow-hidden" style={{ borderColor:"var(--border-color)", background:"var(--surface-1)" }}>
          <div className="px-5 py-3.5 border-b flex items-center gap-2" style={{ borderColor:"var(--border-color)" }}>
            <span className="text-sm font-bold" style={{ color:"var(--text-primary)" }}>
              🌐 {L(lang,"Language mix","Языки посетителей","שפות המבקרים")}
            </span>
          </div>
          <div className="p-4 space-y-2.5">
            {[
              { k:"en", label:"English", color:"#0EA5E9" },
              { k:"ru", label:"Русский", color:"#E1306C" },
              { k:"he", label:"עברית",  color:"#22c55e" },
              { k:"auto", label:L(lang,"Auto-detected","Авто","אוטו"), color:"#9ca3af" },
            ].map(({ k, label, color }) => {
              const v = seo?.language_mix?.[k] || 0;
              const total = Object.values(seo?.language_mix || {}).reduce((s, x) => s + x, 0) || 1;
              const pct = Math.round((v / total) * 100);
              return (
                <div key={k}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-semibold" style={{ color:"var(--text-primary)" }}>{label}</span>
                    <span className="text-[10px]" style={{ color:"var(--text-secondary)" }}>
                      {v.toLocaleString()} ({pct}%)
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full overflow-hidden" style={{ background:"var(--surface-2)" }}>
                    <div className="h-1.5 rounded-full" style={{ width:`${pct}%`, background:color }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Page health audit */}
      <div className="rounded-2xl border overflow-hidden" style={{ borderColor:"var(--border-color)", background:"var(--surface-1)" }}>
        <div className="px-5 py-3.5 border-b flex items-center gap-2" style={{ borderColor:"var(--border-color)" }}>
          <span className="text-sm font-bold" style={{ color:"var(--text-primary)" }}>
            ✅ {L(lang,"Page health audit","Аудит страниц","בדיקת דפים")}
          </span>
          <span className="text-[10px] ml-1" style={{ color:"var(--text-secondary)" }}>
            — {L(lang,"live meta tag check","проверка мета-тегов в реальном времени","בדיקת מטא-תגיות בזמן אמת")}
          </span>
        </div>
        {!seo?.health?.length ? (
          <div className="px-5 py-8 text-sm text-center" style={{ color:"var(--text-secondary)" }}>
            {L(lang,"Loading…","Загрузка…","טוען…")}
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor:"var(--border-color)" }}>
            <div className="grid grid-cols-[1.5fr_1fr_1fr_1fr_1fr_1fr_1fr] gap-2 px-5 py-2 text-[10px] font-bold uppercase tracking-wider" style={{ color:"var(--text-secondary)" }}>
              <span>{L(lang,"Page","Страница","דף")}</span>
              <span>{L(lang,"Title","Title","כותרת")}</span>
              <span>{L(lang,"Description","Описание","תיאור")}</span>
              <span>Canonical</span>
              <span>OG</span>
              <span>hreflang</span>
              <span>JSON-LD</span>
            </div>
            {seo.health.map((h, i) => {
              const Check = ({ ok }) => (
                <span style={{ color: ok ? "#22c55e" : "#ef4444", fontWeight:"bold" }}>{ok ? "✓" : "✗"}</span>
              );
              return (
                <div key={i} className="grid grid-cols-[1.5fr_1fr_1fr_1fr_1fr_1fr_1fr] gap-2 px-5 py-2.5 items-center text-xs">
                  <div className="flex flex-col min-w-0">
                    <span className="font-semibold truncate" style={{ color:"var(--text-primary)" }}>{h.page}</span>
                    <span className="text-[10px] truncate" style={{ color:"var(--text-secondary)" }}>{h.status === 200 ? h.title.slice(0, 50) : `HTTP ${h.status}`}</span>
                  </div>
                  <Check ok={h.title_ok} />
                  <Check ok={h.description_ok} />
                  <Check ok={h.canonical_ok} />
                  <Check ok={h.og_ok} />
                  <Check ok={h.hreflang_ok} />
                  <Check ok={h.jsonld_ok} />
                </div>
              );
            })}
            <div className="grid grid-cols-[1.5fr_1fr_1fr_1fr_1fr_1fr_1fr] gap-2 px-5 py-2.5 items-center text-xs" style={{ background:"var(--surface-2)" }}>
              <span className="font-semibold" style={{ color:"var(--text-primary)" }}>
                {L(lang,"Infrastructure","Инфраструктура","תשתית")}
              </span>
              <span className="col-span-6 flex items-center gap-4" style={{ color:"var(--text-secondary)" }}>
                <span>robots.txt: <span style={{ color: seo?.extras?.robots_ok ? "#22c55e" : "#ef4444" }}>{seo?.extras?.robots_ok ? "✓" : "✗"}</span></span>
                <span>sitemap.xml: <span style={{ color: seo?.extras?.sitemap_ok ? "#22c55e" : "#ef4444" }}>{seo?.extras?.sitemap_ok ? `✓ ${seo.extras.sitemap_urls} URLs` : "✗"}</span></span>
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Target keyword coverage */}
      <div className="rounded-2xl border overflow-hidden" style={{ borderColor:"var(--border-color)", background:"var(--surface-1)" }}>
        <div className="px-5 py-3.5 border-b flex items-center gap-2" style={{ borderColor:"var(--border-color)" }}>
          <span className="text-sm font-bold" style={{ color:"var(--text-primary)" }}>
            🎯 {L(lang,"Target keywords","Целевые ключи","מילות מפתח יעד")}
          </span>
          <span className="text-[10px] ml-1" style={{ color:"var(--text-secondary)" }}>
            — {L(lang,"popular queries we want to rank for","популярные запросы для ранжирования","שאילתות פופולריות לדירוג")}
          </span>
        </div>
        {!seo?.target_keywords?.length ? (
          <div className="px-5 py-8 text-sm text-center" style={{ color:"var(--text-secondary)" }}>
            {L(lang,"Loading…","Загрузка…","טוען…")}
          </div>
        ) : (
          <div className="p-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {["en", "ru", "he"].map(lk => {
              const items = seo.target_keywords.filter(k => k.lang === lk);
              const covered = items.filter(k => k.covered).length;
              return (
                <div key={lk} className="rounded-xl border overflow-hidden" style={{ borderColor:"var(--border-color)", background:"var(--surface-2)" }}>
                  <div className="px-3 py-2 border-b flex items-center justify-between" style={{ borderColor:"var(--border-color)" }}>
                    <span className="text-xs font-bold uppercase tracking-wider" style={{ color:"var(--text-secondary)" }}>
                      {lk === "en" ? "English" : lk === "ru" ? "Русский" : "עברית"}
                    </span>
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                      style={{ background: covered === items.length ? "rgba(34,197,94,0.15)" : "rgba(247,119,55,0.15)", color: covered === items.length ? "#22c55e" : "#F77737" }}>
                      {covered}/{items.length}
                    </span>
                  </div>
                  <div className="divide-y" style={{ borderColor:"var(--border-color)" }}>
                    {items.map((k, i) => (
                      <div key={i} className="flex items-center gap-2 px-3 py-2">
                        <span style={{ color: k.covered ? "#22c55e" : "#ef4444" }}>{k.covered ? "✓" : "✗"}</span>
                        <span className="text-[11px] truncate" style={{ color: k.covered ? "var(--text-primary)" : "var(--text-secondary)" }}>{k.keyword}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Device breakdown + User type pie */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Device pie */}
        <div className="rounded-2xl border overflow-hidden" style={{ borderColor: "var(--border-color)", background: "var(--surface-1)" }}>
          <div className="px-5 py-3.5 border-b flex items-center gap-2" style={{ borderColor: "var(--border-color)" }}>
            <span className="text-sm font-bold" style={{ color: "var(--text-primary)" }}>
              {t("Admin.device_breakdown", lang)}
            </span>
          </div>
          {devData.length > 0 ? (
            <div className="p-4 flex flex-col items-center">
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={devData} cx="50%" cy="50%" outerRadius={70} dataKey="value" label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`}
                    labelLine={false}>
                    {devData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "var(--bg-secondary)", border: "1px solid var(--border-color)", borderRadius: 8, fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="px-5 py-8 text-sm text-center" style={{ color: "var(--text-secondary)" }}>
              {t("Admin.no_data_yet", lang)}
            </div>
          )}
        </div>

        {/* Auth vs anon pie */}
        <div className="rounded-2xl border overflow-hidden" style={{ borderColor: "var(--border-color)", background: "var(--surface-1)" }}>
          <div className="px-5 py-3.5 border-b flex items-center gap-2" style={{ borderColor: "var(--border-color)" }}>
            <span className="text-sm font-bold" style={{ color: "var(--text-primary)" }}>
              {t("Admin.auth_vs_anonymous", lang)}
            </span>
          </div>
          {(userTypeData[0].value + userTypeData[1].value) > 0 ? (
            <div className="p-4">
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={userTypeData} cx="50%" cy="50%" outerRadius={70} dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`} labelLine={false}>
                    <Cell fill="#22c55e" />
                    <Cell fill="#9ca3af" />
                  </Pie>
                  <Tooltip contentStyle={{ background: "var(--bg-secondary)", border: "1px solid var(--border-color)", borderRadius: 8, fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="px-5 py-8 text-sm text-center" style={{ color: "var(--text-secondary)" }}>
              {t("Admin.no_data_yet", lang)}
            </div>
          )}
        </div>
      </div>

      {/* User account age */}
      {userAgeData.length > 0 && (
        <div className="rounded-2xl border overflow-hidden" style={{ borderColor: "var(--border-color)", background: "var(--surface-1)" }}>
          <div className="px-5 py-3.5 border-b flex items-center gap-2" style={{ borderColor: "var(--border-color)" }}>
            <Clock className="w-4 h-4" style={{ color: "#F77737" }} />
            <span className="text-sm font-bold" style={{ color: "var(--text-primary)" }}>
              {t("Admin.account_age_distribution", lang)}
            </span>
          </div>
          <div className="p-4">
            <ResponsiveContainer width="100%" height={150}>
              <BarChart data={userAgeData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: "var(--text-secondary)" }} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: "var(--text-secondary)" }} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background: "var(--bg-secondary)", border: "1px solid var(--border-color)", borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="value" name={t("admin.users", lang)} radius={[4, 4, 0, 0]}>
                  {userAgeData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* New registrations chart */}
      {seo?.daily_registrations?.length > 0 && (
        <div className="rounded-2xl border overflow-hidden" style={{ borderColor: "var(--border-color)", background: "var(--surface-1)" }}>
          <div className="px-5 py-3.5 border-b flex items-center gap-2" style={{ borderColor: "var(--border-color)" }}>
            <UsersIcon className="w-4 h-4" style={{ color: "#22c55e" }} />
            <span className="text-sm font-bold" style={{ color: "var(--text-primary)" }}>
              {t("Admin.new_registrations_day", lang)}
            </span>
          </div>
          <div className="p-4">
            <ResponsiveContainer width="100%" height={160}>
              <AreaChart data={seo.daily_registrations} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="gReg" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                <XAxis dataKey="date" tick={{ fontSize: 9, fill: "var(--text-secondary)" }} tickLine={false}
                  tickFormatter={v => v?.slice(5)} />
                <YAxis tick={{ fontSize: 9, fill: "var(--text-secondary)" }} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ background: "var(--bg-secondary)", border: "1px solid var(--border-color)", borderRadius: 8, fontSize: 12 }} />
                <Area type="monotone" dataKey="count" stroke="#22c55e" strokeWidth={2} fill="url(#gReg)"
                  name={t("Admin.new_users_2", lang)} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Referrers */}
      {seo?.referrers?.length > 0 && (
        <div className="rounded-2xl border overflow-hidden" style={{ borderColor: "var(--border-color)", background: "var(--surface-1)" }}>
          <div className="px-5 py-3.5 border-b flex items-center gap-2" style={{ borderColor: "var(--border-color)" }}>
            <ExternalLink className="w-4 h-4" style={{ color: "#0ea5e9" }} />
            <span className="text-sm font-bold" style={{ color: "var(--text-primary)" }}>
              {t("Admin.traffic_sources", lang)}
            </span>
          </div>
          <div className="divide-y" style={{ borderColor: "var(--border-color)" }}>
            {seo.referrers.map((r, i) => (
              <div key={i} className="px-5 py-3 flex items-center justify-between">
                <span className="text-xs truncate max-w-[75%]" style={{ color: "var(--text-primary)" }}>{r.referrer}</span>
                <span className="text-sm font-bold font-outfit" style={{ color: "#0ea5e9", fontVariantNumeric: "tabular-nums" }}>{r.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ===== ANALYTICS =====

function AnalyticsTab({ lang }) {
  const [history, setHistory] = useState(null);
  const [drillData, setDrillData] = useState(null);
  const [drillInfo, setDrillInfo] = useState(null);
  const [metric, setMetric] = useState("revenue");
  const [loading, setLoading] = useState(true);
  const [drillLoading, setDrillLoading] = useState(false);

  useEffect(() => {
    fetch(`${API}/admin/stats/history`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(d => { setHistory(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const drillDown = async (item) => {
    if (!item.month_num) return;
    setDrillInfo({ year: item.year, month: item.month_num, label: item.label });
    setDrillLoading(true);
    try {
      const r = await fetch(`${API}/admin/stats/month?year=${item.year}&month=${item.month_num}`, { credentials: "include" });
      if (r.ok) setDrillData(await r.json());
    } catch {}
    setDrillLoading(false);
  };

  const backToOverview = () => { setDrillData(null); setDrillInfo(null); };

  const monthParts = new Intl.DateTimeFormat("en-CA", {
    timeZone: ISRAEL_TIMEZONE, year: "numeric", month: "2-digit",
  }).formatToParts(new Date());
  const monthMap = Object.fromEntries(monthParts.map((part) => [part.type, part.value]));
  const currentMonth = Number(monthMap.month || 0);
  const currentYear  = Number(monthMap.year  || 0);

  const metricCfg = {
    revenue: { label: t("Admin.revenue", lang),        suffix: " ₪", color: "#10b981", icon: <DollarSign className="w-3.5 h-3.5" /> },
    profit:  { label: t("Admin.net_profit_2", lang),   suffix: " ₪", color: "#f97316", icon: <TrendingUp   className="w-3.5 h-3.5" /> },
    orders:  { label: t("admin.orders", lang),          suffix: "",   color: "#6366f1", icon: <Package       className="w-3.5 h-3.5" /> },
    users:   { label: t("Admin.new_users_3", lang),     suffix: "",   color: "#a855f7", icon: <UsersIcon     className="w-3.5 h-3.5" /> },
  };
  const mc = metricCfg[metric];

  const addCommission = (rows) => rows.map(r => ({ ...r, commission: Math.round((r.revenue || 0) * 0.25 * 100) / 100 }));
  const currentData = addCommission(drillData
    ? drillData.daily
    : (history?.monthly || []).filter(m =>
        m.year < currentYear || (m.year === currentYear && m.month_num <= currentMonth)
      ));
  const monthlyOverview = addCommission((history?.monthly || []).filter((item) => (
    (item.year === currentYear && item.month_num === currentMonth)
    || item.revenue > 0 || item.profit > 0 || item.orders > 0 || item.users > 0
  )));
  const xKey  = drillData ? "date" : "label";
  const total = currentData.reduce((s, d) => s + (d[metric] || 0), 0);
  const avg   = currentData.length ? total / currentData.length : 0;
  const peak  = Math.max(0, ...currentData.map(d => d[metric] || 0));

  // trend vs previous period
  const trendPct = useMemo(() => {
    const arr = drillData ? currentData : [...(history?.monthly || [])].sort((a, b) =>
      a.year !== b.year ? a.year - b.year : a.month_num - b.month_num);
    if (arr.length < 2) return null;
    const last = arr[arr.length - 1];
    const prev = arr[arr.length - 2];
    if (!prev[metric]) return null;
    return ((last[metric] - prev[metric]) / prev[metric]) * 100;
  }, [history, drillData, currentData, metric]);

  const maxRevenue = Math.max(1, ...monthlyOverview.map(r => r.revenue || 0));

  const AnaTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="rounded-xl px-3 py-2.5 text-xs shadow-2xl" style={{
        background: "rgba(8,8,16,0.96)", border: `1px solid ${mc.color}30`,
        backdropFilter: "blur(12px)",
      }}>
        <div className="font-semibold mb-1.5 text-[11px]" style={{ color: "var(--text-secondary)" }}>{label}</div>
        {payload.map((p, i) => (
          <div key={i} className="flex items-center gap-2 font-bold" style={{ color: p.color }}>
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: p.color }} />
            {p.value?.toLocaleString()}{mc.suffix}
          </div>
        ))}
      </div>
    );
  };

  if (loading) return (
    <div className="min-h-[300px] flex items-center justify-center">
      <div className="animate-spin h-8 w-8 border-2 border-[#E1306C] border-t-transparent rounded-full" />
    </div>
  );

  return (
    <div className="space-y-3 animate-fade-in-up">

      {/* ── Metric selector ── */}
      <div className="grid grid-cols-4 gap-2">
        {Object.entries(metricCfg).map(([k, v]) => {
          const ktotal = currentData.reduce((s, d) => s + (d[k] || 0), 0);
          const active = metric === k;
          return (
            <button key={k} onClick={() => setMetric(k)}
              className="rounded-2xl p-3 text-left transition-all relative overflow-hidden"
              style={{
                background: active ? `linear-gradient(135deg, ${v.color}18 0%, ${v.color}08 100%)` : "var(--surface-1)",
                border: `1px solid ${active ? `${v.color}40` : "var(--border-color)"}`,
                boxShadow: active ? `0 0 20px ${v.color}15` : "none",
              }}>
              {active && (
                <div className="absolute inset-0 pointer-events-none rounded-2xl"
                  style={{ background: `radial-gradient(ellipse at 0% 0%, ${v.color}12, transparent 70%)` }} />
              )}
              <div className="relative flex items-center justify-between mb-2">
                <div className="p-1.5 rounded-lg" style={{ background: `${v.color}18` }}>
                  <span style={{ color: v.color }}>{v.icon}</span>
                </div>
                {active && <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: v.color }} />}
              </div>
              <div className="relative font-black font-outfit text-base leading-tight tabular-nums" style={{ color: active ? v.color : "var(--text-primary)" }}>
                {ktotal.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                {v.suffix && <span className="text-xs font-bold ms-0.5">{v.suffix.trim()}</span>}
              </div>
              <div className="relative text-[10px] font-semibold mt-0.5 truncate" style={{ color: active ? `${v.color}cc` : "var(--text-secondary)" }}>
                {v.label}
              </div>
            </button>
          );
        })}
      </div>

      {/* ── Hero chart block ── */}
      <div className="rounded-2xl overflow-hidden" style={{
        background: "var(--surface-1)", border: "1px solid var(--border-color)",
      }}>
        {/* Chart header */}
        <div className="px-5 pt-4 pb-3 flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            {drillInfo && (
              <button onClick={backToOverview}
                className="h-7 px-2.5 rounded-lg text-xs font-semibold flex items-center gap-1 transition-all hover:bg-white/5"
                style={{ background: "var(--surface-2)", color: "var(--text-secondary)", border: "1px solid var(--border-color)" }}>
                <ChevronLeft className="w-3 h-3" />{t("common.back", lang)}
              </button>
            )}
            <div>
              <div className="flex items-baseline gap-3">
                <span className="text-2xl font-black font-outfit tabular-nums" style={{ color: mc.color }}>
                  {total.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  {mc.suffix && <span className="text-base font-bold ms-1">{mc.suffix.trim()}</span>}
                </span>
                {trendPct !== null && (
                  <span className="flex items-center gap-0.5 text-xs font-bold px-1.5 py-0.5 rounded-full"
                    style={{
                      color: trendPct >= 0 ? "#10b981" : "#f87171",
                      background: trendPct >= 0 ? "rgba(16,185,129,0.12)" : "rgba(248,113,113,0.12)",
                    }}>
                    {trendPct >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    {Math.abs(trendPct).toFixed(1)}%
                  </span>
                )}
              </div>
              <div className="text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>
                {drillInfo ? drillInfo.label : mc.label}
                {" · "}
                <span style={{ color: "var(--text-secondary)" }}>
                  ø {avg.toLocaleString(undefined, { maximumFractionDigits: 1 })}{mc.suffix}
                  {" · "}↑ {peak.toLocaleString()}{mc.suffix}
                </span>
              </div>
            </div>
          </div>
          {!drillData && (
            <span className="text-[10px] font-medium self-center" style={{ color: "var(--text-secondary)" }}>
              {t("Admin.click_bar_daily_view", lang)}
            </span>
          )}
        </div>

        {/* Chart */}
        <div className="px-2 pb-4">
          {drillLoading ? (
            <div className="h-56 flex items-center justify-center">
              <div className="animate-spin h-6 w-6 border-2 border-t-transparent rounded-full" style={{ borderColor: mc.color, borderTopColor: "transparent" }} />
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <ComposedChart data={currentData} margin={{ top: 8, right: 4, left: -28, bottom: 0 }}
                onClick={!drillData ? (data) => { if (data?.activePayload?.[0]?.payload) drillDown(data.activePayload[0].payload); } : undefined}
                style={{ cursor: drillData ? "default" : "pointer" }}>
                <defs>
                  <linearGradient id={`ag-${metric}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"   stopColor={mc.color} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={mc.color} stopOpacity={0.0} />
                  </linearGradient>
                  <linearGradient id={`bg-${metric}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"   stopColor={mc.color} stopOpacity={0.9} />
                    <stop offset="100%" stopColor={mc.color} stopOpacity={0.45} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="2 4" stroke="rgba(255,255,255,0.04)" vertical={false} />
                <XAxis dataKey={xKey} tick={{ fontSize: 9, fill: "var(--text-secondary)" }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 9, fill: "var(--text-secondary)" }} axisLine={false} tickLine={false} width={44} />
                <Tooltip content={<AnaTooltip />} cursor={{ fill: `${mc.color}08` }} />
                <Area dataKey={metric} type="monotone" fill={`url(#ag-${metric})`} stroke={mc.color} strokeWidth={2} dot={false} activeDot={{ r: 4, fill: mc.color, strokeWidth: 0 }} />
                <Bar  dataKey={metric} fill={`url(#bg-${metric})`} radius={[4,4,0,0]} maxBarSize={32} opacity={0.7} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* ── Monthly overview table ── */}
      {!drillData && history?.monthly && (
        <div className="rounded-2xl overflow-hidden" style={{ background: "var(--surface-1)", border: "1px solid var(--border-color)" }}>
          <div className="px-4 py-3 flex items-center gap-2 border-b" style={{ borderColor: "var(--border-color)" }}>
            <Calendar className="w-3.5 h-3.5" style={{ color: "var(--text-secondary)" }} />
            <span className="text-sm font-bold">{t("Admin.monthly_overview", lang)}</span>
            <span className="ms-auto text-[10px]" style={{ color: "var(--text-secondary)" }}>{t("Admin.click_a_row_for_daily_details", lang)}</span>
          </div>

          {/* col headers */}
          <div className="grid text-[10px] font-bold uppercase tracking-wider px-4 py-2 border-b"
            style={{ gridTemplateColumns: "1.2fr 1fr 0.9fr 1fr 60px 60px 20px", borderColor: "var(--border-color)", color: "var(--text-secondary)", background: "var(--surface-2)" }}>
            <span>{t("Admin.month", lang)}</span>
            <span style={{ color: "#10b981" }}>{t("Admin.revenue", lang)}</span>
            <span style={{ color: "#f87171" }}>{t("Admin.cost", lang)}</span>
            <span style={{ color: "#f97316" }}>{t("Admin.profit", lang)}</span>
            <span style={{ color: "#6366f1" }}>{t("admin.orders", lang)}</span>
            <span style={{ color: "#a855f7" }}>{t("Admin.users", lang)}</span>
            <span />
          </div>

          <div>
            {[...monthlyOverview].reverse().map((row, i) => {
              const barW = Math.round(((row.revenue || 0) / maxRevenue) * 100);
              const isNow = row.year === currentYear && row.month_num === currentMonth;
              const profitPct = row.revenue ? Math.round((row.profit || 0) / row.revenue * 100) : 0;
              return (
                <div key={i} onClick={() => drillDown(row)}
                  className="relative grid px-4 py-2.5 cursor-pointer group transition-colors hover:bg-white/[0.03] border-b last:border-b-0"
                  style={{ gridTemplateColumns: "1.2fr 1fr 0.9fr 1fr 60px 60px 20px", borderColor: "var(--border-color)" }}>
                  <div className="absolute inset-y-0 left-0 rounded-r-xl pointer-events-none transition-all"
                    style={{ width: `${barW}%`, background: `linear-gradient(90deg, #10b98114 0%, transparent 100%)` }} />
                  <span className="relative font-semibold text-xs flex items-center gap-1.5" style={{ color: "var(--text-primary)" }}>
                    {row.label}
                    {isNow && <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold" style={{ background: "rgba(139,92,246,0.18)", color: "#a78bfa" }}>NOW</span>}
                  </span>
                  <span className="relative font-bold font-outfit tabular-nums text-xs" style={{ color: "#10b981" }}>{(row.revenue||0).toLocaleString()} ₪</span>
                  <span className="relative font-outfit tabular-nums text-xs" style={{ color: "#f87171" }}>{(row.provider_cost||0).toLocaleString()} ₪</span>
                  <span className="relative font-bold font-outfit tabular-nums text-xs flex items-center gap-1" style={{ color: "#f97316" }}>
                    {(row.profit||0).toLocaleString()} ₪
                    {profitPct > 0 && <span className="text-[9px] opacity-60">{profitPct}%</span>}
                  </span>
                  <span className="relative font-outfit tabular-nums text-xs" style={{ color: "#6366f1" }}>{row.orders||0}</span>
                  <span className="relative font-outfit tabular-nums text-xs" style={{ color: "#a855f7" }}>{row.users||0}</span>
                  <span className="relative flex items-center justify-end">
                    <ChevronLeft className="w-3 h-3 rotate-180 opacity-0 group-hover:opacity-50 transition-opacity" style={{ color: "var(--text-secondary)" }} />
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Daily drill table ── */}
      {drillData && (() => {
        const rows = drillData.daily.filter(d => d.orders > 0 || d.users > 0 || d.revenue > 0);
        const maxRev = Math.max(1, ...rows.map(d => d.revenue || 0));
        return (
          <div className="rounded-2xl overflow-hidden" style={{ background: "var(--surface-1)", border: "1px solid var(--border-color)" }}>
            <div className="px-4 py-3 flex items-center gap-2 border-b" style={{ borderColor: "var(--border-color)" }}>
              <Calendar className="w-3.5 h-3.5" style={{ color: "var(--text-secondary)" }} />
              <span className="text-sm font-bold">{t("Admin.daily_breakdown", lang)}</span>
              <span className="ms-1 text-xs font-semibold px-2 py-0.5 rounded-full" style={{ background: `${mc.color}15`, color: mc.color }}>{drillInfo?.label}</span>
            </div>
            <div className="grid text-[10px] font-bold uppercase tracking-wider px-4 py-2 border-b"
              style={{ gridTemplateColumns: "1fr 1fr 0.9fr 1fr 60px 60px", borderColor: "var(--border-color)", color: "var(--text-secondary)", background: "var(--surface-2)" }}>
              <span>{t("Admin.day", lang)}</span>
              <span style={{ color: "#10b981" }}>{t("Admin.revenue", lang)}</span>
              <span style={{ color: "#f87171" }}>{t("Admin.cost", lang)}</span>
              <span style={{ color: "#f97316" }}>{t("Admin.profit", lang)}</span>
              <span style={{ color: "#6366f1" }}>{t("admin.orders", lang)}</span>
              <span style={{ color: "#a855f7" }}>{t("Admin.users", lang)}</span>
            </div>
            {!rows.length ? (
              <div className="px-4 py-12 text-center text-sm" style={{ color: "var(--text-secondary)" }}>
                {t("Admin.no_activity_recorded_for_this_period", lang)}
              </div>
            ) : rows.map((d, i) => {
              const barW = Math.round(((d.revenue || 0) / maxRev) * 100);
              return (
                <div key={i} className="relative grid px-4 py-2.5 border-b last:border-b-0 hover:bg-white/[0.025] transition-colors"
                  style={{ gridTemplateColumns: "1fr 1fr 0.9fr 1fr 60px 60px", borderColor: "var(--border-color)" }}>
                  <div className="absolute inset-y-0 left-0 rounded-r-xl pointer-events-none"
                    style={{ width: `${barW}%`, background: "linear-gradient(90deg, #10b98110 0%, transparent 100%)" }} />
                  <span className="relative font-semibold text-xs" style={{ color: "var(--text-primary)" }}>{d.date}</span>
                  <span className="relative font-bold font-outfit tabular-nums text-xs" style={{ color: "#10b981" }}>{(d.revenue||0).toLocaleString()} ₪</span>
                  <span className="relative font-outfit tabular-nums text-xs" style={{ color: "#f87171" }}>{(d.provider_cost||0).toLocaleString()} ₪</span>
                  <span className="relative font-bold font-outfit tabular-nums text-xs" style={{ color: "#f97316" }}>{(d.profit||0).toLocaleString()} ₪</span>
                  <span className="relative font-outfit tabular-nums text-xs" style={{ color: "#6366f1" }}>{d.orders||0}</span>
                  <span className="relative font-outfit tabular-nums text-xs" style={{ color: "#a855f7" }}>{d.users||0}</span>
                </div>
              );
            })}
          </div>
        );
      })()}
    </div>
  );
}

// ===== ADMIN TASKS =====
function AdminTasksTab({ lang }) {
  const [tasks, setTasks] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [subFilter, setSubFilter] = useState("");
  const [expandedTasks, setExpandedTasks] = useState(new Set());
  const [showCreate, setShowCreate] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [mainTab, setMainTab] = useState("tasks");
  const [showHistory, setShowHistory] = useState(false);
  const emptyForm = {
    title_en: "", title_ru: "", title_he: "",
    desc_en: "", desc_ru: "", desc_he: "",
    reward_ipoints: "50",
    task_type: "custom", link_url: "",
    link_label_en: "", link_label_ru: "", link_label_he: "",
    max_completions: "0",
    expires_date: "",
    expires_time: "",
    requires_proof: true,
    ai_prompt: "",
  };
  const [form, setForm] = useState(emptyForm);

  const fetch_ = useCallback(async () => {
    const [t,s] = await Promise.all([
      fetch(`${API}/admin/tasks`,{credentials:"include"}).then(r=>r.ok?r.json():[]),
      fetch(`${API}/admin/task-submissions${subFilter?`?status=${subFilter}`:""}`,{credentials:"include"}).then(r=>r.ok?r.json():[]),
    ]);
    setTasks(t); setSubmissions(s);
  }, [subFilter]);
  useEffect(() => { fetch_(); }, [fetch_]);

  const createTask = async () => {
    try {
      const r = await fetch(`${API}/admin/tasks`, {
        method:"POST", headers:{"Content-Type":"application/json"}, credentials:"include",
        body:JSON.stringify({
          title:{en:form.title_en,ru:form.title_ru,he:form.title_he},
          description:{en:form.desc_en,ru:form.desc_ru,he:form.desc_he},
          reward_ipoints:parseInt(form.reward_ipoints)||50,
          task_type:form.task_type, link_url:form.link_url,
          link_label:{en:form.link_label_en,ru:form.link_label_ru,he:form.link_label_he},
          max_completions:parseInt(form.max_completions)||0,
          expires_at_local: form.expires_date && form.expires_time ? `${form.expires_date}T${form.expires_time}` : "",
          requires_proof:form.requires_proof,
        }),
      });
      if(!r.ok) throw new Error("Failed");
      toast.success(t("Admin.task_created", lang));
      setShowCreate(false); setEditingTaskId(null); setForm(emptyForm);
      fetch_();
    } catch(e) { toast.error(e.message); }
  };

  const deleteTask = async (id) => { await fetch(`${API}/admin/tasks/${id}`,{method:"DELETE",credentials:"include"}); fetch_(); };

  const openEdit = (task) => {
    const expiresDate = task.expires_at ? task.expires_at.slice(0,10) : "";
    const expiresTime = task.expires_at ? task.expires_at.slice(11,16) : "";
    setForm({
      title_en: task.title?.en || "", title_ru: task.title?.ru || "", title_he: task.title?.he || "",
      desc_en: task.description?.en || "", desc_ru: task.description?.ru || "", desc_he: task.description?.he || "",
      reward_ipoints: String(task.reward_ipoints || 50),
      task_type: task.task_type || "custom", link_url: task.link_url || "",
      link_label_en: task.link_label?.en || "", link_label_ru: task.link_label?.ru || "", link_label_he: task.link_label?.he || "",
      max_completions: String(task.max_completions || 0),
      expires_date: expiresDate, expires_time: expiresTime,
      requires_proof: task.requires_proof !== false,
      ai_prompt: "",
    });
    setEditingTaskId(task.task_id);
    setShowCreate(true);
  };

  const saveTask = async () => {
    const body = {
      title: {en:form.title_en, ru:form.title_ru, he:form.title_he},
      description: {en:form.desc_en, ru:form.desc_ru, he:form.desc_he},
      reward_ipoints: parseInt(form.reward_ipoints)||50,
      task_type: form.task_type, link_url: form.link_url,
      link_label: {en:form.link_label_en, ru:form.link_label_ru, he:form.link_label_he},
      max_completions: parseInt(form.max_completions)||0,
      expires_at_local: form.expires_date && form.expires_time ? `${form.expires_date}T${form.expires_time}` : "",
      requires_proof: form.requires_proof,
    };
    try {
      if (editingTaskId) {
        const r = await fetch(`${API}/admin/tasks/${editingTaskId}`, {method:"PUT", headers:{"Content-Type":"application/json"}, credentials:"include", body:JSON.stringify(body)});
        if (!r.ok) throw new Error("Failed");
        toast.success(t("Admin.task_updated", lang));
      } else {
        const r = await fetch(`${API}/admin/tasks`, {method:"POST", headers:{"Content-Type":"application/json"}, credentials:"include", body:JSON.stringify(body)});
        if (!r.ok) throw new Error("Failed");
        toast.success(t("Admin.task_created", lang));
      }
      setShowCreate(false); setEditingTaskId(null); setForm(emptyForm); fetch_();
    } catch(e) { toast.error(e.message); }
  };

  const TASK_BTN_LABELS = {
    follow:    { en: "Follow",     ru: "Подписаться",      he: "עקוב" },
    like:      { en: "Like",       ru: "Лайкнуть",         he: "לייק" },
    comment:   { en: "Comment",    ru: "Написать комментарий", he: "הגב" },
    share:     { en: "Share",      ru: "Поделиться",       he: "שתף" },
    visit:     { en: "Visit",      ru: "Перейти",          he: "בקר" },
    subscribe: { en: "Subscribe",  ru: "Подписаться",      he: "הירשם" },
    repost:    { en: "Repost",     ru: "Репостнуть",       he: "שתף מחדש" },
    story:     { en: "Post Story", ru: "Опубликовать сторис", he: "פרסם סטורי" },
    review:    { en: "Leave Review", ru: "Оставить отзыв", he: "כתוב ביקורת" },
    custom:    { en: "Open",       ru: "Открыть",          he: "פתח" },
  };

  const aiTranslateTask = async () => {
    if (!form.ai_prompt.trim() && !form.title_en.trim()) { toast.error(t("Admin.enter_english_title_first", lang)); return; }
    setTranslating(true);
    try {
      const r = await fetch(`${API}/admin/messages/translate-preview`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ source_subject: form.title_en.trim() || form.ai_prompt.trim(), source_content: form.desc_en.trim() || form.ai_prompt.trim() || form.title_en.trim(), source_language: "auto", message_type: "general" }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "AI failed");
      const btnLabels = TASK_BTN_LABELS[form.task_type] || TASK_BTN_LABELS.custom;
      setForm(prev => ({
        ...prev,
        title_en: prev.title_en || d.subject?.en || prev.title_en,
        title_ru: d.subject?.ru || prev.title_ru,
        title_he: d.subject?.he || prev.title_he,
        desc_en: prev.desc_en || d.content?.en || prev.desc_en,
        desc_ru: d.content?.ru || prev.desc_ru,
        desc_he: d.content?.he || prev.desc_he,
        link_label_en: prev.link_label_en || btnLabels.en,
        link_label_ru: prev.link_label_ru || btnLabels.ru,
        link_label_he: prev.link_label_he || btnLabels.he,
      }));
      toast.success(t("Admin.ai_translated", lang));
    } catch(e) { toast.error(e.message); } finally { setTranslating(false); }
  };

  const reviewSubmission = async (subId,action,note) => {
    try {
      const r = await fetch(`${API}/admin/task-submissions/${subId}/review`,{method:"PUT",headers:{"Content-Type":"application/json"},credentials:"include",body:JSON.stringify({action,admin_note:note})});
      const d = await r.json(); if(!r.ok) throw new Error(d.detail);
      toast.success(d.message); fetch_();
    } catch(e) { toast.error(e.message); }
  };

  const pendingSubs = submissions.filter(s=>s.status==="pending").length;
  const activeTasks = tasks.filter(t=>t.status==="active").length;
  const totalSubs = submissions.length;

  return (
    <div className="space-y-6 animate-fade-in-up">
      {/* Header bar */}
      <div className="rounded-2xl p-4" style={{ background: "var(--surface-1)", border: "1px solid var(--border-color)" }}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <AdminIcon emoji="🎯" className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-black font-outfit">{t("nav.tasks", lang)}</h2>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold" style={{ background:"rgba(34,197,94,0.12)", color:"#22c55e" }}>✅ {activeTasks} {t("Admin.active", lang)}</span>
                {pendingSubs > 0 && <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold animate-pulse" style={{ background:"rgba(250,204,21,0.15)", color:"#facc15" }}>⏳ {pendingSubs} {t("Admin.pending_review", lang)}</span>}
                {totalSubs > 0 && <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold" style={{ background:"rgba(131,58,180,0.12)", color:"#c084fc" }}>📋 {totalSubs} {t("Admin.submissions", lang)}</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex gap-1 p-1 rounded-2xl" style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)" }}>
              {[
                { key: "tasks", label: `🎯 ${t("nav.tasks", lang)}`, count: activeTasks },
                { key: "all_submissions", label: `📋 ${t("Admin.submissions_2", lang)}`, count: pendingSubs },
              ].map(tab => (
                <button key={tab.key} onClick={() => setMainTab(tab.key)}
                  className="text-sm font-semibold py-1.5 px-3 rounded-xl transition-all flex items-center gap-1.5"
                  style={{ background: mainTab===tab.key ? "rgba(247,119,55,0.15)" : "transparent", color: mainTab===tab.key ? "#F77737" : "var(--text-secondary)" }}>
                  {tab.label}
                  {tab.count > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold" style={{ background: mainTab===tab.key ? "rgba(247,119,55,0.2)" : "var(--surface-3)", color: mainTab===tab.key ? "#F77737" : "var(--text-secondary)" }}>{tab.count}</span>}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* Create dialog trigger */}
      <div className="flex justify-end">
        <Dialog open={showCreate} onOpenChange={(open) => { setShowCreate(open); if (!open) { setEditingTaskId(null); setForm(emptyForm); } }}>
          <DialogTrigger asChild><Button className="btn-brand text-sm px-4 h-9" data-testid="create-task-btn"><Plus className="w-4 h-4 me-1" />{t("Admin.create_task", lang)}</Button></DialogTrigger>
          <DialogContent style={{ background: "var(--bg-secondary)", borderColor: "var(--border-color)" }} className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="text-base font-bold">{editingTaskId ? t("Admin.edit_task", lang) : t("Admin.new_task", lang)}</DialogTitle></DialogHeader>
            <div className="space-y-4 pb-1">

              {/* Platform type */}
              <div>
                <p className="text-xs font-semibold mb-2" style={{ color: "var(--text-secondary)" }}>{t("Admin.platform", lang)}</p>
                <div className="grid grid-cols-4 gap-1.5">
                  {TASK_TYPES.map(tp => (
                    <button key={tp.value} onClick={() => setForm({ ...form, task_type: tp.value })}
                      className="rounded-xl py-2 px-1 text-xs font-medium border transition-all text-center"
                      style={{ borderColor: form.task_type === tp.value ? "rgba(247,119,55,0.4)" : "var(--border-color)", background: form.task_type === tp.value ? "rgba(247,119,55,0.12)" : "var(--surface-1)", color: form.task_type === tp.value ? "#F77737" : "var(--text-secondary)" }}>
                      <div className="flex justify-center mb-1"><TaskTypeIcon type={tp.value} className="w-4 h-4" /></div>
                      <div className="truncate">{tp.label.split("/")[0]}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* AI */}
              <div className="rounded-xl border p-3 space-y-2" style={{ borderColor: "rgba(139,92,246,0.25)", background: "rgba(139,92,246,0.05)" }}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-3.5 h-3.5" style={{ color: "#8B5CF6" }} />
                    <p className="text-xs font-semibold" style={{ color: "#8B5CF6" }}>AI</p>
                  </div>
                  <button type="button" onClick={aiTranslateTask} disabled={translating}
                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-xl disabled:opacity-50 font-semibold"
                    style={{ background: "#8B5CF6", color: "#fff" }}>
                    <Sparkles className="w-3 h-3" />{translating ? "…" : t("Admin.auto_fill", lang)}
                  </button>
                </div>
                <Textarea value={form.ai_prompt} onChange={e => setForm({...form, ai_prompt: e.target.value})}
                  placeholder={t("Admin.describe_the_task_in_any_language", lang)}
                  className="min-h-[56px] resize-none text-sm"
                  style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }} />
              </div>

              {/* Title */}
              <div className="space-y-1.5">
                <p className="text-xs font-semibold" style={{ color: "var(--text-secondary)" }}>{t("Admin.title", lang)}</p>
                {[["EN","English",form.title_en,v=>setForm({...form,title_en:v}),"task-title-en"],["RU","Русский",form.title_ru,v=>setForm({...form,title_ru:v}),null],["HE","עברית",form.title_he,v=>setForm({...form,title_he:v}),null]].map(([flag,ph,val,onChange,tid]) => (
                  <div key={flag} className="flex items-center gap-2">
                    <span className="w-7 text-[10px] font-bold text-center rounded-lg py-1 flex-shrink-0" style={{ background: "var(--surface-2)", color: "var(--text-secondary)" }}>{flag}</span>
                    <Input value={val} onChange={e=>onChange(e.target.value)} placeholder={ph}
                      className="h-9 text-sm flex-1" style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }}
                      data-testid={tid||undefined} dir={flag==="HE"?"rtl":"ltr"} />
                  </div>
                ))}
              </div>

              {/* Description */}
              <div className="space-y-1.5">
                <p className="text-xs font-semibold" style={{ color: "var(--text-secondary)" }}>{t("Admin.description", lang)}</p>
                {[["EN","English",form.desc_en,v=>setForm({...form,desc_en:v})],["RU","Русский",form.desc_ru,v=>setForm({...form,desc_ru:v})],["HE","עברית",form.desc_he,v=>setForm({...form,desc_he:v})]].map(([flag,ph,val,onChange]) => (
                  <div key={flag} className="flex items-center gap-2">
                    <span className="w-7 text-[10px] font-bold text-center rounded-lg py-1 flex-shrink-0" style={{ background: "var(--surface-2)", color: "var(--text-secondary)" }}>{flag}</span>
                    <Input value={val} onChange={e=>onChange(e.target.value)} placeholder={ph}
                      className="h-9 text-sm flex-1" style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }}
                      dir={flag==="HE"?"rtl":"ltr"} />
                  </div>
                ))}
              </div>

              {/* Link */}
              <div className="space-y-1.5">
                <p className="text-xs font-semibold" style={{ color: "var(--text-secondary)" }}>{t("Admin.action_link", lang)}</p>
                <Input placeholder="https://..." value={form.link_url} onChange={e=>setForm({...form,link_url:e.target.value})}
                  className="h-9 text-sm" style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }}
                  data-testid="task-link-url" />
                <div className="grid grid-cols-3 gap-2">
                  {[["EN","Go",form.link_label_en,v=>setForm({...form,link_label_en:v})],["RU","Открыть",form.link_label_ru,v=>setForm({...form,link_label_ru:v})],["HE","פתח",form.link_label_he,v=>setForm({...form,link_label_he:v})]].map(([flag,ph,val,onChange]) => (
                    <div key={flag} className="relative">
                      <span className="absolute start-2 top-1/2 -translate-y-1/2 text-[10px] font-bold z-10" style={{ color: "var(--text-secondary)" }}>{flag}</span>
                      <Input value={val} onChange={e=>onChange(e.target.value)} placeholder={ph}
                        className="h-9 text-sm ps-8" style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }}
                        dir={flag==="HE"?"rtl":"ltr"} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Rewards */}
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl border p-3" style={{ borderColor: "rgba(247,119,55,0.2)", background: "rgba(247,119,55,0.05)" }}>
                  <StepperInput
                    label="iPoints"
                    value={form.reward_ipoints}
                    onChange={v => setForm({...form, reward_ipoints: v})}
                    min={1} step={5} accent="#F77737"
                  />
                </div>
              </div>

              {/* Max completions */}
              <StepperInput
                label={`${t("Admin.max_completions", lang)} (0=∞)`}
                value={form.max_completions}
                onChange={v => setForm({...form, max_completions: v})}
                min={0} step={1} accent="#9B9BB0"
              />

              {/* Deadline */}
              <div className="rounded-xl border p-3 space-y-2.5" style={{ borderColor: "rgba(139,92,246,0.2)", background: "rgba(139,92,246,0.05)" }}>
                <div className="flex items-center gap-2 text-xs font-semibold" style={{ color: "#8B5CF6" }}>
                  <CalendarClock className="w-3.5 h-3.5" />
                  {t("Admin.deadline_israel_time", lang)}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="relative">
                    <Calendar className="absolute start-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none" style={{ color: "#8B5CF6", opacity: 0.7 }} />
                    <input type="date" value={form.expires_date} onChange={e=>setForm({...form,expires_date:e.target.value})}
                      className="w-full h-10 rounded-xl text-sm ps-9 pe-3 outline-none border transition-all"
                      style={{ background: "var(--surface-2)", borderColor: form.expires_date ? "rgba(139,92,246,0.4)" : "var(--border-color)", color: "var(--text-primary)" }} />
                  </div>
                  <div className="relative">
                    <Clock className="absolute start-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none" style={{ color: "#8B5CF6", opacity: 0.7 }} />
                    <input type="time" value={form.expires_time} onChange={e=>setForm({...form,expires_time:e.target.value})}
                      className="w-full h-10 rounded-xl text-sm ps-9 pe-3 outline-none border transition-all"
                      style={{ background: "var(--surface-2)", borderColor: form.expires_time ? "rgba(139,92,246,0.4)" : "var(--border-color)", color: "var(--text-primary)" }} />
                  </div>
                </div>
                <p className="text-[11px]" style={{ color: "var(--text-secondary)" }}>
                  {t("Admin.leave_empty_for_no_expiry", lang)}
                </p>
              </div>

              {/* Proof toggle */}
              <label className="flex items-center gap-3 px-3 py-3 rounded-xl border cursor-pointer transition-all"
                style={{ borderColor: form.requires_proof ? "rgba(139,92,246,0.3)" : "var(--border-color)", background: form.requires_proof ? "rgba(139,92,246,0.06)" : "var(--surface-1)" }}>
                <input type="checkbox" checked={form.requires_proof} onChange={e=>setForm({...form,requires_proof:e.target.checked})} className="w-4 h-4 rounded accent-[#8B5CF6]" />
                <div>
                  <p className="text-sm font-medium" style={{ color: "var(--text-primary)" }}>{t("Admin.requires_screenshot_proof", lang)}</p>
                  <p className="text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>{t("Admin.user_must_upload_a_screenshot", lang)}</p>
                </div>
              </label>

              <button onClick={saveTask} className="w-full h-11 rounded-xl text-sm font-bold flex items-center justify-center gap-2" style={{ background: "#8B5CF6", color: "#fff" }} data-testid="save-task-btn">
                <Save className="w-4 h-4" />{editingTaskId ? t("Admin.save_changes", lang) : t("Admin.create_task", lang)}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>{/* close flex justify-end */}
      {mainTab === "tasks" && (
        <>
          {tasks.length > 0 && (
            <div className="flex items-center justify-between flex-wrap gap-2">
              <span className="text-xs" style={{ color: "var(--text-secondary)" }}>
                {tasks.filter(t=>t.status==="active").length} {t("Admin.active", lang)}
                {tasks.filter(t=>t.status!=="active").length > 0 && ` · ${tasks.filter(t=>t.status!=="active").length} ${t("Admin.ended", lang)}`}
              </span>
              {tasks.filter(t=>t.status!=="active").length > 0 && (
                <button onClick={()=>setShowHistory(!showHistory)}
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-xl font-medium transition-all"
                  style={{ background: showHistory ? "rgba(64,93,230,0.12)" : "var(--surface-2)", color: showHistory ? "#7aa2ff" : "var(--text-secondary)", border: `1px solid ${showHistory ? "rgba(64,93,230,0.25)" : "var(--surface-3)"}` }}>
                  <History className="w-3.5 h-3.5" />
                  {showHistory ? t("Admin.hide_history", lang) : t("Admin.history", lang)} ({tasks.filter(t=>t.status!=="active").length})
                </button>
              )}
            </div>
          )}
          {tasks.filter(t => showHistory || t.status === "active").length === 0 ? (
            <div className="rounded-2xl border p-14 text-center" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
              <div className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4" style={{ background: "rgba(247,119,55,0.1)" }}>
                <Target className="w-7 h-7 opacity-30" style={{ color: "#F77737" }} />
              </div>
              <p className="font-bold mb-1">{t("Admin.no_tasks_yet", lang)}</p>
              <p className="text-xs" style={{ color: "var(--text-secondary)" }}>{t("Admin.create_your_first_task", lang)}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {tasks.filter(t => showHistory || t.status === "active").map((task, idx, arr) => {
                const tType = getTaskTypeMeta(task.task_type);
                const desc = task.description?.[lang] || task.description?.en;
                const linkLabel = task.link_label?.[lang] || task.link_label?.en;
                const taskSubs = submissions.filter(s => s.task_id === task.task_id);
                const pendingCount = taskSubs.filter(s => s.status === "pending").length;
                const approvedCount = taskSubs.filter(s => s.status === "approved").length;
                const rejectedCount = taskSubs.filter(s => s.status === "rejected").length;
                const isExpanded = expandedTasks.has(task.task_id);
                const isFirstExpired = showHistory && task.status !== "active" && (idx === 0 || arr[idx-1].status === "active");
                const toggleExpand = () => setExpandedTasks(prev => {
                  const next = new Set(prev);
                  if (next.has(task.task_id)) next.delete(task.task_id); else next.add(task.task_id);
                  return next;
                });
                return (
                  <div key={task.task_id}>
                    {isFirstExpired && (
                      <div className="flex items-center gap-3 mb-4">
                        <div className="flex-1 h-px" style={{ background: "var(--surface-2)" }} />
                        <span className="text-xs font-semibold" style={{ color: "var(--text-secondary)" }}>{t("Admin.ended_2", lang)}</span>
                        <div className="flex-1 h-px" style={{ background: "var(--surface-2)" }} />
                      </div>
                    )}
                    <div className="rounded-2xl overflow-hidden" data-testid={`admin-task-${task.task_id}`}
                      style={{ background: "var(--surface-1)", border: "1px solid var(--border-color)", opacity: task.status !== "active" ? 0.75 : 1 }}>

                {/* Colored top stripe */}
                <div className="h-0.5" style={{ background: tType.color, opacity: 0.6 }} />

                {/* ── Header row ── */}
                <div className="px-4 pt-3.5 pb-3 flex items-start gap-3 cursor-pointer" onClick={toggleExpand}>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: `${tType.color}15`, color: tType.color }}>
                    <TaskTypeIcon type={task.task_type} className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-bold" style={{ color: "var(--text-primary)" }}>{task.title?.[lang] || task.title?.en}</h3>
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
                        style={task.status === "active"
                          ? { background: "rgba(34,197,94,0.12)", color: "#22c55e" }
                          : { background: "var(--surface-2)", color: "var(--text-secondary)" }}>
                        {task.status === "active" ? t("Admin.active_2", lang) : t("Admin.ended_2", lang)}
                      </span>
                      {pendingCount > 0 && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-bold animate-pulse"
                          style={{ background: "rgba(250,204,21,0.12)", color: "#facc15" }}>
                          ⏳ {pendingCount}
                        </span>
                      )}
                    </div>
                    {desc && <p className="text-xs mt-1 leading-relaxed" style={{ color: "var(--text-secondary)" }}>{desc}</p>}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {isExpanded
                      ? <ChevronUp className="w-3.5 h-3.5" style={{ color: "var(--text-secondary)" }} />
                      : <ChevronDown className="w-3.5 h-3.5" style={{ color: "var(--text-secondary)" }} />}
                    <button onClick={(e) => { e.stopPropagation(); openEdit(task); }}
                      className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-white/5 transition-colors"
                      style={{ color: "var(--text-secondary)" }}>
                      <PenSquare className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); deleteTask(task.task_id); }}
                      className="w-7 h-7 rounded-lg flex items-center justify-center hover:text-red-400 transition-colors"
                      style={{ color: "var(--text-secondary)" }}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* ── What user must do ── */}
                {task.link_url && (
                  <div className="mx-4 mb-3 rounded-xl px-3 py-2.5 flex items-center gap-2.5"
                    style={{ background: `${tType.color}0e`, border: `1px solid ${tType.color}22` }}>
                    <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" style={{ color: tType.color }} />
                    <div className="min-w-0 flex-1">
                      <a href={task.link_url} target="_blank" rel="noopener noreferrer"
                        className="text-sm font-semibold hover:opacity-80 transition-opacity flex items-center gap-1"
                        style={{ color: tType.color }}>
                        {linkLabel || t("Admin.open_link", lang)}
                        <span>{lang === "he" ? "←" : "→"}</span>
                      </a>
                      <div className="text-[10px] mt-0.5 truncate" style={{ color: "var(--text-secondary)" }}>{task.link_url}</div>
                    </div>
                  </div>
                )}

                {/* ── Rewards + stats ── */}
                <div className="px-4 pb-3.5 flex flex-wrap gap-1.5">
                  <div className="flex items-center gap-1 px-2.5 py-1 rounded-full" style={{ background: "rgba(247,119,55,0.12)", border: "1px solid rgba(247,119,55,0.22)" }}>
                    <Coins className="w-3 h-3 text-[#F77737]" /><span className="text-[11px] font-bold text-[#F77737]">+{task.reward_ipoints} iPoints</span>
                  </div>
                  <div className="flex items-center gap-1 px-2.5 py-1 rounded-full" style={{ background: "var(--surface-2)", border: "1px solid rgba(255,255,255,0.09)" }}>
                    <UsersIcon className="w-3 h-3" style={{ color: "var(--text-secondary)" }} />
                    <span className="text-[11px]" style={{ color: "var(--text-secondary)" }}>
                      {task.completions_count}{task.max_completions > 0 ? `/${task.max_completions}` : ""} {t("Admin.done_3", lang)}
                    </span>
                  </div>
                  {task.expires_at && (
                    <div className="flex items-center gap-1 px-2.5 py-1 rounded-full" style={{ background: "rgba(64,93,230,0.10)", border: "1px solid rgba(64,93,230,0.20)" }}>
                      <Clock className="w-3 h-3 text-[#7aa2ff]" /><span className="text-[11px] text-[#7aa2ff]">{formatIsraelDate(task.expires_at)} · {formatIsraelTime(task.expires_at)}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1 px-2.5 py-1 rounded-full"
                    style={{ background: task.requires_proof ? "rgba(64,93,230,0.08)" : "rgba(34,197,94,0.08)", border: `1px solid ${task.requires_proof ? "rgba(64,93,230,0.18)" : "rgba(34,197,94,0.18)"}` }}>
                    {task.requires_proof
                      ? <><ImagePlus className="w-3 h-3 text-[#7aa2ff]" /><span className="text-[11px] text-[#7aa2ff]">{t("Admin.proof_required", lang)}</span></>
                      : <><Check className="w-3 h-3 text-green-400" /><span className="text-[11px] text-green-400">{t("Admin.no_proof", lang)}</span></>
                    }
                  </div>
                  {/* Submissions summary pill */}
                  {taskSubs.length > 0 && (
                    <button onClick={toggleExpand}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-full transition-all"
                      style={{ background: isExpanded ? "rgba(64,93,230,0.18)" : "rgba(64,93,230,0.08)", border: "1px solid rgba(64,93,230,0.25)", color: "#7aa2ff" }}>
                      <ClipboardList className="w-3 h-3" />
                      <span className="text-[11px] font-bold">
                        {taskSubs.length} {t("Admin.submissions", lang)}
                        {pendingCount > 0 && ` · ⏳${pendingCount}`}
                        {approvedCount > 0 && ` · ✓${approvedCount}`}
                        {rejectedCount > 0 && ` · ✗${rejectedCount}`}
                      </span>
                    </button>
                  )}
                </div>

                {/* ── Inline Submissions ── */}
                {isExpanded && (
                  <div className="border-t" style={{ borderColor: `${tType.color}20` }}>
                    <div className="px-4 py-3" style={{ background: `${tType.color}05` }}>
                      <p className="text-xs font-bold mb-3" style={{ color: tType.color }}>
                        {t("Admin.submissions_for_this_task", lang)} ({taskSubs.length})
                      </p>
                      {taskSubs.length === 0 ? (
                        <p className="text-xs py-4 text-center" style={{ color: "var(--text-secondary)" }}>
                          {t("Admin.no_submissions_yet", lang)}
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {taskSubs.map(sub => (
                            <SubCard key={sub.submission_id} sub={sub} lang={lang} onReview={reviewSubmission} tasks={tasks} compact />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}
        </>
      )}

      {mainTab === "all_submissions" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h3 className="text-base font-bold flex items-center gap-2">
              {t("Admin.submissions_2", lang)}
              {submissions.filter(s => s.status === "pending").length > 0 && (
                <span className="px-2 py-0.5 rounded-full text-xs font-bold" style={{ background: "rgba(247,119,55,0.15)", color: "#F77737" }}>
                  {submissions.filter(s => s.status === "pending").length}
                </span>
              )}
            </h3>
            <div className="flex gap-1.5 flex-wrap">
              {["","pending","approved","rejected"].map(s => (
                <button key={s} onClick={() => setSubFilter(s)}
                  className="text-xs px-3 py-1.5 rounded-full border font-medium transition-all"
                  style={{ background: subFilter===s ? (s==="pending"?"rgba(247,119,55,0.1)":s==="approved"?"rgba(0,200,83,0.1)":s==="rejected"?"rgba(225,48,108,0.1)":"var(--surface-3)") : "transparent", borderColor: subFilter===s ? "rgba(255,255,255,0.15)" : "var(--surface-3)", color: subFilter===s ? "var(--text-primary)" : "var(--text-secondary)" }}>
                  {s==="" ? t("shop.all", lang) : s==="pending" ? t("Admin.pending_3", lang) : s==="approved" ? t("order.approved", lang) : t("order.rejected", lang)}
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-3">
            {submissions.map(sub => (<SubCard key={sub.submission_id} sub={sub} lang={lang} onReview={reviewSubmission} tasks={tasks} />))}
            {submissions.length === 0 && (
              <div className="rounded-2xl border p-10 text-center" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
                <p className="text-sm" style={{ color: "var(--text-secondary)" }}>{t("Admin.no_submissions", lang)}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
function SubCard({ sub, lang, onReview, tasks, compact = false }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const [exp, setExp] = useState(sub.status === "pending");
  const [lbImg, setLbImg] = useState("");

  const task = tasks?.find(t => t.task_id === sub.task_id);
  const tType = task ? getTaskTypeMeta(task.task_type) : getTaskTypeMeta("custom");
  const taskTitle = task ? (task.title?.[lang] || task.title?.en || "—") : "—";
  const taskLink = task?.link_url;
  const taskLinkLabel = task ? (task.link_label?.[lang] || task.link_label?.en) : null;
  const taskDesc = task ? (task.description?.[lang] || task.description?.en) : null;

  const sc = sub.status === "pending"
    ? { color: "#facc15", bg: "rgba(250,204,21,0.12)", label: t("Admin.pending_3", lang) }
    : sub.status === "approved"
      ? { color: "#22c55e", bg: "rgba(34,197,94,0.12)", label: t("order.approved", lang) }
      : { color: "#f87171", bg: "rgba(248,113,113,0.12)", label: t("order.rejected", lang) };

  const handle = async (action) => {
    setBusy(action);
    await onReview(sub.submission_id, action, note);
    setBusy("");
  };

  return (
    <div className="rounded-2xl overflow-hidden" data-testid={`submission-${sub.submission_id}`}
      style={{ background: "var(--surface-1)", border: compact ? `1px solid ${tType.color}25` : "1px solid var(--border-color)" }}>

      {/* ── Task context bar (hidden in compact/inline mode) ── */}
      {!compact && <div className="px-4 pt-3 pb-2 flex items-center gap-2.5"
        style={{ background: `${tType.color}08`, borderBottom: `1px solid var(--border-color)` }}>
        <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: `${tType.color}15`, color: tType.color }}>
          <TaskTypeIcon type={task?.task_type || "custom"} className="w-3.5 h-3.5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold truncate" style={{ color: tType.color }}>{taskTitle}</div>
          {taskLink && (
            <a href={taskLink} target="_blank" rel="noopener noreferrer"
              className="text-[10px] truncate block hover:underline"
              style={{ color: "var(--text-secondary)" }}>
              {taskLinkLabel ? `${taskLinkLabel} ${lang==="he"?"←":"→"} ` : ""}{taskLink}
            </a>
          )}
          {!taskLink && taskDesc && (
            <div className="text-[10px] truncate" style={{ color: "var(--text-secondary)" }}>{taskDesc}</div>
          )}
        </div>
        <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold flex-shrink-0"
          style={{ background: "rgba(247,119,55,0.12)", color: "#F77737" }}>
          +{task?.reward_ipoints ?? "?"} iP
        </span>
      </div>}

      {/* ── User row ── */}
      <div className="px-4 py-3 flex items-center gap-3 cursor-pointer" onClick={() => setExp(!exp)}>
        <div className="w-9 h-9 rounded-xl flex items-center justify-center text-sm font-bold flex-shrink-0" style={{ background: sc.bg, color: sc.color }}>
          {(sub.user_name || "?")[0].toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-bold truncate">{sub.user_name}</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold flex-shrink-0" style={{ background: sc.bg, color: sc.color }}>{sc.label}</span>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-xs truncate" style={{ color: "var(--text-secondary)" }}>{sub.user_email}</span>
            <span className="text-[10px] flex-shrink-0" style={{ color: "var(--text-secondary)" }}>{formatIsraelDate(sub.created_at)}</span>
          </div>
        </div>
        {exp ? <ChevronUp className="w-4 h-4 flex-shrink-0" style={{ color: "var(--text-secondary)" }} /> : <ChevronDown className="w-4 h-4 flex-shrink-0" style={{ color: "var(--text-secondary)" }} />}
      </div>

      {exp && (
        <div className="border-t animate-fade-in" style={{ borderColor: "var(--border-color)" }}>
          {/* Post link for quick verification */}
          {compact && taskLink && (
            <div className="px-4 pt-3">
              <a href={taskLink} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold transition-all hover:opacity-80"
                style={{ background: `${tType.color}12`, border: `1px solid ${tType.color}25`, color: tType.color }}>
                <ExternalLink className="w-3.5 h-3.5" />
                {taskLinkLabel || t("Admin.open_link", lang)}
                <span className="ms-auto">{lang === "he" ? "←" : "→"}</span>
              </a>
            </div>
          )}
          {/* Proof image */}
          {sub.proof_image && (
            <button type="button" onClick={() => setLbImg(`${process.env.REACT_APP_BACKEND_URL}${sub.proof_image}`)}
              className="block w-full overflow-hidden cursor-zoom-in"
              style={{ borderTop: "1px solid var(--border-color)" }}>
              <img src={`${process.env.REACT_APP_BACKEND_URL}${sub.proof_image}`} alt="proof"
                className="w-full max-h-60 object-contain" style={{ background: "var(--surface-2)" }} />
              <div className="py-1.5 text-[11px] text-center" style={{ color: "var(--text-secondary)", borderTop: "1px solid var(--border-color)" }}>
                🧾 {t("Admin.proof_photo", lang)}
              </div>
            </button>
          )}
          {lbImg && <Lightbox src={lbImg} onClose={() => setLbImg("")} />}

          <div className="px-4 py-3 space-y-2.5">
            {sub.admin_note && (
              <p className="text-xs px-3 py-2 rounded-xl" style={{ background: "var(--surface-2)", color: "var(--text-secondary)" }}>
                {sub.admin_note}
              </p>
            )}

            {sub.status === "pending" && (
              <>
                <input value={note} onChange={e => setNote(e.target.value)}
                  placeholder={t("Admin.note_to_user_optional", lang)}
                  className="w-full h-9 rounded-xl px-3 text-sm outline-none"
                  style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)", color: "var(--text-primary)" }} />
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={() => handle("approve")} disabled={!!busy}
                    className="h-10 rounded-xl text-sm font-bold flex items-center justify-center gap-2"
                    style={{ background: "#22c55e", color: "#fff" }}
                    data-testid={`approve-sub-${sub.submission_id}`}>
                    {busy === "approve" ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Check className="w-4 h-4" />}
                    {t("Admin.approve", lang)}
                  </button>
                  <button onClick={() => handle("reject")} disabled={!!busy}
                    className="h-10 rounded-xl text-sm font-bold flex items-center justify-center gap-2"
                    style={{ background: "rgba(239,68,68,0.1)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.2)" }}
                    data-testid={`reject-sub-${sub.submission_id}`}>
                    {busy === "reject" ? <div className="w-4 h-4 border-2 border-red-400/30 border-t-red-400 rounded-full animate-spin" /> : <X className="w-4 h-4" />}
                    {t("Admin.reject", lang)}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ===== ADMIN TOP-UPS =====
function AdminTopUpsTab({ lang }) {
  const [topups, setTopups] = useState([]);
  const [filter, setFilter] = useState("");

  const fetch_ = useCallback(async () => {
    const url = filter ? `${API}/admin/topups?status=${filter}` : `${API}/admin/topups`;
    const r = await fetch(url, { credentials: "include" });
    if (r.ok) setTopups(await r.json());
  }, [filter]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const review = async (id, action, note) => {
    try {
      const r = await fetch(`${API}/admin/topups/${id}/review`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ action, admin_note: note }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail);
      toast.success(d.message);
      fetch_();
    } catch (e) { toast.error(e.message); }
  };

  const expire = (id) => setTopups(prev => prev.filter(t => t.topup_id !== id));

  const pendingCount  = topups.filter(t => t.status === "pending").length;
  const approvedCount = topups.filter(t => t.status === "approved").length;
  const draftCount    = topups.filter(t => t.status === "draft").length;
  const totalILS      = topups.filter(t => t.status === "approved").reduce((s, t) => s + Number(t.amount_ils || 0), 0);

  const FILTERS = [
    { val: "",               label: t("shop.all", lang),              color: "var(--text-primary)" },
    { val: "pending",        label: t("Admin.pending_3", lang),       color: "#facc15" },
    { val: "allpay_pending", label: "💳 Card",                        color: "#a78bfa" },
    { val: "draft",          label: t("Admin.in_progress_4", lang),   color: "#60a5fa" },
    { val: "approved",       label: t("order.approved", lang),        color: "#22c55e" },
    { val: "rejected",       label: t("order.rejected", lang),        color: "#f87171" },
  ];

  return (
    <div className="space-y-4 animate-fade-in-up">

      {/* Stats bar */}
      <div className="rounded-2xl overflow-hidden" style={{ background: "var(--surface-1)", border: "1px solid var(--border-color)" }}>
        <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0" style={{ "--tw-divide-color": "var(--border-color)" }}>
          {[
            { label: t("Admin.pending_4", lang),        value: pendingCount,                    color: "#facc15", dot: true },
            { label: t("Admin.in_progress_4", lang),  value: draftCount,                      color: "#60a5fa", dot: true },
            { label: t("Admin.approved", lang),         value: approvedCount,                   color: "#22c55e", dot: false },
            { label: t("Shop.total", lang),               value: `₪${totalILS.toLocaleString()}`, color: "#F77737", dot: false },
          ].map((stat, i) => (
            <div key={i} className="px-5 py-4" style={{ borderColor: "var(--border-color)" }}>
              <div className="flex items-baseline gap-2">
                {stat.dot && stat.value > 0 && <span className="w-2 h-2 rounded-full flex-shrink-0 animate-pulse" style={{ background: stat.color }} />}
                <span className="font-black font-outfit text-2xl leading-none" style={{ color: stat.color }}>{stat.value}</span>
              </div>
              <div className="text-[11px] mt-1.5 font-medium" style={{ color: "var(--text-secondary)" }}>{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Filter pills */}
      <div className="flex gap-1.5 flex-wrap">
        {FILTERS.map(f => (
          <button key={f.val} onClick={() => setFilter(f.val)}
            className="text-xs px-3.5 py-1.5 rounded-full border font-semibold transition-all"
            style={filter === f.val
              ? { background: `${f.color}15`, borderColor: `${f.color}40`, color: f.color }
              : { background: "transparent", borderColor: "var(--border-color)", color: "var(--text-secondary)" }}>
            {f.label}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="space-y-2">
        {topups.map(tp => (
          <TopUpCard key={tp.topup_id} tp={tp} lang={lang} onReview={review} onExpire={expire} />
        ))}
        {topups.length === 0 && (
          <div className="rounded-2xl py-16 text-center" style={{ background: "var(--surface-1)", border: "1px solid var(--border-color)" }}>
            <Wallet className="w-9 h-9 mx-auto mb-3 opacity-20" style={{ color: "#00C853" }} />
            <p className="text-sm font-semibold" style={{ color: "var(--text-secondary)" }}>{t("Admin.no_requests", lang)}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function useCountdown(createdAt, minutes, onExpire) {
  const [remaining, setRemaining] = useState("");
  const firedRef = useRef(false);
  useEffect(() => {
    if (!createdAt) return;
    const tick = () => {
      const expiry = new Date(createdAt).getTime() + minutes * 60 * 1000;
      const diff = Math.max(0, expiry - Date.now());
      if (diff === 0) {
        setRemaining("00:00");
        if (!firedRef.current) { firedRef.current = true; onExpire?.(); }
        return;
      }
      const m = Math.floor(diff / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setRemaining(`${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [createdAt, minutes]); // eslint-disable-line react-hooks/exhaustive-deps
  return remaining;
}

function TopUpCard({ tp, lang, onReview, onExpire }) {
  const [note, setNote] = useState("");
  const [lbImg, setLbImg] = useState("");
  const [exp, setExp] = useState(false);

  const handleExpire = useCallback(() => {
    if (tp.status === "draft") onExpire?.(tp.topup_id);
  }, [tp.status, tp.topup_id, onExpire]);

  const countdown = useCountdown(tp.created_at, 30, handleExpire);

  const STATUS = {
    pending:        { color: "#facc15", label: t("Admin.pending_3", lang) },
    approved:       { color: "#22c55e", label: t("order.approved", lang) },
    draft:          { color: "#60a5fa", label: countdown || "…" },
    rejected:       { color: "#f87171", label: t("order.rejected", lang) },
    allpay_pending: { color: "#a78bfa", label: "💳 Processing" },
  };
  const sc = STATUS[tp.status] || STATUS.rejected;
  const isPending = tp.status === "pending";

  const dateStr = tp.created_at
    ? new Date(tp.created_at).toLocaleString("ru-RU", { day:"2-digit", month:"2-digit", hour:"2-digit", minute:"2-digit" })
    : "";

  return (
    <div className="rounded-2xl overflow-hidden" data-testid={`admin-topup-${tp.topup_id}`}
      style={{ background: "var(--surface-1)", border: `1px solid ${isPending ? "rgba(250,204,21,0.2)" : "var(--border-color)"}` }}>

      {/* Main row */}
      <div className="flex items-center gap-3 px-4 py-3">

        {/* Status dot */}
        <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: sc.color, opacity: isPending ? 1 : 0.5, boxShadow: isPending ? `0 0 6px ${sc.color}80` : "none" }} />

        {/* Avatar */}
        <div className="w-8 h-8 rounded-xl flex items-center justify-center text-sm font-black flex-shrink-0"
          style={{ background: "var(--surface-2)", color: "var(--text-secondary)" }}>
          {(tp.user_name || "?")[0].toUpperCase()}
        </div>

        {/* Name + email */}
        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => setExp(!exp)}>
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>{tp.user_name}</span>
            {tp.ref_code && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold"
                style={{ background: "rgba(139,92,246,0.12)", color: "#8B5CF6" }}>{tp.ref_code}</span>
            )}
          </div>
          <div className="text-[11px] mt-0.5 flex items-center gap-2" style={{ color: "var(--text-secondary)" }}>
            <span className="truncate">{tp.user_email}</span>
            {dateStr && <><span>·</span><span className="flex-shrink-0">{dateStr}</span></>}
          </div>
        </div>

        {/* Amount */}
        <div className="text-right flex-shrink-0">
          <div className="font-black font-outfit text-base leading-none" style={{ color: "#22c55e" }}>₪{tp.amount_ils}</div>
          <div className="text-[11px] font-semibold mt-0.5" style={{ color: "#F77737" }}>{tp.ipoints_amount} iP</div>
        </div>

        {/* Pending: inline approve/reject */}
        {isPending ? (
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button onClick={() => onReview(tp.topup_id, "approve", note)}
              className="h-8 px-3 rounded-xl text-xs font-bold flex items-center gap-1 transition-all hover:opacity-90"
              style={{ background: "#22c55e", color: "#fff" }}>
              <Check className="w-3.5 h-3.5" />{t("Admin.approve", lang)}
            </button>
            <button onClick={() => onReview(tp.topup_id, "reject", note)}
              className="h-8 px-3 rounded-xl text-xs font-bold flex items-center gap-1 transition-all hover:opacity-90"
              style={{ background: "rgba(239,68,68,0.12)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.2)" }}>
              <X className="w-3.5 h-3.5" />{t("Admin.reject_2", lang)}
            </button>
            <button onClick={() => setExp(!exp)} className="w-8 h-8 rounded-xl flex items-center justify-center transition-all hover:bg-white/5"
              style={{ color: "var(--text-secondary)" }}>
              {exp ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
              style={{ background: `${sc.color}15`, color: sc.color }}>{sc.label}</span>
            <button onClick={() => setExp(!exp)} className="w-7 h-7 rounded-lg flex items-center justify-center transition-all hover:bg-white/5"
              style={{ color: "var(--text-secondary)" }}>
              {exp ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          </div>
        )}
      </div>

      {/* Expanded body */}
      {exp && (
        <div className="px-4 pb-4 space-y-3" style={{ borderTop: "1px solid var(--border-color)" }}>

          {/* Meta row */}
          <div className="flex flex-wrap gap-4 pt-3 text-sm">
            {[
              { label: t("Admin.amount", lang), value: `₪${tp.amount_ils}`, color: "#22c55e" },
              { label: "iPoints", value: `${tp.ipoints_amount}`, color: "#F77737" },
              { label: t("Admin.commission", lang), value: `₪${(Number(tp.amount_ils)*0.25).toFixed(2)}`, color: "#c084fc" },
              { label: t("Admin.method_2", lang), value: tp.payment_method || "Bit", color: "var(--text-primary)" },
            ].map(item => (
              <div key={item.label}>
                <div className="text-[10px] uppercase tracking-wider mb-0.5" style={{ color: "var(--text-secondary)" }}>{item.label}</div>
                <div className="font-bold font-outfit" style={{ color: item.color }}>{item.value}</div>
              </div>
            ))}
          </div>

          {/* Proof image */}
          {tp.proof_image && (
            <button type="button" onClick={() => setLbImg(`${process.env.REACT_APP_BACKEND_URL}${tp.proof_image}`)}
              className="w-full overflow-hidden rounded-xl cursor-zoom-in block"
              style={{ border: "1px solid var(--border-color)" }}>
              <img src={`${process.env.REACT_APP_BACKEND_URL}${tp.proof_image}`} alt="proof"
                className="w-full max-h-72 object-contain" style={{ background: "var(--surface-2)" }} />
              <div className="py-1.5 text-[11px] text-center" style={{ color: "var(--text-secondary)", borderTop: "1px solid var(--border-color)" }}>
                {t("Admin.payment_screenshot", lang)}
              </div>
            </button>
          )}
          {lbImg && <Lightbox src={lbImg} onClose={() => setLbImg("")} />}

          {/* Admin note */}
          {tp.admin_note && (
            <div className="text-xs px-3 py-2 rounded-xl" style={{ background: "var(--surface-2)", color: "var(--text-secondary)" }}>
              {tp.admin_note}
            </div>
          )}

          {/* Note input + actions for pending */}
          {isPending && (
            <div className="flex gap-2 items-center">
              <input value={note} onChange={e => setNote(e.target.value)}
                placeholder={t("Admin.note_to_user_optional_2", lang)}
                className="flex-1 h-9 rounded-xl px-3 text-xs outline-none"
                style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)", color: "var(--text-primary)" }} />
              <button onClick={() => onReview(tp.topup_id, "approve", note)}
                className="h-9 px-4 rounded-xl text-sm font-bold flex items-center gap-1.5 flex-shrink-0"
                style={{ background: "#22c55e", color: "#fff" }}>
                <Check className="w-4 h-4" />{t("Admin.approve", lang)}
              </button>
              <button onClick={() => onReview(tp.topup_id, "reject", note)}
                className="h-9 px-4 rounded-xl text-sm font-bold flex items-center gap-1.5 flex-shrink-0"
                style={{ background: "rgba(239,68,68,0.12)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.2)" }}>
                <X className="w-4 h-4" />{t("Admin.reject", lang)}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ===== ADMIN PROMO CODES =====
function AdminPromosTab({lang}){const [promos,setPromos]=useState([]);const [show,setShow]=useState(false);
  const [showExhausted,setShowExhausted]=useState(false);
  const [showExpired,setShowExpired]=useState(false);
  const [usesMap,setUsesMap]=useState({});   // promo_id → uses[] | "loading"
  const [form,setForm]=useState({code:"",bonus_type:"fixed",bonus_value:"50",max_uses:"0",per_user_limit:"1",min_topup_ils:"0",expires_at:"",desc_en:"",desc_ru:"",desc_he:"",ai_prompt:"",allowed_users:[]});
  const [userSearch,setUserSearch]=useState("");const [userResults,setUserResults]=useState([]);
  const [translating,setTranslating]=useState(false);
  const fetch_=useCallback(async()=>{const r=await fetch(`${API}/admin/promo-codes`,{credentials:"include"});if(r.ok) setPromos(await r.json());},[]);
  const searchUsers=async(q)=>{setUserSearch(q);if(q.length<2){setUserResults([]);return;}
    try{const r=await fetch(`${API}/admin/users/search?q=${encodeURIComponent(q)}`,{credentials:"include"});if(r.ok)setUserResults(await r.json());}catch{}};
  const addAllowedUser=(u)=>{if(!form.allowed_users.find(x=>x.user_id===u.user_id))setForm(f=>({...f,allowed_users:[...f.allowed_users,u]}));setUserSearch("");setUserResults([]);};
  const removeAllowedUser=(uid)=>setForm(f=>({...f,allowed_users:f.allowed_users.filter(u=>u.user_id!==uid)}));
  const fetchUses=async(promoId)=>{
    if(usesMap[promoId]==="loading"||Array.isArray(usesMap[promoId]))return;
    setUsesMap(m=>({...m,[promoId]:"loading"}));
    try{const r=await fetch(`${API}/admin/promo-codes/${promoId}/uses`,{credentials:"include"});
      const d=await r.json();setUsesMap(m=>({...m,[promoId]:Array.isArray(d)?d:[]}));
    }catch{setUsesMap(m=>({...m,[promoId]:[]}));}
  };
  const toggleUses=(promoId)=>{
    if(Array.isArray(usesMap[promoId])||usesMap[promoId]==="loading"){
      setUsesMap(m=>{const n={...m};delete n[promoId];return n;});
    }else{fetchUses(promoId);}
  };
  useEffect(()=>{fetch_();},[fetch_]);
  const aiTranslatePromo=async()=>{
    const src=form.ai_prompt.trim()||form.desc_en.trim()||form.code.trim();
    if(!src){toast.error(t("Admin.enter_description_first", lang));return;}
    setTranslating(true);
    try{const r=await fetch(`${API}/admin/messages/translate-preview`,{method:"POST",headers:{"Content-Type":"application/json"},credentials:"include",
      body:JSON.stringify({source_subject:form.desc_en.trim()||src,source_content:src,source_language:"auto",message_type:"general"})});
      const d=await r.json();if(!r.ok)throw new Error(d.detail||"AI failed");
      setForm(prev=>({...prev,desc_en:prev.desc_en||d.subject?.en||d.content?.en||prev.desc_en,desc_ru:d.subject?.ru||d.content?.ru||prev.desc_ru,desc_he:d.subject?.he||d.content?.he||prev.desc_he}));
      toast.success(t("Admin.ai_translated", lang));}
    catch(e){toast.error(e.message);}finally{setTranslating(false);}};
  const create=async()=>{if(!form.code.trim()){toast.error("Code required");return;}
    const codeUpper=form.code.trim().toUpperCase();
    const dup=promos.find(p=>p.code===codeUpper);
    if(dup){const isExpired=dup.expires_at&&dup.expires_at<new Date().toISOString();toast.error(isExpired?L(lang,`Code "${codeUpper}" already exists (expired)`,`Промокод "${codeUpper}" уже существует (просрочен)`,`הקוד "${codeUpper}" כבר קיים (פג תוקף)`):L(lang,`Code "${codeUpper}" already exists`,`Промокод "${codeUpper}" уже существует`,`הקוד "${codeUpper}" כבר קיים`));return;}
    try{const r=await fetch(`${API}/admin/promo-codes`,{method:"POST",headers:{"Content-Type":"application/json"},credentials:"include",
      body:JSON.stringify({code:form.code,bonus_type:form.bonus_type,bonus_value:parseFloat(form.bonus_value)||50,max_uses:parseInt(form.max_uses)||0,per_user_limit:parseInt(form.per_user_limit)||1,min_topup_ils:parseFloat(form.min_topup_ils)||0,expires_at:form.expires_at||null,description:{en:form.desc_en,ru:form.desc_ru,he:form.desc_he},allowed_user_ids:form.allowed_users.map(u=>u.user_id)})});
      const d=await r.json();if(!r.ok)throw new Error(d.detail);toast.success(d.message);setShow(false);setForm({code:"",bonus_type:"fixed",bonus_value:"50",max_uses:"0",per_user_limit:"1",min_topup_ils:"0",expires_at:"",desc_en:"",desc_ru:"",desc_he:"",ai_prompt:"",allowed_users:[]});setUserSearch("");setUserResults([]);fetch_();}catch(e){toast.error(e.message);}};
  const toggle=async(id)=>{await fetch(`${API}/admin/promo-codes/${id}/toggle`,{method:"PUT",credentials:"include"});fetch_();};
  const del=async(id)=>{await fetch(`${API}/admin/promo-codes/${id}`,{method:"DELETE",credentials:"include"});fetch_();};
  const now = new Date().toISOString();
  const activePromos = promos.filter(p => p.is_active && !(p.max_uses > 0 && p.current_uses >= p.max_uses) && !(p.expires_at && p.expires_at < now));
  const exhaustedPromos = promos.filter(p => p.max_uses > 0 && p.current_uses >= p.max_uses);
  const expiredPromos = promos.filter(p => p.expires_at && p.expires_at < now && !(p.max_uses > 0 && p.current_uses >= p.max_uses));
  const totalUses = promos.reduce((s,p)=>s+(p.current_uses||0),0);

  return(<div className="space-y-6 animate-fade-in-up">
    {/* Header */}
    <div className="rounded-2xl p-5" style={{ background: "var(--surface-1)", border: "1px solid var(--border-color)" }}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <AdminIcon emoji="🏷️" className="w-6 h-6" />
          <div>
            <h2 className="text-xl font-black font-outfit">{t("Admin.promo_codes", lang)}</h2>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold" style={{ background:"rgba(34,197,94,0.12)", color:"#22c55e" }}>✅ {activePromos.length} {t("Admin.active_3", lang)}</span>
              {exhaustedPromos.length > 0 && <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold" style={{ background:"rgba(225,48,108,0.12)", color:"#E1306C" }}>💯 {exhaustedPromos.length} {t("Admin.exhausted", lang)}</span>}
              {expiredPromos.length > 0 && <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold" style={{ background:"rgba(250,204,21,0.12)", color:"#facc15" }}>⌛ {expiredPromos.length} {t("Admin.expired", lang)}</span>}
              <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold" style={{ background:"rgba(131,58,180,0.12)", color:"#c084fc" }}>🎯 {totalUses} {t("Admin.uses", lang)}</span>
            </div>
          </div>
        </div>
        <Dialog open={show} onOpenChange={setShow}>
      <DialogTrigger asChild>
        <Button className="btn-brand text-sm px-4 h-9" data-testid="create-promo-btn">
          <Plus className="w-4 h-4 me-1"/>{t("Admin.create", lang)}
        </Button>
      </DialogTrigger>
      <DialogContent style={{ background: "var(--bg-secondary)", borderColor: "var(--border-color)" }} className="max-w-sm">
        <DialogHeader><DialogTitle className="text-base font-bold">{t("Admin.new_promo_code", lang)}</DialogTitle></DialogHeader>
        <div className="space-y-4 pb-1">

          {/* Code input */}
          <div className="rounded-2xl border p-3 text-center" style={{ borderColor: "rgba(139,92,246,0.25)", background: "rgba(139,92,246,0.06)" }}>
            <p className="text-[11px] font-semibold mb-2 uppercase tracking-wider" style={{ color: "var(--text-secondary)" }}>{t("Admin.promo_code", lang)}</p>
            <Input value={form.code} onChange={e=>setForm({...form,code:e.target.value.toUpperCase()})}
              placeholder="SUMMER50"
              className="text-center text-xl font-black tracking-widest font-outfit uppercase h-12"
              style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "#8B5CF6", letterSpacing: "0.15em" }}
              data-testid="promo-code-field"/>
          </div>

          {/* Type toggle */}
          <div>
            <p className="text-xs font-semibold mb-2" style={{ color: "var(--text-secondary)" }}>{t("Admin.bonus_type", lang)}</p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: "fixed",      label: "iPoints", sub: t("Admin.fixed_amount", lang), color: "#F77737" },
                { value: "percentage", label: "%",        sub: t("Admin.percentage", lang),           color: "#8B5CF6" },
              ].map(opt => (
                <button key={opt.value} onClick={() => setForm({...form, bonus_type: opt.value})}
                  className="rounded-xl border p-3 text-left transition-all"
                  style={{ borderColor: form.bonus_type === opt.value ? `${opt.color}44` : "var(--border-color)", background: form.bonus_type === opt.value ? `${opt.color}12` : "var(--surface-1)" }}>
                  <div className="font-bold text-sm" style={{ color: form.bonus_type === opt.value ? opt.color : "var(--text-primary)" }}>{opt.label}</div>
                  <div className="text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>{opt.sub}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Values */}
          <div className="grid grid-cols-3 gap-2">
            {[
              { label: form.bonus_type === "fixed" ? "iPoints" : "%", key: "bonus_value" },
              { label: <>{t("Admin.people", lang)} <span className="opacity-50">(0=∞)</span></>, key: "max_uses" },
              { label: t("Admin.per_person", lang), key: "per_user_limit" },
            ].map(f => (
              <div key={f.key}>
                <p className="text-xs font-semibold mb-1.5" style={{ color: "var(--text-secondary)" }}>{f.label}</p>
                <Input type="number" value={form[f.key]} onChange={e=>setForm({...form,[f.key]:e.target.value})}
                  className="h-10 text-sm font-bold text-center"
                  style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }}/>
              </div>
            ))}
          </div>

          {/* Minimum top-up */}
          <div>
            <p className="text-xs font-semibold mb-1.5" style={{ color: "var(--text-secondary)" }}>
              {t("Admin.minimum_top_up", lang)}<span className="ms-1.5 font-normal opacity-60">{t("Admin.0_no_minimum", lang)}</span>
            </p>
            <Input type="number" min="0" value={form.min_topup_ils} onChange={e=>setForm({...form,min_topup_ils:e.target.value})}
              className="h-10 text-sm font-bold"
              style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }}/>
          </div>

          {/* Expiry */}
          <div>
            <p className="text-xs font-semibold mb-1.5" style={{ color: "var(--text-secondary)" }}>{t("Admin.expires_at_optional", lang)}</p>
            <Input type="datetime-local" value={form.expires_at} onChange={e=>setForm({...form,expires_at:e.target.value})}
              className="h-10 text-sm [color-scheme:dark] cursor-pointer"
              style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }}/>
          </div>

          {/* Restrict to users */}
          <div className="rounded-xl border p-3 space-y-2" style={{ borderColor: "var(--border-color)", background: "var(--surface-1)" }}>
            <div className="flex items-center gap-2">
              <Shield className="w-3.5 h-3.5" style={{ color: "#8B5CF6" }} />
              <p className="text-xs font-semibold" style={{ color: "#8B5CF6" }}>{t("Admin.restrict_to_users_optional", lang)}</p>
            </div>
            <div className="relative">
              <Input value={userSearch} onChange={e=>searchUsers(e.target.value)}
                placeholder={t("Admin.search_by_email_or_name", lang)}
                className="h-9 text-sm"
                style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }} />
              {userResults.length > 0 && (
                <div className="absolute z-50 left-0 right-0 top-full mt-1 rounded-xl overflow-hidden" style={{ background: "var(--bg-secondary)", border: "1px solid var(--border-color)" }}>
                  {userResults.map(u => (
                    <button key={u.user_id} type="button" onClick={() => addAllowedUser(u)}
                      className="w-full px-3 py-2 text-left text-xs hover:bg-white/5 transition-colors flex items-center gap-2">
                      <span className="font-semibold truncate" style={{ color: "var(--text-primary)" }}>{u.name || u.email}</span>
                      <span className="text-[10px] truncate" style={{ color: "var(--text-secondary)" }}>{u.email}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {form.allowed_users.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {form.allowed_users.map(u => (
                  <span key={u.user_id} className="flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full"
                    style={{ background: "rgba(139,92,246,0.12)", color: "#8B5CF6", border: "1px solid rgba(139,92,246,0.25)" }}>
                    {u.name || u.email}
                    <button type="button" onClick={() => removeAllowedUser(u.user_id)} className="hover:text-red-400 transition-colors"><X className="w-2.5 h-2.5" /></button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* AI Prompt */}
          <div className="rounded-xl border p-3 space-y-2" style={{ borderColor: "rgba(139,92,246,0.25)", background: "rgba(139,92,246,0.05)" }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5" style={{ color: "#8B5CF6" }} />
                <p className="text-xs font-semibold" style={{ color: "#8B5CF6" }}>AI</p>
              </div>
              <button type="button" onClick={aiTranslatePromo} disabled={translating}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-xl transition-all disabled:opacity-50 font-semibold"
                style={{ background: "#8B5CF6", color: "#fff" }}>
                <Sparkles className="w-3 h-3" />{translating ? "…" : t("Admin.generate", lang)}
              </button>
            </div>
            <Textarea value={form.ai_prompt} onChange={e=>setForm({...form,ai_prompt:e.target.value})}
              placeholder={t("Admin.describe_the_promo_in_any_language", lang)}
              className="min-h-[52px] resize-none text-sm"
              style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }} />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <p className="text-xs font-semibold" style={{ color: "var(--text-secondary)" }}>{t("Admin.description_optional", lang)}</p>
            {[["EN","English",form.desc_en,v=>setForm({...form,desc_en:v})],["RU","Русский",form.desc_ru,v=>setForm({...form,desc_ru:v})],["HE","עברית",form.desc_he,v=>setForm({...form,desc_he:v})]].map(([flag,ph,val,onChange]) => (
              <div key={flag} className="flex items-center gap-2">
                <span className="w-7 text-[11px] font-bold text-center rounded-lg py-1 flex-shrink-0" style={{ background: "var(--surface-2)", color: "var(--text-secondary)" }}>{flag}</span>
                <Input value={val} onChange={e=>onChange(e.target.value)} placeholder={ph}
                  className="h-9 text-sm flex-1"
                  style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }}/>
              </div>
            ))}
          </div>

          <button onClick={create} className="w-full h-11 rounded-xl text-sm font-bold flex items-center justify-center gap-2" style={{ background: "#8B5CF6", color: "#fff" }} data-testid="save-promo-btn">
            <Save className="w-4 h-4"/>{t("Admin.create_promo", lang)}
          </button>
        </div>
      </DialogContent>
    </Dialog>
      </div>{/* close relative flex */}
    </div>{/* close header card */}
      {/* ── Active / non-exhausted promos ── */}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {promos.filter(p => { const now = new Date().toISOString(); return !(p.max_uses > 0 && p.current_uses >= p.max_uses) && !(p.expires_at && p.expires_at < now); }).map(p => (
          <PromoCard key={p.promo_id} p={p} lang={lang} onToggle={toggle} onDel={del} usesMap={usesMap} onToggleUses={toggleUses} />
        ))}
        {promos.filter(p => { const now = new Date().toISOString(); return !(p.max_uses > 0 && p.current_uses >= p.max_uses) && !(p.expires_at && p.expires_at < now); }).length === 0 && promos.length === 0 && (
          <div className="sm:col-span-2 xl:col-span-3 rounded-2xl border p-14 text-center" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
            <div className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4" style={{ background: "rgba(131,58,180,0.1)" }}>
              <Percent className="w-7 h-7 opacity-30 text-[#833AB4]" />
            </div>
            <p className="font-bold">{t("Admin.no_promo_codes", lang)}</p>
          </div>
        )}
      </div>

      {/* ── Exhausted promos (collapsible) ── */}
      {promos.some(p => p.max_uses > 0 && p.current_uses >= p.max_uses) && (
        <div className="mt-8">
          <button onClick={() => setShowExhausted(v => !v)}
            className="w-full flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm transition-all hover:opacity-80"
            style={{ background: "rgba(225,48,108,0.06)", border: "1px solid rgba(225,48,108,0.15)" }}>
            <span className="font-bold" style={{ color: "var(--text-secondary)" }}>{t("Admin.exhausted_codes", lang)}</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full font-bold"
              style={{ background: "rgba(225,48,108,0.12)", color: "#E1306C", border: "1px solid rgba(225,48,108,0.2)" }}>
              {promos.filter(p => p.max_uses > 0 && p.current_uses >= p.max_uses).length}
            </span>
            <span className="ms-auto text-xs" style={{ color: "var(--text-secondary)" }}>{showExhausted ? "▲" : "▼"}</span>
          </button>
          {showExhausted && (
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3 mt-3">
              {promos.filter(p => p.max_uses > 0 && p.current_uses >= p.max_uses).map(p => (
                <PromoCard key={p.promo_id} p={p} lang={lang} onToggle={toggle} onDel={del} usesMap={usesMap} onToggleUses={toggleUses} exhausted />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Expired promos (collapsible) ── */}
      {(() => { const now = new Date().toISOString(); const expired = promos.filter(p => p.expires_at && p.expires_at < now && !(p.max_uses > 0 && p.current_uses >= p.max_uses)); return expired.length > 0 && (
        <div className="mt-4">
          <button onClick={() => setShowExpired(v => !v)}
            className="w-full flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm transition-all hover:opacity-80"
            style={{ background: "rgba(250,204,21,0.06)", border: "1px solid rgba(250,204,21,0.15)" }}>
            <span className="font-bold" style={{ color: "var(--text-secondary)" }}>{t("Admin.expired_codes", lang)}</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full font-bold"
              style={{ background: "rgba(250,204,21,0.12)", color: "#facc15", border: "1px solid rgba(250,204,21,0.2)" }}>
              {expired.length}
            </span>
            <span className="ms-auto text-xs" style={{ color: "var(--text-secondary)" }}>{showExpired ? "▲" : "▼"}</span>
          </button>
          {showExpired && (
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3 mt-3">
              {expired.map(p => (
                <PromoCard key={p.promo_id} p={p} lang={lang} onToggle={toggle} onDel={del} usesMap={usesMap} onToggleUses={toggleUses} expired />
              ))}
            </div>
          )}
        </div>
      ); })()}

      {/* ── GLOBAL BONUS EVENT ──────────────────────────────────────── */}
      <BonusEventCard lang={lang} />
    </div>
  );
}

function PromoCard({ p, lang, onToggle, onDel, usesMap, onToggleUses, exhausted = false, expired = false }) {
  const isFixed    = p.bonus_type === "fixed";
  const usePct     = p.max_uses > 0 ? Math.min(100, Math.round((p.current_uses / p.max_uses) * 100)) : 0;
  const showUses   = Array.isArray(usesMap[p.promo_id]);
  const loadingUses= usesMap[p.promo_id] === "loading";
  const uses       = Array.isArray(usesMap[p.promo_id]) ? usesMap[p.promo_id] : [];
  const [confirmDel, setConfirmDel] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const couponUrl = `${window.location.origin}/coupon/${p.code}`;
  const copyLink = () => {
    navigator.clipboard.writeText(couponUrl).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 1800);
    });
  };

  const accentColor = exhausted ? "#ef4444" : expired ? "#facc15" : p.is_active ? "#22c55e" : "#9B9BB0";
  const statusLabel = exhausted ? t("Admin.exhausted_2", lang)
    : expired   ? t("Admin.expired_2", lang)
    : p.is_active ? t("Admin.active_4", lang)
    : t("Admin.off", lang);

  return (
    <div className="rounded-2xl overflow-hidden" data-testid={`promo-${p.promo_id}`}
      style={{ background: "var(--surface-1)", border: "1px solid var(--border-color)", opacity: exhausted || expired ? 0.85 : 1 }}>

      {/* Accent top stripe */}
      <div className="h-0.5" style={{ background: accentColor, opacity: 0.7 }} />

      <div className="p-4 space-y-3">

        {/* Top row: code + actions */}
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="font-outfit font-black text-xl tracking-widest" style={{ color: accentColor }}>{p.code}</div>
            <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
              <span className="text-xs px-2.5 py-0.5 rounded-full font-bold"
                style={{ background: isFixed ? "rgba(247,119,55,0.12)" : "rgba(139,92,246,0.12)", color: isFixed ? "#F77737" : "#8B5CF6" }}>
                {isFixed ? `+${p.bonus_value} iP` : `+${p.bonus_value}%`}
              </span>
              <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
                style={{ background: `${accentColor}15`, color: accentColor }}>
                {statusLabel}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-1 flex-shrink-0">
            {confirmDel ? (
              <>
                <button onClick={() => onDel(p.promo_id)}
                  className="h-8 px-3 rounded-xl text-xs font-bold"
                  style={{ background: "rgba(239,68,68,0.12)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.25)" }}>
                  {t("Admin.delete", lang)}
                </button>
                <button onClick={() => setConfirmDel(false)}
                  className="w-8 h-8 rounded-xl flex items-center justify-center text-xs"
                  style={{ background: "var(--surface-2)", color: "var(--text-secondary)" }}>✕</button>
              </>
            ) : (
              <>
                <button onClick={copyLink} title={couponUrl}
                  className="w-8 h-8 rounded-xl flex items-center justify-center transition-colors"
                  style={{ background: linkCopied ? "rgba(34,197,94,0.12)" : "rgba(139,92,246,0.1)", color: linkCopied ? "#22c55e" : "#8B5CF6" }}>
                  {linkCopied ? <CheckCircle className="w-3.5 h-3.5" /> : <Link2 className="w-3.5 h-3.5" />}
                </button>
                <button onClick={() => onToggle(p.promo_id)}
                  className="w-8 h-8 rounded-xl flex items-center justify-center transition-colors"
                  style={{ background: p.is_active ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)", color: p.is_active ? "#22c55e" : "#ef4444" }}>
                  <Power className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => setConfirmDel(true)}
                  className="w-8 h-8 rounded-xl flex items-center justify-center transition-colors hover:text-red-400"
                  style={{ color: "var(--text-secondary)" }}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Usage bar */}
        {p.max_uses > 0 && (
          <div>
            <div className="flex justify-between text-[11px] mb-1" style={{ color: "var(--text-secondary)" }}>
              <span>{t("Admin.usage", lang)}</span>
              <span className="font-outfit font-bold">{p.current_uses} / {p.max_uses}</span>
            </div>
            <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "var(--surface-2)" }}>
              <div className="h-full rounded-full transition-all" style={{ width: `${usePct}%`, background: exhausted ? "#ef4444" : usePct > 80 ? "#f59e0b" : "#8B5CF6" }} />
            </div>
          </div>
        )}

        {/* Meta row */}
        <div className="flex items-center gap-3 text-[11px] flex-wrap" style={{ color: "var(--text-secondary)" }}>
          {p.max_uses === 0 && <span>{p.current_uses} {t("Admin.uses_2", lang)}</span>}
          <span>{t("Admin.per_user", lang)}: {p.per_user_limit}</span>
          {(p.min_topup_ils > 0) && (
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full"
              style={{ background: "rgba(247,119,55,0.1)", color: "#F77737" }}>
              min ₪{p.min_topup_ils}
            </span>
          )}
          {p.expires_at && (
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />{formatIsraelDateTime(p.expires_at)}
            </span>
          )}
          {p.allowed_user_ids?.length > 0 && (
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full"
              style={{ background: "rgba(139,92,246,0.1)", color: "#8B5CF6" }}>
              <Shield className="w-2.5 h-2.5" />
              {p.allowed_user_ids.length} {t("Admin.users_2", lang)}
            </span>
          )}
        </div>

        {/* Who used button */}
        {p.current_uses > 0 && (
          <button onClick={() => onToggleUses(p.promo_id)}
            className="flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded-xl font-semibold transition-all w-full justify-center"
            style={{ background: showUses ? "rgba(139,92,246,0.12)" : "var(--surface-2)", color: showUses ? "#8B5CF6" : "var(--text-secondary)", border: `1px solid ${showUses ? "rgba(139,92,246,0.25)" : "var(--border-color)"}` }}>
            <UsersIcon className="w-3.5 h-3.5" />
            {loadingUses ? "…" : showUses ? t("Admin.hide", lang) : t("Admin.who_used", lang)}
          </button>
        )}
      </div>

      {/* Uses list */}
      {(showUses || loadingUses) && (
        <div style={{ borderTop: "1px solid var(--border-color)" }}>
          {loadingUses ? (
            <div className="py-4 flex justify-center">
              <div className="w-4 h-4 border-2 border-[#8B5CF6] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : uses.length === 0 ? (
            <div className="px-4 py-3 text-xs text-center" style={{ color: "var(--text-secondary)" }}>
              {t("Admin.no_uses_recorded", lang)}
            </div>
          ) : (
            <div className="px-4 py-3 space-y-1.5" style={{ background: "var(--surface-1)" }}>
              <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: "var(--text-secondary)" }}>
                {t("Admin.used_by", lang)} ({uses.length})
              </p>
              {uses.map((u, i) => (
                <div key={i} className="flex items-center justify-between gap-2 px-3 py-2 rounded-xl"
                  style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)" }}>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold truncate" style={{ color: "var(--text-primary)" }}>{u.user_name || "—"}</div>
                    <div className="text-[10px] truncate" style={{ color: "var(--text-secondary)" }}>{u.user_email}</div>
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <div className="text-xs font-bold" style={{ color: "#F77737" }}>+{u.bonus} iP</div>
                    <div className="text-[10px]" style={{ color: "var(--text-secondary)" }}>{formatIsraelDate(u.created_at)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ===== ADMIN TICKETS =====
const TICKET_TAGS = ["billing","technical","complaint","general","refund","account","order"];
const TAG_COLORS = { billing:"#7aa2ff", technical:"#4ade80", complaint:"#fb7185", general:"#cbd5e1", refund:"#fbbf24", account:"#c4b5fd", order:"#7dd3fc" };
const PRIORITY_META = {
  low:    { label: { en:"Low",    ru:"Низкий",   he:"נמוך"  }, color:"#22c55e", bg:"rgba(34,197,94,0.12)"  },
  medium: { label: { en:"Medium", ru:"Средний",  he:"בינוני"}, color:"#f59e0b", bg:"rgba(245,158,11,0.12)" },
  high:   { label: { en:"High",   ru:"Высокий",  he:"גבוה"  }, color:"#f97316", bg:"rgba(249,115,22,0.12)" },
  urgent: { label: { en:"Urgent", ru:"Срочный",  he:"דחוף"  }, color:"#E1306C", bg:"rgba(225,48,108,0.12)" },
};
const pLabel = (p, lang) => p ? (PRIORITY_META[p]?.label?.[lang] || PRIORITY_META[p]?.label?.en || p) : "";
const pColor = (p) => PRIORITY_META[p]?.color || "#94a3b8";
const pBg    = (p) => PRIORITY_META[p]?.bg    || "var(--surface-3)";

function slaElapsed(created_at, lang) {
  const ms = Date.now() - new Date(created_at).getTime();
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (h >= 48) return { text: `${Math.floor(h/24)}${t("Admin.d_2", lang)}`, color:"#E1306C" };
  if (h >= 24) return { text: `${h}${t("Admin.h", lang)}`, color:"#f97316" };
  if (h >= 4)  return { text: `${h}${t("Admin.h", lang)} ${m}${t("Admin.m", lang)}`, color:"#f59e0b" };
  if (h >= 1)  return { text: `${h}${t("Admin.h", lang)} ${m}${t("Admin.m", lang)}`, color:"#22c55e" };
  return { text: `${m}${t("Admin.m", lang)}`, color:"#22c55e" };
}

function resolutionTime(created_at, closed_at, lang) {
  if (!closed_at) return null;
  const ms = new Date(closed_at) - new Date(created_at);
  const h = Math.floor(ms / 3600000);
  const d = Math.floor(h / 24);
  const m = Math.floor((ms % 3600000) / 60000);
  if (d > 0) return `${d}${t("Admin.d_2", lang)} ${h % 24}${t("Admin.h", lang)}`;
  if (h > 0) return `${h}${t("Admin.h", lang)} ${m}${t("Admin.m", lang)}`;
  return `${m}${t("Admin.m", lang)}`;
}

const NAV_SHORTCUTS = [
  { key: "dashboard", path: "/dashboard",    icon: "🏠", label: { en: "Dashboard",  ru: "Личный кабинет", he: "לוח בקרה"   } },
  { key: "shop",      path: "/shop",          icon: "🛍️", label: { en: "Shop",       ru: "Магазин",        he: "חנות"       } },
  { key: "topup",     path: "/topup",         icon: "💳", label: { en: "Top Up",     ru: "Пополнение",     he: "טעינה"      } },
  { key: "tasks",     path: "/tasks",          icon: "🎯", label: { en: "Tasks",      ru: "Задания",        he: "משימות"     } },
  { key: "messages",  path: "/messages",      icon: "✉️", label: { en: "Messages",   ru: "Сообщения",      he: "הודעות"     } },
  { key: "support",   path: "/support",       icon: "💬", label: { en: "Support",    ru: "Поддержка",      he: "תמיכה"      } },
];

function renderChatText(text, lang) {
  if (!text || typeof text !== "string") return text;
  const parts = text.split(/(\{[\w]+\})/);
  return parts.map((part, i) => {
    const m = part.match(/^\{([\w]+)\}$/);
    if (m) {
      const s = NAV_SHORTCUTS.find(x => x.key === m[1]);
      if (s) return (
        <a key={i} href={s.path}
          className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold mx-0.5 my-0.5 hover:opacity-80 transition-opacity"
          style={{ background: "rgba(0,200,83,0.15)", color: "#00C853", border: "1px solid rgba(0,200,83,0.3)" }}>
          <span className="leading-none">{s.icon}</span><span>{s.label[lang] || s.label.en}</span>
        </a>
      );
    }
    return part;
  });
}

const SUGGESTED_CANNED = [
  {
    title: "greeting",
    text: "שלום! תודה שפנית לתמיכה של iFollowers. אנחנו כאן לעזור – נחזור אליך בהקדם האפשרי.",
  },
  {
    title: "greeting_en",
    text: "Hello! Thank you for reaching out to iFollowers support. We're here to help and will get back to you as soon as possible.",
  },
  {
    title: "checking",
    text: "תודה על פנייתך! אנחנו בודקים את הנושא כרגע ונעדכן אותך בהקדם.",
  },
  {
    title: "order_processing",
    text: "ההזמנה שלך נמצאת בעיבוד. בדרך כלל לוקח בין 24-72 שעות להתחלת העיבוד. אנו מתנצלים על ההמתנה.",
  },
  {
    title: "order_started",
    text: "שמחים לבשר שההזמנה שלך כבר בעיבוד! העוקבים/לייקים יתחילו להגיע בקרוב. אנא אל תגדיר את החשבון לפרטי בזמן זה.",
  },
  {
    title: "payment_received",
    text: "התשלום שלך התקבל בהצלחה! היתרה עודכנה בחשבונך. תוכל להמשיך להזמין שירותים.",
  },
  {
    title: "payment_issue",
    text: "אנחנו מצטערים לשמוע על בעיה בתשלום. אנא ספק לנו את פרטי העסקה (סכום, תאריך, שיטת תשלום) כדי שנוכל לבדוק.",
  },
  {
    title: "refund_policy",
    text: "מדיניות ההחזר שלנו: החזר כספי אפשרי תוך 7 ימים מרגע ההזמנה אם השירות לא התחיל. לאחר תחילת השירות, נבחן כל מקרה לגופו.",
  },
  {
    title: "account_review",
    text: "בדקנו את חשבונך. הכל נראה תקין מצדנו. האם יש בעיה ספציפית שאתה נתקל בה?",
  },
  {
    title: "instagram_private",
    text: "חשוב: יש לוודא שחשבון האינסטגרם שלך ציבורי (לא פרטי) כדי שנוכל לספק את השירות. שנה את ההגדרות ב: הגדרות → פרטיות חשבון.",
  },
  {
    title: "ask_username",
    text: "כדי שנוכל לסייע, אנא שלח לנו את שם המשתמש המלא שלך באינסטגרם (כולל @).",
  },
  {
    title: "ask_order_id",
    text: "כדי לבדוק את ההזמנה שלך, אנא שלח לנו את מספר ההזמנה (מתחיל ב-ORD-).",
  },
  {
    title: "escalate",
    text: "העברתי את הפנייה שלך לצוות הבכיר שלנו. אחד המנהלים יחזור אליך תוך 2-4 שעות.",
  },
  {
    title: "resolved",
    text: "שמחים שהצלחנו לפתור את הבעיה! אם יש לך שאלות נוספות, אל תהסס לפנות אלינו. תודה שבחרת ב-iFollowers!",
  },
  {
    title: "closing",
    text: "תודה שפנית לתמיכה של iFollowers! אם יש לך שאלות נוספות בעתיד, אנחנו תמיד כאן. שיהיה לך יום נעים! 🌟",
  },
];

function AdminTicketsTab({ lang, initialTicketId, onClearDeepLink, onBack, onNavigate, fullscreen, onToggleFullscreen }) {
  const { user: currentAdmin } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [filter, setFilter] = useState("__active");
  const [selectedId, setSelectedId] = useState("");
  const [selectedFull, setSelectedFull] = useState(null); // full ticket with messages
  const [replyText, setReplyText] = useState({});
  const [replyAtts, setReplyAtts] = useState({});
  const [uploading, setUploading] = useState("");
  const [sending, setSending] = useState("");
  const [closing, setClosing] = useState("");
  const [reopening, setReopening] = useState("");
  const [noRespModal, setNoRespModal] = useState(null);
  const [aiSuggesting, setAiSuggesting] = useState("");
  const [aiModal, setAiModal] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState("");
  const [statsPopup, setStatsPopup] = useState(""); // ticket_id of open popup
  const [aiLang, setAiLang] = useState("auto");
  const [aiMode, setAiMode] = useState("auto");
  const [aiPrompt, setAiPrompt] = useState("");
  const [selectedUserDetails, setSelectedUserDetails] = useState(null);
  const [userDetailsLoading, setUserDetailsLoading] = useState(false);
  const [selectedUserSurvey, setSelectedUserSurvey] = useState(null);
  const [userBlockBusy, setUserBlockBusy] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [blockModal, setBlockModal] = useState(null); // { uid, blocked }
  const [blockReason, setBlockReason] = useState("");
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const msgRef = useRef(null);
  const [presence, setPresence] = useState({});
  // user-side presence: { [ticket_id]: { mode, name, expires } }
  const [userPresence, setUserPresence] = useState({});
  // user last read at: { [ticket_id]: iso_string }
  const [userSeenAt, setUserSeenAt] = useState({});
  const typingTimerRef = useRef(null);
  const [noteMode, setNoteMode] = useState(false);
  const [ticketSearch, setTicketSearch] = useState("");
  // Canned responses
  const [cannedResponses, setCannedResponses] = useState([]);
  const [cannedSettings, setCannedSettings] = useState(false); // settings panel open
  const [refreshing, setRefreshing] = useState(false);
  const [cannedSearch, setCannedSearch] = useState("");
  const [cannedDropdown, setCannedDropdown] = useState(false);
  const [cannedSelectedIdx, setCannedSelectedIdx] = useState(0);
  const [newCanned, setNewCanned] = useState({ title: "", text: "" });
  const [cannedSaving, setCannedSaving] = useState(false);
  const cannedTriggerPosRef = useRef(-1); // position of '#' in textarea value
  // Tags/priority/assignment editing
  const [tagDropdown, setTagDropdown] = useState(false);
  const [priorityDropdown, setPriorityDropdown] = useState(false);
  const [assignDropdown, setAssignDropdown] = useState(false);
  const [editingSubject, setEditingSubject] = useState(false);
  const [subjectDraft, setSubjectDraft] = useState("");
  // Nav shortcut picker
  const [navDropdown, setNavDropdown] = useState(false);
  const replyTextareaRef = useRef(null);
  // CRM stats + admin list
  const [ticketStats, setTicketStats] = useState(null);
  const [adminList, setAdminList] = useState([]);
  // Snooze
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [snoozeCustom, setSnoozeCustom] = useState("");

  const fetchCanned = useCallback(async () => {
    try {
      const r = await fetch(`${API}/admin/tickets/canned-responses`, { credentials: "include" });
      if (r.ok) setCannedResponses(await r.json());
    } catch {}
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const r = await fetch(`${API}/admin/tickets/stats`, { credentials: "include" });
      if (r.ok) setTicketStats(await r.json());
    } catch {}
  }, []);

  const fetchSelectedFull = useCallback(async (id) => {
    if (!id) { setSelectedFull(null); return; }
    try {
      const r = await fetch(`${API}/admin/tickets/${id}`, { credentials: "include" });
      if (r.ok) setSelectedFull(await r.json());
    } catch {}
  }, []);

  useEffect(() => {
    fetchCanned();
    fetch(`${API}/admin/users/admins`, { credentials: "include" })
      .then(r => r.ok ? r.json() : []).then(setAdminList).catch(() => {});
  }, [fetchCanned]);

  // Poll presence every 2s
  useEffect(() => {
    const poll = async () => {
      try {
        const r = await fetch(`${API}/admin/tickets/presence`, { credentials: "include" });
        if (r.ok) setPresence(await r.json());
      } catch {}
    };
    poll();
    const iv = setInterval(poll, 2000);
    return () => clearInterval(iv);
  }, []);

  // Ping "viewing" when selected ticket changes
  useEffect(() => {
    if (!selectedId) return;
    const ping = () => fetch(`${API}/admin/tickets/${selectedId}/presence`, {
      method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ mode: "viewing" }),
    }).catch(() => {});
    ping();
    const iv = setInterval(ping, 15000);
    return () => clearInterval(iv);
  }, [selectedId]);

  const pingTyping = useCallback((ticketId) => {
    fetch(`${API}/admin/tickets/${ticketId}/presence`, {
      method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ mode: "typing" }),
    }).catch(() => {});
  }, []);

  const fetch_ = useCallback(async () => {
    // __filters are client-side — always load all tickets
    const serverStatus = !filter.startsWith("__") && filter ? filter : "";
    const url = serverStatus ? `${API}/admin/tickets?status=${serverStatus}` : `${API}/admin/tickets`;
    const r = await fetch(url, { credentials: "include" });
    if (r.ok) {
      const data = await r.json();
      setTickets(data);
      setSelectedId((cur) => data.some((t) => t.ticket_id === cur) ? cur : (data[0]?.ticket_id || ""));
    }
  }, [filter]);

  const fetchUserDetails = useCallback(async (userId) => {
    if (!userId) {
      setSelectedUserDetails(null);
      setSelectedUserSurvey(null);
      return;
    }
    setUserDetailsLoading(true);
    try {
      const [r, sr] = await Promise.all([
        fetch(`${API}/admin/users/${userId}/details`, { credentials: "include" }),
        fetch(`${API}/admin/users/${userId}/survey`, { credentials: "include" }),
      ]);
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Failed");
      setSelectedUserDetails(data);
      if (sr.ok) setSelectedUserSurvey(await sr.json());
      else setSelectedUserSurvey(null);
    } catch (e) {
      toast.error(e.message);
      setSelectedUserDetails(null);
      setSelectedUserSurvey(null);
    } finally {
      setUserDetailsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetch_(); fetchStats();
    const iv = setInterval(() => { fetch_(); fetchStats(); }, 5000);
    return () => clearInterval(iv);
  }, [fetch_, fetchStats]);

  // Fetch full ticket when selected changes
  useEffect(() => { fetchSelectedFull(selectedId); }, [selectedId, fetchSelectedFull]);

  // Instant update via WS event from Navbar
  useEffect(() => {
    const handler = (e) => {
      const msg = e.detail;
      if (msg?.type === "ticket_update") {
        fetch_(); fetchStats();
        if (msg.ticket_id === selectedId) fetchSelectedFull(msg.ticket_id);
      }
      if (msg?.type === "ticket_seen") {
        setUserSeenAt(prev => ({ ...prev, [msg.ticket_id]: msg.user_last_read_at }));
      }
      if (msg?.type === "ticket_user_presence") {
        const expires = Date.now() + (msg.mode === "typing" ? 6000 : 30000);
        setUserPresence(prev => ({
          ...prev,
          [msg.ticket_id]: { mode: msg.mode, name: msg.name, expires },
        }));
      }
    };
    window.addEventListener("ifollowers_ws", handler);
    return () => window.removeEventListener("ifollowers_ws", handler);
  }, [fetch_, fetchStats, selectedId, fetchSelectedFull]);

  // Clean expired user-presence entries every 3s
  useEffect(() => {
    const iv = setInterval(() => {
      const now = Date.now();
      setUserPresence(prev => {
        const next = {};
        for (const [tid, p] of Object.entries(prev)) {
          if (p.expires > now) next[tid] = p;
        }
        return next;
      });
    }, 3000);
    return () => clearInterval(iv);
  }, []);

  // Merge list metadata with full ticket data (messages come from selectedFull)
  const selectedMeta = tickets.find((t) => t.ticket_id === selectedId) || null;
  const selected = selectedMeta ? { ...selectedMeta, ...(selectedFull?.ticket_id === selectedId ? selectedFull : {}) } : (selectedFull || null);

  useEffect(() => {
    if (msgRef.current) msgRef.current.scrollTop = msgRef.current.scrollHeight;
  }, [selectedId, selected?.messages?.length]);

  useEffect(() => {
    if (selected?.user_id) fetchUserDetails(selected.user_id);
    else setSelectedUserDetails(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.user_id, selectedId, fetchUserDetails]);

  const selectTicket = async (ticket) => {
    setSelectedId(ticket.ticket_id);
    if ((ticket.admin_unread_count || 0) > 0) {
      await fetch(`${API}/admin/tickets/${ticket.ticket_id}/read`, { method: "PUT", credentials: "include" });
      setTickets((c) => c.map((t) => t.ticket_id === ticket.ticket_id ? { ...t, admin_unread_count: 0 } : t));
    }
  };

  // Navigate to a specific ticket when coming from user detail panel
  useEffect(() => {
    if (!initialTicketId || tickets.length === 0) return;
    const ticket = tickets.find((t) => t.ticket_id === initialTicketId);
    if (ticket) {
      selectTicket(ticket);
      onClearDeepLink?.();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTicketId, tickets]);

  const uploadFiles = async (id, files) => {
    const arr = Array.from(files || []);
    if (!arr.length) return;
    if ((replyAtts[id] || []).length + arr.length > 6) {
      toast.error(t("Admin.max_6_images", lang));
      return;
    }
    setUploading(id);
    try {
      const uploaded = [];
      for (const f of arr) {
        const compressed = await compressImage(f);
        const fd = new FormData();
        fd.append("file", compressed);
        const r = await fetch(`${API}/upload`, { method: "POST", body: fd, credentials: "include" });
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail);
        uploaded.push({ url: d.url, filename: d.filename });
      }
      setReplyAtts((c) => ({ ...c, [id]: [...(c[id] || []), ...uploaded] }));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUploading("");
    }
  };

  const reply = async (id) => {
    const msg = (replyText[id] || "").trim();
    const atts = replyAtts[id] || [];
    if (!msg && !atts.length) return;
    setSending(id);
    try {
      const st = tickets.find((t) => t.ticket_id === id);
      const r = await fetch(`${API}/admin/tickets/${id}/reply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: msg, status: st?.status === "open" ? "in_progress" : st?.status, attachments: atts }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail);
      setReplyText((c) => ({ ...c, [id]: "" }));
      setReplyAtts((c) => ({ ...c, [id]: [] }));
      fetch_(); fetchSelectedFull(id);
      if (selected?.user_id) fetchUserDetails(selected.user_id);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSending("");
    }
  };

  const sendNote = async (id) => {
    const msg = (replyText[id] || "").trim();
    if (!msg) return;
    setSending(id);
    try {
      const r = await fetch(`${API}/admin/tickets/${id}/note`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ message: msg }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail);
      setReplyText((c) => ({ ...c, [id]: "" }));
      fetch_(); fetchSelectedFull(id);
    } catch (e) { toast.error(e.message); }
    finally { setSending(""); }
  };

  const close = async (id) => {
    setClosing(id);
    try {
      const r = await fetch(`${API}/admin/tickets/${id}/close`, { method: "PUT", credentials: "include" });
      if (!r.ok) throw new Error("Failed");
      toast.success(t("Admin.conversation_closed_2", lang));
      fetch_(); fetchSelectedFull(id); fetchStats();
      if (selected?.user_id) fetchUserDetails(selected.user_id);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setClosing("");
    }
  };

  const NO_RESP_MESSAGES = {
    en: "This ticket has been closed due to lack of response. If you need further help, please open a new chat.",
    ru: "Этот чат закрыт в связи с отсутствием ответа. Если вам нужна помощь — откройте новый чат.",
    he: "הכרטיס נסגר עקב חוסר מענה. אם תצטרך עזרה נוספת, ניתן לפתוח צ'אט חדש.",
  };

  const closeNoResponse = (id) => setNoRespModal(id);

  const confirmNoResponse = async (id, msgLang) => {
    const msgText = NO_RESP_MESSAGES[msgLang] || NO_RESP_MESSAGES.en;
    setNoRespModal(null);
    setClosing(id);
    try {
      const r1 = await fetch(`${API}/admin/tickets/${id}/reply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: msgText, status: "in_progress", attachments: [] }),
      });
      if (!r1.ok) throw new Error((await r1.json()).detail || "Failed");
      const r2 = await fetch(`${API}/admin/tickets/${id}/close`, { method: "PUT", credentials: "include" });
      if (!r2.ok) throw new Error("Failed to close");
      toast.success(t("Admin.closed_no_response", lang));
      fetch_(); fetchSelectedFull(id); fetchStats();
      if (selected?.user_id) fetchUserDetails(selected.user_id);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setClosing("");
    }
  };

  const aiSuggest = async (id, mode = "auto", language = "auto", prompt = "") => {
    setAiSuggesting(id);
    setAiModal(false);
    try {
      const r = await fetch(`${API}/admin/tickets/${id}/ai-suggest`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode, language, prompt }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "AI failed");
      setReplyText((c) => ({ ...c, [id]: d.suggestion }));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setAiSuggesting("");
    }
  };

  const reopen = async (id) => {
    setReopening(id);
    try {
      const r = await fetch(`${API}/admin/tickets/${id}/reopen`, { method: "PUT", credentials: "include" });
      if (!r.ok) throw new Error("Failed");
      toast.success(t("Admin.ticket_reopened", lang));
      fetch_(); fetchStats(); fetchSelectedFull(id);
    } catch (e) { toast.error(e.message); }
    finally { setReopening(""); }
  };

  const snoozeTicket = async (minutes) => {
    const until = new Date(Date.now() + minutes * 60 * 1000).toISOString();
    await fetch(`${API}/admin/tickets/${selectedId}/snooze`, {
      method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify({ until }),
    });
    setSnoozeOpen(false);
    fetch_(); fetchSelectedFull(selectedId);
  };

  const snoozeUntilTomorrow = async () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(9, 0, 0, 0);
    await fetch(`${API}/admin/tickets/${selectedId}/snooze`, {
      method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
      body: JSON.stringify({ until: tomorrow.toISOString() }),
    });
    setSnoozeOpen(false);
    fetch_(); fetchSelectedFull(selectedId);
  };

  const unsnoozeTicket = async () => {
    await fetch(`${API}/admin/tickets/${selectedId}/unsnooze`, {
      method: "PUT", credentials: "include",
    });
    fetch_(); fetchSelectedFull(selectedId);
  };

  const assign = async (id, adminUserId) => {
    await fetch(`${API}/admin/tickets/${id}/assign`, {
      method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: adminUserId || null }),
    });
    setAssignDropdown(false);
    fetch_(); fetchSelectedFull(id);
  };

  const patchMeta = async (id, patch) => {
    await fetch(`${API}/admin/tickets/${id}/meta`, {
      method: "PATCH", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    });
    fetch_(); fetchSelectedFull(id);
  };

  const toggleTag = (id, tag, currentTags) => {
    const tags = (currentTags || []).includes(tag)
      ? (currentTags || []).filter(t => t !== tag)
      : [...(currentTags || []), tag];
    patchMeta(id, { tags });
  };

  const saveCanned = async () => {
    if (!newCanned.title.trim() || !newCanned.text.trim()) return;
    setCannedSaving(true);
    try {
      const r = await fetch(`${API}/admin/tickets/canned-responses`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newCanned),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail);
      setNewCanned({ title: "", text: "" });
      fetchCanned();
    } catch (e) { toast.error(e.message); } finally { setCannedSaving(false); }
  };

  const deleteCanned = async (crId) => {
    await fetch(`${API}/admin/tickets/canned-responses/${crId}`, { method: "DELETE", credentials: "include" });
    fetchCanned();
  };

  const insertCanned = (id, text, currentVal) => {
    const triggerPos = cannedTriggerPosRef.current;
    const val = currentVal || "";
    // Remove everything from '#' up to the current cursor word end
    const before = triggerPos >= 0 ? val.slice(0, triggerPos).trimEnd() : val.trimEnd();
    const newText = before ? before + "\n" + text : text;
    setReplyText(c => ({ ...c, [id]: newText }));
    setCannedDropdown(false);
    setCannedSearch("");
    setCannedSelectedIdx(0);
    cannedTriggerPosRef.current = -1;
  };

  const insertNavShortcut = (id, key) => {
    const ta = replyTextareaRef.current;
    const current = replyText[id] || "";
    const token = `{${key}}`;
    let newText, newCursor;
    if (ta) {
      const start = ta.selectionStart ?? current.length;
      const needSpace = start > 0 && current[start - 1] !== " " && current[start - 1] !== "\n";
      const prefix = needSpace ? " " : "";
      newText = current.slice(0, start) + prefix + token + current.slice(start);
      newCursor = start + prefix.length + token.length;
    } else {
      const space = current && !current.endsWith(" ") ? " " : "";
      newText = current + space + token;
      newCursor = newText.length;
    }
    setReplyText(c => ({ ...c, [id]: newText }));
    setNavDropdown(false);
    requestAnimationFrame(() => {
      if (ta) { ta.selectionStart = ta.selectionEnd = newCursor; ta.focus(); }
    });
  };

  const handleReplyChange = (e, id) => {
    const val = e.target.value;
    setReplyText(c => ({ ...c, [id]: val }));
    e.target.style.height = "auto";
    e.target.style.height = `${Math.min(e.target.scrollHeight, 220)}px`;
    if (!noteMode) {
      clearTimeout(typingTimerRef.current);
      typingTimerRef.current = setTimeout(() => pingTyping(id), 400);
    }
    // # trigger: detect "#word" at cursor position
    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = val.slice(0, cursorPos);
    const hashWordMatch = textBeforeCursor.match(/(^|\s)(#(\S*))$/);
    if (hashWordMatch && !noteMode) {
      // Store absolute position of the '#' character
      cannedTriggerPosRef.current = textBeforeCursor.lastIndexOf("#");
      setCannedDropdown(true);
      setCannedSearch(hashWordMatch[3] || "");
      setCannedSelectedIdx(0);
    } else {
      cannedTriggerPosRef.current = -1;
      setCannedDropdown(false);
      setCannedSearch("");
    }
  };

  const toggleSelectedUserBlock = async (blocked) => {
    if (!selected?.user_id || !selectedUserDetails) return;
    let reason = "";
    if (blocked) {
      const prompted = window.prompt(
        t("Admin.blocking_reason_for_the_user", lang),
        selectedUserDetails.user?.blocked_reason || "",
      );
      if (prompted === null) return;
      reason = prompted;
    }
    setUserBlockBusy(true);
    try {
      const r = await fetch(`${API}/admin/users/${selected.user_id}/block`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ blocked, reason }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Failed");
      toast.success(data.message);
      setSelectedUserDetails(data.details);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUserBlockBusy(false);
    }
  };

  const SC = {
    open: { color: "#facc15", bg: "rgba(250,204,21,0.12)" },
    in_progress: { color: "#60a5fa", bg: "rgba(59,130,246,0.12)" },
    closed: { color: "#22c55e", bg: "rgba(34,197,94,0.12)" },
    snoozed: { color: "#8b5cf6", bg: "rgba(139,92,246,0.12)" },
  };
  const scOf = (s) => SC[s] || SC.open;
  const ORDER_STATUS_META = {
    approved:        { color: "#7aa2ff", bg: "rgba(64,93,230,0.14)",   border: "rgba(122,162,255,0.25)" },
    processing:      { color: "#F77737", bg: "rgba(247,119,55,0.14)",  border: "rgba(247,119,55,0.25)" },
    completed:       { color: "#22c55e", bg: "rgba(34,197,94,0.14)",   border: "rgba(34,197,94,0.25)" },
    rejected:        { color: "#E1306C", bg: "rgba(225,48,108,0.14)",  border: "rgba(225,48,108,0.25)" },
    cancelled_admin: { color: "#9ca3af", bg: "rgba(156,163,175,0.08)", border: "rgba(156,163,175,0.18)" },
  };
  const getSC = (status) => ORDER_STATUS_META[status] || { color: "#facc15", bg: "rgba(250,204,21,0.14)", border: "rgba(250,204,21,0.25)" };
  const stLabel = (s) => s === "open" ? t("Admin.open", lang) : s === "in_progress" ? t("Admin.in_progress_5", lang) : s === "snoozed" ? t("Admin.snoozed_2", lang) : t("Admin.closed", lang);
  const orderStatusLabel = (status) => ({
    draft: t("Admin.draft", lang),
    approved: t("Admin.sent", lang),
    processing: t("Admin.processing", lang),
    completed: t("Admin.completed", lang),
    rejected: t("Admin.rejected", lang),
    cancelled_admin: t("Admin.cancelled", lang),
  }[status] || status || "—");
  const roleBadge = (userDetails) => {
    const role = userDetails?.user?.role;
    if (role === "super_admin") return { text: "⭐ Super Admin", color: "#F77737", bg: "rgba(247,119,55,0.16)", border: "1px solid rgba(247,119,55,0.3)" };
    if (role === "admin") return { text: "🛡 Admin", color: "#c084fc", bg: "rgba(131,58,180,0.16)", border: "1px solid rgba(192,132,252,0.25)" };
    return { text: "user", color: "var(--text-secondary)", bg: "var(--surface-2)" };
  };

  const relT = (iso) => {
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60000);
    const h = Math.floor(diff / 3600000);
    const d = Math.floor(diff / 86400000);
    if (m < 2) return t("Admin.now", lang);
    if (m < 60) return `${m}${t("Admin.m", lang)}`;
    if (h < 24) return `${h}${t("Admin.h", lang)}`;
    if (d < 7) return `${d}${t("Admin.d_2", lang)}`;
    return formatIsraelDate(iso);
  };

  const FILTER_DEFS = [
    { key: "",         label: t("shop.all", lang),                  count: tickets.length },
    { key: "__new",    label: t("Admin.new", lang),      count: tickets.filter(t=>t.status==="open").length || null },
    { key: "__active", label: t("Admin.active_5", lang),        count: tickets.filter(t=>t.status!=="closed").length || null },
    { key: "closed",   label: t("Admin.closed_2", lang),        count: null },
    { key: "__urgent", label: t("Admin.urgent_3", lang),  count: tickets.filter(t=>t.priority==="urgent"&&t.status!=="closed").length || null },
    { key: "__unread", label: t("Admin.unread_2", lang),    count: ticketStats?.total_unread || null },
    { key: "__mine",   label: t("Admin.mine", lang),                  count: tickets.filter(t=>t.assigned_to?.user_id===currentAdmin?.user_id).length || null },
    { key: "snoozed",  label: t("Admin.snoozed_3", lang), count: tickets.filter(t => t.status === "snoozed").length || null },
  ];
  const clientFilterFn = (t) => {
    if (filter === "__new")    return t.status === "open";
    if (filter === "__active") return t.status !== "closed";
    if (filter === "__urgent") return t.priority === "urgent" && t.status !== "closed";
    if (filter === "__unread") return (t.admin_unread_count || 0) > 0;
    if (filter === "__mine")   return t.assigned_to?.user_id === currentAdmin?.user_id;
    return true;
  };
  const visibleTickets = tickets
    .filter(t => !ticketSearch || (t.user_name||t.user_email||"").toLowerCase().includes(ticketSearch.toLowerCase()) || (t.subject||"").toLowerCase().includes(ticketSearch.toLowerCase()) || (t.assigned_to?.name||"").toLowerCase().includes(ticketSearch.toLowerCase()))
    .filter(clientFilterFn);

  // Canned dropdown matches — sorted: title-startsWith first, then title-contains, then text-contains
  const cannedMatches = (() => {
    if (!cannedDropdown) return [];
    const q = cannedSearch.toLowerCase().trim();
    if (!q) return cannedResponses.slice(0, 8);
    return cannedResponses
      .filter(cr => cr.title.toLowerCase().includes(q) || cr.text.toLowerCase().includes(q))
      .sort((a, b) => {
        const aS = a.title.toLowerCase().startsWith(q) ? 0 : 1;
        const bS = b.title.toLowerCase().startsWith(q) ? 0 : 1;
        return aS - bS;
      })
      .slice(0, 8);
  })();

  const totalUnread = tickets.reduce((s, t) => s + (t.admin_unread_count || 0), 0);
  const selectedRoleBadge = roleBadge(selectedUserDetails);
  const openProfileModal = async () => {
    if (!selected?.user_id) return;
    if (!selectedUserDetails) {
      await fetchUserDetails(selected.user_id);
    }
    setProfileOpen(true);
  };

  useEffect(() => {
    if (!selectedId) setSidePanelOpen(false);
  }, [selectedId]);

  const selectedMessages = selected?.messages || [];
  const lastVisibleAdminMessage = selectedMessages.filter((message) => message.sender_type === "admin" && !message.deleted).slice(-1)[0] || null;
  const selectedReplyValue = selected ? (replyText[selected.ticket_id] || "") : "";
  const selectedReplyAttachments = selected ? (replyAtts[selected.ticket_id] || []) : [];
  const selectedSla = selected && selected.status !== "closed" ? slaElapsed(selected.created_at, lang) : null;
  const selectedThreadPresence = selected ? (presence[selected.ticket_id] || []) : [];
  const selectedConversationPresence = selected ? userPresence[selected.ticket_id] : null;
  const selectedAdminTypers = selectedThreadPresence.filter((entry) => entry.mode === "typing");
  const selectedAdminViewers = selectedThreadPresence.filter((entry) => entry.mode === "viewing");
  const openQueueCount = tickets.filter((ticket) => ticket.status !== "closed").length;
  const myQueueCount = tickets.filter((ticket) => ticket.assigned_to?.user_id === currentAdmin?.user_id && ticket.status !== "closed").length;
  const snoozedCount = tickets.filter((ticket) => ticket.status === "snoozed").length;
  const inboxSummary = [
    { label: t("Admin.open_2", lang), value: ticketStats?.total_open ?? openQueueCount, color: "#405DE6", icon: Ticket },
    { label: t("Admin.urgent", lang), value: ticketStats?.urgent ?? tickets.filter((ticket) => ticket.priority === "urgent" && ticket.status !== "closed").length, color: "#E1306C", icon: AlertTriangle },
    { label: t("Admin.unread", lang), value: ticketStats?.total_unread ?? totalUnread, color: "#F77737", icon: Bell },
    { label: t("Admin.mine", lang), value: myQueueCount, color: "#22c55e", icon: User },
  ];
  const openMacrosPanel = () => {
    setCannedSettings(true);
    setSidePanelOpen(true);
  };
  const openCustomerPanel = () => {
    setCannedSettings(false);
    setSidePanelOpen(true);
  };

  const renderMacrosSidebar = () => (
    <div className="flex-1 overflow-y-auto p-3 md:p-4 space-y-3">
      <div className="rounded-2xl border p-4" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4" style={{ color: "#7aa2ff" }} />
          <div className="text-xs font-black uppercase tracking-[0.24em]" style={{ color: "#7aa2ff" }}>
            {t("Admin.macros_library", lang)}
          </div>
        </div>
        <p className="text-[12px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
          {t("Admin.type_while_replying_to_insert_a_saved_answer_ins", lang)}
        </p>
      </div>

      <div className="rounded-2xl border p-4 space-y-3" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.18em]" style={{ color: "var(--text-secondary)" }}>
            {t("Admin.create_macro", lang)}
          </div>
          <p className="text-[11px] mt-1" style={{ color: "var(--text-secondary)" }}>
            {t("Admin.use_this_for_billing_delivery_account_refund_and", lang)}
          </p>
        </div>
        <Input
          value={newCanned.title}
          onChange={(e) => setNewCanned((current) => ({ ...current, title: e.target.value }))}
          placeholder={t("Admin.macro_title", lang)}
          className="rounded-2xl border-white/10"
          style={{ background: "var(--surface-2)" }}
        />
        <Textarea
          value={newCanned.text}
          onChange={(e) => setNewCanned((current) => ({ ...current, text: e.target.value }))}
          placeholder={t("Admin.saved_response_text", lang)}
          className="min-h-[120px] rounded-2xl border-white/10"
          style={{ background: "var(--surface-2)" }}
        />
        <button
          onClick={saveCanned}
          disabled={cannedSaving || !newCanned.title.trim() || !newCanned.text.trim()}
          className="w-full rounded-2xl px-4 py-3 text-sm font-semibold transition-all disabled:opacity-50"
          style={{ background: "#8B5CF6", color: "#fff" }}
        >
          {cannedSaving ? t("Admin.saving", lang) : t("Admin.save_macro", lang)}
        </button>
      </div>

      {SUGGESTED_CANNED.filter((item) => !cannedResponses.some((response) => response.title === item.title)).length > 0 && (
        <div className="rounded-2xl border p-4" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
          <div className="text-xs font-bold uppercase tracking-[0.18em] mb-3" style={{ color: "var(--text-secondary)" }}>
            {t("Admin.suggested", lang)}
          </div>
          <div className="space-y-2">
            {SUGGESTED_CANNED.filter((item) => !cannedResponses.some((response) => response.title === item.title)).slice(0, 4).map((item) => (
              <div key={item.title} className="rounded-2xl border px-3 py-3" style={{ background: "var(--surface-2)", borderColor: "var(--border-color)" }}>
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-[11px] font-black" style={{ color: "#7aa2ff" }}>#{item.title}</div>
                    <div className="text-[11px] mt-1 leading-relaxed" style={{ color: "var(--text-secondary)" }}>{item.text}</div>
                  </div>
                  <button
                    onClick={() => setNewCanned({ title: item.title, text: item.text })}
                    className="flex-shrink-0 rounded-xl px-2.5 py-1.5 text-[10px] font-black"
                    style={{ background: "rgba(122,162,255,0.12)", color: "#7aa2ff" }}
                  >
                    + {t("Admin.use", lang)}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-2xl border p-4" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="text-xs font-bold uppercase tracking-[0.18em]" style={{ color: "var(--text-secondary)" }}>
            {t("Admin.saved_macros", lang)}
          </div>
          <span className="text-[10px] px-2 py-1 rounded-full font-bold" style={{ background: "rgba(122,162,255,0.12)", color: "#7aa2ff" }}>
            {cannedResponses.length}
          </span>
        </div>
        <div className="space-y-2">
          {cannedResponses.length === 0 ? (
            <div className="rounded-2xl border px-3 py-6 text-center text-xs" style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-secondary)" }}>
              {t("Admin.no_macros_yet", lang)}
            </div>
          ) : cannedResponses.map((response) => (
            <div key={response.id} className="group rounded-2xl border px-3 py-3" style={{ background: "var(--surface-2)", borderColor: "var(--border-color)" }}>
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-[11px] font-black" style={{ color: "#7aa2ff" }}>#{response.title}</div>
                  <div className="text-[11px] mt-1 leading-relaxed" style={{ color: "var(--text-secondary)" }}>
                    {response.text.slice(0, 180)}{response.text.length > 180 ? "…" : ""}
                  </div>
                </div>
                <button
                  onClick={() => deleteCanned(response.id)}
                  className="opacity-60 group-hover:opacity-100 transition-opacity rounded-xl w-8 h-8 flex items-center justify-center flex-shrink-0"
                  style={{ background: "rgba(225,48,108,0.08)", color: "#E1306C" }}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderCustomerSidebar = () => {
    if (userDetailsLoading) {
      return (
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin h-6 w-6 border-2 border-[#405DE6] border-t-transparent rounded-full" />
        </div>
      );
    }
    if (!selectedUserDetails || !selected) {
      return (
        <div className="flex-1 flex items-center justify-center px-5 text-center">
          <p className="text-xs" style={{ color: "var(--text-secondary)" }}>
            {t("Admin.select_a_conversation_to_load_customer_context", lang)}
          </p>
        </div>
      );
    }

    const recentOrders = selectedUserDetails.recent_orders || [];
    const recentTopups = selectedUserDetails.recent_topups || [];
    const previousTickets = (selectedUserDetails.recent_tickets || []).filter((ticket) => ticket.ticket_id !== selected.ticket_id);
    const promoBonus = selectedUserDetails.promo_stats?.bonus_ipoints || 0;
    const taskApproved = selectedUserDetails.task_stats?.approved || 0;
    const referralCount = selectedUserDetails.referral_stats?.referral_count || 0;
    const conversationHealth = [
      selected.assigned_to ? `${t("Admin.owner", lang)}: ${selected.assigned_to.name}` : t("Admin.unassigned", lang),
      selected.first_admin_reply_at ? `FRT ${resolutionTime(selected.created_at, selected.first_admin_reply_at, lang)}` : t("Admin.no_first_reply_yet", lang),
      selected.closed_at ? `${t("Admin.resolution_2", lang)} ${resolutionTime(selected.created_at, selected.closed_at, lang)}` : selectedSla ? `${t("Admin.sla_clock", lang)} ${selectedSla.text}` : null,
    ].filter(Boolean);

    return (
      <div className="flex-1 overflow-y-auto p-3 md:p-4 space-y-3">
        <div className="rounded-2xl border p-4" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
          <div className="flex items-start gap-3">
            <button type="button" onClick={openProfileModal} className="flex-shrink-0 rounded-xl overflow-hidden transition-opacity hover:opacity-85">
              {selectedUserDetails.user?.picture
                ? <img src={selectedUserDetails.user.picture} alt="" className="w-14 h-14 object-cover rounded-xl" />
                : <div className="w-14 h-14 rounded-xl flex items-center justify-center font-black text-lg" style={{ background: "rgba(225,48,108,0.12)", color: "#E1306C" }}>{(selectedUserDetails.user?.name || selectedUserDetails.user?.email || "?")[0].toUpperCase()}</div>}
            </button>
            <div className="min-w-0 flex-1">
              <button type="button" onClick={openProfileModal} className="text-sm font-black truncate text-start hover:opacity-80 transition-opacity" style={{ color: "var(--text-primary)" }}>
                {selectedUserDetails.user?.name || selectedUserDetails.user?.email}
              </button>
              <div className="text-[11px] mt-1 break-all" style={{ color: "var(--text-secondary)" }}>{selectedUserDetails.user?.email}</div>
              <div className="flex items-center gap-1.5 flex-wrap mt-2">
                <span className="text-[10px] px-2 py-1 rounded-full font-semibold" style={{ background: selectedRoleBadge.bg, color: selectedRoleBadge.color }}>{selectedRoleBadge.text}</span>
                {selectedUserDetails.user?.is_blocked && <span className="text-[10px] px-2 py-1 rounded-full bg-red-500/12 text-red-400">{t("Admin.blocked", lang)}</span>}
                {selected.rating && <span className="text-[10px] px-2 py-1 rounded-full" style={{ background: selected.rating === "up" ? "rgba(34,197,94,0.12)" : "rgba(239,68,68,0.12)", color: selected.rating === "up" ? "#22c55e" : "#f87171" }}>{selected.rating === "up" ? "👍 CSAT" : "👎 CSAT"}</span>}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-4">
            {[
              { label: t("Admin.balance", lang), value: `${(selectedUserDetails.user?.ipoints || 0).toLocaleString()} iP`, color: "#F77737" },
              { label: t("admin.orders", lang), value: selectedUserDetails.order_stats?.total || 0, color: "#E1306C" },
              { label: t("Admin.top_ups", lang), value: `${selectedUserDetails.topup_stats?.approved_amount_ils || 0}₪`, color: "#22c55e" },
              { label: t("Admin.open_tickets", lang), value: selectedUserDetails.ticket_stats?.open || 0, color: "#405DE6" },
            ].map((item) => (
              <div key={item.label} className="rounded-2xl px-3 py-2.5 border" style={{ background: "rgba(255,255,255,0.02)", borderColor: "rgba(255,255,255,0.08)" }}>
                <div className="text-[10px]" style={{ color: "var(--text-secondary)" }}>{item.label}</div>
                <div className="text-sm font-black mt-1" style={{ color: item.color }}>{item.value}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border p-4 space-y-2" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
          <div className="text-xs font-bold uppercase tracking-[0.18em]" style={{ color: "var(--text-secondary)" }}>
            {t("Admin.conversation_snapshot", lang)}
          </div>
          {conversationHealth.map((item) => (
            <div key={item} className="rounded-2xl px-3 py-2 text-[11px]" style={{ background: "var(--surface-2)", color: "var(--text-secondary)" }}>
              {item}
            </div>
          ))}
          {(selected.tags || []).length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {(selected.tags || []).map((tag) => (
                <span key={tag} className="text-[10px] px-2 py-1 rounded-full font-semibold" style={{ background: `${TAG_COLORS[tag] || "#94a3b8"}20`, color: TAG_COLORS[tag] || "#94a3b8" }}>
                  {tag}
                </span>
              ))}
            </div>
          )}
          {(selected.linked_order || selected.linked_topup) && (
            <div className="rounded-2xl border px-3 py-3" style={{ background: "rgba(64,93,230,0.06)", borderColor: "rgba(122,162,255,0.18)" }}>
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] mb-1.5" style={{ color: "#7aa2ff" }}>
                {selected.linked_order ? t("Admin.linked_order_2", lang) : t("Admin.linked_top_up_2", lang)}
              </div>
              <div className="text-[11px]" style={{ color: "var(--text-primary)" }}>
                {selected.linked_order
                  ? `#${selected.linked_order.order_number || selected.linked_order.order_id} · ${selected.linked_order.product_name || "—"}`
                  : `${selected.linked_topup.amount_ils || selected.linked_topup.amount || 0} ₪ · ${selected.linked_topup.status || "—"}`}
              </div>
              {onNavigate && (
                <button onClick={() => onNavigate(selected.linked_order ? "orders" : "topups")} className="mt-2 rounded-xl px-3 py-1.5 text-[10px] font-bold" style={{ background: "rgba(122,162,255,0.12)", color: "#7aa2ff" }}>
                  {t("Admin.open_in_admin", lang)}
                </button>
              )}
            </div>
          )}
        </div>

        <div className="rounded-2xl border p-4" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
          <div className="text-xs font-bold uppercase tracking-[0.18em] mb-3" style={{ color: "var(--text-secondary)" }}>
            {t("Admin.signals", lang)}
          </div>
          <div className="grid grid-cols-2 gap-2">
            {[
              { label: t("Admin.referral_code", lang), value: selectedUserDetails.referral_stats?.referral_code || "—", tone: "#F77737" },
              { label: t("Admin.referrals", lang), value: referralCount, tone: "#7aa2ff" },
              { label: t("Admin.promo_bonus", lang), value: `${promoBonus} iP`, tone: "#a78bfa" },
              { label: t("Admin.tasks_approved", lang), value: taskApproved, tone: "#22c55e" },
            ].map((item) => (
              <div key={item.label} className="rounded-2xl px-3 py-2.5 border" style={{ background: "var(--surface-2)", borderColor: "var(--border-color)" }}>
                <div className="text-[10px]" style={{ color: "var(--text-secondary)" }}>{item.label}</div>
                <div className="text-xs font-black mt-1 truncate" style={{ color: item.tone }}>{item.value}</div>
              </div>
            ))}
          </div>
          <div className="grid gap-1.5 mt-3 text-[11px]" style={{ color: "var(--text-secondary)" }}>
            <div>{t("Admin.joined_2", lang)}: <span style={{ color: "var(--text-primary)" }}>{formatDateTime(selectedUserDetails.user?.created_at)}</span></div>
            <div>{t("Admin.last_login", lang)}: <span style={{ color: "var(--text-primary)" }}>{selectedUserDetails.registration?.last_login_at ? formatDateTime(selectedUserDetails.registration.last_login_at) : "—"}</span></div>
            <div>{t("Admin.auth", lang)}: <span style={{ color: "var(--text-primary)" }}>{selectedUserDetails.registration?.auth_provider || "—"}</span></div>
            {selectedUserDetails.user?.instagram_username && <div style={{ color: "#E1306C" }}>@{selectedUserDetails.user.instagram_username}</div>}
          </div>
        </div>

        <div className="rounded-2xl border p-4 space-y-2" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
          <div className="flex items-center justify-between gap-2">
            <div className="text-xs font-bold uppercase tracking-[0.18em]" style={{ color: "var(--text-secondary)" }}>
              {t("Admin.recent_orders", lang)}
            </div>
            <span className="text-[10px] px-2 py-1 rounded-full font-bold" style={{ background: "rgba(225,48,108,0.1)", color: "#E1306C" }}>{selectedUserDetails.order_stats?.total || 0}</span>
          </div>
          {recentOrders.length === 0 ? (
            <div className="rounded-2xl border px-3 py-4 text-xs text-center" style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-secondary)" }}>
              {t("Admin.no_orders_yet", lang)}
            </div>
          ) : recentOrders.slice(0, 3).map((order) => {
            const sc = getSC(order.status);
            const isOpen = expandedOrderId === order.order_id;
            return (
              <div key={order.order_id} className="rounded-2xl overflow-hidden border" style={{ background: "var(--surface-2)", borderColor: isOpen ? sc.border : "var(--border-color)" }}>
                <button type="button" onClick={() => setExpandedOrderId(isOpen ? null : order.order_id)} className="w-full px-3 py-2.5 text-start flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-[11px] font-black flex-shrink-0" style={{ color: sc.color }}>#{order.order_number || "—"}</span>
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full flex-shrink-0" style={{ background: sc.bg, color: sc.color }}>{orderStatusLabel(order.status)}</span>
                    </div>
                    <div className="text-[10px] mt-1 truncate" style={{ color: "var(--text-secondary)" }}>{formatDateTime(order.created_at)}</div>
                  </div>
                  {isOpen ? <ChevronUp className="w-3.5 h-3.5 opacity-40 flex-shrink-0" /> : <ChevronDown className="w-3.5 h-3.5 opacity-40 flex-shrink-0" />}
                </button>
                {isOpen && (
                  <div className="px-3 pb-3 space-y-1.5 border-t" style={{ borderColor: "var(--border-color)" }}>
                    {(order.items || []).slice(0, 2).map((item, index) => (
                      <div key={index} className="rounded-xl px-2.5 py-2 border" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
                        <div className="text-[11px] font-semibold">{item.product_name?.[lang] || item.product_name?.en || "—"}</div>
                        <div className="text-[10px] mt-1" style={{ color: "var(--text-secondary)" }}>×{item.quantity?.toLocaleString?.() || 0}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="rounded-2xl border p-4 space-y-2" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
          <div className="text-xs font-bold uppercase tracking-[0.18em]" style={{ color: "var(--text-secondary)" }}>
            {t("Admin.top_ups_tickets", lang)}
          </div>
          <div className="space-y-2">
            {recentTopups.slice(0, 3).map((topup) => (
              <div key={topup.topup_id} className="rounded-2xl px-3 py-2.5 border" style={{ background: "var(--surface-2)", borderColor: "var(--border-color)" }}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[11px] font-semibold">{topup.amount_ils || 0} ₪</span>
                  <span className="text-[10px]" style={{ color: topup.status === "approved" ? "#22c55e" : "var(--text-secondary)" }}>{topup.status}</span>
                </div>
                <div className="text-[10px] mt-1" style={{ color: "var(--text-secondary)" }}>{formatDateTime(topup.submitted_at || topup.created_at)}</div>
              </div>
            ))}
            {!recentTopups.length && (
              <div className="rounded-2xl px-3 py-4 border text-xs text-center" style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-secondary)" }}>
                {t("Admin.no_top_ups_yet", lang)}
              </div>
            )}
            {previousTickets.length > 0 && (
              <div className="pt-1 space-y-2">
                {previousTickets.slice(0, 3).map((ticket) => {
                  const statusMeta = SC[ticket.status] || SC.open;
                  return (
                    <button key={ticket.ticket_id} type="button" onClick={() => selectTicket(ticket)} className="w-full rounded-2xl px-3 py-2.5 text-start border transition-colors" style={{ background: "var(--surface-2)", borderColor: selectedId === ticket.ticket_id ? "rgba(122,162,255,0.24)" : "var(--border-color)" }}>
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold flex-shrink-0" style={{ background: statusMeta.bg, color: statusMeta.color }}>{stLabel(ticket.status)}</span>
                        <span className="text-[11px] truncate" style={{ color: "var(--text-primary)" }}>{ticket.subject || ticket.ticket_code || ticket.ticket_id.slice(-8)}</span>
                      </div>
                      <div className="text-[10px] mt-1" style={{ color: "var(--text-secondary)" }}>{relT(ticket.updated_at || ticket.created_at)}</div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2 pt-1">
            <button
              onClick={() => toggleSelectedUserBlock(!selectedUserDetails.user?.is_blocked)}
              disabled={userBlockBusy || !selectedUserDetails.permissions?.can_block}
              className="rounded-2xl px-3 py-2.5 text-[11px] font-semibold disabled:opacity-50"
              style={{ background: selectedUserDetails.user?.is_blocked ? "rgba(34,197,94,0.12)" : "rgba(239,68,68,0.1)", color: selectedUserDetails.user?.is_blocked ? "#22c55e" : "#f87171" }}
            >
              {selectedUserDetails.user?.is_blocked ? t("Admin.unblock_user", lang) : t("Admin.block_user", lang)}
            </button>
            <button onClick={openProfileModal} className="rounded-2xl px-3 py-2.5 text-[11px] font-semibold" style={{ background: "rgba(122,162,255,0.12)", color: "#7aa2ff" }}>
              {t("Admin.full_profile", lang)}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ── CRM adapters ─────────────────────────────────────────────────────────
  const crmCounts = {
    active:      tickets.filter(t => t.status !== "closed").length,
    open:        tickets.filter(t => t.status === "open").length,
    pending:     tickets.filter(t => t.status === "pending").length,
    closed:      tickets.filter(t => t.status === "closed").length,
    snoozed:     tickets.filter(t => t.status === "snoozed").length,
    high_urgent: tickets.filter(t => ["high","urgent"].includes(t.priority) && t.status !== "closed").length,
    unread:      tickets.filter(t => (t.admin_unread_count || 0) > 0).length,
    my_queue:    tickets.filter(t => t.assigned_to?.user_id === currentAdmin?.user_id && t.status !== "closed").length,
  };

  // Map CRM filter keys → iFollowers filter keys
  const CRM_TO_IF_FILTER = {
    active: "__active", open: "__new", pending: "pending",
    closed: "closed", snoozed: "snoozed",
    high_urgent: "__urgent", unread: "__unread", my_queue: "__mine",
  };
  const IF_TO_CRM_FILTER = Object.fromEntries(Object.entries(CRM_TO_IF_FILTER).map(([k,v]) => [v,k]));
  const crmActiveFilter = IF_TO_CRM_FILTER[filter] || "active";

  const handleFilterChange = (crmKey) => {
    setFilter(CRM_TO_IF_FILTER[crmKey] || "__active");
  };

  const handleSendMessage = async (text, isNote, images) => {
    const id = selectedId;
    if (!id) return;
    if (isNote) {
      setSending(id);
      try {
        const r = await fetch(`${API}/admin/tickets/${id}/note`, {
          method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
          body: JSON.stringify({ message: text }),
        });
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail);
        fetch_(); fetchSelectedFull(id);
      } catch (e) { toast.error(e.message); }
      finally { setSending(""); }
    } else {
      setSending(id);
      try {
        const st = tickets.find(t => t.ticket_id === id);
        const r = await fetch(`${API}/admin/tickets/${id}/reply`, {
          method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
          body: JSON.stringify({
            message: text,
            status: st?.status === "open" ? "in_progress" : st?.status,
            attachments: images?.map(url => ({ url })) || [],
          }),
        });
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail);
        // Auto-assign to the first replying admin if ticket is unassigned
        if (!st?.assigned_to && currentAdmin?.user_id) {
          fetch(`${API}/admin/tickets/${id}/assign`, {
            method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
            body: JSON.stringify({ user_id: currentAdmin.user_id }),
          }).catch(() => {});
        }
        fetch_(); fetchSelectedFull(id);
        if (selected?.user_id) fetchUserDetails(selected.user_id);
      } catch (e) { toast.error(e.message); }
      finally { setSending(""); }
    }
  };

  const handleUpdateTicket = async (updates) => {
    const id = selectedId;
    if (!id) return;
    if (updates.status === "closed") { await close(id); return; }
    if (updates.status === "open") { await reopen(id); return; }
    if (updates.snooze_until) {
      await fetch(`${API}/admin/tickets/${id}/snooze`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ until: updates.snooze_until }),
      });
      fetch_(); fetchSelectedFull(id);
      return;
    }
    if ("assign_to_user_id" in updates) {
      try {
        const r = await fetch(`${API}/admin/tickets/${id}/assign`, {
          method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
          body: JSON.stringify({ user_id: updates.assign_to_user_id || null }),
        });
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail || "Assign failed");
        toast.success("Assigned");
        fetch_(); fetchSelectedFull(id);
      } catch (e) { toast.error(e.message); }
      return;
    }
    const patch = {};
    if (updates.status)       patch.status       = updates.status;
    if (updates.priority)     patch.priority     = updates.priority;
    if (updates.tags)         patch.tags         = updates.tags;
    if (updates.subject)      patch.subject      = updates.subject;
    if (Object.keys(patch).length) await patchMeta(id, patch);
  };

  const handleAISuggest = async (language, prompt) => {
    const id = selectedId;
    if (!id) return "";
    try {
      const r = await fetch(`${API}/admin/tickets/${id}/ai-suggest`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode: "auto", language: language || "auto", prompt: prompt || "" }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "AI failed");
      return d.suggestion || "";
    } catch (e) { toast.error(e.message); return ""; }
  };

  const handleDeleteMessage = async (createdAt) => {
    if (!createdAt || !selectedId) return;
    await fetch(`${API}/admin/tickets/${selectedId}/messages`, {
      method: "DELETE", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ created_at: createdAt }),
    });
    fetchSelectedFull(selectedId);
  };

  const handleCreateCanned = async (data) => {
    try {
      const r = await fetch(`${API}/admin/tickets/canned-responses`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title: data.name, text: data.content || data.text, name: data.name }),
      });
      if (r.ok) fetchCanned();
    } catch {}
  };

  const handleBlockUser = (uid, blocked) => {
    if (blocked) {
      setBlockReason(selectedUserDetails?.user?.blocked_reason || "");
      setBlockModal({ uid, blocked });
    } else {
      doBlock(uid, false, "");
    }
  };

  const doBlock = async (uid, blocked, reason) => {
    setBlockModal(null);
    setUserBlockBusy(true);
    try {
      const r = await fetch(`${API}/admin/users/${uid}/block`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ blocked, reason }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Failed");
      toast.success(data.message);
      setSelectedUserDetails(data.details);
    } catch (e) { toast.error(e.message); }
    finally { setUserBlockBusy(false); }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden bg-background">
      {/* Block User Modal */}
      {blockModal && (
        <div className="fixed inset-0 z-[200] bg-black/50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <h3 className="text-base font-bold mb-1 text-destructive">🚫 Block User</h3>
            <p className="text-xs text-muted-foreground mb-4">
              This will prevent the user from accessing the platform. Please provide a reason.
            </p>
            <textarea
              value={blockReason}
              onChange={e => setBlockReason(e.target.value)}
              placeholder="Reason for blocking (e.g. spam, abuse, fraud)..."
              rows={3}
              className="w-full px-3 py-2 text-sm bg-secondary rounded-xl border border-border focus:outline-none focus:ring-1 focus:ring-destructive resize-none mb-4"
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={() => setBlockModal(null)}
                className="flex-1 py-2 rounded-xl text-sm font-medium bg-secondary hover:bg-secondary/80 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => doBlock(blockModal.uid, blockModal.blocked, blockReason.trim())}
                className="flex-1 py-2 rounded-xl text-sm font-bold bg-destructive text-white hover:bg-destructive/90 transition-colors"
              >
                Block User
              </button>
            </div>
          </div>
        </div>
      )}
      {/* CRM Top bar */}
      <div className="flex items-center gap-2 px-3 py-2 shrink-0" style={{ borderBottom: "1px solid var(--border-color)", background: "var(--bg-secondary)" }}>
        {onBack && (
          <button
            onClick={onBack}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors"
            style={{ color: "var(--text-secondary)", background: "var(--surface-2)" }}
            onMouseEnter={e => { e.currentTarget.style.color = "var(--text-primary)"; e.currentTarget.style.background = "var(--surface-3)"; }}
            onMouseLeave={e => { e.currentTarget.style.color = "var(--text-secondary)"; e.currentTarget.style.background = "var(--surface-2)"; }}
          >
            ← Admin
          </button>
        )}
        <span className="text-xs font-semibold" style={{ color: "var(--text-primary)" }}>Support CRM</span>
        <div className="flex-1" />
        <span className="text-[10px] mr-2" style={{ color: "var(--text-secondary)" }}>
          {visibleTickets.length} ticket{visibleTickets.length !== 1 ? "s" : ""}
        </span>
        {onToggleFullscreen && (
          <button
            onClick={onToggleFullscreen}
            title={fullscreen ? "Exit fullscreen" : "Fullscreen (hide navbar)"}
            className="flex items-center justify-center w-6 h-6 rounded-lg text-xs transition-colors"
            style={{ color: "var(--text-secondary)" }}
            onMouseEnter={e => { e.currentTarget.style.color = "var(--text-primary)"; e.currentTarget.style.background = "var(--surface-2)"; }}
            onMouseLeave={e => { e.currentTarget.style.color = "var(--text-secondary)"; e.currentTarget.style.background = "transparent"; }}
          >
            {fullscreen ? "⊡" : "⊞"}
          </button>
        )}
      </div>
      <div className="flex flex-1 overflow-hidden">
        <CRMTicketList
          tickets={visibleTickets}
          counts={crmCounts}
          activeFilter={crmActiveFilter}
          searchQuery={ticketSearch}
          selectedTicketId={selectedId}
          onFilterChange={handleFilterChange}
          onSearchChange={setTicketSearch}
          onTicketSelect={selectTicket}
        />
        <CRMChatPanel
          ticket={selected}
          messages={selectedMessages}
          cannedResponses={cannedResponses}
          loading={sending === selectedId}
          onSendMessage={handleSendMessage}
          onUpdateTicket={handleUpdateTicket}
          onAISuggest={handleAISuggest}
          onDeleteMessage={handleDeleteMessage}
          onCreateCanned={handleCreateCanned}
          onDeleteCanned={deleteCanned}
          agents={adminList}
        />
        <CRMUserProfilePanel
          userDetails={selectedUserDetails}
          ticket={selected}
          onBlockUser={handleBlockUser}
          loading={userDetailsLoading}
          onSelectTicket={selectTicket}
        />
      </div>
    </div>
  );
}

// ===== ORDERS =====
function OrdersTab({lang,onUpdate}){
  const [orders,setOrders]=useState([]);
  const [filter,setFilter]=useState("");
  const [exp,setExp]=useState(null);
  const [busyKey,setBusyKey]=useState("");
  const [newIds,setNewIds]=useState(new Set());
  const [pendingCount,setPendingCount]=useState(0);
  const [liveConnected,setLiveConnected]=useState(false);
  const [searchInput,setSearchInput]=useState("");
  const [search,setSearch]=useState("");
  const [showHist,setShowHist]=useState(new Set());
  const [page,setPage]=useState(1);
  const [manualResolveModal,setManualResolveModal]=useState(null); // order_id or null
  const [totalPages,setTotalPages]=useState(1);
  const [totalOrders,setTotalOrders]=useState(0);
  const PAGE_SIZE=50;

  const fetch_=useCallback(async(pg=page)=>{
    const params=new URLSearchParams({limit:String(PAGE_SIZE),page:String(pg)});
    if(filter) params.set("status",filter);
    if(search.trim()) params.set("q",search.trim());
    const r=await fetch(`${API}/admin/orders?${params}`,{credentials:"include"});
    if(r.ok){const d=await r.json();setOrders(d.orders||[]);setTotalPages(d.pages||1);setTotalOrders(d.total||0);}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[filter,search,page]);
  useEffect(()=>{const t=setTimeout(()=>setSearch(searchInput.trim()),350);return()=>clearTimeout(t);},[searchInput]);
  useEffect(()=>{setPage(1);},[filter,search]);
  useEffect(()=>{fetch_(page);const interval=setInterval(()=>fetch_(page),30000);return()=>clearInterval(interval);},[fetch_,page]);

  // SSE live stream for new orders
  useEffect(()=>{
    let es;
    const connect=()=>{
      es=new EventSource(`${BACKEND}/api/admin/orders/stream`,{withCredentials:true});
      es.onopen=()=>setLiveConnected(true);
      es.onerror=()=>{setLiveConnected(false);es.close();setTimeout(connect,8000);};
      es.onmessage=(e)=>{
        try{
          const data=JSON.parse(e.data);
          if(data.new_orders?.length){
            const ids=new Set(data.new_orders.map(o=>o.order_id));
            setNewIds(prev=>{const n=new Set(prev);ids.forEach(id=>n.add(id));return n;});
            setOrders(prev=>{
              const existingIds=new Set(prev.map(o=>o.order_id));
              const fresh=data.new_orders.filter(o=>!existingIds.has(o.order_id));
              if(!fresh.length) return prev;
              toast.success(L(lang,`${fresh.length} new order${fresh.length>1?"s":""}!`,`${fresh.length} новый заказ!`,`${fresh.length} הזמנה חדשה!`),{duration:5000});
              return [...fresh,...prev];
            });
          }
        }catch{}
      };
      es.addEventListener("stats",(e)=>{
        try{const d=JSON.parse(e.data);if(d.pending!=null)setPendingCount(d.pending);}catch{}
      });
    };
    connect();
    return()=>{es?.close();};
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);
  const syncProvider=async(id)=>{setBusyKey(`sync:${id}`);try{const r=await fetch(`${API}/admin/orders/${id}/sync-provider`,{method:"POST",credentials:"include"});const d=await r.json();if(!r.ok) throw new Error(d.detail||"Failed");toast.success(t("Admin.provider_status_refreshed", lang));fetch_();onUpdate();}catch(err){toast.error(err.message);}finally{setBusyKey("");}};
  const retryProvider=async(id)=>{setBusyKey(`retry:${id}`);try{const r=await fetch(`${API}/admin/orders/${id}/retry-provider`,{method:"POST",credentials:"include"});const d=await r.json();if(!r.ok) throw new Error(d.detail||"Failed");toast.success(t("Admin.order_resent_to_provider", lang));fetch_();onUpdate();}catch(err){toast.error(err.message);}finally{setBusyKey("");}};
  const cancelProvider=async(id)=>{setBusyKey(`cancel:${id}`);try{const r=await fetch(`${API}/admin/orders/${id}/cancel-provider`,{method:"POST",credentials:"include"});const d=await r.json();if(!r.ok) throw new Error(d.detail||"Failed");toast.success(t("Admin.order_cancelled_by_admin", lang));fetch_();onUpdate();}catch(err){toast.error(err.message);}finally{setBusyKey("");}};
  const approveJap=async(id)=>{setBusyKey(`jap:${id}`);try{const r=await fetch(`${API}/admin/orders/${id}/approve-jap`,{method:"POST",credentials:"include"});const d=await r.json();if(!r.ok) throw new Error(d.detail||"Failed");toast.success("✅ Sent to JAP");fetch_();onUpdate();}catch(err){toast.error(err.message);}finally{setBusyKey("");}};
  const manualComplete=async(id)=>{setBusyKey(`mresolve:${id}`);setManualResolveModal(null);try{const r=await fetch(`${API}/admin/orders/${id}/manual-complete`,{method:"POST",credentials:"include"});const d=await r.json();if(!r.ok) throw new Error(d.detail||"Failed");toast.success("✅ Order marked as completed");fetch_();onUpdate();}catch(err){toast.error(err.message);}finally{setBusyKey("");}};
  const manualCancel=async(id)=>{setBusyKey(`mresolve:${id}`);setManualResolveModal(null);try{const r=await fetch(`${API}/admin/orders/${id}/manual-cancel`,{method:"POST",credentials:"include"});const d=await r.json();if(!r.ok) throw new Error(d.detail||"Failed");toast.success("♻️ Order cancelled & iPoints refunded");fetch_();onUpdate();}catch(err){toast.error(err.message);}finally{setBusyKey("");}};

  // JAP ID panel: Set of order IDs with panel open
  const [provIdPanel,setProvIdPanel]=useState(new Set());
  // JAP ID input values: { "orderId:itemIndex": "value" }
  const [provIdValues,setProvIdValues]=useState({});
  const toggleProvIdPanel=(o)=>{
    setProvIdPanel(prev=>{const n=new Set(prev);if(n.has(o.order_id)){n.delete(o.order_id);}else{n.add(o.order_id);// init inputs with current values
      const inits={};(o.provider_orders||[]).forEach((po,i)=>{inits[`${o.order_id}:${i}`]=po.provider_order_id||"";});(o.items||[]).forEach((_,i)=>{if(inits[`${o.order_id}:${i}`]===undefined)inits[`${o.order_id}:${i}`]="";});setProvIdValues(prev2=>({...prev2,...inits}));}return n;});
  };
  const saveProvId=async(orderId,itemIndex)=>{
    const key=`${orderId}:${itemIndex}`;
    const value=(provIdValues[key]||"").trim();
    if(!value){toast.error("Provider ID cannot be empty");return;}
    setBusyKey(`provid:${key}`);
    try{
      const r=await fetch(`${API}/admin/orders/${orderId}/set-provider-id`,{method:"PUT",headers:{"Content-Type":"application/json"},credentials:"include",body:JSON.stringify({item_index:itemIndex,provider_order_id:value})});
      const d=await r.json();
      if(!r.ok) throw new Error(d.detail||"Failed");
      toast.success("✅ JAP ID updated & synced");
      fetch_();onUpdate();
    }catch(err){toast.error(err.message);}finally{setBusyKey("");}
  };

  // Per-order editable item state: { [order_id]: [ {target_url, quantity, provider_service_id}, ... ] }
  const [editMap,setEditMap]=useState({});
  const initEdit=(o)=>{
    if(editMap[o.order_id]) return;
    setEditMap(prev=>({...prev,[o.order_id]:(o.items||[]).map(item=>({
      target_url: item.target_url||item.instagram_link||"",
      quantity: String(item.quantity||""),
      provider_service_id: String(item.provider_service_id||""),
    }))}));
  };
  const setEditField=(orderId,idx,field,val)=>{
    setEditMap(prev=>{
      const arr=[...(prev[orderId]||[])];
      arr[idx]={...arr[idx],[field]:val};
      return {...prev,[orderId]:arr};
    });
  };
  const saveOrderEdit=async(id)=>{
    setBusyKey(`edit:${id}`);
    try{
      const items=(editMap[id]||[]).map((it,i)=>({index:i,...it}));
      const r=await fetch(`${API}/admin/orders/${id}/edit`,{method:"PUT",credentials:"include",headers:{"Content-Type":"application/json"},body:JSON.stringify({items})});
      const d=await r.json();
      if(!r.ok) throw new Error(d.detail||"Failed");
      toast.success("✏️ Order updated");
      fetch_();
    }catch(err){toast.error(err.message);}finally{setBusyKey("");}
  };;
  const providerSyncLabel=(status)=>({completed:t("Admin.provider_completed", lang),submitted:t("Admin.provider_order_created", lang),processing:t("Admin.provider_in_progress", lang),pending:t("Admin.waiting_for_provider_start", lang),pending_review:"⏳ Pending JAP review",partial:t("Admin.partial", lang),error:t("Admin.error", lang),skipped:t("Admin.skipped", lang),rejected:t("order.rejected", lang),cancelled:t("Admin.cancelled_2", lang),cancelled_admin:t("order.cancelled_admin", lang),not_configured:t("Admin.not_configured", lang)}[status]||status||"—");
  const providerDeliveryLabel=(status)=>({pending:t("Admin.waiting", lang),completed:t("order.completed", lang),processing:t("Admin.in_progress_6", lang),partial:t("Admin.partial", lang),cancelled:t("Admin.cancelled", lang),rejected:t("order.rejected", lang),error:t("Admin.error", lang),unknown:t("Admin.unknown", lang)}[status]||status||"—");
  const siteStatusLabel=(status)=>({draft:t("Admin.draft", lang),approved:t("Admin.sent_by_user", lang),processing:t("Admin.in_progress_7", lang),completed:t("Admin.completed", lang),rejected:t("Admin.cancelled_failed", lang),cancelled_admin:t("Admin.cancelled_by_admin", lang)}[status]||status||"—");
  const timelineMeta=(event)=>({user_submitted:{title:t("Admin.order_sent_by_user", lang),tone:{bg:"rgba(64,93,230,0.12)",color:"#7aa2ff"}},provider_order_created:{title:t("Admin.provider_order_created_2", lang),tone:{bg:"rgba(131,58,180,0.12)",color:"#c084fc"}},provider_pending:{title:t("Admin.waiting_for_provider_start_2", lang),tone:{bg:"rgba(250,204,21,0.12)",color:"#facc15"}},provider_processing:{title:t("Admin.provider_is_working", lang),tone:{bg:"rgba(247,119,55,0.12)",color:"#F77737"}},provider_completed:{title:t("Admin.provider_completed_the_order", lang),tone:{bg:"rgba(0,200,83,0.12)",color:"#22c55e"}},provider_cancelled:{title:t("Admin.provider_cancelled_the_order", lang),tone:{bg:"rgba(225,48,108,0.12)",color:"#E1306C"}},provider_rejected:{title:t("Admin.provider_rejected_the_order", lang),tone:{bg:"rgba(225,48,108,0.12)",color:"#E1306C"}},provider_partial:{title:t("Admin.provider_returned_partial_delivery", lang),tone:{bg:"rgba(168,85,247,0.12)",color:"#a855f7"}},provider_error:{title:t("Admin.provider_returned_an_error", lang),tone:{bg:"rgba(239,68,68,0.12)",color:"#ef4444"}},site_processing:{title:t("Admin.site_moved_order_to_processing", lang),tone:{bg:"rgba(247,119,55,0.12)",color:"#F77737"}},site_completed:{title:t("Admin.site_marked_order_as_completed", lang),tone:{bg:"rgba(0,200,83,0.12)",color:"#22c55e"}},site_cancelled:{title:t("Admin.site_marked_order_as_cancelled", lang),tone:{bg:"rgba(225,48,108,0.12)",color:"#E1306C"}}}[event?.code]||{title:event?.code||"—",tone:{bg:"var(--surface-2)",color:"var(--text-secondary)"}})
  const timelineDetails=(event)=>[
    event?.provider_order_id?{label:t("order.provider_id",lang),value:event.provider_order_id}:null,
    event?.provider_service_id?{label:"Svc",value:event.provider_service_id}:null,
    event?.provider_status?{label:t("Admin.provider", lang),value:event.provider_status}:null,
    event?.provider_start_count!==undefined&&event?.provider_start_count!==null&&event?.provider_start_count!==""?{label:t("Admin.start", lang),value:event.provider_start_count}:null,
    event?.provider_remains!==undefined&&event?.provider_remains!==null&&event?.provider_remains!==""?{label:t("Admin.remains", lang),value:event.provider_remains}:null,
    event?.provider_charge!==undefined&&event?.provider_charge!==null&&event?.provider_charge!==""?{label:t("Admin.charge", lang),value:event.provider_charge}:null,
  ].filter(Boolean);
  const renderTimeline=(timeline=[])=>(
    <div className="space-y-3">
      {timeline.map((event,index)=>{
        const meta=timelineMeta(event);
        const details=timelineDetails(event);
        return(
          <div key={event.event_id||`${event.code}-${index}`} className="flex gap-3">
            <div className="flex shrink-0 flex-col items-center pt-1">
              <div
                className="h-3 w-3 rounded-full border"
                style={{
                  background:meta.tone.bg,
                  borderColor:meta.tone.color,
                }}
              />
              {index<timeline.length-1&&<div className="mt-2 w-px flex-1" style={{background:"var(--surface-3)"}} />}
            </div>
            <div className="min-w-0 flex-1 pb-3">
              <div className="rounded-2xl border px-4 py-3.5" style={{background:meta.tone.bg,borderColor:"var(--surface-2)"}}>
                <div className="flex flex-col gap-1 md:flex-row md:items-start md:justify-between">
                  <div className="font-semibold text-sm leading-6" style={{color:meta.tone.color}}>
                    {meta.title}
                  </div>
                  <div className="text-[11px] md:text-xs whitespace-nowrap" style={{color:"var(--text-secondary)"}}>
                    {formatDateTime(event.occurred_at)}
                  </div>
                </div>
                {details.length>0&&(
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px]" style={{color:"var(--text-secondary)"}}>
                    {details.map((detail,detailIndex)=>(
                      <span key={`${event.event_id||event.code}-${detail.label}-${detailIndex}`}>
                        <span style={{color:"var(--text-primary)"}}>{detail.label}:</span>{" "}
                        <span>{detail.value}</span>
                      </span>
                    ))}
                  </div>
                )}
                {event.provider_error_message&&<div className="mt-2 text-xs text-red-400">{event.provider_error_message}</div>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
  const STATUS_META = {
    approved:        { color: "#7aa2ff", bg: "rgba(64,93,230,0.14)",   border: "rgba(122,162,255,0.25)", dot: "#405DE6" },
    processing:      { color: "#F77737", bg: "rgba(247,119,55,0.14)",  border: "rgba(247,119,55,0.25)",  dot: "#F77737" },
    completed:       { color: "#22c55e", bg: "rgba(34,197,94,0.14)",   border: "rgba(34,197,94,0.25)",   dot: "#22c55e" },
    rejected:        { color: "#E1306C", bg: "rgba(225,48,108,0.14)",  border: "rgba(225,48,108,0.25)",  dot: "#E1306C" },
    cancelled_admin: { color: "#9ca3af", bg: "rgba(156,163,175,0.08)", border: "rgba(156,163,175,0.18)", dot: "#6b7280" },
  };
  const getSC = (status) => STATUS_META[status] || { color: "#facc15", bg: "rgba(250,204,21,0.14)", border: "rgba(250,204,21,0.25)", dot: "#facc15" };

  const filteredOrders = orders; // search/filter is server-side

  const getItemMeta = (item) => {
    const cat = (item.category || "").toLowerCase();
    const plat = (item.platform || "").toLowerCase();
    if (plat === "facebook") return { emoji: "📘", color: "#1877F2", bg: "rgba(24,119,242,0.12)", border: "rgba(24,119,242,0.25)" };
    if (plat === "twitter" || plat === "x") return { emoji: "𝕏", color: "#9CA3AF", bg: "rgba(156,163,175,0.12)", border: "rgba(156,163,175,0.25)" };
    if (plat === "tiktok") return { emoji: "🎵", color: "#F8FAFC", bg: "rgba(248,250,252,0.08)", border: "rgba(248,250,252,0.18)" };
    if (plat === "youtube") return { emoji: "▶", color: "#FF3131", bg: "rgba(255,49,49,0.12)", border: "rgba(255,49,49,0.25)" };
    if (cat === "followers") return { emoji: "👥", color: "#A970FF", bg: "rgba(131,58,180,0.12)", border: "rgba(131,58,180,0.22)" };
    if (cat === "likes") return { emoji: "❤️", color: "#E1306C", bg: "rgba(225,48,108,0.12)", border: "rgba(225,48,108,0.22)" };
    if (cat === "views") return { emoji: "👁", color: "#38bdf8", bg: "rgba(56,189,248,0.12)", border: "rgba(56,189,248,0.22)" };
    if (cat === "comments") return { emoji: "💬", color: "#F77737", bg: "rgba(247,119,55,0.12)", border: "rgba(247,119,55,0.22)" };
    if (cat === "story" || cat === "story_views") return { emoji: "⭕", color: "#c084fc", bg: "rgba(192,132,252,0.12)", border: "rgba(192,132,252,0.22)" };
    return { emoji: "📦", color: "#9ca3af", bg: "rgba(156,163,175,0.10)", border: "rgba(156,163,175,0.20)" };
  };

  const orderProvider = (o) => {
    const provs = new Set((o.items || []).map(item => item.provider || "followsale"));
    if (provs.has("jap") && provs.has("followsale")) return "mixed";
    return provs.has("jap") ? "jap" : "followsale";
  };

  const ProviderBadge = ({ prov, size = "xs" }) => {
    const isJap = prov === "jap";
    const isMixed = prov === "mixed";
    return (
      <span className={`text-[${size === "xs" ? "10" : "11"}px] px-1.5 py-0.5 rounded-full font-bold flex-shrink-0`}
        style={isMixed
          ? { background: "rgba(250,204,21,0.12)", color: "#facc15", border: "1px solid rgba(250,204,21,0.25)" }
          : isJap
            ? { background: "rgba(56,189,248,0.12)", color: "#38bdf8", border: "1px solid rgba(56,189,248,0.25)" }
            : { background: "rgba(192,132,252,0.12)", color: "#c084fc", border: "1px solid rgba(192,132,252,0.25)" }}>
        {isMixed ? "MIX" : isJap ? "JAP" : "FS"}
      </span>
    );
  };

  const orderStatusCounts = {
    approved: orders.filter(o=>o.status==="approved").length,
    processing: orders.filter(o=>o.status==="processing").length,
    completed: orders.filter(o=>o.status==="completed").length,
    rejected: orders.filter(o=>o.status==="rejected").length,
  };

  return (
    <>
    {/* ── Manual Resolve Modal — rendered outside animated container so position:fixed works ── */}
    {manualResolveModal && (() => {
      const mo = orders.find(o=>o.order_id===manualResolveModal);
      const ipoints = mo?.total_ipoints || 0;
      return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" style={{ background:"rgba(0,0,0,0.7)" }}
          onClick={()=>setManualResolveModal(null)}>
          <div className="w-full max-w-sm rounded-2xl p-5 flex flex-col gap-4" style={{ background:"var(--surface-2)", border:"1px solid var(--border-color)" }}
            onClick={e=>e.stopPropagation()}>
            <div>
              <div className="text-base font-bold mb-1" style={{ color:"var(--text-primary)" }}>
                {L(lang,"Resolve Order Manually","Завершить заказ вручную","סיום הזמנה ידנית")}
              </div>
              <div className="text-xs" style={{ color:"var(--text-secondary)" }}>
                {L(lang,`Order #${mo?.order_number}`,`Заказ #${mo?.order_number}`,`הזמנה #${mo?.order_number}`)}
              </div>
            </div>
            <button onClick={()=>manualComplete(manualResolveModal)}
              className="h-11 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all hover:opacity-90"
              style={{ background:"linear-gradient(135deg,#22c55e,#16a34a)", color:"#fff", boxShadow:"0 4px 14px rgba(34,197,94,0.35)" }}>
              <CheckCircle className="w-4 h-4"/>
              {L(lang,"Mark as Completed","Завершить заказ","סמן כהושלם")}
            </button>
            <button onClick={()=>manualCancel(manualResolveModal)}
              className="h-11 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all hover:opacity-90"
              style={{ background:"linear-gradient(135deg,#ef4444,#b91c1c)", color:"#fff", boxShadow:"0 4px 14px rgba(239,68,68,0.32)" }}>
              <X className="w-4 h-4"/>
              {L(lang,`Cancel & Refund${ipoints>0?` ${ipoints} iP`:""}`,`Отменить и вернуть${ipoints>0?` ${ipoints} iP`:""}`,`בטל והחזר${ipoints>0?` ${ipoints} iP`:""}`)}
            </button>
            <button onClick={()=>setManualResolveModal(null)}
              className="text-xs hover:opacity-70 transition-opacity"
              style={{ color:"var(--text-secondary)" }}>
              {L(lang,"Cancel","Отмена","ביטול")}
            </button>
          </div>
        </div>
      );
    })()}
    <div className="space-y-4 animate-fade-in-up">

      {/* ── Page header ── */}
      <div className="rounded-2xl p-4 border" style={{ borderColor: "var(--border-color)", background: "var(--surface-1)" }}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-bold" style={{ color: "var(--text-primary)" }}>
                  {t("admin.orders", lang)}
                </h2>
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full tabular-nums" style={{ background: "var(--surface-2)", color: "var(--text-secondary)", border: "1px solid var(--border-color)" }}>
                  {totalOrders}
                </span>
              </div>
              {/* Status mini-strip */}
              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                {[
                  { key: "approved",   label: t("Admin.pending_4", lang),  color: "#7aa2ff",  bg: "rgba(122,162,255,0.12)",  count: orderStatusCounts.approved },
                  { key: "processing", label: t("Admin.active_6", lang),     color: "#F77737",  bg: "rgba(247,119,55,0.12)",   count: orderStatusCounts.processing },
                  { key: "completed",  label: t("Admin.done_2", lang),         color: "#22c55e",  bg: "rgba(34,197,94,0.12)",    count: orderStatusCounts.completed },
                  { key: "rejected",   label: t("Admin.failed", lang),       color: "#E1306C",  bg: "rgba(225,48,108,0.12)",   count: orderStatusCounts.rejected },
                ].filter(s => s.count > 0).map(s => (
                  <button key={s.key} onClick={() => setFilter(s.key)}
                    className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full transition-all"
                    style={{ background: s.bg, color: s.color }}>
                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: s.color }} />
                    {s.count} {s.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Live indicator */}
            <div className="flex items-center gap-1.5 h-8 px-3 rounded-xl text-xs font-semibold"
              style={{ background: liveConnected ? "rgba(34,197,94,0.1)" : "var(--surface-2)", color: liveConnected ? "#22c55e" : "var(--text-secondary)", border: `1px solid ${liveConnected ? "rgba(34,197,94,0.25)" : "var(--border-color)"}` }}>
              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${liveConnected ? "bg-green-500 animate-pulse" : "bg-gray-500"}`} />
              {liveConnected ? "LIVE" : t("Admin.offline", lang)}
            </div>
            <button onClick={fetch_}
              className="h-8 px-3 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-all hover:bg-white/5"
              style={{ background: "var(--surface-2)", color: "var(--text-secondary)", border: "1px solid var(--border-color)" }}>
              <RefreshCw className="w-3.5 h-3.5" />
              {t("Admin.refresh", lang)}
            </button>
          </div>
        </div>
      </div>

      {/* ── Search + Filter bar ── */}
      <div className="flex gap-2 flex-wrap items-center">
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none" style={{ color: "var(--text-secondary)" }} />
          <input value={searchInput} onChange={e => setSearchInput(e.target.value)}
            placeholder={t("Admin.search_order_user_link", lang)}
            className="w-full h-9 rounded-xl text-xs ps-8 outline-none transition-colors"
            style={{ background: "var(--surface-2)", border: `1px solid ${searchInput ? "rgba(139,92,246,0.4)" : "var(--border-color)"}`, color: "var(--text-primary)", paddingInlineEnd: searchInput ? "2rem" : "0.75rem" }}
          />
          {searchInput && (
            <button onClick={()=>{setSearchInput("");setSearch("");}}
              className="absolute end-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full flex items-center justify-center hover:opacity-70 transition-opacity"
              style={{ background: "var(--surface-3)" }}>
              <X className="w-3 h-3" style={{ color: "var(--text-secondary)" }} />
            </button>
          )}
        </div>
        {/* Status filter pills */}
        <div className="flex gap-1 p-1 rounded-xl" style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)" }}>
          {["", "approved", "processing", "completed", "rejected", "cancelled_admin"].map(s => {
            const sm = s ? getSC(s) : null;
            const active = filter === s;
            return (
              <button key={s} onClick={() => setFilter(s)}
                className="h-7 px-3 rounded-lg text-xs font-semibold transition-all"
                style={{
                  background: active ? (sm ? sm.bg : "var(--surface-3)") : "transparent",
                  color: active ? (sm ? sm.color : "var(--text-primary)") : "var(--text-secondary)",
                  border: `1px solid ${active ? (sm ? sm.border : "var(--border-color)") : "transparent"}`,
                }}>
                {s ? siteStatusLabel(s) : t("shop.all", lang)}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Orders list ── */}
      <div className="space-y-2">
        {filteredOrders.map(o => {
          const sc = getSC(o.status);
          const isNew = newIds.has(o.order_id);
          const isExp = exp === o.order_id;
          const hasHist = showHist.has(o.order_id);
          const toggleHist = (e) => {
            e.stopPropagation();
            setShowHist(prev => { const n = new Set(prev); n.has(o.order_id) ? n.delete(o.order_id) : n.add(o.order_id); return n; });
          };

          return (
            <div key={o.order_id} data-testid={`admin-order-${o.order_id}`}
              className="rounded-2xl overflow-hidden transition-all duration-200"
              style={{
                background: isNew ? `rgba(34,197,94,0.05)` : "var(--surface-1)",
                border: `1px solid ${isNew ? "rgba(34,197,94,0.3)" : isExp ? sc.border : "var(--border-color)"}`,
                boxShadow: isExp ? `0 0 0 1px ${sc.border}` : "none",
              }}>

              {/* ── Collapsed row ── */}
              <div className="px-4 py-3 flex items-center gap-3 cursor-pointer select-none transition-colors"
                style={{ background: isExp ? `linear-gradient(90deg,${sc.bg},transparent 60%)` : "transparent" }}
                onMouseEnter={e=>e.currentTarget.style.background=isExp?`linear-gradient(90deg,${sc.bg},transparent 60%)`:`${sc.bg}40`}
                onMouseLeave={e=>e.currentTarget.style.background=isExp?`linear-gradient(90deg,${sc.bg},transparent 60%)`:"transparent"}
                onClick={() => { setExp(isExp ? null : o.order_id); setNewIds(prev=>{const n=new Set(prev);n.delete(o.order_id);return n;}); }}>

                {/* Status accent bar + order number */}
                <div className="flex-shrink-0 flex items-center gap-2.5">
                  <div className="w-1 self-stretch rounded-full flex-shrink-0" style={{ background: sc.dot, minHeight:32 }} />
                  <div className="flex flex-col items-start">
                    <div className="font-outfit font-black text-[15px] leading-none tabular-nums" style={{ color: sc.color }}>#{o.order_number||"—"}</div>
                    {isNew
                      ? <div className="text-[8px] font-black px-1.5 py-0.5 rounded-full mt-0.5 animate-pulse" style={{ background:"rgba(34,197,94,0.2)", color:"#22c55e" }}>NEW</div>
                      : <div className="text-[9px] mt-0.5 tabular-nums" style={{ color:"var(--text-tertiary, var(--text-secondary))" }}>{formatIsraelDateTime(o.created_at)}</div>
                    }
                  </div>
                </div>

                {/* Item pills + user */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap gap-1 items-center">
                    {(o.items||[]).slice(0,3).map((item,i) => {
                      const meta = getItemMeta(item);
                      const link = item.instagram_link||item.target_url||"";
                      const shortLink = link.replace(/^https?:\/\/(www\.)?/,"").replace(/\/$/,"").slice(0,20);
                      return (
                        <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold flex-shrink-0"
                          style={{ background: meta.bg, color: meta.color, border: `1px solid ${meta.border}` }}>
                          <AdminIcon emoji={meta.emoji} className="w-3 h-3" />
                          <span className="font-bold">×{item.quantity?.toLocaleString()}</span>
                          {shortLink && <span className="opacity-50 text-[10px] font-normal">{shortLink}</span>}
                        </span>
                      );
                    })}
                    {(o.items||[]).length > 3 && <span className="text-[11px] font-semibold" style={{ color:"var(--text-secondary)" }}>+{o.items.length-3}</span>}
                    <ProviderBadge prov={orderProvider(o)} />
                  </div>
                  <div className="text-[11px] mt-0.5 truncate font-medium" style={{ color:"var(--text-secondary)" }}>{o.user_name||o.user_email}</div>
                </div>

                {/* Right meta */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {/* iPoints */}
                  <div className="hidden sm:flex flex-col items-end gap-0.5">
                    <span className="text-xs font-bold flex items-center gap-0.5" style={{ color:"#F77737" }}>
                      {o.is_free_gift ? <Gift className="w-3.5 h-3.5" /> : <><Coins className="w-3 h-3" /><span className="tabular-nums">{o.total_ipoints}</span></>}
                    </span>
                    {o.ipoints_refunded > 0 && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold" style={{ background:"rgba(168,85,247,0.12)", color:"#a855f7" }}>↩{o.ipoints_refunded}</span>
                    )}
                  </div>

                  {/* JAP review badge */}
                  {o.needs_admin_review && (
                    <span className="text-[10px] px-2 py-1 rounded-lg font-bold animate-pulse hidden sm:flex items-center gap-1"
                      style={{ background:"rgba(247,119,55,0.15)", color:"#F77737", border:"1px solid rgba(247,119,55,0.3)" }}>
                      🔍 JAP
                    </span>
                  )}

                  {/* Status pill */}
                  <span className="hidden md:flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-lg font-semibold"
                    style={{ background:sc.bg, color:sc.color, border:`1px solid ${sc.border}` }}>
                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background:sc.dot }} />
                    {siteStatusLabel(o.status)}
                  </span>

                  {/* Quick action buttons */}
                  <div className="flex gap-1" onClick={e=>e.stopPropagation()}>
                    {o.needs_admin_review && (
                      <button title="Approve & send to JAP" disabled={busyKey===`jap:${o.order_id}`}
                        onClick={() => approveJap(o.order_id)}
                        className="h-7 px-2.5 rounded-lg flex items-center gap-1 text-[11px] font-bold transition-all hover:opacity-90 active:scale-95"
                        style={{ background:"linear-gradient(135deg,#F77737,#E1306C)", color:"#fff", boxShadow:"0 2px 8px rgba(247,119,55,0.35)" }}>
                        {busyKey===`jap:${o.order_id}` ? <RotateCcw className="w-3 h-3 animate-spin"/> : <span>✓ JAP</span>}
                      </button>
                    )}
                    {o.provider_order_ids?.length > 0 && (
                      <button title={t("Admin.sync", lang)} disabled={busyKey===`sync:${o.order_id}`}
                        onClick={() => syncProvider(o.order_id)}
                        className="w-7 h-7 rounded-lg flex items-center justify-center transition-colors hover:bg-white/8"
                        style={{ color:"#7aa2ff", border:"1px solid var(--border-color)" }}>
                        <RefreshCw className={`w-3 h-3 ${busyKey===`sync:${o.order_id}`?"animate-spin":""}`} />
                      </button>
                    )}
                    {canRetryProvider(o) && (
                      <button title={t("Admin.retry", lang)} disabled={busyKey===`retry:${o.order_id}`}
                        onClick={() => retryProvider(o.order_id)}
                        className="w-7 h-7 rounded-lg flex items-center justify-center transition-colors hover:bg-white/8"
                        style={{ color:"#F77737", border:"1px solid var(--border-color)" }}>
                        <RotateCcw className={`w-3 h-3 ${busyKey===`retry:${o.order_id}`?"animate-spin":""}`} />
                      </button>
                    )}
                  </div>

                  <div className="w-5 flex items-center justify-center flex-shrink-0">
                    {isExp
                      ? <ChevronUp className="w-4 h-4" style={{color:sc.color}} />
                      : <ChevronDown className="w-4 h-4" style={{color:"var(--text-secondary)"}} />}
                  </div>
                </div>
              </div>

              {/* ── Expanded panel ── */}
              {isExp && (
                <div className="border-t" style={{ borderColor:"var(--border-color)" }}>
                  <div className="grid lg:grid-cols-[1fr_220px]">

                    {/* Left: Items */}
                    <div className="p-4 space-y-2" style={{ borderRight:"1px solid var(--border-color)" }}
                      ref={el => { if(el && o.needs_admin_review && !editMap[o.order_id]) initEdit(o); }}>
                      <div className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color:"var(--text-secondary)" }}>
                        {t("Admin.items", lang)} · {o.items?.length||0}
                        {o.needs_admin_review && <span className="ms-2 px-1.5 py-0.5 rounded-md font-bold" style={{background:"rgba(247,119,55,0.15)",color:"#F77737"}}>✏️ editable</span>}
                      </div>
                      {(o.items||[]).map((item,i) => {
                        const ic = getItemMeta(item);
                        const name = item.product_name?.[lang]||item.product_name?.en||"—";
                        const link = item.instagram_link||item.target_url||"";
                        const prov = o.provider_orders?.[i];
                        const provStatusColor = {completed:"#22c55e",processing:"#F77737",partial:"#a855f7",error:"#ef4444",rejected:"#E1306C"}[prov?.provider_status_bucket]||"#9ca3af";
                        const itemProvider = item.provider||prov?.provider||"followsale";
                        const editVals = (editMap[o.order_id]||[])[i] || {};
                        return (
                          <div key={i} className="rounded-xl border overflow-hidden" style={{ borderColor: o.needs_admin_review ? "rgba(247,119,55,0.3)" : "var(--border-color)", background:"var(--surface-2)" }}>
                            <div className="px-3 py-2.5 flex items-start gap-3">
                              <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background:ic.bg, border:`1px solid ${ic.border}` }}>
                                <AdminIcon emoji={ic.emoji} className="w-4 h-4" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className="text-sm font-bold" style={{ color:"var(--text-primary)" }}>{name}</span>
                                  <span className="text-xs font-bold px-1.5 py-0.5 rounded-full" style={{ background:ic.bg, color:ic.color }}>×{item.quantity?.toLocaleString()}</span>
                                  {o.is_free_gift ? <Gift className="w-3.5 h-3.5" style={{color:"#22c55e"}} /> : item.ipoints_cost > 0 && <span className="text-xs font-bold" style={{color:"#F77737"}}>{item.ipoints_cost} iP</span>}
                                  <ProviderBadge prov={itemProvider} size="xs" />
                                </div>
                                {link && (
                                  <a href={ensureHttps(link)} target="_blank" rel="noopener noreferrer"
                                    className="text-[11px] mt-0.5 inline-flex items-center gap-1 hover:underline break-all"
                                    style={{ color:"#7aa2ff" }}>
                                    <ExternalLink className="w-2.5 h-2.5 flex-shrink-0" />
                                    {link.replace(/^https?:\/\/(www\.)?/,"").replace(/\/$/,"").slice(0,52)}
                                  </a>
                                )}
                              </div>
                            </div>
                            {prov && (
                              <div className="px-3 py-2 flex flex-wrap gap-x-3 gap-y-1 text-[10px] border-t" style={{ borderColor:"var(--border-color)", background:"var(--surface-1)", color:"var(--text-secondary)" }}>
                                <span className="flex items-center gap-1.5">
                                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{background:provStatusColor}} />
                                  {providerDeliveryLabel(prov.provider_status_bucket)}
                                </span>
                                {prov.provider_order_id && <span>ID: <b style={{color:"var(--text-primary)"}}>{prov.provider_order_id}</b></span>}
                                {prov.provider_service_id > 0 && <span>Svc: <b style={{color:"var(--text-primary)"}}>{prov.provider_service_id}</b></span>}
                                {prov.provider_start_count != null && <span>{t("Admin.start_2", lang)}: <b style={{color:"var(--text-primary)"}}>{prov.provider_start_count}</b></span>}
                                {prov.provider_remains != null && <span>{t("Admin.remains", lang)}: <b style={{color:"var(--text-primary)"}}>{prov.provider_remains}</b></span>}
                              </div>
                            )}
                            {/* Editable fields shown when order needs admin review */}
                            {o.needs_admin_review && (
                              <div className="px-3 py-2.5 border-t space-y-2" style={{ borderColor:"rgba(247,119,55,0.2)", background:"rgba(247,119,55,0.04)" }}>
                                <div className="text-[10px] font-bold uppercase tracking-wider" style={{color:"#F77737"}}>✏️ Edit before sending</div>
                                <div>
                                  <div className="text-[10px] mb-1" style={{color:"var(--text-secondary)"}}>URL / Link</div>
                                  <input
                                    value={editVals.target_url ?? link}
                                    onChange={e => setEditField(o.order_id, i, "target_url", e.target.value)}
                                    className="w-full h-8 px-2 rounded-lg text-xs"
                                    style={{background:"var(--surface-3)",border:"1px solid rgba(247,119,55,0.3)",color:"var(--text-primary)",outline:"none"}}
                                    placeholder="https://..."
                                  />
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  <div>
                                    <div className="text-[10px] mb-1" style={{color:"var(--text-secondary)"}}>Quantity</div>
                                    <input
                                      type="number" min="1"
                                      value={editVals.quantity ?? String(item.quantity||"")}
                                      onChange={e => setEditField(o.order_id, i, "quantity", e.target.value)}
                                      className="w-full h-8 px-2 rounded-lg text-xs"
                                      style={{background:"var(--surface-3)",border:"1px solid rgba(247,119,55,0.3)",color:"var(--text-primary)",outline:"none"}}
                                    />
                                  </div>
                                  <div>
                                    <div className="text-[10px] mb-1" style={{color:"var(--text-secondary)"}}>Service ID (JAP)</div>
                                    <input
                                      type="number" min="1"
                                      value={editVals.provider_service_id ?? String(item.provider_service_id||"")}
                                      onChange={e => setEditField(o.order_id, i, "provider_service_id", e.target.value)}
                                      className="w-full h-8 px-2 rounded-lg text-xs"
                                      style={{background:"var(--surface-3)",border:"1px solid rgba(247,119,55,0.3)",color:"var(--text-primary)",outline:"none"}}
                                    />
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {/* Save edits button */}
                      {o.needs_admin_review && (
                        <button
                          disabled={busyKey===`edit:${o.order_id}`}
                          onClick={() => saveOrderEdit(o.order_id)}
                          className="w-full h-8 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-all hover:opacity-90 disabled:opacity-40 mt-1"
                          style={{background:"rgba(247,119,55,0.15)",color:"#F77737",border:"1px solid rgba(247,119,55,0.35)"}}>
                          {busyKey===`edit:${o.order_id}` ? <RotateCcw className="w-3 h-3 animate-spin"/> : "💾 Save Changes"}
                        </button>
                      )}
                    </div>

                    {/* Right: Meta + Actions */}
                    <div className="p-4 flex flex-col gap-3">
                      {/* Status + iPoints row */}
                      <div className="rounded-xl border p-3 space-y-2" style={{ borderColor:"var(--border-color)", background:"var(--surface-2)" }}>
                        <div className="flex items-center justify-between text-xs">
                          <span style={{color:"var(--text-secondary)"}}>{t("order.status", lang)}</span>
                          <span className="font-bold px-2 py-0.5 rounded-full" style={{ background:sc.bg, color:sc.color }}>{siteStatusLabel(o.status)}</span>
                        </div>
                        <div style={{height:"1px",background:"var(--border-color)"}} />
                        <div className="flex items-center justify-between text-xs">
                          <span style={{color:"var(--text-secondary)"}}>{t("Admin.ipoints", lang)}</span>
                          <span className="font-bold font-outfit flex items-center gap-1" style={{color:"#F77737"}}>
                            {o.is_free_gift ? <><Gift className="w-3 h-3" />{t("Admin.free", lang)}</> : <>{o.total_ipoints} iP</>}
                          </span>
                        </div>
                        {o.ipoints_refunded > 0 && (
                          <div className="flex items-center justify-between text-xs">
                            <span style={{color:"var(--text-secondary)"}}>{t("Admin.refunded", lang)}</span>
                            <span className="font-bold" style={{color:"#a855f7"}}>↩ {o.ipoints_refunded} iP</span>
                          </div>
                        )}
                      </div>

                      {/* Meta */}
                      <div className="rounded-xl border p-3 space-y-1.5 text-[11px]" style={{ borderColor:"var(--border-color)", background:"var(--surface-2)" }}>
                        <div className="flex justify-between gap-2">
                          <span style={{color:"var(--text-secondary)"}}>{t("Admin.order", lang)}</span>
                          <span className="font-bold font-outfit" style={{color:sc.color}}>#{o.order_number}</span>
                        </div>
                        <div className="flex justify-between gap-2">
                          <span style={{color:"var(--text-secondary)"}}>ID</span>
                          <span className="font-mono" style={{color:"var(--text-primary)"}}>{o.order_id?.slice(-8)}</span>
                        </div>
                        {o.provider_order_ids?.length > 0 && (
                          <div className="flex justify-between gap-2">
                            <span style={{color:"var(--text-secondary)"}}>Prov ID</span>
                            <span className="font-mono" style={{color:"var(--text-primary)"}}>{formatProviderIds(o.provider_order_ids)}</span>
                          </div>
                        )}
                        <div className="flex justify-between gap-2">
                          <span style={{color:"var(--text-secondary)"}}>{t("Admin.provider", lang)}</span>
                          <ProviderBadge prov={orderProvider(o)} size="sm" />
                        </div>
                        <div className="flex justify-between gap-2">
                          <span style={{color:"var(--text-secondary)"}}>{t("Admin.sync_2", lang)}</span>
                          <span style={{color:"var(--text-primary)"}}>{providerSyncLabel(o.provider_sync_status)}</span>
                        </div>
                        <div className="flex justify-between gap-2">
                          <span style={{color:"var(--text-secondary)"}}>{t("order.date", lang)}</span>
                          <span style={{color:"var(--text-primary)"}}>{formatIsraelDateTime(o.created_at)}</span>
                        </div>
                      </div>

                      {/* Action buttons */}
                      <div className="flex flex-col gap-1.5 mt-auto">
                        {o.needs_admin_review && (
                          <button disabled={busyKey===`jap:${o.order_id}`} onClick={() => approveJap(o.order_id)}
                            className="h-9 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 transition-all hover:opacity-90 disabled:opacity-40"
                            style={{ background:"linear-gradient(135deg,#F77737,#E1306C)", color:"#fff", boxShadow:"0 3px 12px rgba(247,119,55,0.4)" }}>
                            {busyKey===`jap:${o.order_id}` ? <RotateCcw className="w-3 h-3 animate-spin"/> : <>✓ Approve &amp; Send to JAP</>}
                          </button>
                        )}
                        {(o.provider_order_ids?.length > 0 || (o.provider_orders||[]).some(p=>p.provider_order_id)) && (
                          <button disabled={busyKey===`sync:${o.order_id}`} onClick={() => syncProvider(o.order_id)}
                            className="h-9 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 transition-all hover:opacity-80 disabled:opacity-40"
                            style={{ background:"rgba(64,93,230,0.12)", color:"#7aa2ff", border:"1px solid rgba(64,93,230,0.25)" }}>
                            <RefreshCw className={`w-3 h-3 ${busyKey===`sync:${o.order_id}`?"animate-spin":""}`} />
                            {t("Admin.sync_provider", lang)}
                          </button>
                        )}
                        {canRetryProvider(o) && (
                          <button disabled={busyKey===`retry:${o.order_id}`} onClick={() => retryProvider(o.order_id)}
                            className="h-9 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 transition-all hover:opacity-80 disabled:opacity-40"
                            style={{ background:"rgba(247,119,55,0.12)", color:"#F77737", border:"1px solid rgba(247,119,55,0.25)" }}>
                            <RotateCcw className={`w-3 h-3 ${busyKey===`retry:${o.order_id}`?"animate-spin":""}`} />
                            {o.status==="cancelled_admin" ? t("Admin.re_send", lang) : t("Admin.retry", lang)}
                          </button>
                        )}
                        {canCancelProvider(o) && (
                          <button disabled={busyKey===`cancel:${o.order_id}`} onClick={() => cancelProvider(o.order_id)}
                            className="h-9 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 transition-all hover:opacity-80 disabled:opacity-40"
                            style={{ background:"rgba(239,68,68,0.1)", color:"#ef4444", border:"1px solid rgba(239,68,68,0.22)" }}>
                            <X className="w-3 h-3" />
                            {t("Admin.cancel_order", lang)}
                          </button>
                        )}
                        {canManualResolve(o) && (
                          <button disabled={busyKey===`mresolve:${o.order_id}`} onClick={() => setManualResolveModal(o.order_id)}
                            className="h-9 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 transition-all hover:opacity-80 disabled:opacity-40"
                            style={{ background:"rgba(168,85,247,0.12)", color:"#a855f7", border:"1px solid rgba(168,85,247,0.28)" }}>
                            {busyKey===`mresolve:${o.order_id}` ? <RotateCcw className="w-3 h-3 animate-spin"/> : <CheckCircle className="w-3 h-3"/>}
                            {L(lang,"Resolve","Завершить","סיים ידנית")}
                          </button>
                        )}
                        {/* Set JAP Provider ID */}
                        <button onClick={()=>toggleProvIdPanel(o)}
                          className="h-9 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 transition-all hover:opacity-80"
                          style={{ background: provIdPanel.has(o.order_id) ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.08)", color:"#a78bfa", border:"1px solid rgba(139,92,246,0.3)" }}>
                          <Pencil className="w-3 h-3"/>
                          {L(lang,"Set JAP ID","Задать JAP ID","הגדר JAP ID")}
                        </button>
                        {provIdPanel.has(o.order_id) && (
                          <div className="rounded-xl border p-3 space-y-2.5" style={{borderColor:"rgba(139,92,246,0.3)",background:"rgba(139,92,246,0.06)"}}>
                            <div className="text-[10px] font-bold uppercase tracking-wider" style={{color:"#a78bfa"}}>JAP Order IDs</div>
                            {(o.items||[]).map((_item,i)=>{
                              const po=o.provider_orders?.[i];
                              const iptKey=`${o.order_id}:${i}`;
                              const iname=_item.product_name?.[lang]||_item.product_name?.en||`Item ${i+1}`;
                              return(
                                <div key={i} className="space-y-1.5">
                                  {(o.items||[]).length>1 && <div className="text-[10px]" style={{color:"var(--text-secondary)"}}>{iname}</div>}
                                  <input
                                    value={provIdValues[iptKey]??po?.provider_order_id??""}
                                    onChange={e=>setProvIdValues(v=>({...v,[iptKey]:e.target.value}))}
                                    onKeyDown={e=>{if(e.key==="Enter")saveProvId(o.order_id,i);}}
                                    placeholder="Введи JAP order ID…"
                                    className="w-full h-8 px-2.5 rounded-lg font-mono text-xs"
                                    style={{background:"var(--surface-3)",border:"1px solid rgba(139,92,246,0.4)",color:"var(--text-primary)",outline:"none",boxSizing:"border-box"}}
                                  />
                                  <button
                                    onClick={()=>saveProvId(o.order_id,i)}
                                    disabled={busyKey===`provid:${iptKey}`}
                                    className="w-full h-8 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 transition-all hover:opacity-80 disabled:opacity-40"
                                    style={{background:"rgba(34,197,94,0.18)",color:"#22c55e",border:"1px solid rgba(34,197,94,0.35)"}}>
                                    {busyKey===`provid:${iptKey}` ? <><RotateCcw className="w-3 h-3 animate-spin"/>Syncing…</> : <><Check className="w-3.5 h-3.5"/>Сохранить и синхронизировать</>}
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* ── Timeline / History toggle ── */}
                  {(o.status_timeline?.length > 0 || o.provider_attempts?.length > 0) && (
                    <div className="border-t" style={{ borderColor:"var(--border-color)" }}>
                      <button onClick={toggleHist}
                        className="w-full px-4 py-2.5 flex items-center gap-2 text-xs font-medium hover:bg-white/[0.02] transition-colors"
                        style={{ color:"var(--text-secondary)" }}>
                        <History className="w-3.5 h-3.5 flex-shrink-0" />
                        {hasHist ? t("Admin.hide_history", lang) : t("Admin.show_history", lang)}
                        {o.status_timeline?.length > 0 && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background:"var(--surface-2)", border:"1px solid var(--border-color)" }}>
                            {o.status_timeline.length}
                          </span>
                        )}
                        <span className="ms-auto">{hasHist ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}</span>
                      </button>
                      {hasHist && (
                        <div className="px-4 pb-4 space-y-4">
                          {o.status_timeline?.length > 0 && (
                            <div>
                              <div className="text-[10px] font-bold uppercase tracking-wider mb-3" style={{ color:"var(--text-secondary)" }}>
                                {t("Admin.timeline", lang)}
                              </div>
                              {renderTimeline(o.status_timeline)}
                            </div>
                          )}
                          {o.provider_attempts?.length > 0 && (
                            <div className="rounded-xl border p-3" style={{ borderColor:"var(--border-color)", background:"var(--surface-2)" }}>
                              <div className="text-[10px] font-bold uppercase tracking-wider mb-2.5" style={{ color:"var(--text-secondary)" }}>
                                {t("Admin.provider_attempts", lang)} · {o.provider_attempts.length}
                              </div>
                              <div className="space-y-1.5 text-xs">
                                {o.provider_attempts.map((attempt, index) => (
                                  <div key={attempt.attempt_id||`${o.order_id}-a-${index}`}
                                    className="flex items-center gap-2 py-1.5 border-b last:border-b-0"
                                    style={{ borderColor:"var(--border-color)" }}>
                                    <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0"
                                      style={{ background:"var(--surface-1)", border:"1px solid var(--border-color)", color:"var(--text-secondary)" }}>{index+1}</span>
                                    <span style={{color:"var(--text-secondary)"}}>{providerSyncLabel(attempt.provider_sync_status)}</span>
                                    {attempt.provider_order_ids?.length ? <span style={{color:"var(--text-primary)"}}>ID: {attempt.provider_order_ids.join(", ")}</span> : null}
                                    {attempt.attempted_at ? <span className="ms-auto text-[10px]" style={{color:"var(--text-secondary)"}}>{formatIsraelDateTime(attempt.attempted_at)}</span> : null}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {filteredOrders.length === 0 && (
          <div className="text-center py-16 rounded-2xl border" style={{ borderColor:"var(--border-color)", background:"var(--surface-1)" }}>
            {search ? (
              <>
                <Search className="w-8 h-8 mx-auto mb-2.5 opacity-20" />
                <p className="text-sm font-semibold mb-1" style={{ color:"var(--text-primary)" }}>
                  {L(lang,"No orders found","Ничего не найдено","לא נמצאו תוצאות")}
                </p>
                <p className="text-xs" style={{ color:"var(--text-secondary)" }}>
                  {L(lang,`No orders match "${search}"`,`По запросу «${search}» ничего не найдено`,`אין הזמנות עבור "${search}"`)}
                </p>
                <button onClick={()=>{setSearchInput("");setSearch("");}}
                  className="mt-4 text-xs px-3 py-1.5 rounded-lg transition-opacity hover:opacity-70"
                  style={{ background:"rgba(139,92,246,0.1)", color:"#a78bfa", border:"1px solid rgba(139,92,246,0.2)" }}>
                  {L(lang,"Clear search","Сбросить поиск","נקה חיפוש")}
                </button>
              </>
            ) : (
              <>
                <Package className="w-8 h-8 mx-auto mb-2.5 opacity-20" />
                <p className="text-sm" style={{ color:"var(--text-secondary)" }}>
                  {t("Admin.no_orders_found", lang)}
                </p>
              </>
            )}
          </div>
        )}
      </div>

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-1.5 pt-2 flex-wrap">
          <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page<=1}
            className="h-8 px-3 rounded-xl text-xs font-semibold disabled:opacity-30 transition-all hover:bg-white/5"
            style={{ background:"var(--surface-2)", color:"var(--text-primary)", border:"1px solid var(--border-color)" }}>
            ← {t("Admin.prev", lang)}
          </button>
          {Array.from({length: Math.min(totalPages, 7)}, (_, i) => {
            let p; const half=3;
            if(totalPages<=7){p=i+1;}
            else if(page<=half+1){p=i+1;}
            else if(page>=totalPages-half){p=totalPages-6+i;}
            else{p=page-half+i;}
            return (
              <button key={p} onClick={()=>setPage(p)}
                className="w-8 h-8 rounded-xl text-xs font-bold transition-all"
                style={{
                  background: page===p ? "#8B5CF6" : "var(--surface-2)",
                  color: page===p ? "#fff" : "var(--text-secondary)",
                  border: `1px solid ${page===p ? "#8B5CF6" : "var(--border-color)"}`,
                }}>
                {p}
              </button>
            );
          })}
          <button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page>=totalPages}
            className="h-8 px-3 rounded-xl text-xs font-semibold disabled:opacity-30 transition-all hover:bg-white/5"
            style={{ background:"var(--surface-2)", color:"var(--text-primary)", border:"1px solid var(--border-color)" }}>
            {t("Admin.next", lang)} →
          </button>
          <span className="text-xs" style={{ color:"var(--text-secondary)" }}>{page}/{totalPages} · {totalOrders}</span>
        </div>
      )}

    </div>
    </>
  );
}

// ===== PRODUCTS =====
const PLATFORM_URL_INFO = {
  instagram_profile:  { regex: "^https?://(www\\.)?instagram\\.com/[A-Za-z0-9_.]{1,30}/?$",                        examples: ["https://instagram.com/nickname", "https://www.instagram.com/nickname"] },
  instagram_post:     { regex: "^https?://(www\\.)?instagram\\.com/(p|reel|tv)/[A-Za-z0-9_-]+/?",                  examples: ["https://www.instagram.com/p/POST_ID/", "https://www.instagram.com/reel/POST_ID/"] },
  instagram_story:    { regex: "^https?://(www\\.)?instagram\\.com/stories/[A-Za-z0-9_.]+/[0-9]+/?$",             examples: ["https://www.instagram.com/stories/nickname/3882953131684581738/"] },
  facebook_page:      { regex: "^https?://(www\\.)?facebook\\.com/[A-Za-z0-9_.]+/?$",                              examples: ["https://facebook.com/nickname", "https://www.facebook.com/nickname"] },
  facebook_post:      { regex: "^https?://(www\\.)?facebook\\.com/.+/posts/[0-9]+",                                examples: ["https://www.facebook.com/nickname/posts/POST_ID"] },
  twitter_profile:    { regex: "^https?://(www\\.)?(twitter|x)\\.com/[A-Za-z0-9_]{1,15}/?$",                       examples: ["https://x.com/nickname", "https://twitter.com/nickname"] },
  twitter_post:       { regex: "^https?://(www\\.)?(twitter|x)\\.com/[A-Za-z0-9_]+/status/[0-9]+",                 examples: ["https://x.com/nickname/status/POST_ID", "https://twitter.com/nickname/status/POST_ID"] },
  tiktok_profile:     { regex: "^https?://(www\\.)?tiktok\\.com/@[A-Za-z0-9_.]+/?$",                               examples: ["https://www.tiktok.com/@nickname", "https://tiktok.com/@nickname"] },
  tiktok_video:       { regex: "^https?://(www\\.)?tiktok\\.com/@[A-Za-z0-9_.]+/video/[0-9]+",                     examples: ["https://www.tiktok.com/@nickname/video/VIDEO_ID"] },
  youtube_channel:    { regex: "^https?://(www\\.)?youtube\\.com/(@[A-Za-z0-9_.]+|channel/[A-Za-z0-9_-]+)/?$",    examples: ["https://www.youtube.com/@nickname", "https://youtube.com/channel/CHANNEL_ID"] },
  youtube_video:      { regex: "^https?://(www\\.)?youtube\\.com/watch\\?v=[A-Za-z0-9_-]+",                        examples: ["https://www.youtube.com/watch?v=VIDEO_ID", "https://youtu.be/VIDEO_ID"] },
  telegram_channel:   { regex: "^https?://t\\.me/[A-Za-z0-9_.]+$",                                                 examples: ["https://t.me/nickname"] },
  telegram_post:      { regex: "^https?://t\\.me/[A-Za-z0-9_.]+/[0-9]+$",                                          examples: ["https://t.me/nickname/POST_ID"] },
  twitch_channel:     { regex: "^https?://(www\\.)?twitch\\.tv/[A-Za-z0-9_]{4,25}/?$",                             examples: ["https://www.twitch.tv/nickname", "https://twitch.tv/nickname"] },
  linkedin_profile:   { regex: "^https?://(www\\.)?linkedin\\.com/in/[A-Za-z0-9_-]+/?$",                           examples: ["https://linkedin.com/in/nickname", "https://www.linkedin.com/in/nickname"] },
  linkedin_post:      { regex: "^https?://(www\\.)?linkedin\\.com/(posts|feed/update)/[A-Za-z0-9_:-]+",             examples: ["https://www.linkedin.com/posts/nickname_POST_ID"] },
  kick_channel:       { regex: "^https?://(www\\.)?kick\\.com/[A-Za-z0-9_]+/?$",                                   examples: ["https://kick.com/nickname", "https://www.kick.com/nickname"] },
  spotify_artist:     { regex: "^https?://open\\.spotify\\.com/artist/[A-Za-z0-9]+",                               examples: ["https://open.spotify.com/artist/ARTIST_ID"] },
  spotify_track:      { regex: "^https?://open\\.spotify\\.com/track/[A-Za-z0-9]+",                                examples: ["https://open.spotify.com/track/TRACK_ID"] },
  discord_server:     { regex: "^https?://(www\\.)?discord\\.gg/[A-Za-z0-9]+$",                                    examples: ["https://discord.gg/INVITE_CODE"] },
  snapchat_profile:   { regex: "^https?://(www\\.)?snapchat\\.com/add/[A-Za-z0-9_.]+/?$",                          examples: ["https://snapchat.com/add/nickname", "https://www.snapchat.com/add/nickname"] },
};

function ProductsTab({lang}){
  const EMPTY_FORM={name_en:"",name_ru:"",name_he:"",desc_en:"",desc_ru:"",desc_he:"",category:"followers",platform:"instagram",target_mode:"instagram_profile",requires_custom_comments:false,ipoints_per_1000:"75",min_quantity:"10",max_quantity:"200000",provider_service_id:"0",provider:"jap",is_active:true,total_ipoints:"",max_split_posts:"1",bundle_items:[],link_regex:"",link_regex_enabled:false,admin_note:"",emoji_options:[]};
  const [products,setProducts]=useState([]);const [showEditor,setShowEditor]=useState(false);const [editingProductId,setEditingProductId]=useState("");const [saving,setSaving]=useState(false);const [translating,setTranslating]=useState(false);
  const [aiPrompt,setAiPrompt]=useState("");
  const [aiGenMode,setAiGenMode]=useState("both");
  const [aiAllowEmoji,setAiAllowEmoji]=useState(false);
  const [formLang,setFormLang]=useState("en");
  const [aiRegexLoading,setAiRegexLoading]=useState(false);
  const [regexTestUrl,setRegexTestUrl]=useState("");
  const [form,setForm]=useState(EMPTY_FORM);
  const isBundle = form.category === "bundle";
  const categoryOptions = [
    { value:"followers", label:t("shop.followers", lang) },
    { value:"likes", label:t("shop.likes", lang) },
    { value:"views", label:t("Shop.views", lang) },
    { value:"comments", label:t("Shop.comments", lang) },
    { value:"story_views", label:t("Admin.story_views", lang) },
    { value:"story_likes", label:t("Admin.story_likes", lang) },
    { value:"page_likes", label:t("Admin.page_likes", lang) },
    { value:"bundle", label:`📦 ${t("Admin.bundle", lang)}` },
  ];
  const platformOptions = [
    { value:"instagram", label:"Instagram" },
    { value:"tiktok",   label:"TikTok" },
    { value:"youtube",  label:"YouTube" },
    { value:"telegram", label:"Telegram" },
    { value:"twitch",   label:"Twitch" },
    { value:"facebook", label:"Facebook" },
    { value:"twitter",  label:"X / Twitter" },
    { value:"linkedin", label:"LinkedIn" },
    { value:"kick",     label:"Kick" },
    { value:"discord",  label:"Discord" },
    { value:"snapchat", label:"Snapchat" },
    { value:"spotify",  label:"Spotify" },
  ];
  const targetModeOptions = [
    { value:"instagram_profile", label:"Instagram — Profile" },
    { value:"instagram_post",    label:"Instagram — Post / Reel" },
    { value:"instagram_story",   label:"Instagram — Story" },
    { value:"tiktok_profile",    label:"TikTok — Profile" },
    { value:"tiktok_video",      label:"TikTok — Video" },
    { value:"youtube_channel",   label:"YouTube — Channel" },
    { value:"youtube_video",     label:"YouTube — Video" },
    { value:"telegram_channel",  label:"Telegram — Channel" },
    { value:"telegram_post",     label:"Telegram — Post" },
    { value:"twitch_channel",    label:"Twitch — Channel" },
    { value:"facebook_page",     label:"Facebook — Page" },
    { value:"facebook_post",     label:"Facebook — Post" },
    { value:"twitter_profile",   label:"X / Twitter — Profile" },
    { value:"twitter_post",      label:"X / Twitter — Post" },
    { value:"linkedin_profile",  label:"LinkedIn — Profile" },
    { value:"linkedin_post",     label:"LinkedIn — Post" },
    { value:"kick_channel",      label:"Kick — Channel" },
    { value:"spotify_artist",    label:"Spotify — Artist" },
    { value:"spotify_track",     label:"Spotify — Track" },
    { value:"discord_server",    label:"Discord — Server" },
    { value:"snapchat_profile",  label:"Snapchat — Profile" },
    { value:"generic",           label:"Generic Link" },
  ];
  const fetch_=useCallback(async()=>{const r=await fetch(`${API}/products?active_only=false`);if(r.ok) setProducts(await r.json());},[]);
  useEffect(()=>{fetch_();},[fetch_]);

  const openCreate=()=>{setEditingProductId("");setForm(EMPTY_FORM);setFormLang("en");setShowEditor(true);};
  const openEdit=(product)=>{setEditingProductId(product.product_id);setForm({
    name_en:product.name?.en||"",name_ru:product.name?.ru||"",name_he:product.name?.he||"",
    desc_en:product.description?.en||"",desc_ru:product.description?.ru||"",desc_he:product.description?.he||"",
    category:product.category||"followers",
    platform:product.platform||"instagram",
    target_mode:product.target_mode||(product.category==="followers"?"instagram_profile":product.category==="likes"?"instagram_post":"generic"),
    requires_custom_comments:Boolean(product.requires_custom_comments),
    ipoints_per_1000:String(product.ipoints_per_1000 ?? 75),
    min_quantity:String(product.min_quantity ?? 10),
    max_quantity:String(product.max_quantity ?? 200000),
    provider_service_id:String(product.provider_service_id ?? 0),
    provider:product.provider||"followsale",
    is_active:Boolean(product.is_active),
    total_ipoints:String(product.total_ipoints ?? ""),
    max_split_posts:String(product.max_split_posts ?? 1),
    bundle_items:product.bundle_items ? product.bundle_items.map(bi=>({product_id:bi.product_id,quantity:String(bi.quantity)})) : [],
    link_regex:product.link_regex||"",
    link_regex_enabled:Boolean(product.link_regex),
    admin_note:product.admin_note||"",
    emoji_options:(product.emoji_options||[]).map(o=>({emoji:o.emoji||"",label:o.label||"",service_id:String(o.service_id||"")})),
  });setShowEditor(true);};

  const aiDescribe = async () => {
    const src = aiPrompt.trim() || form.name_en.trim() || form.name_ru.trim() || form.name_he.trim();
    if (!src) { toast.error(t("Admin.enter_a_description_or_name_first", lang)); return; }
    setTranslating(true);
    try {
      const r = await fetch(`${API}/admin/products/ai-describe`, {
        method:"POST", headers:{"Content-Type":"application/json"}, credentials:"include",
        body: JSON.stringify({ source_text: src, category: form.category, gen_mode: aiGenMode, allow_emoji: aiAllowEmoji }),
      });
      const d = await r.json(); if (!r.ok) throw new Error(d.detail||"AI failed");
      setForm(prev => ({
        ...prev,
        ...(aiGenMode !== "desc" ? { name_en: d.name?.en || prev.name_en, name_ru: d.name?.ru || prev.name_ru, name_he: d.name?.he || prev.name_he } : {}),
        ...(aiGenMode !== "name" ? { desc_en: d.description?.en || prev.desc_en, desc_ru: d.description?.ru || prev.desc_ru, desc_he: d.description?.he || prev.desc_he } : {}),
      }));
      setAiPrompt("");
      toast.success(t("Admin.ai_generated", lang));
    } catch(e) { toast.error(e.message); } finally { setTranslating(false); }
  };

  const aiGenRegex = async () => {
    setAiRegexLoading(true);
    try {
      const r = await fetch(`${API}/admin/products/ai-regex`, {
        method:"POST", headers:{"Content-Type":"application/json"}, credentials:"include",
        body: JSON.stringify({ target_mode: form.target_mode, example_url: regexTestUrl }),
      });
      const d = await r.json(); if (!r.ok) throw new Error(d.detail||"AI failed");
      setForm(prev => ({ ...prev, link_regex: d.regex }));
      toast.success(d.explanation || t("Admin.regex_generated", lang));
    } catch(e) { toast.error(e.message); } finally { setAiRegexLoading(false); }
  };

  const addBundleItem = () => setForm(prev => ({...prev, bundle_items: [...prev.bundle_items, {product_id:"",quantity:"100"}]}));
  const removeBundleItem = (i) => setForm(prev => ({...prev, bundle_items: prev.bundle_items.filter((_,idx)=>idx!==i)}));
  const updateBundleItem = (i, field, val) => setForm(prev => ({...prev, bundle_items: prev.bundle_items.map((bi,idx)=>idx===i?{...bi,[field]:val}:bi)}));

  const saveProduct=async()=>{try{setSaving(true);
    const payload={
      name:{en:form.name_en,ru:form.name_ru,he:form.name_he},
      description:{en:form.desc_en,ru:form.desc_ru,he:form.desc_he},
      category:form.category,
      platform:isBundle?"instagram":form.platform,
      target_mode:isBundle?null:form.target_mode,
      requires_custom_comments:isBundle?false:Boolean(form.requires_custom_comments),
      ipoints_per_1000:isBundle?0:parseFloat(form.ipoints_per_1000)||0,
      min_quantity:isBundle?1:parseInt(form.min_quantity,10)||0,
      max_quantity:isBundle?1:parseInt(form.max_quantity,10)||0,
      provider_service_id:isBundle?0:parseInt(form.provider_service_id,10)||0,
      provider:isBundle?"followsale":form.provider,
      is_active:Boolean(form.is_active),
      link_regex:(form.link_regex_enabled && form.link_regex.trim()) ? form.link_regex.trim() : null,
      admin_note:form.admin_note.trim()||null,
      emoji_options: (form.emoji_options||[]).filter(o=>o.emoji&&o.service_id).map(o=>({emoji:o.emoji,label:o.label||o.emoji,service_id:parseInt(o.service_id,10)||0})),
    };
    if(payload.emoji_options.length===0) payload.emoji_options=null;
    if(isBundle){
      payload.total_ipoints = parseFloat(form.total_ipoints)||0;
      payload.max_split_posts = parseInt(form.max_split_posts,10)||1;
      payload.bundle_items = form.bundle_items.filter(bi=>bi.product_id).map(bi=>({product_id:bi.product_id,quantity:parseInt(bi.quantity,10)||0}));
    }
    const res=await fetch(editingProductId?`${API}/admin/products/${editingProductId}`:`${API}/admin/products`,{method:editingProductId?"PUT":"POST",headers:{"Content-Type":"application/json"},credentials:"include",body:JSON.stringify(payload)});
    const data=await res.json().catch(()=>({}));if(!res.ok)throw new Error(data.detail||"Failed");
    toast.success(editingProductId?t("Admin.product_updated", lang):t("Admin.product_created", lang));
    setShowEditor(false);setEditingProductId("");setForm(EMPTY_FORM);fetch_();
  }catch(err){toast.error(err.message||"Failed");}finally{setSaving(false);}};
  const del=async(id)=>{if(!window.confirm(t("Admin.delete_this_product", lang)))return;await fetch(`${API}/admin/products/${id}`,{method:"DELETE",credentials:"include"});fetch_();};

  const nonBundleProducts = products.filter(p => p.category !== "bundle");

  return (
    <div className="animate-fade-in-up space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h2 className="text-lg font-bold">{t("admin.products", lang)} <span className="text-sm font-normal opacity-50">({products.length})</span></h2>
        <div className="flex items-center gap-2">
        <Dialog open={showEditor} onOpenChange={v => { setShowEditor(v); if (!v) setAiPrompt(""); }}>
          <DialogTrigger asChild>
            <Button className="btn-brand text-sm px-4 h-9" onClick={openCreate} data-testid="add-product-btn">
              <Plus className="w-4 h-4 me-1" />{t("admin.add_product", lang)}
            </Button>
          </DialogTrigger>
          <DialogContent style={{ background: "var(--bg-secondary)", borderColor: "var(--border-color)" }} className="w-[calc(100vw-24px)] max-w-[960px] max-h-[94vh] overflow-y-auto p-0">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 sticky top-0 z-10" style={{ background: "var(--bg-secondary)", borderBottom: "1px solid var(--border-color)" }}>
              <h2 className="text-base font-bold" style={{ color: "var(--text-primary)" }}>
                {editingProductId ? t("Admin.edit_product", lang) : t("admin.add_product", lang)}
              </h2>
              <div className="flex items-center gap-2">
                {/* Active toggle */}
                <button type="button" onClick={() => setForm({ ...form, is_active: !form.is_active })}
                  className="flex items-center gap-2 h-8 px-3 rounded-xl border text-xs font-semibold transition-all"
                  style={ form.is_active
                    ? { background:"rgba(34,197,94,0.1)", borderColor:"rgba(34,197,94,0.3)", color:"#22c55e" }
                    : { background:"var(--surface-2)", borderColor:"var(--border-color)", color:"var(--text-secondary)" }}>
                  <div className={`w-3.5 h-3.5 rounded-full border-2 ${form.is_active ? "bg-[#22c55e] border-[#22c55e]" : "border-[var(--text-secondary)]"}`} />
                  {form.is_active ? t("Admin.active_4", lang) : t("Admin.hidden", lang)}
                </button>
                <button onClick={saveProduct} disabled={saving}
                  className="h-8 px-4 rounded-xl text-xs font-bold flex items-center gap-1.5 disabled:opacity-50"
                  style={{ background: "#8B5CF6", color: "#fff" }} data-testid="save-product-btn">
                  {saving ? <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  {saving ? t("Admin.saving", lang) : t("admin.save", lang)}
                </button>
              </div>
            </div>

            {/* Body — two column */}
            <div className="grid lg:grid-cols-[1fr_320px]">

              {/* ── Left: content ── */}
              <div className="p-6 space-y-5" style={{ borderRight: "1px solid var(--border-color)" }}>

                {/* AI describe */}
                <div className="rounded-2xl p-4 space-y-3" style={{ background: "rgba(139,92,246,0.06)", border: "1px solid rgba(139,92,246,0.2)" }}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-3.5 h-3.5" style={{ color: "#8B5CF6" }} />
                      <span className="text-xs font-bold" style={{ color: "#8B5CF6" }}>AI {t("Admin.generate_name_description", lang)}</span>
                    </div>
                    {/* Emoji toggle */}
                    <button type="button" onClick={() => setAiAllowEmoji(v => !v)}
                      className="flex items-center gap-1.5 h-6 px-2.5 rounded-lg text-[10px] font-bold border transition-all"
                      style={aiAllowEmoji
                        ? { background: "rgba(245,158,11,0.12)", borderColor: "rgba(245,158,11,0.35)", color: "#f59e0b" }
                        : { background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-secondary)" }}>
                      😊 {aiAllowEmoji ? "Emoji ON" : "Emoji OFF"}
                    </button>
                  </div>
                  {/* Mode selector */}
                  <div className="flex gap-1.5">
                    {[
                      { val: "both",  label: "Name + Desc" },
                      { val: "name",  label: "Name only" },
                      { val: "desc",  label: "Desc only" },
                    ].map(m => (
                      <button key={m.val} type="button" onClick={() => setAiGenMode(m.val)}
                        className="flex-1 h-7 rounded-lg text-[11px] font-semibold border transition-all"
                        style={aiGenMode === m.val
                          ? { background: "rgba(139,92,246,0.18)", borderColor: "rgba(139,92,246,0.45)", color: "#a78bfa" }
                          : { background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-secondary)" }}>
                        {m.label}
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <textarea value={aiPrompt} onChange={e => setAiPrompt(e.target.value)} rows={2}
                      placeholder={t("Admin.describe_the_product_in_any_language", lang)}
                      className="flex-1 rounded-xl px-3 py-2 text-xs resize-none outline-none"
                      style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)", color: "var(--text-primary)" }}
                      onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); aiDescribe(); } }} />
                    <button type="button" onClick={aiDescribe} disabled={translating}
                      className="px-3 rounded-xl text-xs font-bold flex-shrink-0 disabled:opacity-50 flex items-center gap-1.5"
                      style={{ background: "#8B5CF6", color: "#fff" }}>
                      {translating ? <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                      {t("Admin.generate_2", lang)}
                    </button>
                  </div>
                </div>

                {/* Names */}
                <div>
                  <div className="text-xs font-semibold mb-2" style={{ color: "var(--text-secondary)" }}>{t("Admin.product_name", lang)}</div>
                  <div className="space-y-2">
                    {[["name_en","🇬🇧 English","EN"],["name_ru","🇷🇺 Русский","RU"],["name_he","🇮🇱 עברית","HE"]].map(([field,label,ph]) => (
                      <div key={field} className="flex items-center gap-2">
                        <span className="text-[10px] w-16 flex-shrink-0 font-semibold" style={{ color: "var(--text-secondary)" }}>{label}</span>
                        <Input value={form[field]} onChange={e => setForm({ ...form, [field]: e.target.value })}
                          placeholder={ph} className="flex-1 h-9 text-sm"
                          style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }}
                          data-testid={field === "name_en" ? "product-name-en" : undefined} />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Descriptions */}
                <div>
                  <div className="text-xs font-semibold mb-2" style={{ color: "var(--text-secondary)" }}>{t("Admin.description", lang)}</div>
                  <div className="space-y-2">
                    {[["desc_en","🇬🇧 English"],["desc_ru","🇷🇺 Русский"],["desc_he","🇮🇱 עברית"]].map(([field,label]) => (
                      <div key={field} className="flex items-start gap-2">
                        <span className="text-[10px] w-16 flex-shrink-0 font-semibold mt-2.5" style={{ color: "var(--text-secondary)" }}>{label}</span>
                        <Textarea value={form[field]} onChange={e => setForm({ ...form, [field]: e.target.value })}
                          className="flex-1 text-xs min-h-[56px] resize-y"
                          style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }} />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Bundle items */}
                {isBundle && (
                  <div className="rounded-2xl p-4 space-y-3" style={{ background: "var(--surface-1)", border: "1px solid var(--border-color)" }}>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-bold" style={{ color: "#8B5CF6" }}>{t("Admin.bundle_items", lang)}</span>
                      <button type="button" onClick={addBundleItem} className="text-[11px] px-2.5 py-1 rounded-lg font-semibold" style={{ background: "rgba(139,92,246,0.12)", color: "#8B5CF6" }}>+ {t("Admin.add", lang)}</button>
                    </div>
                    <div className="space-y-2">
                      {form.bundle_items.map((bi,i) => (
                        <div key={i} className="flex gap-2 items-center">
                          <select value={bi.product_id} onChange={e=>updateBundleItem(i,"product_id",e.target.value)} className="flex-1 h-9 rounded-xl px-2 text-xs outline-none" style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)", color: "var(--text-primary)" }}>
                            <option value="">— {t("Admin.select_product", lang)} —</option>
                            {nonBundleProducts.map(p=><option key={p.product_id} value={p.product_id}>{p.name?.en||p.product_id} ({p.category})</option>)}
                          </select>
                          <Input type="number" placeholder="qty" value={bi.quantity} onChange={e=>updateBundleItem(i,"quantity",e.target.value)} className="w-20 text-xs h-9" style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }} />
                          <button type="button" onClick={()=>removeBundleItem(i)} className="w-8 h-8 flex items-center justify-center rounded-xl hover:text-red-400" style={{ color: "var(--text-secondary)" }}><Trash2 className="w-3.5 h-3.5"/></button>
                        </div>
                      ))}
                      {form.bundle_items.length === 0 && <p className="text-xs py-1" style={{ color: "var(--text-secondary)" }}>{t("Admin.no_items_yet", lang)}</p>}
                    </div>
                  </div>
                )}
              </div>

              {/* ── Right: settings ── */}
              <div className="p-6 space-y-4">

                {/* Category */}
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-semibold mb-1.5 block" style={{ color: "var(--text-secondary)" }}>{t("Admin.category", lang)}</label>
                  <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full h-10 rounded-xl px-3 text-sm outline-none" style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)", color: "var(--text-primary)" }}>
                    {categoryOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </select>
                </div>

                {!isBundle && (<>
                  {/* Platform + Target mode */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] uppercase tracking-wider font-semibold mb-1.5 block" style={{ color: "var(--text-secondary)" }}>{t("Admin.platform", lang)}</label>
                      <select value={form.platform} onChange={e => setForm({ ...form, platform: e.target.value })} className="w-full h-9 rounded-xl px-3 text-xs outline-none" style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)", color: "var(--text-primary)" }}>
                        {platformOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider font-semibold mb-1.5 block" style={{ color: "var(--text-secondary)" }}>{t("Admin.link_type", lang)}</label>
                      <select value={form.target_mode} onChange={e => setForm({ ...form, target_mode: e.target.value })} className="w-full h-9 rounded-xl px-3 text-xs outline-none" style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)", color: "var(--text-primary)" }}>
                        {targetModeOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                      </select>
                    </div>
                  </div>

                  {/* Pricing */}
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-semibold mb-1.5 block" style={{ color: "var(--text-secondary)" }}>{t("Admin.pricing", lang)}</label>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { label:"iP/1000", field:"ipoints_per_1000", testid:"product-price" },
                        { label:t("Admin.min", lang), field:"min_quantity" },
                        { label:t("Admin.max", lang), field:"max_quantity" },
                      ].map(({label,field,testid}) => (
                        <div key={field}>
                          <div className="text-[10px] mb-1" style={{ color: "var(--text-secondary)" }}>{label}</div>
                          <Input type="number" step={field === "ipoints_per_1000" ? "0.01" : "1"} value={form[field]} onChange={e => setForm({ ...form, [field]: e.target.value })} className="h-9 text-xs" style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }} data-testid={testid} />
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Provider */}
                  <div>
                    <label className="text-[10px] uppercase tracking-wider font-semibold mb-1.5 block" style={{ color: "var(--text-secondary)" }}>{t("Admin.provider", lang)}</label>
                    <div className="flex gap-2 mb-2">
                      {[{val:"followsale",label:"Follow.Sale",color:"#c084fc"},{val:"jap",label:"JAP",color:"#38bdf8"}].map(p=>(
                        <button key={p.val} type="button" className="flex-1 h-9 rounded-xl text-xs font-bold border transition-all"
                          onClick={()=>setForm(f=>({...f,provider:p.val,provider_service_id:f.provider===p.val?f.provider_service_id:"0"}))}
                          style={form.provider===p.val?{borderColor:p.color,color:p.color,background:`rgba(${p.val==="jap"?"56,189,248":"192,132,252"},0.1)`}:{borderColor:"var(--border-color)",color:"var(--text-secondary)",background:"transparent"}}>
                          {p.label}
                        </button>
                      ))}
                    </div>
                    <Input type="number" placeholder="Service ID" value={form.provider_service_id} onChange={e=>setForm({...form,provider_service_id:e.target.value})} className="h-9 text-xs" style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }} />
                  </div>

                  {/* Link Regex */}
                  {(() => {
                    const enabled = form.link_regex_enabled;
                    const urlInfo = PLATFORM_URL_INFO[form.target_mode];
                    let regexValid = null;
                    if (enabled && form.link_regex) {
                      try { new RegExp(form.link_regex); regexValid = true; }
                      catch { regexValid = false; }
                    }
                    const regexBorder = regexValid === true ? "rgba(34,197,94,0.35)"
                      : regexValid === false ? "rgba(239,68,68,0.45)"
                      : "var(--border-color)";
                    let matchResult = null;
                    if (enabled && regexTestUrl && form.link_regex && regexValid === true) {
                      try { matchResult = new RegExp(form.link_regex).test(regexTestUrl); }
                      catch { matchResult = false; }
                    }
                    const testPlaceholder = urlInfo?.examples?.[0] || t("Admin.paste_a_url_to_test", lang);
                    return (
                      <div className="rounded-xl overflow-hidden" style={{ border: "1px solid var(--border-color)" }}>
                        {/* Header row with toggle */}
                        <div className="flex items-center justify-between px-3 py-2" style={{ background: "var(--surface-2)", borderBottom: "1px solid var(--border-color)" }}>
                          <span className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: enabled ? "var(--text-primary)" : "var(--text-secondary)" }}>
                            {t("Admin.link_url_validation", lang)}
                          </span>
                          <div className="flex items-center gap-2">
                            {enabled && (
                              <>
                                {urlInfo && (
                                  <button type="button" onClick={() => setForm(f => ({ ...f, link_regex: urlInfo.regex }))}
                                    className="text-[10px] px-2 py-0.5 rounded-lg font-bold flex items-center gap-1"
                                    style={{ background: "rgba(34,197,94,0.10)", color: "#22c55e" }}>
                                    <Check className="w-2.5 h-2.5" /> Use
                                  </button>
                                )}
                                <button type="button" onClick={aiGenRegex} disabled={aiRegexLoading}
                                  className="text-[10px] px-2 py-0.5 rounded-lg font-bold flex items-center gap-1 disabled:opacity-50"
                                  style={{ background: "rgba(139,92,246,0.12)", color: "#8B5CF6" }}>
                                  {aiRegexLoading ? <div className="w-2.5 h-2.5 border border-[#8B5CF6]/30 border-t-[#8B5CF6] rounded-full animate-spin" /> : <Sparkles className="w-2.5 h-2.5" />}
                                  AI
                                </button>
                              </>
                            )}
                            <button type="button" dir="ltr"
                              onClick={() => setForm(f => {
                                const next = !f.link_regex_enabled;
                                const autoRegex = next && !f.link_regex && PLATFORM_URL_INFO[f.target_mode]?.regex;
                                return { ...f, link_regex_enabled: next, ...(autoRegex ? { link_regex: autoRegex } : {}) };
                              })}
                              className="w-8 h-4 rounded-full transition-colors flex-shrink-0"
                              style={{ background: enabled ? "#8B5CF6" : "var(--surface-3)" }}>
                              <div className={`w-3 h-3 bg-white rounded-full transition-transform mt-[2px] mx-[2px] ${enabled ? "translate-x-4" : "translate-x-0"}`} />
                            </button>
                          </div>
                        </div>

                        {/* Valid URL formats — always visible */}
                        {urlInfo && (
                          <div className="px-3 py-2.5" style={{ background: "var(--surface-1)", borderBottom: enabled ? "1px solid var(--border-color)" : "none" }}>
                            <p className="text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: "var(--text-secondary)" }}>
                              Valid URL formats
                            </p>
                            <ul className="space-y-1">
                              {urlInfo.examples.map((ex, i) => (
                                <li key={i} className="flex items-center gap-2 text-[11px] font-mono" style={{ color: "var(--text-primary)" }}>
                                  <span style={{ color: "#22c55e", flexShrink: 0 }}>•</span>
                                  <span className="break-all">{ex}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {enabled && (
                          <div className="p-3 space-y-2">
                            {/* Regex input */}
                            <input value={form.link_regex} onChange={e => setForm({ ...form, link_regex: e.target.value })}
                              placeholder={urlInfo?.regex || "^https?://(www\\.)?instagram\\.com/nickname/?$"}
                              className="w-full h-9 rounded-xl px-3 text-xs outline-none font-mono"
                              style={{ background: "var(--surface-2)", border: `1px solid ${regexBorder}`, color: "var(--text-primary)" }} />
                            {regexValid === false && (
                              <div className="text-[10px] px-1" style={{ color: "#f87171" }}>{t("Admin.invalid_regex_syntax", lang)}</div>
                            )}

                            {/* Test URL row */}
                            <div className="flex gap-2 items-center">
                              <input value={regexTestUrl} onChange={e => setRegexTestUrl(e.target.value)}
                                placeholder={testPlaceholder}
                                className="flex-1 h-8 rounded-lg px-2.5 text-xs outline-none font-mono"
                                style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)", color: "var(--text-primary)" }} />
                              {matchResult !== null && (
                                <span className="flex items-center gap-1 h-8 px-2.5 rounded-lg text-[11px] font-bold flex-shrink-0"
                                  style={{ background: matchResult ? "rgba(34,197,94,0.12)" : "rgba(239,68,68,0.1)", color: matchResult ? "#22c55e" : "#f87171", border: `1px solid ${matchResult ? "rgba(34,197,94,0.25)" : "rgba(239,68,68,0.25)"}` }}>
                                  {matchResult ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                                  {matchResult ? t("Admin.match", lang) : t("Admin.no_match", lang)}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Custom comments toggle */}
                  <div className="flex items-center justify-between rounded-xl px-3 py-2.5" style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)" }}>
                    <div>
                      <div className="text-xs font-medium" style={{ color: "var(--text-primary)" }}>{t("Admin.custom_comments", lang)}</div>
                      <div className="text-[10px]" style={{ color: "var(--text-secondary)" }}>{t("Admin.user_enters_comment_text", lang)}</div>
                    </div>
                    <button type="button" dir="ltr" onClick={() => setForm({ ...form, requires_custom_comments: !form.requires_custom_comments })} className="w-10 h-5 rounded-full transition-colors flex-shrink-0" style={{ background: form.requires_custom_comments ? "#f59e0b" : "var(--surface-3)" }}>
                      <div className={`w-3.5 h-3.5 bg-white rounded-full transition-transform mt-[3px] mx-[3px] ${form.requires_custom_comments ? "translate-x-5" : "translate-x-0"}`} />
                    </button>
                  </div>

                  {/* Emoji Options */}
                  <div className="rounded-xl overflow-hidden" style={{ border: "1px solid var(--border-color)" }}>
                    <div className="flex items-center justify-between px-3 py-2" style={{ background: "var(--surface-2)", borderBottom: form.emoji_options?.length > 0 ? "1px solid var(--border-color)" : "none" }}>
                      <div>
                        <span className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: "var(--text-primary)" }}>Emoji Reactions</span>
                        <span className="text-[10px] ms-2" style={{ color: "var(--text-secondary)" }}>each emoji → own service ID</span>
                      </div>
                      <button type="button"
                        onClick={() => setForm(f => ({ ...f, emoji_options: [...(f.emoji_options||[]), { emoji:"", label:"", service_id:"" }] }))}
                        className="text-[10px] px-2 py-0.5 rounded-lg font-bold"
                        style={{ background: "rgba(139,92,246,0.12)", color: "#8B5CF6" }}>
                        + Add
                      </button>
                    </div>
                    {(form.emoji_options||[]).length > 0 && (
                      <div className="p-2 space-y-1.5">
                        {(form.emoji_options||[]).map((opt, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <input value={opt.emoji} onChange={e => setForm(f => ({ ...f, emoji_options: f.emoji_options.map((o,j) => j===i ? {...o, emoji:e.target.value} : o) }))}
                              placeholder="😍"
                              className="w-12 h-8 rounded-lg text-center text-base outline-none"
                              style={{ background:"var(--surface-2)", border:"1px solid var(--border-color)", color:"var(--text-primary)" }} />
                            <input value={opt.label} onChange={e => setForm(f => ({ ...f, emoji_options: f.emoji_options.map((o,j) => j===i ? {...o, label:e.target.value} : o) }))}
                              placeholder="Love"
                              className="flex-1 h-8 rounded-lg px-2.5 text-xs outline-none"
                              style={{ background:"var(--surface-2)", border:"1px solid var(--border-color)", color:"var(--text-primary)" }} />
                            <input value={opt.service_id} onChange={e => setForm(f => ({ ...f, emoji_options: f.emoji_options.map((o,j) => j===i ? {...o, service_id:e.target.value} : o) }))}
                              placeholder="Service ID"
                              className="w-24 h-8 rounded-lg px-2.5 text-xs outline-none font-mono"
                              style={{ background:"var(--surface-2)", border:"1px solid var(--border-color)", color:"var(--text-primary)" }} />
                            <button type="button" onClick={() => setForm(f => ({ ...f, emoji_options: f.emoji_options.filter((_,j) => j!==i) }))}
                              className="w-7 h-7 flex items-center justify-center rounded-lg hover:text-red-400 flex-shrink-0"
                              style={{ color:"var(--text-secondary)" }}>
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>)}

                {/* Bundle-only pricing */}
                {isBundle && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] uppercase tracking-wider font-semibold mb-1.5 block" style={{ color: "var(--text-secondary)" }}>{t("Admin.fixed_ipoints", lang)}</label>
                      <Input type="number" step="0.01" value={form.total_ipoints} onChange={e => setForm({ ...form, total_ipoints: e.target.value })} className="h-9 text-xs" style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }} data-testid="product-price" />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider font-semibold mb-1.5 block" style={{ color: "var(--text-secondary)" }}>{t("Admin.max_post_links", lang)}</label>
                      <Input type="number" min="1" max="10" value={form.max_split_posts} onChange={e => setForm({ ...form, max_split_posts: e.target.value })} className="h-9 text-xs" style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }} />
                    </div>
                  </div>
                )}

                {/* Admin note */}
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-semibold mb-1.5 block" style={{ color: "var(--text-secondary)" }}>{t("Admin.admin_note_internal", lang)}</label>
                  <textarea value={form.admin_note} onChange={e => setForm({ ...form, admin_note: e.target.value })} rows={2}
                    placeholder={t("Admin.e_g_service_changed_nov_2024", lang)}
                    className="w-full rounded-xl px-3 py-2 text-xs resize-none outline-none"
                    style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)", color: "var(--text-primary)" }} />
                </div>

              </div>
            </div>
          </DialogContent>
        </Dialog>
        </div>{/* close flex wrapper with sync btn */}
      </div>

      {/* Product list — grouped by platform/service */}
      {(() => {
        if (products.length === 0) return (
          <div className="rounded-2xl border py-12 text-center text-sm" style={{ borderColor: "var(--border-color)", color: "var(--text-secondary)" }}>
            {t("Admin.no_products_yet", lang)}
          </div>
        );

        const PLAT_META = {
          instagram: { label: "Instagram", color: "#E1306C",  emoji: "📸" },
          tiktok:    { label: "TikTok",    color: "#69C9D0",  emoji: "🎵" },
          youtube:   { label: "YouTube",   color: "#FF0000",  emoji: "▶️" },
          telegram:  { label: "Telegram",  color: "#2AABEE",  emoji: "✈️" },
          twitch:    { label: "Twitch",    color: "#9146FF",  emoji: "🎮" },
          facebook:  { label: "Facebook",  color: "#1877F2",  emoji: "📘" },
          twitter:   { label: "X / Twitter", color: "#9CA3AF", emoji: "𝕏" },
          linkedin:  { label: "LinkedIn",  color: "#0A66C2",  emoji: "💼" },
          kick:      { label: "Kick",      color: "#53FC18",  emoji: "🟢" },
          discord:   { label: "Discord",   color: "#5865F2",  emoji: "🎧" },
          snapchat:  { label: "Snapchat",  color: "#FFFC00",  emoji: "👻" },
          spotify:   { label: "Spotify",   color: "#1DB954",  emoji: "🎧" },
          bundle:    { label: t("Admin.bundles", lang), color: "#c084fc", emoji: "📦" },
        };

        const CAT_LABELS = {
          followers:   { label: t("shop.followers", lang),      color: "#8B5CF6" },
          likes:       { label: t("shop.likes", lang),          color: "#E1306C" },
          views:       { label: t("Shop.views", lang),          color: "#38bdf8" },
          comments:    { label: t("Shop.comments", lang),       color: "#f59e0b" },
          story_views: { label: t("Admin.story_views_2", lang), color: "#06b6d4" },
          story_likes: { label: t("Shop.story_likes", lang),    color: "#f43f5e" },
          page_likes:  { label: t("Admin.page_likes_2", lang),  color: "#3b82f6" },
          reactions:   { label: "Reactions",                    color: "#f59e0b" },
          live_views:  { label: "Live Views",                   color: "#ef4444" },
          members:     { label: "Members",                      color: "#2AABEE" },
        };

        // Group by platform (bundles get their own group)
        const groups = {};
        products.forEach(p => {
          const key = p.category === "bundle" ? "bundle" : (p.platform || "instagram");
          if (!groups[key]) groups[key] = [];
          groups[key].push(p);
        });

        const ORDER = ["instagram","tiktok","youtube","telegram","twitch","facebook","twitter","linkedin","kick","discord","snapchat","spotify","bundle"];
        const sortedEntries = Object.entries(groups).sort(([a],[b]) => {
          const ai = ORDER.indexOf(a), bi = ORDER.indexOf(b);
          return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
        });

        // Shared grid: name | type | iP | range | provider | status | actions
        const GRID = "1fr 90px 64px 96px 96px 72px 68px";

        return sortedEntries.map(([plat, items]) => {
          const meta = PLAT_META[plat] || { label: plat, color: "#8B5CF6", emoji: "📦" };
          const isBundle = plat === "bundle";
          return (
            <div key={plat} className="rounded-2xl overflow-hidden" style={{ border: `1px solid var(--border-color)` }}>

              {/* ── Group header ── */}
              <div className="flex items-center gap-2.5 px-4 py-2.5" style={{ background: `${meta.color}0F`, borderBottom: `1px solid var(--border-color)` }}>
                <span className="text-sm leading-none">{meta.emoji}</span>
                <span className="text-xs font-black uppercase tracking-widest" style={{ color: meta.color }}>{meta.label}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold" style={{ background: `${meta.color}20`, color: meta.color }}>{items.length}</span>
              </div>

              {/* ── Column header ── */}
              <div className="hidden sm:grid items-center px-4 py-1.5" style={{ gridTemplateColumns: GRID, borderBottom: `1px solid var(--border-color)`, background: "var(--surface-1)" }}>
                <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "var(--text-secondary)" }}>{t("Admin.product", lang)}</span>
                <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "var(--text-secondary)" }}>{t("Admin.type", lang)}</span>
                <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "var(--text-secondary)" }}>{isBundle?"iP":"iP/1K"}</span>
                <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "var(--text-secondary)" }}>{t("Admin.range", lang)}</span>
                <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "var(--text-secondary)" }}>{t("Admin.provider", lang)}</span>
                <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "var(--text-secondary)" }}>{t("order.status", lang)}</span>
                <span />
              </div>

              {/* ── Rows ── */}
              {items.map((p, idx) => {
                const cm = CAT_LABELS[p.category];
                return (
                  <div key={p.product_id}
                    className="sm:grid flex flex-wrap items-center gap-2 px-4 py-2.5 transition-colors hover:bg-white/[0.025]"
                    style={{ gridTemplateColumns: GRID, borderTop: idx === 0 ? "none" : `1px solid var(--border-color)` }}
                    data-testid={`admin-product-${p.product_id}`}
                  >
                    {/* Name */}
                    <div className="flex items-center gap-2 min-w-0">
                      <div className="w-0.5 h-6 rounded-full flex-shrink-0" style={{ background: meta.color, opacity: 0.6 }} />
                      <div className="min-w-0">
                        <div className="text-sm font-semibold truncate" style={{ color: "var(--text-primary)" }}>{p.name?.[lang] || p.name?.en}</div>
                        {isBundle && (p.bundle_items||[]).length > 0 && (
                          <div className="text-[10px] truncate mt-0.5" style={{ color: "var(--text-secondary)" }}>
                            {(p.bundle_items||[]).map((bi,i) => { const bp = products.find(x=>x.product_id===bi.product_id); return (i?", ":"")+(bp?.name?.en||bi.product_id)+" ×"+bi.quantity; }).join("")}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Type */}
                    <div className="sm:flex hidden">
                      {!isBundle && cm
                        ? <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold truncate" style={{ background: `${cm.color}18`, color: cm.color }}>{cm.label}</span>
                        : !isBundle ? <span className="text-[10px]" style={{ color: "var(--text-secondary)" }}>{p.category||"—"}</span>
                        : null}
                      {p.requires_custom_comments && <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold ms-1" style={{ background: "rgba(245,158,11,0.1)", color: "#f59e0b" }}>CMT</span>}
                    </div>

                    {/* iP */}
                    <div className="hidden sm:block text-sm font-semibold font-outfit" style={{ color: "#F77737" }}>
                      {isBundle ? `${p.total_ipoints}` : `${p.ipoints_per_1000}`}
                    </div>

                    {/* Range */}
                    <div className="hidden sm:block text-[11px]" style={{ color: "var(--text-secondary)" }}>
                      {isBundle
                        ? `≤${p.max_split_posts||1} posts`
                        : `${p.min_quantity?.toLocaleString()}–${(p.max_quantity||0)>=10000?(Math.round(p.max_quantity/1000)+"K"):p.max_quantity}`}
                    </div>

                    {/* Provider */}
                    <div className="hidden sm:flex items-center gap-1">
                      {!isBundle && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-bold"
                          style={p.provider==="jap"
                            ? {background:"rgba(56,189,248,0.12)",color:"#38bdf8"}
                            : {background:"rgba(192,132,252,0.12)",color:"#c084fc"}}>
                          {p.provider==="jap"?"JAP":"FS"}
                          {p.provider_service_id>0 && <span className="opacity-50 ms-0.5">#{p.provider_service_id}</span>}
                        </span>
                      )}
                    </div>

                    {/* Status */}
                    <div className="hidden sm:block">
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
                        style={{ background: p.is_active?"rgba(34,197,94,0.1)":"rgba(239,68,68,0.1)", color: p.is_active?"#22c55e":"#ef4444" }}>
                        {p.is_active ? t("Admin.active_4", lang) : t("Admin.off", lang)}
                      </span>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 ms-auto sm:ms-0">
                      <button onClick={() => openEdit(p)} className="w-7 h-7 rounded-lg flex items-center justify-center transition-colors hover:bg-white/5" style={{ color: "var(--text-secondary)" }} data-testid={`edit-product-${p.product_id}`}><PenSquare className="w-3.5 h-3.5" /></button>
                      <button onClick={() => del(p.product_id)} className="w-7 h-7 rounded-lg flex items-center justify-center transition-colors hover:bg-red-500/10 hover:text-red-400" style={{ color: "var(--text-secondary)" }}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        });
      })()}
    </div>
  );
}

// ===== USERS =====
function UserDetailsContent({
  details, lang, currentUser,
  detailsUserId, allOrders, allOrdersLoading,
  expandedOrderId, setExpandedOrderId,
  expandedTicketId, setExpandedTicketId,
  editUser, setEditUser,
  adjustSign, setAdjustSign, newBal, setNewBal, adjustReason, setAdjustReason,
  blockBusy, pendingBlock, setPendingBlock, blockReason, setBlockReason,
  orderStatusLabel, topupStatusLabel, ticketStatusLabel,
  fetchDetails, loadAllOrders, adjustPoints, toggleBlock, resetGift, setRole,
  setDetailsOpen, onOpenTicket, renderRoleBadge,
}) {
  const u = details.user || {};
  const [permModalOpen, setPermModalOpen] = useState(false);
  const orderSC = (s) => ({
    draft:           { color: "#94a3b8", bg: "var(--border-color)" },
    approved:        { color: "#7aa2ff", bg: "rgba(64,93,230,0.14)" },
    processing:      { color: "#F77737", bg: "rgba(247,119,55,0.14)" },
    completed:       { color: "#22c55e", bg: "rgba(34,197,94,0.14)" },
    rejected:        { color: "#f87171", bg: "rgba(239,68,68,0.14)" },
    cancelled_admin: { color: "#9ca3af", bg: "rgba(156,163,175,0.1)" },
  }[s] || { color: "#facc15", bg: "rgba(250,204,21,0.12)" });
  const isVIP = Number(details.topup_stats?.approved_amount_ils || 0) >= (details.vip_threshold_ils || 100);
  const uAccent = u.is_blocked ? "#ef4444" : u.role === "super_admin" ? "#F77737" : u.role === "admin" ? "#c084fc" : isVIP ? "#22c55e" : "#E1306C";
  const history = details.ipoints_history || details.ipoints_ledger || [];
  return (
    <div>
      <div className="h-1 w-full" style={{ background: uAccent, opacity: 0.7 }} />
      <div className="px-5 py-4" style={{ borderBottom: "1px solid var(--border-color)" }}>
        <div className="flex items-center gap-4">
          <div className="relative flex-shrink-0">
            <div className="p-[2px] rounded-xl" style={{ background: uAccent }}>
              {u.picture
                ? <img src={u.picture} alt="" className="w-14 h-14 rounded-xl object-cover block" />
                : <div className="w-14 h-14 rounded-xl flex items-center justify-center font-outfit font-black text-2xl" style={{ background: "var(--surface-1)", color: uAccent }}>{(u.name || u.email || "?")[0].toUpperCase()}</div>}
            </div>
            {u.is_blocked && <div className="absolute -top-1.5 -end-1.5 px-1.5 py-0.5 rounded-full text-[8px] font-black leading-tight" style={{ background: "#ef4444", color: "#fff" }}>✕</div>}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-0.5">
              <span className="text-lg font-bold font-outfit leading-tight truncate">{u.name || u.email}</span>
              {renderRoleBadge(u)}
              {isVIP && <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold" style={{ background: "rgba(34,197,94,0.12)", color: "#22c55e", border: "1px solid rgba(34,197,94,0.25)" }}>💎 VIP</span>}
              {u.is_blocked && <span className="text-[10px] px-1.5 py-0.5 rounded-full font-black" style={{ background: "rgba(239,68,68,0.15)", color: "#ef4444" }}>BLOCKED</span>}
            </div>
            <div className="text-xs mb-2" style={{ color: "var(--text-secondary)" }}>{u.email}</div>
            <div className="flex flex-wrap gap-1">
              {u.instagram_username && <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-lg" style={{ background: "rgba(225,48,108,0.1)", color: "#E1306C", border: "1px solid rgba(225,48,108,0.2)" }}><Instagram className="w-2.5 h-2.5 flex-shrink-0" />@{u.instagram_username}</span>}
              <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-lg" style={{ background: "var(--surface-2)", color: "var(--text-secondary)", border: "1px solid var(--border-color)" }}><Crown className="w-2.5 h-2.5 flex-shrink-0" />{u.xp || 0} XP · {u.level_info?.level?.name?.[lang] || u.level_info?.level?.name?.en || "—"}</span>
              <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-lg" style={{ background: "var(--surface-2)", color: "var(--text-secondary)", border: "1px solid var(--border-color)" }}><Calendar className="w-2.5 h-2.5 flex-shrink-0" />{formatDateTime(u.created_at)}</span>
              {u.last_login_at && <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-lg" style={{ background: "var(--surface-2)", color: "var(--text-secondary)", border: "1px solid var(--border-color)" }}><Clock className="w-2.5 h-2.5 flex-shrink-0" />{formatDateTime(u.last_login_at)}</span>}
              {u.last_active_at && <span className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-lg font-medium" style={{ background: "rgba(34,197,94,0.08)", color: "#22c55e", border: "1px solid rgba(34,197,94,0.2)" }}><span className="w-1.5 h-1.5 rounded-full bg-[#22c55e] animate-pulse flex-shrink-0" />{formatDateTime(u.last_active_at)}{u.last_page && <span className="opacity-70 ms-1 font-mono text-[10px]">{u.last_page}</span>}</span>}
            </div>
          </div>
          <div className="flex flex-col gap-1.5 flex-shrink-0">
            <button onClick={() => { fetchDetails(detailsUserId); setAllOrders(null); }} className="h-8 px-3 text-xs rounded-xl border flex items-center gap-1.5 transition-all hover:opacity-80 whitespace-nowrap" style={{ borderColor: "var(--border-color)", color: "var(--text-secondary)", background: "var(--surface-1)" }}><RefreshCw className="w-3 h-3" />{t("Admin.refresh", lang)}</button>
            {u.is_blocked
              ? <button disabled={blockBusy || !details.permissions?.can_block} onClick={() => toggleBlock(false)} className="h-8 px-3 text-xs rounded-xl border flex items-center gap-1.5 transition-all hover:opacity-80 whitespace-nowrap font-semibold" style={{ background: "rgba(34,197,94,0.1)", borderColor: "rgba(34,197,94,0.25)", color: "#22c55e" }}><Power className="w-3 h-3" />{t("Admin.unblock", lang)}</button>
              : <button disabled={blockBusy || !details.permissions?.can_block} onClick={() => { setPendingBlock(true); setBlockReason(""); }} className="h-8 px-3 text-xs rounded-xl border flex items-center gap-1.5 transition-all hover:opacity-80 whitespace-nowrap font-semibold" style={{ background: "rgba(239,68,68,0.1)", borderColor: "rgba(239,68,68,0.25)", color: "#f87171" }}><Power className="w-3 h-3" />{t("Admin.block", lang)}</button>}
            {currentUser?.role === "super_admin" && u.user_id !== currentUser?.user_id && u.role !== "super_admin" && (
              <Select value={u.role || "user"} onValueChange={(v) => setRole(u.user_id, v)}>
                <SelectTrigger className="h-8 text-xs w-28" style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }}><SelectValue /></SelectTrigger>
                <SelectContent style={{ background: "var(--bg-secondary)", borderColor: "var(--border-color)" }}>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            )}
            {currentUser?.role === "super_admin" && u.role === "admin" && u.user_id !== currentUser?.user_id && (
              <button onClick={() => setPermModalOpen(true)}
                className="h-8 px-3 text-xs rounded-xl border flex items-center gap-1.5 transition-all hover:opacity-80 whitespace-nowrap font-semibold"
                style={{ background: "rgba(139,92,246,0.1)", borderColor: "rgba(139,92,246,0.3)", color: "#a78bfa" }}>
                <Shield className="w-3 h-3" /> Permissions
              </button>
            )}
          </div>
        </div>
        {u.is_blocked && u.blocked_reason && <div className="mt-3 px-3 py-2 rounded-xl text-xs" style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.18)", color: "#f87171" }}>🚫 {u.blocked_reason}</div>}
        {permModalOpen && (
          <UserPermissionsModal
            userId={u.user_id}
            userName={u.name || u.email || u.user_id}
            onClose={() => setPermModalOpen(false)}
            onSaved={() => fetchDetails(detailsUserId)}
          />
        )}
        {pendingBlock && !u.is_blocked && (
          <div className="mt-3 rounded-xl p-3 space-y-2" style={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.2)" }}>
            <div className="text-xs font-semibold" style={{ color: "#f87171" }}>{t("Admin.block_reason_shown_to_user", lang)}</div>
            <Textarea value={blockReason} onChange={(e) => setBlockReason(e.target.value)} placeholder={t("Admin.enter_reason", lang)} className="text-xs min-h-[52px] resize-none" style={{ background: "var(--surface-2)", borderColor: "rgba(239,68,68,0.25)", color: "var(--text-primary)" }} rows={2} />
            <div className="flex gap-2">
              <button onClick={() => toggleBlock(true)} disabled={blockBusy} className="flex-1 h-8 rounded-xl text-xs font-bold transition-all hover:opacity-90" style={{ background: "rgba(239,68,68,0.2)", border: "1px solid rgba(239,68,68,0.35)", color: "#f87171" }}>{blockBusy ? "…" : t("Admin.confirm_block", lang)}</button>
              <button onClick={() => { setPendingBlock(false); setBlockReason(""); }} className="h-8 px-4 rounded-xl text-xs font-semibold transition-all hover:opacity-80" style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)", color: "var(--text-secondary)" }}>{t("Admin.cancel", lang)}</button>
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-wrap divide-x px-0" style={{ borderBottom: "1px solid var(--border-color)", borderColor: "var(--border-color)" }}>
        <div className="px-5 py-3 flex flex-col justify-center" style={{ minWidth: editUser === u.user_id ? 260 : 120 }}>
          <div className="text-[10px] uppercase tracking-wider font-semibold mb-0.5" style={{ color: "var(--text-secondary)" }}>{t("Admin.balance", lang)}</div>
          {editUser === u.user_id
            ? <div className="space-y-1.5" onClick={(e) => e.stopPropagation()}>
                <div className="text-xs font-semibold" style={{ color: "var(--text-secondary)" }}>{t("Admin.now_2", lang)}: <span style={{ color: "#F77737" }}>{(u.ipoints || 0).toLocaleString()} iP</span></div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => setAdjustSign("+")} className="w-7 h-7 rounded-lg font-bold text-sm flex items-center justify-center transition-all" style={{ background: adjustSign === "+" ? "rgba(34,197,94,0.2)" : "var(--surface-2)", color: adjustSign === "+" ? "#22c55e" : "var(--text-secondary)", border: `1px solid ${adjustSign === "+" ? "rgba(34,197,94,0.35)" : "var(--border-color)"}` }}>+</button>
                  <button onClick={() => setAdjustSign("-")} className="w-7 h-7 rounded-lg font-bold text-sm flex items-center justify-center transition-all" style={{ background: adjustSign === "-" ? "rgba(239,68,68,0.2)" : "var(--surface-2)", color: adjustSign === "-" ? "#f87171" : "var(--text-secondary)", border: `1px solid ${adjustSign === "-" ? "rgba(239,68,68,0.35)" : "var(--border-color)"}` }}>−</button>
                  <input type="number" min="1" value={newBal} onChange={(e) => setNewBal(e.target.value)} placeholder="0" className="w-20 rounded-lg px-2 py-1 text-sm text-center outline-none font-outfit font-bold" style={{ background: "var(--surface-2)", border: `1px solid ${adjustSign === "+" ? "rgba(34,197,94,0.3)" : "rgba(239,68,68,0.3)"}`, color: adjustSign === "+" ? "#22c55e" : "#f87171" }} autoFocus />
                  <span className="text-xs font-bold" style={{ color: "var(--text-secondary)" }}>iP</span>
                </div>
                <input value={adjustReason} onChange={(e) => setAdjustReason(e.target.value)} placeholder={t("Admin.reason_optional", lang)} className="w-full rounded-lg px-2 py-1 text-xs outline-none" style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)", color: "var(--text-primary)" }} />
                <div className="flex gap-1.5">
                  <button onClick={() => adjustPoints(u.user_id)} className="flex-1 h-7 rounded-lg text-xs font-bold transition-all" style={{ background: adjustSign === "+" ? "rgba(34,197,94,0.2)" : "rgba(239,68,68,0.2)", color: adjustSign === "+" ? "#22c55e" : "#f87171", border: `1px solid ${adjustSign === "+" ? "rgba(34,197,94,0.35)" : "rgba(239,68,68,0.35)"}` }}>{t("dash.apply", lang)}</button>
                  <button onClick={() => { setEditUser(null); setNewBal(""); setAdjustReason(""); }} className="h-7 px-3 rounded-lg text-xs transition-all" style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)", color: "var(--text-secondary)" }}>{t("Admin.cancel", lang)}</button>
                </div>
              </div>
            : <button onClick={() => { setEditUser(u.user_id); setNewBal(""); setAdjustSign("+"); }} className="font-black font-outfit text-lg leading-tight hover:opacity-60 transition-opacity flex items-baseline gap-1" style={{ color: "#F77737", fontVariantNumeric: "tabular-nums" }}>{(u.ipoints || 0).toLocaleString()} <span className="text-xs font-bold">iP</span> <PenSquare className="w-2.5 h-2.5 opacity-30" /></button>}
        </div>
        {[
          { label: t("Admin.top_ups", lang), value: `${details.topup_stats?.approved_amount_ils || 0} ₪`, sub: `${details.topup_stats?.approved || 0}×`, color: "#22c55e" },
          { label: t("admin.orders", lang), value: details.order_stats?.total || 0, sub: `${details.order_stats?.active || 0} ${t("Admin.active_7", lang)}`, color: "#E1306C" },
          { label: t("Admin.tickets", lang), value: details.ticket_stats?.total || 0, sub: `${details.ticket_stats?.open || 0} ${t("Admin.open_3", lang)}`, color: (details.ticket_stats?.open || 0) > 0 ? "#7aa2ff" : "var(--text-primary)" },
          { label: t("nav.tasks", lang), value: details.task_stats?.approved || 0, sub: null, color: "var(--text-primary)" },
          { label: t("Admin.referrals", lang), value: details.referral_stats?.referral_count || 0, sub: null, color: "var(--text-primary)" },
          { label: t("shop.followers", lang), value: (details.followers_ordered || 0).toLocaleString(), sub: null, color: "var(--text-primary)" },
        ].map((item, i) => (
          <div key={i} className="px-5 py-3 flex flex-col justify-center min-w-[90px]" style={{ borderColor: "var(--border-color)" }}>
            <div className="text-[10px] uppercase tracking-wider font-semibold mb-0.5" style={{ color: "var(--text-secondary)" }}>{item.label}</div>
            <div className="text-lg font-black font-outfit leading-tight" style={{ color: item.color, fontVariantNumeric: "tabular-nums" }}>{item.value}</div>
            {item.sub && <div className="text-[10px]" style={{ color: "var(--text-secondary)" }}>{item.sub}</div>}
          </div>
        ))}
      </div>
      {(u.free_followers_claimed || u.free_likes_claimed) && (
        <div className="px-5 py-2.5 flex items-center gap-2 flex-wrap border-b" style={{ borderColor: "var(--border-color)" }}>
          <span className="text-xs font-semibold" style={{ color: "var(--text-secondary)" }}>{t("Admin.free_gifts", lang)}:</span>
          {[{ key: "free_followers_claimed", label: t("shop.followers", lang), type: "followers" }, { key: "free_likes_claimed", label: t("shop.likes", lang), type: "likes" }].map(({ key, label, type }) => u[key] ? (
            <div key={key} className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg" style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.18)", color: "#f87171" }}>
              <span>{label}</span>
              <button onClick={() => resetGift(u.user_id, type)} className="opacity-60 hover:opacity-100 transition-opacity"><RotateCcw className="w-3 h-3" /></button>
            </div>
          ) : null)}
        </div>
      )}
      <div className="grid lg:grid-cols-[1fr_300px]">
        <div className="p-5 border-r" style={{ borderColor: "var(--surface-2)" }}>
          <div className="flex items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-sm">{t("admin.orders", lang)}</h3>
              <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: "var(--surface-2)", color: "var(--text-secondary)" }}>{allOrders ? allOrders.length : details.recent_orders?.length || 0}/{details.order_stats?.total || 0}</span>
            </div>
            {!allOrders && (details.order_stats?.total || 0) > (details.recent_orders?.length || 0) && (
              <button onClick={() => loadAllOrders(detailsUserId)} disabled={allOrdersLoading} className="text-xs px-3 py-1.5 rounded-xl border font-semibold transition-all hover:opacity-80" style={{ borderColor: "rgba(64,93,230,0.3)", color: "#7aa2ff", background: "rgba(64,93,230,0.1)" }}>{allOrdersLoading ? "…" : t("Admin.load_all", lang)}</button>
            )}
          </div>
          <div className="space-y-2">
            {(allOrders || details.recent_orders || []).length ? (allOrders || details.recent_orders).map((order) => {
              const sc = orderSC(order.status);
              const isOpen = expandedOrderId === order.order_id;
              return (
                <div key={order.order_id} className="rounded-2xl overflow-hidden" style={{ background: "var(--surface-1)", border: `1px solid ${isOpen ? sc.color+"55" : "var(--surface-2)"}` }}>
                  <button className="w-full px-4 py-3 flex items-start gap-3 text-start hover:bg-white/[0.03] transition-colors" onClick={() => setExpandedOrderId(isOpen ? null : order.order_id)}>
                    <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ background: sc.color }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <span className="font-bold font-outfit text-sm">#{order.order_number || "—"}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold flex-shrink-0" style={{ background: sc.bg, color: sc.color }}>{orderStatusLabel(order.status)}</span>
                          <ChevronDown className="w-3.5 h-3.5 transition-transform flex-shrink-0" style={{ color: "var(--text-secondary)", transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }} />
                        </div>
                      </div>
                      <div className="text-sm mt-1 flex gap-2 flex-wrap" style={{ color: "var(--text-primary)" }}>
                        {(order.items || []).map((item) => <span key={item.category} className="text-xs font-medium">{item.category === "followers" ? t("Admin.followers", lang) : t("shop.likes", lang)} {(item.quantity || 0).toLocaleString()}</span>)}
                      </div>
                      <div className="flex flex-wrap gap-3 mt-1.5 text-[11px]" style={{ color: "var(--text-secondary)" }}>
                        <span>{order.is_free_gift ? "🎁" : `${(order.total_ipoints || 0).toLocaleString()} iP`}</span>
                        <span>{formatIsraelDate(order.created_at)}</span>
                        {order.provider_sync_status === "partial" ? <span className="font-bold px-1.5 py-0.5 rounded-md" style={{ background: "rgba(168,85,247,0.18)", color: "#a855f7" }}>⚡ Partial{order.provider_progress?.remains_total > 0 ? ` (−${order.provider_progress.remains_total})` : ""}</span>
                          : order.provider_sync_status && order.provider_sync_status !== "not_configured" ? <span className="opacity-60">{order.provider_sync_status}</span> : null}
                      </div>
                    </div>
                  </button>
                  {isOpen && (
                    <div className="px-4 pb-4 space-y-3 border-t" style={{ borderColor: "var(--surface-2)" }}>
                      <div className="pt-3 space-y-2">
                        {(order.items || []).map((item, idx) => {
                          const itemName = item.product_name?.ru || item.product_name?.en || item.product_name?.he || item.category || "—";
                          const link = item.instagram_link || order.instagram_link;
                          const link2 = item.instagram_link_2 || order.instagram_link_2;
                          const extraLinks = item.instagram_links_extra || order.instagram_links_extra || [];
                          return (
                            <div key={idx} className="rounded-xl p-3 text-xs" style={{ background: "var(--surface-2)" }}>
                              <div className="font-semibold mb-1">{itemName} × {(item.quantity || 0).toLocaleString()}</div>
                              {link && <div className="truncate" style={{ color: "var(--text-secondary)" }}>🔗 {link}</div>}
                              {link2 && <div className="truncate" style={{ color: "var(--text-secondary)" }}>🔗 {link2}</div>}
                              {extraLinks.map((el, ei) => <div key={ei} className="truncate" style={{ color: "var(--text-secondary)" }}>🔗 {el}</div>)}
                              {order.is_free_gift ? <div>🎁</div> : item.ipoints_cost > 0 && <div style={{ color: "#a855f7" }}>{item.ipoints_cost.toLocaleString()} iP</div>}
                            </div>
                          );
                        })}
                      </div>
                      {(order.provider_orders || []).length > 0 && (
                        <div className="space-y-1.5">
                          <div className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: "var(--text-secondary)" }}>Provider</div>
                          {order.provider_orders.map((po, pi) => (
                            <div key={pi} className="rounded-xl px-3 py-2 text-[11px]" style={{ background: "var(--surface-1)" }}>
                              <span className="font-mono">{po.provider_order_id || "—"}</span>
                              {po.status && <span className="ml-2 opacity-60">{po.status}</span>}
                              {po.remains != null && <span className="ml-2 opacity-60">remains: {po.remains}</span>}
                            </div>
                          ))}
                        </div>
                      )}
                      {order.is_free_gift && <div className="text-[11px] px-2 py-1 rounded-lg inline-block" style={{ background: "rgba(34,197,94,0.12)", color: "#22c55e" }}>🎁 Free gift</div>}
                      {order.ipoints_refunded && <div className="text-[11px] px-2 py-1 rounded-lg inline-block" style={{ background: "rgba(168,85,247,0.12)", color: "#a855f7" }}>↩ iPoints refunded</div>}
                    </div>
                  )}
                </div>
              );
            }) : <div className="text-center py-8 text-sm" style={{ color: "var(--text-secondary)" }}>{t("Admin.no_orders_yet", lang)}</div>}
          </div>
        </div>
        <div className="flex flex-col">
          <div className="p-5 border-b" style={{ borderColor: "var(--surface-2)" }}>
            <div className="flex items-center gap-2 mb-3">
              <h3 className="font-bold text-sm">{t("Admin.top_ups", lang)}</h3>
              <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: "var(--surface-2)", color: "var(--text-secondary)" }}>{details.recent_topups?.length || 0}</span>
            </div>
            <div>
              {details.recent_topups?.length ? details.recent_topups.map((topup) => (
                <div key={topup.topup_id} className="flex items-center justify-between py-2 border-b last:border-0" style={{ borderColor: "var(--surface-2)" }}>
                  <div>
                    <div className="text-sm font-bold">{topup.amount_ils || 0} ₪</div>
                    <div className="text-[11px]" style={{ color: "var(--text-secondary)" }}>{topup.ipoints_amount || 0} iP · {formatIsraelDate(topup.submitted_at || topup.created_at)}</div>
                  </div>
                  <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold flex-shrink-0" style={{ background: topup.status === "approved" ? "rgba(34,197,94,0.14)" : "var(--surface-2)", color: topup.status === "approved" ? "#22c55e" : "var(--text-secondary)" }}>{topupStatusLabel(topup.status)}</span>
                </div>
              )) : <div className="text-sm py-2" style={{ color: "var(--text-secondary)" }}>{t("Admin.no_top_ups_yet", lang)}</div>}
            </div>
          </div>
          <div className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <h3 className="font-bold text-sm">{t("Admin.tickets", lang)}</h3>
              <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: "var(--surface-2)", color: "var(--text-secondary)" }}>{details.ticket_stats?.total || 0}</span>
            </div>
            <div className="space-y-1.5">
              {details.recent_tickets?.length ? details.recent_tickets.map((ticket) => {
                const tsc = { open: { color: "#facc15", bg: "rgba(250,204,21,0.12)" }, in_progress: { color: "#60a5fa", bg: "rgba(59,130,246,0.12)" }, closed: { color: "#22c55e", bg: "rgba(34,197,94,0.12)" } }[ticket.status] || { color: "#facc15", bg: "rgba(250,204,21,0.12)" };
                const isOpenT = expandedTicketId === ticket.ticket_id;
                const msgs = ticket.messages || [];
                return (
                  <div key={ticket.ticket_id} className="rounded-2xl overflow-hidden" style={{ background: "var(--surface-1)", border: `1px solid ${isOpenT ? tsc.color+"40" : "var(--surface-2)"}` }}>
                    <button className="w-full px-3 py-2.5 flex items-start gap-2 text-start hover:bg-white/[0.03] transition-colors" onClick={() => setExpandedTicketId(isOpenT ? null : ticket.ticket_id)}>
                      <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ background: tsc.color }} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-semibold truncate">{ticket.subject || t("Admin.support_ticket", lang)}</span>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: tsc.bg, color: tsc.color }}>{ticketStatusLabel(ticket.status)}</span>
                            <ChevronDown className="w-3 h-3 transition-transform flex-shrink-0" style={{ color: "var(--text-secondary)", transform: isOpenT ? "rotate(180deg)" : "rotate(0deg)" }} />
                          </div>
                        </div>
                        <div className="text-[11px] mt-0.5 flex gap-3" style={{ color: "var(--text-secondary)" }}>
                          <span>{formatIsraelDate(ticket.updated_at || ticket.created_at)}</span>
                          {msgs.length > 0 && <span>{msgs.length} msg</span>}
                        </div>
                      </div>
                    </button>
                    {isOpenT && (
                      <div className="border-t px-3 pb-3 pt-2 space-y-2" style={{ borderColor: "var(--surface-2)" }}>
                        {msgs.length ? msgs.map((msg, mi) => {
                          const isAdmin = msg.sender_type === "admin" || msg.sender_type === "note";
                          const isSystem = msg.sender_type === "system";
                          return (
                            <div key={mi} className={`flex ${isAdmin ? "justify-end" : isSystem ? "justify-center" : "justify-start"}`}>
                              {isSystem
                                ? <div className="text-[10px] px-2 py-1 rounded-lg" style={{ background: "var(--surface-2)", color: "var(--text-secondary)" }}>{msg.content}</div>
                                : <div className="max-w-[85%]">
                                    <div className={`px-3 py-2 rounded-xl text-xs ${isAdmin ? "text-end" : ""}`} style={{ background: isAdmin ? "rgba(139,92,246,0.15)" : "var(--surface-2)", color: "var(--text-primary)", border: `1px solid ${isAdmin ? "rgba(139,92,246,0.25)" : "var(--surface-2)"}` }}>
                                      {msg.content}
                                      {msg.attachments?.length > 0 && <div className="flex flex-wrap gap-1 mt-1">{msg.attachments.map((att, ai) => <a key={ai} href={att.url} target="_blank" rel="noreferrer" className="text-[10px] underline opacity-60">{att.filename || `img ${ai+1}`}</a>)}</div>}
                                    </div>
                                    <div className={`text-[9px] mt-0.5 px-1 ${isAdmin ? "text-end" : ""}`} style={{ color: "var(--text-secondary)" }}>{isAdmin ? (msg.sender_name || "Support") : "User"} · {formatIsraelDateTime(msg.created_at)}</div>
                                  </div>}
                            </div>
                          );
                        }) : <div className="text-xs py-1" style={{ color: "var(--text-secondary)" }}>{t("Admin.no_messages_yet", lang)}</div>}
                        <button className="text-xs flex items-center gap-1 mt-1 opacity-50 hover:opacity-80 transition-opacity" onClick={() => { setDetailsOpen(false); onOpenTicket?.(ticket.ticket_id); }} style={{ color: "var(--text-secondary)" }}><ExternalLink className="w-3 h-3" />{t("Admin.open_in_tickets", lang)}</button>
                      </div>
                    )}
                  </div>
                );
              }) : <div className="text-sm py-2" style={{ color: "var(--text-secondary)" }}>{t("Admin.no_tickets_yet", lang)}</div>}
            </div>
          </div>
        </div>
      </div>
      <div className="grid md:grid-cols-2 xl:grid-cols-4 border-t" style={{ borderColor: "var(--border-color)" }}>
        <div className="p-5 space-y-3 border-r" style={{ borderColor: "var(--surface-2)" }}>
          <h3 className="font-bold text-sm mb-4">{t("Admin.registration_referral", lang)}</h3>
          <div className="text-sm">
            {[
              { label: t("Admin.registered", lang), value: formatDateTime(details.registration?.created_at) },
              { label: t("Admin.last_login_2", lang), value: details.registration?.last_login_at ? formatDateTime(details.registration.last_login_at) : "—" },
              { label: "IP", value: details.registration?.ip || "—", mono: true },
              { label: t("Admin.auth_3", lang), value: details.registration?.auth_provider || "—" },
              { label: t("Admin.ref_code", lang), value: details.referral_stats?.referral_code || "—", mono: true },
              { label: t("Admin.referrals_2", lang), value: details.referral_stats?.referral_count || 0 },
              { label: t("Admin.referred_by_2", lang), value: details.referral_stats?.referred_by_user ? (details.referral_stats.referred_by_user.name || details.referral_stats.referred_by_user.email) : t("Admin.direct", lang) },
              { label: t("Admin.level_bonus", lang), value: `+${details.topup_bonus_profile?.level_bonus_pct || 0}%` },
              { label: t("Admin.large_topup", lang), value: `+${details.topup_bonus_profile?.large_topup_bonus_pct || 0}% from ${details.topup_bonus_profile?.large_topup_bonus_threshold_ils || 0}₪` },
            ].map(({ label, value, mono }) => (
              <div key={label} className="flex items-start justify-between gap-3 py-1.5 border-b" style={{ borderColor: "var(--surface-2)" }}>
                <span className="text-[11px] flex-shrink-0" style={{ color: "var(--text-secondary)" }}>{label}</span>
                <span className={`text-[11px] font-medium text-end break-all ${mono ? "font-mono" : ""}`}>{value}</span>
              </div>
            ))}
            {details.registration?.user_agent && <div className="text-[10px] mt-2 line-clamp-2 opacity-40">{details.registration.user_agent}</div>}
          </div>
        </div>
        <div className="p-5 space-y-3 border-r" style={{ borderColor: "var(--surface-2)" }}>
          <h3 className="font-bold text-sm mb-4">{t("Admin.task_activity", lang)}</h3>
          <div className="flex gap-6 mb-4">
            <div><div className="text-[11px]" style={{ color: "var(--text-secondary)" }}>{t("Admin.approved", lang)}</div><div className="text-base font-black font-outfit">{details.task_stats?.approved || 0}</div></div>
            <div><div className="text-[11px]" style={{ color: "var(--text-secondary)" }}>{t("Admin.earned", lang)}</div><div className="text-base font-black font-outfit">{details.task_stats?.reward_given_ipoints || 0} iP</div></div>
          </div>
          <div>
            {details.recent_task_submissions?.length ? details.recent_task_submissions.map((submission) => (
              <div key={submission.submission_id} className="py-2 border-b last:border-0" style={{ borderColor: "var(--surface-2)" }}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <TaskTypeIcon type={submission.task_type} className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "var(--text-secondary)" }} />
                    <span className="text-sm font-semibold truncate">{submission.task_title?.[lang] || submission.task_title?.en || submission.task_id}</span>
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full flex-shrink-0" style={{ background: submission.status === "approved" ? "rgba(34,197,94,0.14)" : submission.status === "rejected" ? "rgba(239,68,68,0.14)" : "rgba(250,204,21,0.12)", color: submission.status === "approved" ? "#22c55e" : submission.status === "rejected" ? "#f87171" : "#facc15" }}>{submission.status}</span>
                </div>
                <div className="text-[11px] mt-0.5 flex flex-wrap gap-3" style={{ color: "var(--text-secondary)" }}>
                  <span>{formatIsraelDateTime(submission.created_at)}</span>
                  {!!submission.reward_given && <span>+{submission.reward_given} iP</span>}
                </div>
              </div>
            )) : <div className="text-sm py-2" style={{ color: "var(--text-secondary)" }}>{t("Admin.no_task_submissions_yet_2", lang)}</div>}
          </div>
        </div>
        <div className="p-5 space-y-3 border-r" style={{ borderColor: "var(--surface-2)" }}>
          <h3 className="font-bold text-sm mb-4">{t("Admin.promo_codes_2", lang)}</h3>
          <div className="flex gap-6 mb-4">
            <div><div className="text-[11px]" style={{ color: "var(--text-secondary)" }}>{t("Admin.used", lang)}</div><div className="text-base font-black font-outfit">{details.promo_stats?.total || 0}</div></div>
            <div><div className="text-[11px]" style={{ color: "var(--text-secondary)" }}>{t("Admin.bonus_earned", lang)}</div><div className="text-base font-black font-outfit">{details.promo_stats?.bonus_ipoints || 0} iP</div></div>
          </div>
          <div>
            {details.recent_promo_uses?.length ? details.recent_promo_uses.map((promoUse, index) => (
              <div key={`${promoUse.promo_id}-${index}`} className="flex items-center justify-between py-1.5 border-b last:border-0" style={{ borderColor: "var(--surface-2)" }}>
                <div>
                  <span className="text-sm font-semibold font-mono">{promoUse.code || promoUse.promo_id || "—"}</span>
                  <div className="text-[11px]" style={{ color: "var(--text-secondary)" }}>{formatIsraelDateTime(promoUse.created_at)}</div>
                </div>
                <span className="text-[11px] font-semibold flex-shrink-0" style={{ color: "#22c55e" }}>+{promoUse.bonus || 0} iP</span>
              </div>
            )) : <div className="text-sm py-2" style={{ color: "var(--text-secondary)" }}>{t("Admin.no_promo_usage_yet_2", lang)}</div>}
          </div>
        </div>
        <div className="p-5 space-y-3">
          <h3 className="font-bold text-sm mb-4">{t("Admin.achievements", lang)}</h3>
          <div>
          </div>
        </div>
      </div>
      <div className="p-5 border-t" style={{ borderColor: "var(--surface-2)" }}>
        <h3 className="font-bold text-sm mb-4">{t("Admin.ipoints_history_2", lang)}</h3>
        <IpointsHistoryPanel lang={lang} history={history} meta={details.ipoints_history_meta} balance={details.user?.ipoints || 0} />
      </div>
      <div className="p-5 border-t" style={{ borderColor: "var(--surface-2)" }}>
        <div className="flex items-center gap-2 mb-4">
          <h3 className="font-bold text-sm">{t("Admin.recent_referrals_2", lang)}</h3>
          <span className="text-xs font-normal px-2 py-0.5 rounded-full" style={{ background: "rgba(131,58,180,0.12)", color: "#c084fc" }}>{details.referral_stats?.referral_count || 0}</span>
        </div>
        {(details.referral_stats?.recent_referrals || []).length ? (
          <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
            {details.referral_stats.recent_referrals.map((refUser) => (
              <div key={refUser.user_id} className="rounded-2xl px-3 py-3 flex items-center gap-3" style={{ background: "var(--surface-1)", border: "1px solid var(--surface-2)" }}>
                {refUser.picture ? <img src={refUser.picture} alt="" className="w-10 h-10 rounded-xl object-cover flex-shrink-0" /> : <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 font-bold" style={{ background: "rgba(131,58,180,0.16)", color: "#c084fc" }}>{(refUser.name || refUser.email || "?")[0].toUpperCase()}</div>}
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">{refUser.name || refUser.email}</div>
                  <div className="text-[11px] truncate" style={{ color: "var(--text-secondary)" }}>{refUser.email}</div>
                  <div className="text-[11px] mt-0.5" style={{ color: "var(--text-secondary)" }}>{formatIsraelDateTime(refUser.created_at)}</div>
                </div>
              </div>
            ))}
          </div>
        ) : <div className="text-sm" style={{ color: "var(--text-secondary)" }}>{t("Admin.no_referrals_yet_2", lang)}</div>}
      </div>
      {(details.admin_messages?.length > 0) && (
        <div className="p-5 border-t" style={{ borderColor: "var(--surface-2)" }}>
          <div className="flex items-center gap-2 mb-4">
            <h3 className="font-bold text-sm">{t("Admin.messages_from_admin", lang)}</h3>
            <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: "var(--surface-2)", color: "var(--text-secondary)" }}>{details.admin_messages.length}</span>
          </div>
          <div className="space-y-2">
            {details.admin_messages.map((msg) => (
              <div key={msg.message_id || msg._id} className="rounded-xl px-4 py-3" style={{ background: "var(--surface-1)", border: "1px solid var(--border-color)" }}>
                <div className="flex items-start justify-between gap-3 mb-1">
                  <span className="text-sm font-semibold">{(msg.subject?.[lang] || msg.subject?.en || (typeof msg.subject === "string" ? msg.subject : null) || msg.title?.[lang] || msg.title?.en || (typeof msg.title === "string" ? msg.title : null) || t("Admin.message", lang))}</span>
                  <span className="text-[10px] flex-shrink-0" style={{ color: "var(--text-secondary)" }}>{formatIsraelDateTime(msg.created_at)}</span>
                </div>
                {(msg.body || msg.content) && <div className="text-xs leading-relaxed" style={{ color: "var(--text-secondary)" }}>{msg.content?.[lang] || msg.content?.en || (typeof msg.content === "string" ? msg.content : null) || msg.body?.[lang] || msg.body?.en || (typeof msg.body === "string" ? msg.body : null)}</div>}
                {msg.sender_name && <div className="text-[10px] mt-1.5 font-medium" style={{ color: "#8B5CF6" }}>{msg.sender_name}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
      {(details.admin_actions?.length > 0) && (
        <div className="p-5 border-t" style={{ borderColor: "var(--surface-2)" }}>
          <div className="flex items-center gap-2 mb-4">
            <h3 className="font-bold text-sm">{t("Admin.admin_actions", lang)}</h3>
            <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: "var(--surface-2)", color: "var(--text-secondary)" }}>{details.admin_actions.length}</span>
          </div>
          <div className="space-y-2">
            {details.admin_actions.map((act) => {
              const actionMeta = {
                block:        { label: t("Admin.blocked_2", lang),          color: "#ef4444", bg: "rgba(239,68,68,0.08)",    border: "rgba(239,68,68,0.2)" },
                unblock:      { label: t("Admin.unblocked", lang), color: "#22c55e", bg: "rgba(34,197,94,0.08)",    border: "rgba(34,197,94,0.2)" },
                set_role:     { label: t("Admin.role_changed", lang), color: "#c084fc", bg: "rgba(192,132,252,0.08)", border: "rgba(192,132,252,0.2)" },
                set_balance:  { label: t("Admin.set_balance", lang), color: "#F77737", bg: "rgba(247,119,55,0.08)", border: "rgba(247,119,55,0.2)" },
                award_points: { label: t("Admin.adjusted_ip", lang),  color: "#F77737", bg: "rgba(247,119,55,0.08)", border: "rgba(247,119,55,0.2)" },
                note:         { label: t("Admin.note_2", lang),                   color: "#9B9BB0", bg: "var(--surface-1)",        border: "var(--border-color)" },
              }[act.action_type] || { label: act.action_type, color: "var(--text-secondary)", bg: "var(--surface-1)", border: "var(--border-color)" };
              return (
                <div key={act.action_id} className="rounded-xl px-3 py-2.5 flex items-start gap-3" style={{ background: actionMeta.bg, border: `1px solid ${actionMeta.border}` }}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-bold" style={{ color: actionMeta.color }}>{actionMeta.label}</span>
                      {act.action_type === "set_role" && act.details && <span className="text-xs" style={{ color: "var(--text-secondary)" }}>{act.details.old_role} → {act.details.new_role}</span>}
                      {act.action_type === "set_balance" && act.details && <span className="text-xs" style={{ color: "var(--text-secondary)" }}>{act.details.old_balance} → {act.details.new_balance} iP ({act.details.delta >= 0 ? "+" : ""}{act.details.delta})</span>}
                      {act.action_type === "award_points" && act.details && <span className="text-xs" style={{ color: "var(--text-secondary)" }}>{act.details.delta >= 0 ? "+" : ""}{act.details.delta} iP</span>}
                      {act.action_type === "block" && act.details?.reason && <span className="text-xs truncate max-w-xs" style={{ color: "var(--text-secondary)" }}>"{act.details.reason}"</span>}
                    </div>
                    <div className="text-[10px] mt-0.5 flex gap-2" style={{ color: "var(--text-secondary)" }}>
                      <span>{act.admin_name || act.admin_email}</span><span>·</span><span>{formatIsraelDateTime(act.created_at)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function UsersTab({ lang, currentUser, onUpdate, onOpenTicket }) {
  const [users, setUsers] = useState([]);
  const [editUser, setEditUser] = useState(null);
  const [newBal, setNewBal] = useState("");
  const [adjustSign, setAdjustSign] = useState("+");
  const [adjustReason, setAdjustReason] = useState("");
  const [search, setSearch] = useState("");
  const [sortMode, setSortMode] = useState("registered_desc");
  const [roleFilter, setRoleFilter] = useState("all");
  const [accountStateFilter, setAccountStateFilter] = useState("all");
  const [minIpoints, setMinIpoints] = useState("");
  const [maxIpoints, setMaxIpoints] = useState("");
  const [minReferrals, setMinReferrals] = useState("");
  const [minTasks, setMinTasks] = useState("");
  const [minOrders, setMinOrders] = useState("");
  const [minTopupAmount, setMinTopupAmount] = useState("");
  const [registeredFrom, setRegisteredFrom] = useState("");
  const [registeredTo, setRegisteredTo] = useState("");
  const [quickFlags, setQuickFlags] = useState({
    hasReferrals: false,
    hasTasks: false,
    hasOpenTickets: false,
    hasActiveOrders: false,
    hasTopups: false,
  });
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [details, setDetails] = useState(null);
  const [detailsUserId, setDetailsUserId] = useState("");
  const [blockReason, setBlockReason] = useState("");
  const [blockBusy, setBlockBusy] = useState(false);
  const [pendingBlock, setPendingBlock] = useState(false);
  const [allOrders, setAllOrders] = useState(null);
  const [allOrdersLoading, setAllOrdersLoading] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [expandedTicketId, setExpandedTicketId] = useState(null);
  const [orderCodeQuery, setOrderCodeQuery] = useState("");
  const [orderCodeResult, setOrderCodeResult] = useState(null); // { order, user }
  const [orderCodeLoading, setOrderCodeLoading] = useState(false);
  const [orderCodeError, setOrderCodeError] = useState("");

  const searchByOrderCode = async (code) => {
    const q = (code || orderCodeQuery).trim();
    if (!q) return;
    setOrderCodeLoading(true);
    setOrderCodeResult(null);
    setOrderCodeError("");
    try {
      const r = await fetch(`${API}/admin/orders/find-user?q=${encodeURIComponent(q)}`, { credentials: "include" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Not found");
      setOrderCodeResult(d);
    } catch (e) {
      setOrderCodeError(e.message);
    } finally {
      setOrderCodeLoading(false);
    }
  };

  const loadAllOrders = useCallback(async (userId) => {
    if (!userId) return;
    setAllOrdersLoading(true);
    try {
      const r = await fetch(`${API}/admin/users/${userId}/orders`, { credentials: "include" });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Failed");
      setAllOrders(data);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setAllOrdersLoading(false);
    }
  }, []);

  const [usersPage, setUsersPage] = useState(1);
  const [usersTotalPages, setUsersTotalPages] = useState(1);
  const [usersTotal, setUsersTotal] = useState(0);
  const USERS_PAGE_SIZE = 50;

  const fetch_ = useCallback(async (pg = usersPage) => {
    const params = new URLSearchParams({ limit: String(USERS_PAGE_SIZE), page: String(pg) });
    if (search.trim()) params.set("q", search.trim());
    const r = await fetch(`${API}/admin/users?${params}`, { credentials: "include" });
    if (!r.ok) return;
    const data = await r.json();
    setUsers(data.users || []);
    setUsersTotalPages(data.pages || 1);
    setUsersTotal(data.total || 0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, usersPage]);

  const fetchDetails = useCallback(async (userId) => {
    if (!userId) return;
    setDetailsLoading(true);
    try {
      const r = await fetch(`${API}/admin/users/${userId}/details`, { credentials: "include" });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Failed");
      setDetails(data);
      setBlockReason(data.user?.blocked_reason || "");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDetailsLoading(false);
    }
  }, []);

  useEffect(() => { setUsersPage(1); }, [search]);
  useEffect(() => { fetch_(usersPage); }, [fetch_, usersPage]);

  const setRole = async (uid, role) => {
    try {
      const r = await fetch(`${API}/admin/users/${uid}/set-role`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ role }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail);
      toast.success(d.message);
      fetch_();
      if (detailsUserId === uid) fetchDetails(uid);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const resetGift = async (uid, type) => {
    const r = await fetch(`${API}/admin/users/${uid}/reset-gift`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ type }),
    });
    if (r.ok) {
      toast.success(t("Admin.gift_reset", lang));
      fetch_();
      if (detailsUserId === uid) fetchDetails(uid);
    }
  };

  const adjustPoints = async (uid) => {
    const amount = parseInt(newBal, 10);
    if (!newBal || isNaN(amount) || amount <= 0) return;
    const delta = adjustSign === "+" ? amount : -amount;
    try {
      const r = await fetch(`${API}/admin/users/award-points`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ user_id: uid, points: delta, reason: adjustReason.trim() || undefined }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail);
      toast.success(`${delta > 0 ? "+" : ""}${delta} iP`);
      setEditUser(null);
      setNewBal("");
      setAdjustReason("");
      fetch_();
      if (detailsUserId === uid) fetchDetails(uid);
      onUpdate();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const openDetails = async (userId) => {
    setDetailsUserId(userId);
    setDetailsOpen(true);
    setAllOrders(null);
    setPendingBlock(false);
    await fetchDetails(userId);
  };

  const toggleBlock = async (blocked) => {
    if (!detailsUserId) return;
    setBlockBusy(true);
    try {
      const r = await fetch(`${API}/admin/users/${detailsUserId}/block`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ blocked, reason: blocked ? blockReason : "" }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Failed");
      toast.success(data.message);
      setDetails(data.details);
      setBlockReason(data.details?.user?.blocked_reason || "");
      setPendingBlock(false);
      fetch_();
      onUpdate();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBlockBusy(false);
    }
  };

  const roleLabel = (role) => role === "super_admin"
    ? "⭐ Super Admin"
    : role === "admin"
      ? "🛡 Admin"
      : "user";
  const orderStatusLabel = (status) => ({
    draft: t("Admin.draft", lang),
    approved: t("Admin.sent", lang),
    processing: t("Admin.processing", lang),
    completed: t("Admin.completed", lang),
    rejected: t("Admin.rejected", lang),
    cancelled_admin: t("Admin.cancelled", lang),
  }[status] || status || "—");
  const topupStatusLabel = (status) => ({
    draft: t("Admin.draft", lang),
    pending: t("Admin.pending", lang),
    approved: t("order.approved", lang),
    rejected: t("order.rejected", lang),
  }[status] || status || "—");
  const ticketStatusLabel = (status) => ({
    open: t("Admin.open", lang),
    in_progress: t("Admin.in_progress_8", lang),
    closed: t("Admin.closed", lang),
  }[status] || status || "—");
  const q = search.trim().toLowerCase();
  const fromTs = registeredFrom ? Date.parse(`${registeredFrom}T00:00:00`) : null;
  const toTs = registeredTo ? Date.parse(`${registeredTo}T23:59:59.999`) : null;
  const minIpValue = minIpoints === "" ? null : Number(minIpoints);
  const maxIpValue = maxIpoints === "" ? null : Number(maxIpoints);
  const minReferralValue = minReferrals === "" ? null : Number(minReferrals);
  const minTasksValue = minTasks === "" ? null : Number(minTasks);
  const minOrdersValue = minOrders === "" ? null : Number(minOrders);
  const minTopupAmountValue = minTopupAmount === "" ? null : Number(minTopupAmount);
  const visible = [...users]
    .filter((u) => {
      // name/email search is server-side; other filters remain local
      if ((u.role || "user") === "super_admin" && roleFilter !== "super_admin") return false;
      if (roleFilter !== "all" && (u.role || "user") !== roleFilter) return false;
      if (accountStateFilter === "blocked" && !u.is_blocked) return false;
      if (accountStateFilter === "active" && u.is_blocked) return false;
      const metrics = u.metrics || {};
      const balanceValue = Number(u.ipoints || 0);
      if (minIpValue !== null && Number.isFinite(minIpValue) && balanceValue < minIpValue) return false;
      if (maxIpValue !== null && Number.isFinite(maxIpValue) && balanceValue > maxIpValue) return false;
      if (minReferralValue !== null && Number.isFinite(minReferralValue) && Number(metrics.referral_count || 0) < minReferralValue) return false;
      if (minTasksValue !== null && Number.isFinite(minTasksValue) && Number(metrics.tasks_approved || 0) < minTasksValue) return false;
      if (minOrdersValue !== null && Number.isFinite(minOrdersValue) && Number(metrics.orders_total || 0) < minOrdersValue) return false;
      if (minTopupAmountValue !== null && Number.isFinite(minTopupAmountValue) && Number(metrics.topups_approved_amount_ils || 0) < minTopupAmountValue) return false;
      if (quickFlags.hasReferrals && Number(metrics.referral_count || 0) <= 0) return false;
      if (quickFlags.hasTasks && Number(metrics.tasks_approved || 0) <= 0) return false;
      if (quickFlags.hasOpenTickets && Number(metrics.tickets_open || 0) <= 0) return false;
      if (quickFlags.hasActiveOrders && Number(metrics.orders_active || 0) <= 0) return false;
      if (quickFlags.hasTopups && Number(metrics.topups_approved || 0) <= 0) return false;
      const createdTs = Date.parse(u.created_at || "");
      if (fromTs !== null && Number.isFinite(fromTs) && Number.isFinite(createdTs) && createdTs < fromTs) return false;
      if (toTs !== null && Number.isFinite(toTs) && Number.isFinite(createdTs) && createdTs > toTs) return false;
      return true;
    })
    .sort((a, b) => {
      const aMetrics = a.metrics || {};
      const bMetrics = b.metrics || {};
      if (sortMode === "registered_asc") return String(a.created_at || "").localeCompare(String(b.created_at || ""));
      if (sortMode === "ipoints_desc") return Number(b.ipoints || 0) - Number(a.ipoints || 0);
      if (sortMode === "ipoints_asc") return Number(a.ipoints || 0) - Number(b.ipoints || 0);
      if (sortMode === "referrals_desc") return Number(bMetrics.referral_count || 0) - Number(aMetrics.referral_count || 0);
      if (sortMode === "tasks_desc") return Number(bMetrics.tasks_approved || 0) - Number(aMetrics.tasks_approved || 0);
      if (sortMode === "orders_desc") return Number(bMetrics.orders_total || 0) - Number(aMetrics.orders_total || 0);
      if (sortMode === "active_orders_desc") return Number(bMetrics.orders_active || 0) - Number(aMetrics.orders_active || 0);
      if (sortMode === "spent_desc") return Number(bMetrics.orders_spent_ipoints || 0) - Number(aMetrics.orders_spent_ipoints || 0);
      if (sortMode === "spent_asc") return Number(aMetrics.orders_spent_ipoints || 0) - Number(bMetrics.orders_spent_ipoints || 0);
      if (sortMode === "topups_amount_desc") return Number(bMetrics.topups_approved_amount_ils || 0) - Number(aMetrics.topups_approved_amount_ils || 0);
      if (sortMode === "topups_amount_asc") return Number(aMetrics.topups_approved_amount_ils || 0) - Number(bMetrics.topups_approved_amount_ils || 0);
      if (sortMode === "tickets_open_desc") return Number(bMetrics.tickets_open || 0) - Number(aMetrics.tickets_open || 0);
      if (sortMode === "xp_desc") return Number(b.xp || 0) - Number(a.xp || 0);
      if (sortMode === "last_login_desc") return String(b.last_login_at || "").localeCompare(String(a.last_login_at || ""));
      if (sortMode === "name_asc") return String(a.name || a.email || "").localeCompare(String(b.name || b.email || ""));
      if (sortMode === "orders_asc") return Number(aMetrics.orders_total || 0) - Number(bMetrics.orders_total || 0);
      if (sortMode === "referrals_asc") return Number(aMetrics.referral_count || 0) - Number(bMetrics.referral_count || 0);
      if (sortMode === "registered_asc") return String(a.created_at || "").localeCompare(String(b.created_at || ""));
      return String(b.created_at || "").localeCompare(String(a.created_at || ""));
    });

  const toggleQuickFlag = (key) => {
    setQuickFlags((current) => ({ ...current, [key]: !current[key] }));
  };

  const quickFilterItems = [
    { key: "hasReferrals", label: t("Admin.with_referrals", lang), color: "#833AB4" },
    { key: "hasTasks", label: t("Admin.completed_tasks", lang), color: "#F77737" },
    { key: "hasOpenTickets", label: t("Admin.open_tickets_2", lang), color: "#405DE6" },
    { key: "hasActiveOrders", label: t("Admin.active_orders", lang), color: "#E1306C" },
    { key: "hasTopups", label: t("Admin.top_up_users", lang), color: "#22c55e" },
  ];
  const quickSortItems = [
    { key: "registered_desc", label: t("Admin.newest", lang), icon: Clock, color: "#7aa2ff" },
    { key: "ipoints_desc", label: t("Admin.top_ipoints", lang), icon: Coins, color: "#F77737" },
    { key: "referrals_desc", label: t("Admin.top_referrals", lang), icon: UsersIcon, color: "#833AB4" },
    { key: "tasks_desc", label: t("Admin.top_task_users", lang), icon: ClipboardList, color: "#F59E0B" },
    { key: "spent_desc", label: t("Admin.top_spenders", lang), icon: Wallet, color: "#22c55e" },
    { key: "tickets_open_desc", label: t("Admin.needs_support", lang), icon: Ticket, color: "#405DE6" },
  ];
  const activeFilterCount = [
    Boolean(search),
    sortMode !== "registered_desc",
    roleFilter !== "all",
    accountStateFilter !== "all",
    Boolean(minIpoints),
    Boolean(maxIpoints),
    Boolean(minReferrals),
    Boolean(minTasks),
    Boolean(minOrders),
    Boolean(minTopupAmount),
    Boolean(registeredFrom),
    Boolean(registeredTo),
    ...Object.values(quickFlags),
  ].filter(Boolean).length;

  const renderRoleBadge = (u) => {
    if (u.role === "super_admin") return (
      <span className="text-xs px-2.5 py-0.5 rounded-xl flex items-center gap-1.5 font-bold tracking-wide"
        style={{ background: "rgba(247,119,55,0.12)", border: "1px solid rgba(247,119,55,0.3)", color: "#F77737" }}>
        <Crown className="w-3 h-3" style={{ color: "#facc15" }} /> Super Admin
      </span>
    );
    if (u.role === "admin") return (
      <span className="text-xs px-2 py-0.5 rounded-xl flex items-center gap-1" style={{ background: "rgba(131,58,180,0.15)", border: "1px solid rgba(131,58,180,0.25)", color: "#c084fc" }}>
        <Shield className="w-2.5 h-2.5" /> Admin
      </span>
    );
    return (
      <span className="text-xs px-2 py-0.5 rounded-xl flex items-center gap-1" style={{ background: "var(--surface-2)", color: "var(--text-secondary)", border: "1px solid var(--border-color)" }}>
        <User className="w-2.5 h-2.5" /> User
      </span>
    );
  };

  const blockedCount = users.filter(u=>u.is_blocked).length;
  const adminCount = users.filter(u=>u.role==="admin"||u.role==="super_admin").length;

  return (
    <div className="animate-fade-in-up">
      {/* Users header */}
      <div className="rounded-2xl p-5 mb-4" style={{ background: "var(--surface-1)", border: "1px solid var(--border-color)" }}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <AdminIcon emoji="👥" className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-black font-outfit">{t("admin.users", lang)}</h2>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold" style={{ background:"rgba(225,48,108,0.12)", color:"#E1306C" }}>👤 {usersTotal} {t("Admin.total", lang)}</span>
                <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold" style={{ background:"rgba(122,162,255,0.1)", color:"#7aa2ff" }}>🔍 {visible.length} {t("Admin.shown", lang)}</span>
                {blockedCount > 0 && <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold" style={{ background:"rgba(239,68,68,0.12)", color:"#f87171" }}>🚫 {blockedCount} {t("Admin.blocked_3", lang)}</span>}
                {adminCount > 0 && <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold" style={{ background:"rgba(247,119,55,0.12)", color:"#F77737" }}>⭐ {adminCount} {t("Admin.admins", lang)}</span>}
                {activeFilterCount > 0 && <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold" style={{ background:"rgba(131,58,180,0.12)", color:"#c084fc" }}>🔎 {activeFilterCount} {t("Admin.filters", lang)}</span>}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl p-4 mb-4" style={{ background:"var(--surface-1)", border:"1px solid var(--surface-2)" }}>
        {/* Order code search */}
        <div className="flex gap-2 mb-3">
          <div className="relative flex-1">
            <Package className="w-4 h-4 absolute start-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-secondary)" }} />
            <Input value={orderCodeQuery} onChange={(e) => { setOrderCodeQuery(e.target.value); setOrderCodeResult(null); setOrderCodeError(""); }}
              onKeyDown={(e) => e.key === "Enter" && searchByOrderCode()}
              placeholder={t("Admin.search_by_order_code_ord_or_provider_id", lang)}
              className="text-sm h-9 ps-9" style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }} />
          </div>
          <button type="button" onClick={() => searchByOrderCode()} disabled={!orderCodeQuery.trim() || orderCodeLoading}
            className="h-9 px-4 rounded-xl text-xs font-semibold flex items-center gap-1.5 flex-shrink-0 disabled:opacity-40 transition-all"
            style={{ background: "rgba(139,92,246,0.15)", border: "1px solid rgba(139,92,246,0.25)", color: "#a78bfa" }}>
            {orderCodeLoading ? <div className="w-3 h-3 rounded-full border-2 border-purple-300/30 border-t-purple-300 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
            {t("Admin.find", lang)}
          </button>
        </div>
        {/* Order code result */}
        {(orderCodeResult || orderCodeError) && (
          <div className="mb-3 rounded-xl border px-4 py-3 flex items-center gap-3"
            style={{ borderColor: orderCodeResult ? "rgba(34,197,94,0.25)" : "rgba(239,68,68,0.25)", background: orderCodeResult ? "rgba(34,197,94,0.05)" : "rgba(239,68,68,0.05)" }}>
            {orderCodeError ? (
              <><AlertTriangle className="w-4 h-4 flex-shrink-0" style={{ color: "#ef4444" }} /><span className="text-sm" style={{ color: "#f87171" }}>{orderCodeError}</span></>
            ) : orderCodeResult && (
              <>
                <div className="w-9 h-9 rounded-xl flex-shrink-0 flex items-center justify-center text-sm font-black"
                  style={{ background: "rgba(34,197,94,0.15)", color: "#22c55e" }}>
                  {(orderCodeResult.user?.name || orderCodeResult.user?.email || "?")[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold" style={{ color: "var(--text-primary)" }}>{orderCodeResult.user?.name || orderCodeResult.user?.email || "Unknown"}</div>
                  <div className="text-xs" style={{ color: "var(--text-secondary)" }}>{orderCodeResult.user?.email} · {t("Admin.order_2", lang)}: <span dir="ltr">{orderCodeResult.order?.order_id}</span> · {orderCodeResult.order?.status}</div>
                </div>
                <button type="button" onClick={() => openDetails(orderCodeResult.user?.user_id)}
                  className="h-8 px-3 rounded-xl text-xs font-semibold flex-shrink-0"
                  style={{ background: "rgba(34,197,94,0.15)", color: "#22c55e", border: "1px solid rgba(34,197,94,0.25)" }}>
                  {t("Admin.open_4", lang)}
                </button>
              </>
            )}
          </div>
        )}
        {/* Row 1: search + sort */}
        <div className="flex flex-wrap gap-2 mb-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute start-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-secondary)" }} />
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder={{"en":"Search by name or email","ru":"Поиск по имени или email","he":"חיפוש לפי שם או אימייל"}[lang] || "Search by name or email"}
              className="text-sm h-9 ps-9" style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }} />
          </div>
          <Select value={sortMode} onValueChange={setSortMode}>
            <SelectTrigger className="h-9 text-sm w-44" style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }}><SelectValue /></SelectTrigger>
            <SelectContent style={{ background: "var(--bg-secondary)", borderColor: "var(--border-color)" }}>
              <SelectItem value="registered_desc">🕐 {t("Admin.newest_first", lang)}</SelectItem>
              <SelectItem value="registered_asc">🕐 {t("Admin.oldest_first", lang)}</SelectItem>
              <SelectItem value="ipoints_desc">🔥 {t("Admin.top_ipoints", lang)}</SelectItem>
              <SelectItem value="orders_desc">📦 {t("Admin.most_orders", lang)}</SelectItem>
              <SelectItem value="spent_desc">💵 {t("Admin.most_spent", lang)}</SelectItem>
              <SelectItem value="topups_amount_desc">💳 {t("Admin.most_top_ups", lang)}</SelectItem>
              <SelectItem value="tickets_open_desc">🎫 {t("Admin.needs_support", lang)}</SelectItem>
              <SelectItem value="last_login_desc">📅 {t("Admin.recent_logins", lang)}</SelectItem>
              <SelectItem value="name_asc">🔤 {t("Admin.name_a_z", lang)}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {/* Row 2: role + state pill filters */}
        <div className="flex flex-wrap gap-1.5 mb-2">
          {[
            { v:"all", label:t("Admin.all_roles", lang), color:"#9ca3af" },
            { v:"user", label:`👤 ${t("admin.users", lang)}`, color:"#9ca3af" },
            { v:"admin", label:`🛡 Admin`, color:"#c084fc" },
            { v:"super_admin", label:`⭐ Super`, color:"#F77737" },
          ].map(opt => (
            <button key={opt.v} type="button" onClick={() => setRoleFilter(opt.v)}
              className="px-3 py-1 rounded-full text-xs font-semibold transition-all border"
              style={roleFilter===opt.v ? { background:`${opt.color}18`, borderColor:`${opt.color}40`, color:opt.color } : { background:"transparent", borderColor:"var(--surface-3)", color:"var(--text-secondary)" }}>
              {opt.label}
            </button>
          ))}
          <div className="w-px mx-1 self-stretch" style={{ background:"var(--surface-2)" }} />
          {[
            { v:"all", label:t("shop.all", lang), color:"#9ca3af" },
            { v:"active", label:`✅ ${t("Admin.active_5", lang)}`, color:"#22c55e" },
            { v:"blocked", label:`🚫 ${t("Admin.blocked_4", lang)}`, color:"#ef4444" },
          ].map(opt => (
            <button key={opt.v} type="button" onClick={() => setAccountStateFilter(opt.v)}
              className="px-3 py-1 rounded-full text-xs font-semibold transition-all border"
              style={accountStateFilter===opt.v ? { background:`${opt.color}18`, borderColor:`${opt.color}40`, color:opt.color } : { background:"transparent", borderColor:"var(--surface-3)", color:"var(--text-secondary)" }}>
              {opt.label}
            </button>
          ))}
          <div className="ms-auto flex items-center gap-1.5">
            <button type="button" onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="h-7 px-2.5 rounded-xl text-xs font-semibold flex items-center gap-1 transition-all"
              style={{ background: showAdvancedFilters || (minIpoints||maxIpoints||minReferrals||minTasks||minOrders||minTopupAmount||registeredFrom||registeredTo||Object.values(quickFlags).some(Boolean)) ? "rgba(131,58,180,0.15)" : "var(--surface-2)", border:"1px solid var(--surface-3)", color:"var(--text-secondary)" }}>
              <Filter className="w-3 h-3" />{t("Admin.filters_2", lang)}
              {[minIpoints,maxIpoints,minReferrals,minTasks,minOrders,minTopupAmount,registeredFrom,registeredTo,...Object.values(quickFlags)].filter(Boolean).length > 0 && (
                <span className="w-4 h-4 rounded-full bg-[#833AB4] text-white text-[9px] flex items-center justify-center font-black">{[minIpoints,maxIpoints,minReferrals,minTasks,minOrders,minTopupAmount,registeredFrom,registeredTo,...Object.values(quickFlags)].filter(Boolean).length}</span>
              )}
            </button>
            {activeFilterCount > 0 && (
              <button type="button" onClick={() => { setSearch(""); setSortMode("registered_desc"); setRoleFilter("all"); setAccountStateFilter("all"); setMinIpoints(""); setMaxIpoints(""); setMinReferrals(""); setMinTasks(""); setMinOrders(""); setMinTopupAmount(""); setRegisteredFrom(""); setRegisteredTo(""); setQuickFlags({ hasReferrals:false, hasTasks:false, hasOpenTickets:false, hasActiveOrders:false, hasTopups:false }); }}
                className="h-7 px-2.5 rounded-xl text-xs font-semibold"
                style={{ background:"rgba(239,68,68,0.1)", border:"1px solid rgba(239,68,68,0.2)", color:"#f87171" }}>
                ✕ {t("Admin.reset", lang)}
              </button>
            )}
          </div>
        </div>

        {/* Advanced filters */}
        {showAdvancedFilters && (
          <div className="mt-2 pt-3 border-t space-y-3" style={{ borderColor: "var(--surface-2)" }}>
            <div className="flex flex-wrap gap-1.5">
              {quickFilterItems.map((item) => {
                const active = quickFlags[item.key];
                return (
                  <button key={item.key} type="button" onClick={() => toggleQuickFlag(item.key)}
                    className="px-3 py-1.5 rounded-xl text-xs font-semibold transition-all"
                    style={{ background:active?`${item.color}18`:"var(--surface-1)", border:`1px solid ${active?`${item.color}35`:"var(--surface-3)"}`, color:active?item.color:"var(--text-secondary)" }}>
                    {item.label}
                  </button>
                );
              })}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2">
              <Input type="number" min="0" value={minIpoints} onChange={(e) => setMinIpoints(e.target.value)} placeholder={t("Admin.min_ip", lang)} className="text-sm h-8" style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }} />
              <Input type="number" min="0" value={maxIpoints} onChange={(e) => setMaxIpoints(e.target.value)} placeholder={t("Admin.max_ip", lang)} className="text-sm h-8" style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }} />
              <Input type="number" min="0" value={minReferrals} onChange={(e) => setMinReferrals(e.target.value)} placeholder={t("Admin.min_referrals", lang)} className="text-sm h-8" style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }} />
              <Input type="number" min="0" value={minOrders} onChange={(e) => setMinOrders(e.target.value)} placeholder={t("Admin.min_orders", lang)} className="text-sm h-8" style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }} />
              <Input type="number" min="0" value={minTopupAmount} onChange={(e) => setMinTopupAmount(e.target.value)} placeholder={t("Admin.min_top_up", lang)} className="text-sm h-8" style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }} />
              <Input type="number" min="0" value={minTasks} onChange={(e) => setMinTasks(e.target.value)} placeholder={t("Admin.min_tasks", lang)} className="text-sm h-8" style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input type="date" value={registeredFrom} onChange={(e) => setRegisteredFrom(e.target.value)} className="text-sm h-8" style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }} />
              <Input type="date" value={registeredTo} onChange={(e) => setRegisteredTo(e.target.value)} className="text-sm h-8" style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }} />
            </div>
          </div>
        )}
      </div>

      <div className="rounded-2xl overflow-hidden" style={{ border: "1px solid var(--border-color)" }}>
        {/* Table header */}
        {(() => {
          const colSort = (descKey, ascKey) => {
            const active = sortMode === descKey || sortMode === ascKey;
            const isDesc = sortMode === descKey;
            return {
              active,
              arrow: active ? (isDesc ? " ↓" : " ↑") : "",
              toggle: () => setSortMode(active ? (isDesc ? ascKey : descKey) : descKey),
            };
          };
          const ip = colSort("ipoints_desc", "ipoints_asc");
          const sp = colSort("topups_amount_desc", "topups_amount_asc");
          const or = colSort("orders_desc", "orders_asc");
          const rf = colSort("referrals_desc", "referrals_asc");
          const dt = colSort("registered_desc", "registered_asc");
          const hdrStyle = (s) => ({ cursor: "pointer", userSelect: "none", color: s.active ? "var(--text-primary)" : "var(--text-secondary)", transition: "color 0.15s" });
          return (
            <div className="grid px-4 py-2 text-[10px] uppercase tracking-wider font-semibold" style={{ gridTemplateColumns: "1fr 100px 90px 72px 60px 36px 80px", background: "var(--surface-1)", borderBottom: "1px solid var(--border-color)" }}>
              <span style={{ color: "var(--text-secondary)" }}>{t("Admin.user", lang)}</span>
              <span className="text-end hidden sm:block" style={hdrStyle(ip)} onClick={ip.toggle}>iPoints{ip.arrow}</span>
              <span className="text-end hidden md:block" style={hdrStyle(sp)} onClick={sp.toggle}>{t("Admin.spent", lang)}{sp.arrow}</span>
              <span className="text-end hidden md:block" style={hdrStyle(or)} onClick={or.toggle}>{t("admin.orders", lang)}{or.arrow}</span>
              <span className="text-end hidden lg:block" style={hdrStyle(rf)} onClick={rf.toggle}>{t("Admin.refs", lang)}{rf.arrow}</span>
              <span className="text-end hidden lg:block" style={{ color: "var(--text-secondary)" }}>{t("Admin.lang", lang)}</span>
              <span className="text-end hidden lg:block" style={hdrStyle(dt)} onClick={dt.toggle}>{t("Admin.joined_3", lang)}{dt.arrow}</span>
            </div>
          );
        })()}
        {visible.map((u, idx) => {
          const metrics = u.metrics || {};
          const isVip = Number(metrics.topups_approved_amount_ils || 0) >= 100;
          const hasIssues = Number(metrics.tickets_open || 0) > 0;
          const accentColor = u.is_blocked ? "#ef4444" : u.role === "super_admin" ? "#F77737" : u.role === "admin" ? "#c084fc" : isVip ? "#22c55e" : hasIssues ? "#7aa2ff" : "#E1306C";
          return (
            <div key={u.user_id}
              data-testid={`admin-user-${u.user_id}`}
              className="grid px-4 py-2.5 cursor-pointer group transition-colors hover:bg-white/[0.025]"
              style={{ gridTemplateColumns: "1fr 100px 90px 72px 60px 36px 80px", borderTop: idx === 0 ? "none" : "1px solid var(--border-color)", background: u.is_blocked ? "rgba(239,68,68,0.025)" : "transparent" }}
              onClick={() => openDetails(u.user_id)}>

              {/* Identity col */}
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: accentColor }} />
                <div className="flex-shrink-0 relative">
                  {u.picture
                    ? <img src={u.picture} alt="" className="w-8 h-8 rounded-lg object-cover" />
                    : <div className="w-8 h-8 rounded-lg flex items-center justify-center font-outfit font-bold text-sm flex-shrink-0"
                        style={{ background: `${accentColor}15`, color: accentColor }}>
                        {(u.name || u.email || "?")[0].toUpperCase()}
                      </div>}
                  {u.is_blocked && (
                    <div className="absolute -top-0.5 -end-0.5 w-3 h-3 rounded-full flex items-center justify-center" style={{ background: "#ef4444", border: "1px solid var(--bg-secondary)" }}>
                      <X className="w-1.5 h-1.5 text-white" />
                    </div>
                  )}
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-medium text-sm leading-tight truncate">{u.name || u.email}</span>
                    {renderRoleBadge(u)}
                    {u.is_blocked && <span className="text-[9px] px-1 py-0.5 rounded font-black" style={{ background:"rgba(239,68,68,0.15)", color:"#ef4444" }}>BL</span>}
                    {isVip && !u.is_blocked && <span className="text-[9px] px-1 py-0.5 rounded font-bold" style={{ background:"rgba(34,197,94,0.12)", color:"#22c55e" }}>VIP</span>}
                    {hasIssues && <span className="text-[9px] px-1 py-0.5 rounded font-bold animate-pulse" style={{ background:"rgba(64,93,230,0.12)", color:"#7aa2ff" }}>{metrics.tickets_open} ticket</span>}
                  </div>
                  <div className="flex items-center gap-1.5 text-[11px] mt-0.5" style={{ color: "var(--text-secondary)" }}>
                    <span className="truncate max-w-[150px]">{u.email}</span>
                    {u.instagram_username && <span className="flex-shrink-0" style={{ color: "#E1306C" }}>@{u.instagram_username}</span>}
                  </div>
                </div>
              </div>

              {/* iPoints col — click opens modal for adjustment */}
              <div className="hidden sm:flex items-center justify-end"
                onClick={(e) => { e.stopPropagation(); openDetails(u.user_id); }}>
                {false ? null : (
                  <span className="text-sm font-black font-outfit cursor-pointer hover:opacity-70 flex items-center gap-0.5" style={{ color:"#F77737", fontVariantNumeric:"tabular-nums" }}>
                    {(u.ipoints||0).toLocaleString()} <span className="text-[9px] font-bold opacity-60">iP</span>
                    <PenSquare className="w-2.5 h-2.5 opacity-0 group-hover:opacity-40 ms-0.5" style={{ color:"#F77737" }} />
                  </span>
                )}
              </div>

              {/* Spent col */}
              <div className="hidden md:flex items-center justify-end">
                <span className="text-sm font-bold font-outfit" style={{ color: (metrics.topups_approved_amount_ils||0) > 0 ? "#22c55e" : "var(--text-secondary)", opacity: (metrics.topups_approved_amount_ils||0) > 0 ? 1 : 0.3, fontVariantNumeric:"tabular-nums" }}>
                  {(metrics.topups_approved_amount_ils||0) > 0 ? `₪${metrics.topups_approved_amount_ils}` : "—"}
                </span>
              </div>

              {/* Orders col */}
              <div className="hidden md:flex items-center justify-end">
                <span className="text-sm font-bold font-outfit" style={{ color: (metrics.orders_total||0) > 0 ? "#E1306C" : "var(--text-secondary)", opacity: (metrics.orders_total||0) > 0 ? 1 : 0.3, fontVariantNumeric:"tabular-nums" }}>
                  {metrics.orders_total || "—"}
                </span>
              </div>

              {/* Referrals col */}
              <div className="hidden lg:flex items-center justify-end">
                <span className="text-sm font-bold font-outfit" style={{ color: (metrics.referral_count||0) > 0 ? "#c084fc" : "var(--text-secondary)", opacity: (metrics.referral_count||0) > 0 ? 1 : 0.3, fontVariantNumeric:"tabular-nums" }}>
                  {metrics.referral_count || "—"}
                </span>
              </div>

              {/* Lang col */}
              <div className="hidden lg:flex items-center justify-end">
                <span className="text-base leading-none" title={u.preferred_lang || "—"}>
                  {u.preferred_lang === "en" ? "🇺🇸" : u.preferred_lang === "ru" ? "🇷🇺" : u.preferred_lang === "he" ? "🇮🇱" : "🌐"}
                </span>
              </div>

              {/* Date col */}
              <div className="hidden lg:flex flex-col items-end justify-center">
                <span className="text-[10px]" style={{ color: "var(--text-secondary)" }}>{formatIsraelDate(u.created_at)}</span>
                {u.last_login_at && <span className="text-[10px] opacity-40" style={{ color: "var(--text-secondary)" }}>{formatIsraelDate(u.last_login_at)}</span>}
              </div>
            </div>
          );
        })}
        {visible.length === 0 && (
          <div className="p-12 text-center">
            <p className="font-bold text-sm">{t("Admin.no_users_found", lang)}</p>
            <p className="text-xs mt-1" style={{ color:"var(--text-secondary)" }}>{t("Admin.try_adjusting_filters", lang)}</p>
          </div>
        )}
      </div>

      <Dialog open={detailsOpen} onOpenChange={(open) => { setDetailsOpen(open); if (!open) { setDetails(null); setDetailsUserId(""); setBlockReason(""); setAllOrders(null); setPendingBlock(false); setExpandedTicketId(null); } }}>
        <DialogContent className="w-[calc(100vw-24px)] max-w-[1400px] max-h-[94vh] overflow-y-auto p-0" style={{ background: "var(--bg-secondary)", borderColor: "var(--border-color)" }}>
          {detailsLoading ? (
            <div className="py-20 flex items-center justify-center">
              <div className="animate-spin h-8 w-8 border-2 border-[#E1306C] border-t-transparent rounded-full" />
            </div>
          ) : !details ? (
            <div className="py-20 flex flex-col items-center justify-center gap-3">
              <AlertCircle className="w-8 h-8" style={{ color: "#ef4444" }} />
              <p className="text-sm" style={{ color: "var(--text-secondary)" }}>{t("Admin.failed_to_load_user_data", lang)}</p>
            </div>
          ) : <AdminErrorBoundary key={detailsUserId}><UserDetailsContent
              details={details} lang={lang} currentUser={currentUser}
              detailsUserId={detailsUserId} allOrders={allOrders} allOrdersLoading={allOrdersLoading}
              expandedOrderId={expandedOrderId} setExpandedOrderId={setExpandedOrderId}
              expandedTicketId={expandedTicketId} setExpandedTicketId={setExpandedTicketId}
              editUser={editUser} setEditUser={setEditUser}
              adjustSign={adjustSign} setAdjustSign={setAdjustSign}
              newBal={newBal} setNewBal={setNewBal}
              adjustReason={adjustReason} setAdjustReason={setAdjustReason}
              blockBusy={blockBusy} pendingBlock={pendingBlock} setPendingBlock={setPendingBlock}
              blockReason={blockReason} setBlockReason={setBlockReason}
              orderStatusLabel={orderStatusLabel} topupStatusLabel={topupStatusLabel} ticketStatusLabel={ticketStatusLabel}
              fetchDetails={fetchDetails} loadAllOrders={loadAllOrders} adjustPoints={adjustPoints}
              toggleBlock={toggleBlock} resetGift={resetGift} setRole={setRole}
              setDetailsOpen={setDetailsOpen} onOpenTicket={onOpenTicket} renderRoleBadge={renderRoleBadge}
            /></AdminErrorBoundary>}
        </DialogContent>
      </Dialog>

      {/* Users Pagination */}
      {usersTotalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6 flex-wrap">
          <button onClick={()=>setUsersPage(p=>Math.max(1,p-1))} disabled={usersPage<=1}
            className="px-3 py-1.5 rounded-xl text-sm font-semibold disabled:opacity-30 transition-all"
            style={{ background: "var(--surface-2)", color: "var(--text-primary)", border: "1px solid var(--surface-3)" }}>
            {t("Admin.prev_2", lang)}
          </button>
          {Array.from({length: Math.min(usersTotalPages, 7)}, (_, i) => {
            let p; const half=3;
            if(usersTotalPages<=7){p=i+1;}
            else if(usersPage<=half+1){p=i+1;}
            else if(usersPage>=usersTotalPages-half){p=usersTotalPages-6+i;}
            else{p=usersPage-half+i;}
            return (
              <button key={p} onClick={()=>setUsersPage(p)}
                className="w-9 h-9 rounded-xl text-sm font-bold transition-all"
                style={{ background: usersPage===p ? "#8B5CF6" : "var(--surface-2)", color: usersPage===p ? "#fff" : "var(--text-primary)", border: "1px solid var(--border-color)" }}>
                {p}
              </button>
            );
          })}
          <button onClick={()=>setUsersPage(p=>Math.min(usersTotalPages,p+1))} disabled={usersPage>=usersTotalPages}
            className="px-3 py-1.5 rounded-xl text-sm font-semibold disabled:opacity-30 transition-all"
            style={{ background: "var(--surface-2)", color: "var(--text-primary)", border: "1px solid var(--surface-3)" }}>
            {t("Admin.next_2", lang)}
          </button>
          <span className="text-xs ml-2" style={{ color: "var(--text-secondary)" }}>{usersPage}/{usersTotalPages} · {usersTotal} {t("Admin.total_2", lang)}</span>
        </div>
      )}
    </div>
  );
}

// ===== MESSAGES =====
function MessagesTab({ lang }) {
  const defaultSourceLanguage = lang === "ru" ? "ru" : lang === "he" ? "he" : "en";
  const emptyPreview = {
    subject: { en: "", ru: "", he: "" },
    content: { en: "", ru: "", he: "" },
  };
  const [users, setUsers] = useState([]);
  const [recipientMode, setRecipientMode] = useState("user");
  const [recipientQuery, setRecipientQuery] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [draft, setDraft] = useState({
    source_language: defaultSourceLanguage,
    source_subject: "",
    source_content: "",
    message_type: "general",
  });
  const [preview, setPreview] = useState(emptyPreview);
  const [previewMeta, setPreviewMeta] = useState({ source_language: defaultSourceLanguage, mode: "idle", model: "" });
  const [previewReady, setPreviewReady] = useState(false);
  const [previewDirty, setPreviewDirty] = useState(false);
  const [sending, setSending] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [aiConfig, setAiConfig] = useState({ enabled: false, model: "" });
  const [sentLog, setSentLog] = useState([]);
  const [logLoading, setLogLoading] = useState(true);
  const [selectedLogDetails, setSelectedLogDetails] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [deletingLogId, setDeletingLogId] = useState("");
  const [previewLang, setPreviewLang] = useState("en");
  const [expandedLogId, setExpandedLogId] = useState(null);
  const [logPage, setLogPage] = useState(1);
  const LOG_PER_PAGE = 20;

  const fetchLog = async () => {
    setLogLoading(true);
    try {
      const r = await fetch(`${API}/admin/messages/log`, { credentials: "include" });
      if (r.ok) setSentLog(await r.json());
    } catch {}
    setLogLoading(false);
  };

  useEffect(() => {
    (async () => {
      try {
        const [usersRes, aiRes] = await Promise.all([
          fetch(`${API}/admin/users?limit=500`, { credentials: "include" }),
          fetch(`${API}/admin/messages/ai-config`, { credentials: "include" }),
        ]);
        if (usersRes.ok) {
          const data = await usersRes.json();
          const arr = Array.isArray(data) ? data : (data.users || []);
          arr.sort((a, b) => String(a.name || a.email || "").localeCompare(String(b.name || b.email || "")));
          setUsers(arr);
        }
        if (aiRes.ok) {
          setAiConfig(await aiRes.json());
        }
      } catch {}
      await fetchLog();
    })();
  }, []);

  const selectedUser = users.find((userItem) => userItem.user_id === selectedUserId) || null;
  const normalizedQuery = recipientQuery.trim().toLowerCase();
  const filteredUsers = users.filter((userItem) => {
    if (!normalizedQuery) return true;
    const haystack = `${userItem.name || ""} ${userItem.email || ""}`.toLowerCase();
    return haystack.includes(normalizedQuery);
  }).slice(0, 8);
  const previewCards = [
    { key: "en", label: "English", color: "#405DE6" },
    { key: "ru", label: "Русский", color: "#F77737" },
    { key: "he", label: "עברית", color: "#00C853" },
  ];
  const messageTypeOptions = [
    { value: "general", label: t("Admin.general", lang), color: "#405DE6" },
    { value: "promotion", label: t("Admin.promo", lang), color: "#00C853" },
    { value: "warning", label: t("Admin.warning", lang), color: "#F77737" },
    { value: "order_update", label: t("Admin.order_update", lang), color: "#833AB4" },
  ];

  const updateDraft = (patch) => {
    setDraft((current) => ({ ...current, ...patch }));
    if (!aiConfig.enabled) {
      const next = { ...draft, ...patch };
      const s = (next.source_subject || "").trim();
      const c = (next.source_content || "").trim();
      setPreview({ subject: { en: s, ru: s, he: s }, content: { en: c, ru: c, he: c } });
      setPreviewReady(true);
      setPreviewDirty(false);
    } else if (previewReady) {
      setPreviewDirty(true);
    }
  };

  const createManualPreview = () => {
    const sourceSubject = draft.source_subject.trim();
    const sourceContent = draft.source_content.trim();
    return {
      subject: { en: sourceSubject, ru: sourceSubject, he: sourceSubject },
      content: { en: sourceContent, ru: sourceContent, he: sourceContent },
    };
  };

  const preparePreview = async () => {
    if (!draft.source_subject.trim()) {
      toast.error(t("Admin.subject_required", lang));
      return;
    }
    if (!draft.source_content.trim()) {
      toast.error(t("Admin.content_required", lang));
      return;
    }
    if (!aiConfig.enabled) {
      setPreview(createManualPreview());
      setPreviewMeta({
        source_language: draft.source_language === "auto" ? "other" : draft.source_language,
        mode: "manual",
        model: "",
      });
      setPreviewReady(true);
      setPreviewDirty(false);
      toast.success(t("Admin.editable_draft_prepared", lang));
      return;
    }
    setPreparing(true);
    try {
      const response = await fetch(`${API}/admin/messages/translate-preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(draft),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Failed");
      setPreview({
        subject: data.subject || emptyPreview.subject,
        content: data.content || emptyPreview.content,
      });
      setPreviewMeta({
        source_language: data.source_language || draft.source_language,
        mode: "ai",
        model: data.model || aiConfig.model || "",
      });
      setPreviewReady(true);
      setPreviewDirty(false);
      toast.success(t("Admin.ai_preview_ready", lang));
    } catch (error) {
      toast.error(error.message);
    } finally {
      setPreparing(false);
    }
  };

  const updatePreviewField = (type, locale, value) => {
    setPreview((current) => ({
      ...current,
      [type]: {
        ...current[type],
        [locale]: value,
      },
    }));
  };

  const resetComposer = () => {
    setRecipientMode("user");
    setRecipientQuery("");
    setSelectedUserId("");
    setDraft({
      source_language: defaultSourceLanguage,
      source_subject: "",
      source_content: "",
      message_type: "general",
    });
    setPreview(emptyPreview);
    setPreviewMeta({ source_language: defaultSourceLanguage, mode: "idle", model: "" });
    setPreviewReady(false);
    setPreviewDirty(false);
  };

  const openLogDetails = async (entry) => {
    setDetailsOpen(true);
    setDetailsLoading(true);
    try {
      const response = await fetch(`${API}/admin/messages/log/${entry.message_id}`, { credentials: "include" });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Failed");
      setSelectedLogDetails(data);
    } catch (error) {
      toast.error(error.message);
      setDetailsOpen(false);
      setSelectedLogDetails(null);
    } finally {
      setDetailsLoading(false);
    }
  };

  const deleteLogEntry = async (entry) => {
    const confirmed = window.confirm(
      entry.is_broadcast
        ? t("Admin.delete_this_broadcast_for_everyone", lang)
        : t("Admin.delete_this_message_completely", lang)
    );
    if (!confirmed) return;
    setDeletingLogId(entry.message_id);
    try {
      const response = await fetch(`${API}/admin/messages/log/${entry.message_id}`, {
        method: "DELETE",
        credentials: "include",
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Failed");
      toast.success(
        entry.is_broadcast
          ? t("Admin.broadcast_deleted_for_everyone", lang)
          : t("Admin.message_deleted", lang)
      );
      if (selectedLogDetails?.message_id === entry.message_id) {
        setDetailsOpen(false);
        setSelectedLogDetails(null);
      }
      await fetchLog();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setDeletingLogId("");
    }
  };

  const send = async () => {
    if (recipientMode === "user" && !selectedUserId) {
      toast.error(t("Admin.choose_a_recipient", lang));
      return;
    }
    if (!draft.source_subject.trim()) {
      toast.error(t("Admin.subject_required", lang));
      return;
    }
    if (!draft.source_content.trim()) {
      toast.error(t("Admin.content_required", lang));
      return;
    }
    setSending(true);
    try {
      let finalPreview = preview;
      if (!previewReady || previewDirty) {
        if (aiConfig.enabled) {
          const res = await fetch(`${API}/admin/messages/translate-preview`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify(draft),
          });
          const d = await res.json();
          if (!res.ok) throw new Error(d.detail || "Failed");
          finalPreview = { subject: d.subject || emptyPreview.subject, content: d.content || emptyPreview.content };
          setPreview(finalPreview);
          setPreviewMeta({ source_language: d.source_language || draft.source_language, mode: "ai", model: d.model || aiConfig.model || "" });
          setPreviewReady(true);
          setPreviewDirty(false);
        } else {
          finalPreview = createManualPreview();
          setPreview(finalPreview);
          setPreviewReady(true);
          setPreviewDirty(false);
        }
      }
      const response = await fetch(`${API}/admin/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          recipient_id: recipientMode === "broadcast" ? "" : selectedUserId,
          subject: finalPreview.subject,
          content: finalPreview.content,
          message_type: draft.message_type,
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Failed");
      toast.success(data.message);
      resetComposer();
      fetchLog();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="animate-fade-in-up">

      {/* ── Details Dialog ── */}
      <Dialog open={detailsOpen} onOpenChange={(open) => { setDetailsOpen(open); if (!open) { setSelectedLogDetails(null); setDetailsLoading(false); } }}>
        <DialogContent className="border-white/10 max-w-4xl max-h-[88vh] overflow-hidden p-0" style={{ background: "#0c0c10" }}>
          <div className="max-h-[88vh] overflow-y-auto">
            <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--surface-3)" }}>
              <DialogTitle className="text-base font-bold flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-[#F77737]" />
                {t("Admin.message_details", lang)}
              </DialogTitle>
            </div>
            <div className="p-5">
              {detailsLoading || !selectedLogDetails ? (
                <div className="flex justify-center py-10"><div className="animate-spin h-6 w-6 border-2 border-[#F77737] border-t-transparent rounded-full" /></div>
              ) : (() => {
                const dlang = previewLang;
                return (
                  <div className="space-y-4">
                    {/* Meta row */}
                    <div className="rounded-2xl p-4 flex flex-wrap items-start justify-between gap-3" style={{ background: "var(--surface-1)", border: "1px solid var(--surface-3)" }}>
                      <div className="min-w-0 flex-1">
                        <div className="text-base font-bold mb-1.5">{selectedLogDetails.subject?.[lang] || selectedLogDetails.subject?.en || "—"}</div>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]" style={{ color: "var(--text-secondary)" }}>
                          <span className="flex items-center gap-1">
                            {selectedLogDetails.is_broadcast ? <Megaphone className="w-3 h-3" /> : <User className="w-3 h-3" />}
                            {selectedLogDetails.is_broadcast
                              ? `${t("Admin.broadcast", lang)} · ${(selectedLogDetails.recipient_count||0).toLocaleString()} ${t("Admin.users_3", lang)}`
                              : (selectedLogDetails.recipient?.name || selectedLogDetails.recipient?.email || t("Admin.direct_2", lang))}
                          </span>
                          <span>·</span>
                          <span>{selectedLogDetails.sender_name || "—"}</span>
                          <span>·</span>
                          <span>{formatIsraelDateTime(selectedLogDetails.created_at)}</span>
                        </div>
                      </div>
                      <button type="button" onClick={() => deleteLogEntry(selectedLogDetails)} disabled={deletingLogId === selectedLogDetails.message_id}
                        className="h-8 px-3 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-all flex-shrink-0"
                        style={{ background: "rgba(239,68,68,0.12)", color: "#f87171", border: "1px solid rgba(239,68,68,0.2)" }}>
                        {deletingLogId === selectedLogDetails.message_id ? <div className="w-3 h-3 rounded-full border-2 border-red-300/30 border-t-red-300 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                        {t("Admin.delete_for_everyone", lang)}
                      </button>
                    </div>
                    {/* Lang tabs */}
                    <div className="inline-flex rounded-2xl border p-0.5 gap-0.5" style={{ borderColor: "var(--surface-3)", background: "var(--surface-1)" }}>
                      {previewCards.map((c) => (
                        <button key={c.key} onClick={() => setPreviewLang(c.key)}
                          className="h-8 px-4 rounded-xl text-xs font-semibold transition-all"
                          style={{ background: dlang === c.key ? `${c.color}22` : "transparent", color: dlang === c.key ? c.color : "var(--text-secondary)", border: dlang === c.key ? `1px solid ${c.color}33` : "1px solid transparent" }}>
                          {c.label}
                        </button>
                      ))}
                    </div>
                    <div className="space-y-3">
                      <div className="rounded-xl px-4 py-2.5 text-sm font-semibold" style={{ background: "rgba(0,0,0,0.25)", border: "1px solid var(--surface-3)" }}>
                        {selectedLogDetails.subject?.[dlang] || "—"}
                      </div>
                      <div className="rounded-xl px-4 py-4 text-sm whitespace-pre-wrap leading-relaxed min-h-[200px]" style={{ background: "rgba(0,0,0,0.25)", border: "1px solid var(--surface-3)" }}>
                        {selectedLogDetails.content?.[dlang] || "—"}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="text-xl font-bold">{t("Admin.message_center", lang)}</h2>
          <p className="text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>
            {t("Admin.send_to_a_user_or_broadcast_to_everyone", lang)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border"
            style={{ borderColor: aiConfig.enabled ? "rgba(0,200,83,0.25)" : "rgba(247,119,55,0.22)", color: aiConfig.enabled ? "#00C853" : "#F77737", background: aiConfig.enabled ? "rgba(0,200,83,0.08)" : "rgba(247,119,55,0.07)" }}>
            {aiConfig.enabled ? <Sparkles className="w-3 h-3" /> : <Languages className="w-3 h-3" />}
            {aiConfig.enabled ? `AI · ${aiConfig.model || "ready"}` : t("Admin.ai_off", lang)}
          </div>
        </div>
      </div>

      {/* ── Main: compose LEFT, log RIGHT ── */}
      <div className="grid gap-4 xl:grid-cols-[420px_1fr]">

        {/* ── LEFT: Compose ── */}
        <div className="rounded-2xl border p-5 space-y-4" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>

          {/* To: */}
          <div className="flex items-center gap-3">
            <span className="text-[11px] font-bold uppercase tracking-widest flex-shrink-0" style={{ color: "var(--text-secondary)" }}>
              {t("Admin.to", lang)}:
            </span>
            <div className="flex-1 inline-flex rounded-2xl border p-0.5" style={{ borderColor: "var(--surface-3)", background: "var(--surface-1)" }}>
              {[
                { value: "user", icon: <User className="w-3.5 h-3.5" />, label: t("Admin.user_2", lang) },
                { value: "broadcast", icon: <Megaphone className="w-3.5 h-3.5" />, label: t("Admin.everyone", lang) },
              ].map((opt) => (
                <button key={opt.value} type="button" onClick={() => setRecipientMode(opt.value)}
                  className="flex-1 h-8 rounded-xl text-xs font-semibold transition-all flex items-center justify-center gap-1.5"
                  style={{ background: recipientMode === opt.value ? "rgba(225,48,108,0.18)" : "transparent", color: recipientMode === opt.value ? "#E1306C" : "var(--text-secondary)" }}>
                  {opt.icon}{opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* User picker / broadcast badge */}
          {recipientMode === "broadcast" ? (
            <div className="rounded-2xl px-4 py-3 flex items-center gap-3" style={{ background: "rgba(225,48,108,0.07)", border: "1px solid rgba(225,48,108,0.18)" }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(225,48,108,0.15)", color: "#E1306C" }}>
                <Megaphone className="w-5 h-5" />
              </div>
              <div>
                <div className="text-sm font-bold">{t("Admin.broadcast_to_all", lang)}</div>
                <div className="text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>
                  {users.length.toLocaleString()} {t("Admin.users_4", lang)}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {selectedUser ? (
                <div className="rounded-2xl border px-3 py-2.5 flex items-center gap-3" style={{ borderColor: "rgba(64,93,230,0.35)", background: "rgba(64,93,230,0.1)" }}>
                  {selectedUser.picture
                    ? <img src={selectedUser.picture} alt="" className="w-9 h-9 rounded-xl object-cover flex-shrink-0" />
                    : <div className="w-9 h-9 rounded-xl flex items-center justify-center font-bold flex-shrink-0" style={{ background: "rgba(64,93,230,0.25)", color: "#7b9dff" }}>
                        {(selectedUser.name || selectedUser.email || "?")[0]?.toUpperCase()}
                      </div>
                  }
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-bold truncate">{selectedUser.name || selectedUser.email}</div>
                    <div className="text-xs truncate" style={{ color: "rgba(255,255,255,0.5)" }}>{selectedUser.email}</div>
                  </div>
                  <button type="button" onClick={() => { setSelectedUserId(""); setRecipientQuery(""); }} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-white/10 transition-colors flex-shrink-0" style={{ color: "var(--text-secondary)" }}>
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute start-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--text-secondary)" }} />
                  <Input value={recipientQuery} onChange={(e) => setRecipientQuery(e.target.value)}
                    placeholder={t("Admin.search_user_by_name_or_email", lang)}
                    className="ps-9 h-9 text-sm" style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }} data-testid="msg-recipient-search" />
                </div>
              )}
              {!selectedUser && (
                <div className="rounded-xl border overflow-hidden" style={{ borderColor: "var(--surface-3)" }} data-testid="msg-recipient">
                  {filteredUsers.length ? filteredUsers.map((userItem) => {
                    const active = userItem.user_id === selectedUserId;
                    return (
                      <button key={userItem.user_id} type="button" onClick={() => { setSelectedUserId(userItem.user_id); setRecipientQuery(""); }}
                        className="w-full text-start px-3 py-3 flex items-center gap-3 transition-all border-b last:border-0 hover:bg-white/[0.04]"
                        style={{ background: active ? "rgba(64,93,230,0.1)" : "transparent", borderColor: "var(--surface-2)" }}>
                        {userItem.picture
                          ? <img src={userItem.picture} alt="" className="w-8 h-8 rounded-lg object-cover flex-shrink-0" />
                          : <div className="w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm flex-shrink-0"
                              style={{ background: "rgba(64,93,230,0.15)", color: "#7b9dff" }}>
                              {(userItem.name || userItem.email || "?")[0]?.toUpperCase()}
                            </div>
                        }
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium truncate">{userItem.name || userItem.email}</div>
                          <div className="text-[11px] truncate" style={{ color: "var(--text-secondary)" }}>{userItem.email}</div>
                        </div>
                        <div className="text-[11px] flex-shrink-0 font-semibold" style={{ color: "#F77737" }}>{(userItem.ipoints||0).toLocaleString()} iP</div>
                      </button>
                    );
                  }) : (
                    <div className="px-3 py-5 text-center text-sm" style={{ color: "var(--text-secondary)" }}>
                      {recipientQuery ? t("Admin.no_users_found_2", lang) : t("Admin.type_to_search", lang)}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Divider */}
          <div style={{ height: 1, background: "var(--surface-2)" }} />

          {/* Type + language */}
          <div className="flex flex-wrap items-center gap-1.5">
            {messageTypeOptions.map((opt) => (
              <button key={opt.value} type="button" onClick={() => updateDraft({ message_type: opt.value })}
                className="h-7 px-3 rounded-full text-[11px] font-semibold border transition-all"
                style={{ borderColor: draft.message_type === opt.value ? `${opt.color}55` : "var(--surface-3)", background: draft.message_type === opt.value ? `${opt.color}18` : "var(--surface-1)", color: draft.message_type === opt.value ? opt.color : "var(--text-secondary)" }}>
                {opt.label}
              </button>
            ))}
            {aiConfig.enabled && (
              <Select value={draft.source_language} onValueChange={(v) => updateDraft({ source_language: v })}>
                <SelectTrigger className="h-7 text-xs px-2 gap-1 ms-auto min-w-[80px]" style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }}><SelectValue /></SelectTrigger>
                <SelectContent style={{ background: "var(--bg-secondary)", borderColor: "var(--border-color)" }}>
                  <SelectItem value="auto">{t("Admin.auto", lang)}</SelectItem>
                  <SelectItem value="en">EN</SelectItem>
                  <SelectItem value="ru">RU</SelectItem>
                  <SelectItem value="he">HE</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Subject + content */}
          <div className="space-y-2">
            <Input value={draft.source_subject} onChange={(e) => updateDraft({ source_subject: e.target.value })}
              placeholder={t("Admin.subject", lang)}
              className="h-10 font-medium" style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }} data-testid="msg-subject-en" />
            <Textarea value={draft.source_content} onChange={(e) => updateDraft({ source_content: e.target.value })}
              placeholder={t("Admin.write_your_message", lang)}
              className="min-h-[140px] resize-y text-sm" style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }} data-testid="msg-content-en" />
            <div className="text-[10px] text-right" style={{ color: "var(--text-secondary)" }}>
              {draft.source_content.length} {t("Admin.chars", lang)}
            </div>
          </div>

          {/* AI translate button */}
          {aiConfig.enabled && draft.source_subject.trim() && draft.source_content.trim() && (
            <button type="button" onClick={preparePreview} disabled={preparing}
              className="w-full h-9 rounded-xl border text-xs font-semibold flex items-center justify-center gap-2 transition-all"
              style={{ borderColor: "rgba(225,48,108,0.25)", background: "rgba(225,48,108,0.07)", color: "#E1306C" }}
              data-testid="prepare-message-preview-btn">
              {preparing ? <div className="w-3.5 h-3.5 rounded-full border-2 border-[#E1306C]/30 border-t-[#E1306C] animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {preparing ? t("Admin.translating", lang) : previewDirty ? t("Admin.refresh_ai_translation", lang) : t("Admin.translate_with_ai", lang)}
            </button>
          )}

          {/* Tabbed preview */}
          {previewReady && (
            <div className="rounded-2xl border overflow-hidden" style={{ borderColor: "var(--surface-3)" }}>
              <div className="flex items-center justify-between px-3 py-2 border-b" style={{ borderColor: "var(--surface-3)", background: "var(--surface-1)" }}>
                <div className="flex gap-0.5">
                  {previewCards.map((c) => (
                    <button key={c.key} onClick={() => setPreviewLang(c.key)}
                      className="h-7 px-3 rounded-lg text-[11px] font-semibold transition-all"
                      style={{ background: previewLang === c.key ? `${c.color}22` : "transparent", color: previewLang === c.key ? c.color : "var(--text-secondary)" }}>
                      {c.label}
                    </button>
                  ))}
                </div>
                {previewDirty && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: "rgba(247,119,55,0.12)", color: "#F77737" }}>
                    {t("Admin.outdated", lang)}
                  </span>
                )}
                {!previewDirty && previewMeta.mode === "ai" && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1" style={{ background: "rgba(0,200,83,0.1)", color: "#00C853" }}>
                    <Sparkles className="w-2.5 h-2.5" />AI
                  </span>
                )}
              </div>
              <div className="p-3 space-y-2">
                <Input value={preview.subject[previewLang]} onChange={(e) => updatePreviewField("subject", previewLang, e.target.value)}
                  placeholder={t("Admin.subject_2", lang)} className="h-9 text-sm font-medium" style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }} />
                <Textarea value={preview.content[previewLang]} onChange={(e) => updatePreviewField("content", previewLang, e.target.value)}
                  placeholder={t("Admin.content", lang)} className="min-h-[120px] resize-y text-sm" style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }} />
              </div>
            </div>
          )}

          {/* Send + Reset */}
          <div className="flex gap-2 pt-1">
            <Button onClick={send} disabled={sending || (aiConfig.enabled && (!previewReady || previewDirty))} className="btn-brand flex-1 h-11" data-testid="send-message-btn">
              {sending
                ? <><div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin me-2" />{t("Admin.sending_2", lang)}</>
                : aiConfig.enabled && (!previewReady || previewDirty)
                  ? <><Sparkles className="w-4 h-4 me-2 opacity-50" />{t("Admin.translate_first", lang)}</>
                  : <><Send className="w-4 h-4 me-2" />{recipientMode === "broadcast" ? t("Admin.send_broadcast", lang) : t("Admin.send", lang)}</>
              }
            </Button>
            {(draft.source_subject || draft.source_content || selectedUserId) && (
              <button type="button" onClick={resetComposer}
                className="h-11 px-4 rounded-xl text-xs font-semibold transition-all flex items-center gap-1.5"
                style={{ background: "var(--surface-2)", border: "1px solid var(--surface-3)", color: "var(--text-secondary)" }}>
                <RotateCcw className="w-3.5 h-3.5" />
                {t("Admin.clear", lang)}
              </button>
            )}
          </div>
        </div>

        {/* ── RIGHT: Sent Log ── */}
        <div className="rounded-2xl border flex flex-col overflow-hidden" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)", minHeight: 560 }}>
          {/* Header */}
          <div className="px-4 py-3 border-b flex items-center gap-3 flex-shrink-0" style={{ borderColor: "var(--border-color)" }}>
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <div className="w-7 h-7 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(247,119,55,0.12)", color: "#F77737" }}>
                <ClipboardList className="w-3.5 h-3.5" />
              </div>
              <span className="text-sm font-bold">{t("Admin.all_messages", lang)}</span>
              {sentLog.length > 0 && (
                <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold" style={{ background: "rgba(247,119,55,0.12)", color: "#F77737" }}>{sentLog.length}</span>
              )}
            </div>
            <button onClick={fetchLog} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-white/5 transition-colors flex-shrink-0" style={{ color: "var(--text-secondary)" }}>
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          </div>

          {logLoading ? (
            <div className="flex-1 flex justify-center items-center py-10">
              <div className="animate-spin h-5 w-5 border-2 border-[#F77737] border-t-transparent rounded-full" />
            </div>
          ) : sentLog.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center py-10" style={{ color: "var(--text-secondary)" }}>
              <MessageSquare className="w-10 h-10 mb-3 opacity-20" />
              <div className="text-sm">{t("Admin.no_messages_yet_2", lang)}</div>
            </div>
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="overflow-y-auto flex-1">
                {sentLog.slice((logPage - 1) * LOG_PER_PAGE, logPage * LOG_PER_PAGE).map((entry, i) => {
                  const subj = entry.subject?.[lang] || entry.subject?.en || entry.subject?.ru || entry.subject?.he || "—";
                  const bodyText = entry.content?.[lang] || entry.content?.en || entry.content?.ru || entry.content?.he || "";
                  const isBc = entry.is_broadcast;
                  const isSys = !entry.sender_id || entry.sender_id === "system" || entry.sender_name === "System";
                  const typeColors = { general: "#405DE6", promotion: "#22c55e", warning: "#F77737", order_update: "#8B5CF6", broadcast: "#E1306C" };
                  const tc = isBc ? "#E1306C" : (typeColors[entry.message_type] || "#405DE6");
                  const isExpanded = expandedLogId === (entry.message_id || i);
                  return (
                    <div key={entry.message_id || i}>
                      {i > 0 && <div style={{ height: 1, background: "var(--border-color)" }} />}
                      <div className={`transition-colors ${isExpanded ? "" : "hover:bg-white/[0.015]"}`}
                        style={{ background: isExpanded ? "var(--surface-2)" : "transparent" }}>
                        {/* Main row */}
                        <div className="px-4 py-3 flex items-center gap-3">
                          {/* Type icon */}
                          <button type="button" onClick={() => setExpandedLogId(isExpanded ? null : (entry.message_id || i))}
                            className="w-9 h-9 rounded-xl flex-shrink-0 flex items-center justify-center transition-all"
                            style={{ background: `${tc}12`, color: tc }}>
                            {isBc ? <Megaphone className="w-4 h-4" /> : isSys ? <Sparkles className="w-4 h-4" /> : <User className="w-4 h-4" />}
                          </button>
                          {/* Content */}
                          <button type="button" onClick={() => setExpandedLogId(isExpanded ? null : (entry.message_id || i))}
                            className="flex-1 min-w-0 text-start">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-sm font-semibold truncate max-w-[180px]" style={{ color: "var(--text-primary)" }}>{subj}</span>
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold flex-shrink-0"
                                style={{ background: `${tc}15`, color: tc }}>
                                {isBc ? t("Admin.broadcast", lang) : (entry.message_type || "general")}
                              </span>
                              {isSys && !isBc && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold flex-shrink-0"
                                  style={{ background: "rgba(156,163,175,0.12)", color: "#9ca3af" }}>
                                  {t("Admin.system", lang)}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-0.5">
                              {isBc ? (
                                <span className="text-[11px] flex items-center gap-1" style={{ color: "var(--text-secondary)" }}>
                                  <UsersIcon className="w-3 h-3" />
                                  {(entry.recipient_count||0).toLocaleString()} {t("Admin.rcvd", lang)}
                                </span>
                              ) : (
                                <span className="text-[11px] truncate max-w-[120px]" style={{ color: "var(--text-secondary)" }}>
                                  {entry.recipient_name || entry.recipient_email || "—"}
                                </span>
                              )}
                              <span className="text-[10px] opacity-40">·</span>
                              <span className="text-[11px]" dir="ltr" style={{ color: "var(--text-secondary)", opacity: 0.6 }}>
                                {formatIsraelDateTime(entry.created_at)}
                              </span>
                            </div>
                          </button>
                          {/* Delete btn */}
                          <button type="button" onClick={() => deleteLogEntry(entry)} disabled={deletingLogId === entry.message_id}
                            className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 transition-all hover:opacity-80 disabled:opacity-30"
                            style={{ background: "rgba(239,68,68,0.08)", color: "#f87171" }}
                            title={t("Admin.delete_for_everyone", lang)}>
                            {deletingLogId === entry.message_id
                              ? <div className="w-3 h-3 rounded-full border-2 border-red-300/30 border-t-red-300 animate-spin" />
                              : <Trash2 className="w-3.5 h-3.5" />}
                          </button>
                          <ChevronDown className="w-3.5 h-3.5 flex-shrink-0 transition-transform"
                            style={{ color: "var(--text-secondary)", opacity: 0.4, transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)" }} />
                        </div>
                        {/* Expanded */}
                        {isExpanded && (
                          <div className="px-4 pb-4 pt-0 border-t" style={{ borderColor: "var(--border-color)" }}>
                            <div className="flex items-center gap-1 pt-3 mb-3">
                              {previewCards.map((c) => (
                                <button key={c.key} onClick={() => setPreviewLang(c.key)}
                                  className="h-6 px-3 rounded-lg text-[10px] font-semibold transition-all"
                                  style={{ background: previewLang === c.key ? `${c.color}20` : "var(--surface-2)", color: previewLang === c.key ? c.color : "var(--text-secondary)" }}>
                                  {c.label}
                                </button>
                              ))}
                              <button type="button" onClick={() => openLogDetails(entry)}
                                className="ms-auto h-6 px-3 rounded-lg text-[10px] font-semibold flex items-center gap-1 transition-all"
                                style={{ background: "rgba(64,93,230,0.1)", color: "#7aa2ff", border: "1px solid rgba(64,93,230,0.15)" }}>
                                <ExternalLink className="w-2.5 h-2.5" />{t("Admin.full", lang)}
                              </button>
                            </div>
                            <div className="text-xs font-semibold px-3 py-2 rounded-xl mb-2" style={{ background: "rgba(0,0,0,0.25)", border: "1px solid var(--border-color)" }}>
                              {entry.subject?.[previewLang] || entry.subject?.en || "—"}
                            </div>
                            <div className="text-xs px-3 py-3 rounded-xl whitespace-pre-wrap leading-relaxed" style={{ background: "rgba(0,0,0,0.2)", border: "1px solid var(--border-color)", color: "var(--text-secondary)", maxHeight: 160, overflowY: "auto" }}>
                              {entry.content?.[previewLang] || entry.content?.en || bodyText || "—"}
                            </div>
                            <div className="flex items-center gap-1.5 mt-2 text-[10px]" style={{ color: "var(--text-secondary)" }}>
                              <Shield className="w-3 h-3" />
                              <span>{entry.sender_name || "—"}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              {/* Pagination */}
              {sentLog.length > LOG_PER_PAGE && (
                <div className="flex items-center justify-between px-4 py-2.5 border-t flex-shrink-0" style={{ borderColor: "var(--border-color)" }}>
                  <span className="text-xs" style={{ color: "var(--text-secondary)" }}>
                    {(logPage - 1) * LOG_PER_PAGE + 1}–{Math.min(logPage * LOG_PER_PAGE, sentLog.length)} / {sentLog.length}
                  </span>
                  <div className="flex gap-1.5">
                    <button disabled={logPage === 1} onClick={() => setLogPage(p => p - 1)}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-30"
                      style={{ background: "var(--surface-2)", color: "var(--text-primary)" }}>←</button>
                    <button disabled={logPage * LOG_PER_PAGE >= sentLog.length} onClick={() => setLogPage(p => p + 1)}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-30"
                      style={{ background: "var(--surface-2)", color: "var(--text-primary)" }}>→</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

    </div>
  );
}

function BonusEventCard({ lang }) {
  const [bonusEvent, setBonusEvent] = useState(null);
  const [bonusForm, setBonusForm] = useState({ bonus_pct: 20, duration_hours: 24, min_topup_ils: 0, message_ru: "", message_en: "", message_he: "" });
  const [bonusBusy, setBonusBusy] = useState(false);
  // Email campaign confirmation modal
  const [campaignPreview, setCampaignPreview] = useState(null); // shown after activation
  const [sendingCampaign, setSendingCampaign] = useState(false);
  const [lastCampaignId, setLastCampaignId] = useState(null);
  // Campaign list
  const [campaigns, setCampaigns] = useState([]);
  const [campaignDetail, setCampaignDetail] = useState(null);
  const [campaignDetailOpen, setCampaignDetailOpen] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);
  const [showCampaigns, setShowCampaigns] = useState(false);

  useEffect(() => {
    fetch(`${API}/admin/bonus-event`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null).then(d => { if (d) setBonusEvent(d); });
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const r = await fetch(`${API}/admin/bonus-campaigns?per_page=10`, { credentials: "include" });
      if (r.ok) { const d = await r.json(); setCampaigns(d.campaigns || []); }
    } catch {}
  };

  const activateBonus = async () => {
    setBonusBusy(true);
    try {
      const r = await fetch(`${API}/admin/bonus-event`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(bonusForm),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail);
      const r2 = await fetch(`${API}/admin/bonus-event`, { credentials: "include" });
      if (r2.ok) setBonusEvent(await r2.json());
      toast.success(L(lang, `Bonus activated! ${d.notified} users notified in-app.`, `Бонус активирован! ${d.notified} пользователей уведомлены в приложении.`, `הבונוס הופעל! הודע ל-${d.notified} משתמשים`));
      // Show email campaign confirmation modal
      if (d.campaign_preview) setCampaignPreview({ ...d.campaign_preview, bonus_pct: bonusForm.bonus_pct, duration_hours: bonusForm.duration_hours });
    } catch (e) { toast.error(e.message); } finally { setBonusBusy(false); }
  };

  const deactivateBonus = async () => {
    setBonusBusy(true);
    try {
      const r = await fetch(`${API}/admin/bonus-event`, { method: "DELETE", credentials: "include" });
      if (!r.ok) throw new Error("Failed");
      setBonusEvent(null);
      toast.success(t("Admin.bonus_event_deactivated", lang));
    } catch (e) { toast.error(e.message); } finally { setBonusBusy(false); }
  };

  const sendEmailCampaign = async () => {
    setSendingCampaign(true);
    try {
      const r = await fetch(`${API}/admin/bonus-campaign/send`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({}),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail);
      setCampaignPreview(null);
      setLastCampaignId(d.campaign_id);
      toast.success(L(lang, `Campaign started! Sending to ${d.total_recipients} users.`, `Кампания запущена! Отправка ${d.total_recipients} пользователям.`, `הקמפיין החל! שולח ל-${d.total_recipients} משתמשים.`));
      fetchCampaigns();
    } catch (e) { toast.error(e.message); } finally { setSendingCampaign(false); }
  };

  const openCampaignDetail = async (campaignId) => {
    try {
      const r = await fetch(`${API}/admin/bonus-campaigns/${campaignId}`, { credentials: "include" });
      if (r.ok) { setCampaignDetail(await r.json()); setCampaignDetailOpen(true); }
    } catch {}
  };

  const resendCampaign = async (campaignId) => {
    setResendBusy(true);
    try {
      const r = await fetch(`${API}/admin/bonus-campaigns/${campaignId}/resend`, { method: "POST", credentials: "include" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail);
      toast.success(L(lang, `Resending to ${d.resent} users.`, `Повторная отправка ${d.resent} пользователям.`, `שולח שוב ל-${d.resent} משתמשים.`));
      fetchCampaigns();
      if (campaignDetail?.campaign_id === campaignId) openCampaignDetail(campaignId);
    } catch (e) { toast.error(e.message); } finally { setResendBusy(false); }
  };

  const CAMPAIGN_STATUS_CFG = {
    pending:  { label: { en: "Pending",  ru: "Ожидает",   he: "ממתין" },  color: "#F77737" },
    sending:  { label: { en: "Sending",  ru: "Отправка",  he: "שולח" },   color: "#06B6D4" },
    done:     { label: { en: "Done",     ru: "Готово",    he: "הסתיים" }, color: "#22c55e" },
  };

  return (
    <div className="space-y-4">
    <div className="rounded-2xl overflow-hidden border" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
      <div className="px-5 py-4 flex items-center gap-3 border-b" style={{ borderColor: "var(--border-color)", background: "rgba(225,48,108,0.04)" }}>
        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(225,48,108,0.14)", color: "#E1306C" }}>
          <Megaphone className="w-4 h-4" />
        </div>
        <div>
          <p className="text-sm font-bold">{t("Admin.global_bonus_event", lang)}</p>
          <p className="text-[11px]" style={{ color: "var(--text-secondary)" }}>{t("Admin.temporary_top_up_bonus_for_all_users", lang)}</p>
        </div>
        {bonusEvent?.active && (
          <span className="ms-auto text-[10px] px-2 py-1 rounded-full font-bold animate-pulse" style={{ background: "rgba(225,48,108,0.2)", color: "#E1306C" }}>
            ● {t("Admin.live", lang)}
          </span>
        )}
      </div>
      <div className="p-5">
        {bonusEvent?.active ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl font-black text-sm" style={{ background: "rgba(225,48,108,0.15)", color: "#E1306C" }}>
                🎉 +{bonusEvent.bonus_pct}% {t("Admin.bonus_active", lang)}
              </div>
              {bonusEvent.min_topup_ils > 0 && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold" style={{ background: "rgba(247,119,55,0.13)", color: "#F77737", border: "1px solid rgba(247,119,55,0.25)" }}>
                  <span>⚡</span>
                  {t("Admin.min_top_up_2", lang)}: {bonusEvent.min_topup_ils} ₪
                </div>
              )}
            </div>
            <div className="rounded-xl p-3 text-xs space-y-1.5" style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)" }}>
              {bonusEvent.min_topup_ils > 0 ? (
                <div className="flex items-start gap-2">
                  <span className="mt-0.5">💡</span>
                  <span style={{ color: "var(--text-secondary)" }}>
                    {L(lang, `+${bonusEvent.bonus_pct}% bonus applies to top-ups of ${bonusEvent.min_topup_ils} ₪+.`, `Бонус +${bonusEvent.bonus_pct}% при пополнении от ${bonusEvent.min_topup_ils} ₪.`, `בונוס +${bonusEvent.bonus_pct}% לטעינות של ${bonusEvent.min_topup_ils} ₪+.`)}
                  </span>
                </div>
              ) : (
                <div className="flex items-start gap-2">
                  <span className="mt-0.5">💡</span>
                  <span style={{ color: "var(--text-secondary)" }}>
                    {L(lang, `+${bonusEvent.bonus_pct}% applies to all top-ups (no minimum).`, `Бонус +${bonusEvent.bonus_pct}% на все пополнения (без минимума).`, `+${bonusEvent.bonus_pct}% על כל הטענות (ללא מינימום).`)}
                  </span>
                </div>
              )}
              {bonusEvent.expires_at && (
                <div className="flex items-center gap-2" style={{ color: "var(--text-secondary)" }}>
                  <span>⏱</span>{t("Admin.expires", lang)}: <span className="font-semibold">{new Date(bonusEvent.expires_at).toLocaleString()}</span>
                </div>
              )}
              {bonusEvent.message?.ru && (
                <div className="flex items-center gap-2 opacity-60"><span>💬</span> {bonusEvent.message.ru}</div>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={deactivateBonus} disabled={bonusBusy}
                className="flex items-center gap-1.5 text-xs px-4 py-2 rounded-xl font-bold"
                style={{ background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.22)", color: "#f87171" }}>
                <Power className="w-3.5 h-3.5" />{t("Admin.deactivate", lang)}
              </button>
              <button onClick={() => { setCampaignPreview(null); fetch(`${API}/admin/bonus-campaign/preview`, { credentials: "include" }).then(r => r.ok ? r.json() : null).then(d => { if (d) setCampaignPreview({ ...d, bonus_pct: bonusEvent.bonus_pct, duration_hours: 24 }); }); }}
                className="flex items-center gap-1.5 text-xs px-4 py-2 rounded-xl font-bold"
                style={{ background: "rgba(225,48,108,0.12)", border: "1px solid rgba(225,48,108,0.25)", color: "#E1306C" }}>
                ✉️ {L(lang, "Send Email Campaign", "Отправить email-кампанию", "שלח קמפיין מייל")}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs" style={{ color: "var(--text-secondary)" }}>
              {t("Admin.activate_a_global_bonus_so_all_top_ups_get_extra", lang)}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <StepperInput label={t("Admin.bonus", lang)} value={bonusForm.bonus_pct}
                onChange={v => setBonusForm({ ...bonusForm, bonus_pct: Math.max(1, Math.min(500, v)) })}
                min={1} max={500} step={5} unit="%" accent="#F77737" />
              <StepperInput label={t("Admin.duration_hours", lang)} value={bonusForm.duration_hours}
                onChange={v => setBonusForm({ ...bonusForm, duration_hours: Math.max(1, v) })}
                min={1} step={1} unit="h" accent="#8B5CF6" />
            </div>
            <div>
              <label className="text-xs font-semibold mb-1.5 block" style={{ color: "var(--text-secondary)" }}>
                {t("Admin.minimum_top_up", lang)}<span className="ms-1.5 font-normal opacity-60">{t("Admin.0_no_minimum", lang)}</span>
              </label>
              <StepperInput value={bonusForm.min_topup_ils}
                onChange={v => setBonusForm({ ...bonusForm, min_topup_ils: Math.max(0, v) })}
                min={0} step={5} unit="₪" accent="#22c55e" />
              {bonusForm.min_topup_ils > 0 && (
                <p className="text-[11px] mt-1.5 flex items-center gap-1" style={{ color: "#F77737" }}>
                  <span>⚡</span>
                  {L(lang, `Only top-ups of ${bonusForm.min_topup_ils} ₪+ get the bonus`, `Бонус только при пополнении от ${bonusForm.min_topup_ils} ₪`, `רק טעינות של ${bonusForm.min_topup_ils} ₪+ יקבלו בונוס`)}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <label className="text-xs font-semibold block" style={{ color: "var(--text-secondary)" }}>{t("Admin.notification_message", lang)}</label>
              {[["RU", bonusForm.message_ru, v => setBonusForm({ ...bonusForm, message_ru: v }), "Сегодня +20% к пополнению!"],
                ["EN", bonusForm.message_en, v => setBonusForm({ ...bonusForm, message_en: v }), "Today +20% on all top-ups!"],
                ["HE", bonusForm.message_he, v => setBonusForm({ ...bonusForm, message_he: v }), "היום +20% על כל הטענות!"]
              ].map(([flag, val, onChange, ph]) => (
                <div key={flag} className="relative">
                  <span className="absolute start-2.5 top-1/2 -translate-y-1/2 text-[10px] font-bold" style={{ color: "var(--text-secondary)" }}>{flag}</span>
                  <Input value={val} onChange={e => onChange(e.target.value)} placeholder={ph}
                    className="text-sm ps-8" style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }} />
                </div>
              ))}
            </div>
            <Button onClick={activateBonus} disabled={bonusBusy} className="btn-brand">
              {bonusBusy ? <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full me-2" /> : <Megaphone className="w-4 h-4 me-2" />}
              {t("Admin.activate_notify_all", lang)}
            </Button>
          </div>
        )}
      </div>
    </div>

    {/* ── Campaign history ── */}
    {campaigns.length > 0 && (
      <div className="rounded-2xl overflow-hidden border" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
        <button className="w-full px-5 py-3.5 flex items-center gap-3 border-b text-left"
          style={{ borderColor: "var(--border-color)", background: "rgba(6,182,212,0.03)" }}
          onClick={() => setShowCampaigns(v => !v)}>
          <span className="text-sm font-bold flex-1" style={{ color: "var(--text-primary)" }}>
            ✉️ {L(lang, "Email Campaigns", "Email-кампании", "קמפיינים במייל")}
          </span>
          <span className="text-xs px-2 py-0.5 rounded-full font-bold" style={{ background: "rgba(6,182,212,0.15)", color: "#06B6D4" }}>{campaigns.length}</span>
          <span style={{ color: "var(--text-secondary)" }}>{showCampaigns ? "▲" : "▼"}</span>
        </button>
        {showCampaigns && (
          <div className="p-4 space-y-2">
            {campaigns.map(c => {
              const cfg = CAMPAIGN_STATUS_CFG[c.status] || CAMPAIGN_STATUS_CFG.done;
              const prog = c.total_recipients > 0 ? Math.round(((c.sent_count + c.failed_count) / c.total_recipients) * 100) : 0;
              return (
                <div key={c.campaign_id} className="rounded-xl p-3 cursor-pointer hover:opacity-90 transition-opacity"
                  style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)" }}
                  onClick={() => openCampaignDetail(c.campaign_id)}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-bold" style={{ color: "#E1306C" }}>🎉 +{c.bonus_pct}%</span>
                    <span className="text-xs px-1.5 py-0.5 rounded-md font-semibold" style={{ background: `${cfg.color}18`, color: cfg.color }}>
                      {cfg.label[lang] || cfg.label.en}
                    </span>
                    <span className="ms-auto text-[11px]" style={{ color: "var(--text-secondary)" }}>
                      {c.created_at ? new Date(c.created_at).toLocaleString("he-IL", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : ""}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs" style={{ color: "var(--text-secondary)" }}>
                    <span>👥 {c.total_recipients}</span>
                    <span style={{ color: "#22c55e" }}>✓ {c.sent_count}</span>
                    {c.failed_count > 0 && <span style={{ color: "#ef4444" }}>✗ {c.failed_count}</span>}
                    <span>⊘ {c.skipped_count}</span>
                  </div>
                  {c.status === "sending" && (
                    <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ background: "var(--border-color)" }}>
                      <div className="h-full rounded-full transition-all" style={{ width: `${prog}%`, background: "linear-gradient(90deg,#06B6D4,#8B5CF6)" }} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    )}

    {/* ── Email campaign confirmation modal ── */}
    {campaignPreview && (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-3" style={{ background: "rgba(0,0,0,0.8)" }}>
        <div className="w-full max-w-lg rounded-2xl overflow-hidden flex flex-col" style={{ background: "var(--surface-1)", border: "1px solid var(--border-color)", maxHeight: "90vh" }}>
          {/* Header */}
          <div className="px-5 py-4 border-b flex items-center gap-3" style={{ borderColor: "var(--border-color)", background: "rgba(225,48,108,0.04)" }}>
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(225,48,108,0.15)", color: "#E1306C" }}>✉️</div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-sm" style={{ color: "var(--text-primary)" }}>
                {L(lang, "Send Email Campaign?", "Отправить email-кампанию?", "לשלוח קמפיין מייל?")}
              </p>
              <p className="text-[11px]" style={{ color: "var(--text-secondary)" }}>
                {L(lang, "Review details before sending", "Проверьте детали перед отправкой", "בדוק פרטים לפני שליחה")}
              </p>
            </div>
            <button onClick={() => setCampaignPreview(null)} className="text-lg" style={{ color: "var(--text-secondary)" }}>✕</button>
          </div>

          <div className="overflow-y-auto flex-1 p-5 space-y-4">
            {/* Subject preview */}
            <div className="rounded-xl p-3 space-y-1.5" style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)" }}>
              <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: "var(--text-secondary)" }}>
                {L(lang, "Email Subject", "Тема письма", "נושא המייל")}
              </p>
              {["en","ru","he"].map(l => (
                <div key={l} className="flex items-start gap-2 text-xs">
                  <span>{l === "en" ? "🇺🇸" : l === "ru" ? "🇷🇺" : "🇮🇱"}</span>
                  <span style={{ color: "var(--text-primary)" }}>{(campaignPreview.preview_subjects || {})[l] || ""}</span>
                </div>
              ))}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl p-3 text-center" style={{ background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.2)" }}>
                <div className="text-2xl font-black" style={{ color: "#22c55e" }}>{(campaignPreview.eligible_count || 0).toLocaleString()}</div>
                <div className="text-xs mt-1" style={{ color: "#86efac" }}>{L(lang, "Will receive email", "Получат письмо", "יקבלו מייל")}</div>
              </div>
              <div className="rounded-xl p-3 text-center" style={{ background: "rgba(107,114,128,0.08)", border: "1px solid rgba(107,114,128,0.2)" }}>
                <div className="text-2xl font-black" style={{ color: "var(--text-secondary)" }}>{(campaignPreview.skipped_count || 0).toLocaleString()}</div>
                <div className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>{L(lang, "Skipped", "Пропущено", "מדולגים")}</div>
              </div>
            </div>

            {/* Skip reasons */}
            {campaignPreview.skipped_reasons && Object.keys(campaignPreview.skipped_reasons).length > 0 && (
              <div className="rounded-xl p-3" style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)" }}>
                <p className="text-[11px] font-bold uppercase tracking-wider mb-2" style={{ color: "var(--text-secondary)" }}>
                  {L(lang, "Skip Reasons", "Причины пропуска", "סיבות דילוג")}
                </p>
                {Object.entries(campaignPreview.skipped_reasons).map(([reason, count]) => {
                  const labels = {
                    no_email:        { en: "No email address",     ru: "Нет email",              he: "אין כתובת מייל" },
                    opted_out:       { en: "Opted out (marketing)", ru: "Отписался от маркетинга", he: "ביטל הסכמה לשיווק" },
                    duplicate_email: { en: "Duplicate email",       ru: "Дубликат email",         he: "מייל כפול" },
                  };
                  const label = (labels[reason] || {})[lang] || reason;
                  return count > 0 ? (
                    <div key={reason} className="flex items-center justify-between text-xs py-0.5">
                      <span style={{ color: "var(--text-secondary)" }}>{label}</span>
                      <span className="font-bold" style={{ color: "var(--text-primary)" }}>{count}</span>
                    </div>
                  ) : null;
                })}
              </div>
            )}

            {/* Email preview */}
            {campaignPreview.preview_html && (
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wider mb-2" style={{ color: "var(--text-secondary)" }}>
                  {L(lang, "Email Preview (English)", "Предпросмотр письма (English)", "תצוגה מקדימה (אנגלית)")}
                </p>
                <div className="rounded-xl overflow-hidden" style={{ border: "1px solid var(--border-color)", height: 280 }}>
                  <iframe srcDoc={campaignPreview.preview_html} title="email-preview"
                    className="w-full h-full" style={{ border: "none", background: "#fff" }} sandbox="allow-same-origin" />
                </div>
              </div>
            )}

            {/* Compliance note */}
            <div className="rounded-xl p-3 flex items-start gap-2" style={{ background: "rgba(6,182,212,0.06)", border: "1px solid rgba(6,182,212,0.2)" }}>
              <span className="mt-0.5">ℹ️</span>
              <p className="text-[11px]" style={{ color: "#67e8f9" }}>
                {L(lang,
                  "Each email includes an unsubscribe link. Emails are sent according to each user's preferred language.",
                  "Каждое письмо содержит ссылку на отписку. Письма отправляются на языке пользователя.",
                  "כל מייל כולל קישור לביטול הסכמה. המיילים נשלחים בשפת המשתמש."
                )}
              </p>
            </div>
          </div>

          {/* Footer buttons */}
          <div className="px-5 py-4 border-t flex items-center gap-3" style={{ borderColor: "var(--border-color)" }}>
            <button onClick={() => setCampaignPreview(null)} className="flex-1 px-4 py-2.5 rounded-xl text-sm font-bold"
              style={{ background: "var(--surface-2)", color: "var(--text-secondary)", border: "1px solid var(--border-color)" }}>
              {L(lang, "Cancel", "Отмена", "ביטול")}
            </button>
            <button onClick={sendEmailCampaign} disabled={sendingCampaign || (campaignPreview.eligible_count || 0) === 0}
              className="flex-1 px-4 py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ background: "linear-gradient(135deg,#E1306C,#8B5CF6)", color: "#fff" }}>
              {sendingCampaign
                ? <><div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />{L(lang, "Starting…", "Запуск…", "מתחיל…")}</>
                : <>✉️ {L(lang, `Confirm & Send to ${campaignPreview.eligible_count || 0}`, `Подтвердить и отправить ${campaignPreview.eligible_count || 0}`, `אשר ושלח ל-${campaignPreview.eligible_count || 0}`)}</>
              }
            </button>
          </div>
        </div>
      </div>
    )}

    {/* ── Campaign detail modal ── */}
    {campaignDetailOpen && campaignDetail && (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-3" style={{ background: "rgba(0,0,0,0.8)" }}>
        <div className="w-full max-w-lg rounded-2xl overflow-hidden flex flex-col" style={{ background: "var(--surface-1)", border: "1px solid var(--border-color)", maxHeight: "90vh" }}>
          <div className="px-5 py-4 border-b flex items-center gap-3" style={{ borderColor: "var(--border-color)" }}>
            <p className="font-bold text-sm flex-1" style={{ color: "var(--text-primary)" }}>
              ✉️ +{campaignDetail.bonus_pct}% {L(lang, "Campaign Details", "Детали кампании", "פרטי הקמפיין")}
            </p>
            <button onClick={() => setCampaignDetailOpen(false)} style={{ color: "var(--text-secondary)" }}>✕</button>
          </div>
          <div className="overflow-y-auto flex-1 p-5 space-y-4">
            {/* Progress summary */}
            <div className="grid grid-cols-3 gap-2 text-center">
              {[
                { label: { en: "Sent", ru: "Отправлено", he: "נשלח" }, val: campaignDetail.sent_count, color: "#22c55e" },
                { label: { en: "Failed", ru: "Ошибка", he: "נכשל" }, val: campaignDetail.failed_count, color: "#ef4444" },
                { label: { en: "Skipped", ru: "Пропущено", he: "דולג" }, val: campaignDetail.skipped_count, color: "var(--text-secondary)" },
              ].map(item => (
                <div key={item.color} className="rounded-xl p-3" style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)" }}>
                  <div className="text-xl font-black" style={{ color: item.color }}>{item.val}</div>
                  <div className="text-[11px] mt-0.5" style={{ color: "var(--text-secondary)" }}>{item.label[lang] || item.label.en}</div>
                </div>
              ))}
            </div>
            {/* Timestamps */}
            <div className="text-xs space-y-1" style={{ color: "var(--text-secondary)" }}>
              {campaignDetail.created_at && <p>📅 {L(lang,"Created","Создано","נוצר")}: {new Date(campaignDetail.created_at).toLocaleString()}</p>}
              {campaignDetail.sent_at && <p>🚀 {L(lang,"Started","Начато","החל")}: {new Date(campaignDetail.sent_at).toLocaleString()}</p>}
              {campaignDetail.finished_at && <p>✅ {L(lang,"Finished","Завершено","הסתיים")}: {new Date(campaignDetail.finished_at).toLocaleString()}</p>}
            </div>
            {/* Recipient list sample */}
            {campaignDetail.recipients?.length > 0 && (
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wider mb-2" style={{ color: "var(--text-secondary)" }}>
                  {L(lang,"Recipients (up to 500)","Получатели (до 500)","נמענים (עד 500)")}
                </p>
                <div className="rounded-xl overflow-hidden" style={{ border: "1px solid var(--border-color)", maxHeight: 250, overflowY: "auto" }}>
                  {campaignDetail.recipients.map((r, i) => (
                    <div key={i} className="flex items-center gap-2 px-3 py-2 text-xs"
                      style={{ borderTop: i > 0 ? "1px solid var(--border-color)" : "none", background: i % 2 === 0 ? "var(--surface-2)" : "var(--surface-1)" }}>
                      <span>{r.lang === "en" ? "🇺🇸" : r.lang === "ru" ? "🇷🇺" : "🇮🇱"}</span>
                      <span className="flex-1 truncate" style={{ color: "var(--text-primary)" }}>{r.email}</span>
                      <span className="font-semibold" style={{ color: r.status === "sent" ? "#22c55e" : r.status === "failed" ? "#ef4444" : "var(--text-secondary)" }}>
                        {r.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Resend option */}
            {(campaignDetail.failed_count > 0 || campaignDetail.status === "done") && (
              <button onClick={() => resendCampaign(campaignDetail.campaign_id)} disabled={resendBusy || campaignDetail.status === "sending" || campaignDetail.failed_count === 0}
                className="w-full px-4 py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-40"
                style={{ background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.22)", color: "#f87171" }}>
                {resendBusy ? <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" /> : "↩️"}
                {L(lang, `Resend to ${campaignDetail.failed_count} failed`, `Повторить для ${campaignDetail.failed_count} ошибок`, `שלח שוב ל-${campaignDetail.failed_count} שנכשלו`)}
              </button>
            )}
          </div>
        </div>
      </div>
    )}
    </div>
  );
}

// ===== SETTINGS =====
function SettingsTab({ lang, providerFxRate }) {
  const [settings, setSettings] = useState({
    bit_phone_number: "",
    ipoint_exchange_rate: 1,
    min_topup_ils: 25,
    max_topup_ils: 3600,
    brand_instagram_handle: "",
    brand_instagram_url: "",
    footer_text: { en: "", ru: "", he: "" },
    footer_links: [],
    receipt_enabled: false,
    vip_threshold_ils: 100,
    support_ai_prompt: "",
    support_welcome_message: "",
    support_ai_enabled: true,
    support_ai_max_tokens: 250,
  });

  // ── User Reset ───────────────────────────────────────────────────────────────
  const RESET_TARGETS = [
    { id: "ipoints",          label: { en: "iPoints balance",      ru: "Баланс iPoints",      he: "יתרת iPoints" },       group: "account" },
    { id: "xp",               label: { en: "XP & Level",           ru: "XP и уровень",        he: "XP ורמה" },             group: "account" },
    { id: "streak",           label: { en: "Daily streak",         ru: "Ежедневная серия",     he: "רצף יומי" },            group: "account" },
    { id: "gifts",            label: { en: "Free gift flags",      ru: "Флаги подарков",       he: "דגלי מתנות" },          group: "account" },
    { id: "orders",           label: { en: "Orders",               ru: "Заказы",               he: "הזמנות" },              group: "data" },
    { id: "cart",             label: { en: "Cart",                 ru: "Корзина",              he: "עגלה" },                group: "data" },
    { id: "tickets",          label: { en: "Support tickets",      ru: "Тикеты поддержки",    he: "כרטיסי תמיכה" },        group: "data" },
    { id: "task_submissions", label: { en: "Task submissions",     ru: "Выполнения заданий",   he: "הגשות משימות" },        group: "data" },
    { id: "promo_uses",       label: { en: "Promo code uses",      ru: "Использования промо",  he: "שימושי פרומו" },        group: "data" },
    { id: "messages",         label: { en: "Messages",             ru: "Сообщения",            he: "הודעות" },              group: "data" },
    { id: "xp_log",           label: { en: "XP history log",       ru: "История XP",           he: "היסטוריית XP" },        group: "data" },
    { id: "topup_requests",   label: { en: "Top-up requests",      ru: "Запросы пополнения",   he: "בקשות טעינה" },         group: "data" },
    { id: "tgbot",            label: { en: "Telegram link",        ru: "Привязка Telegram",    he: "קישור טלגרם" },         group: "other" },
    { id: "sessions",         label: { en: "Sessions",             ru: "Сессии",               he: "סשנים" },               group: "other" },
    { id: "delete_account",   label: { en: "DELETE ACCOUNT",       ru: "УДАЛИТЬ АККАУНТ",      he: "מחק חשבון" },           group: "danger" },
  ];
  const [resetEmail, setResetEmail] = useState("");
  const [resetSelected, setResetSelected] = useState(new Set());
  const [resetModal, setResetModal] = useState(null); // null | "confirm1" | "confirm2"
  const [resetConfirmEmail, setResetConfirmEmail] = useState("");
  const [resetBusy, setResetBusy] = useState(false);
  const [resetResult, setResetResult] = useState(null);

  const resetToggle = (id) => setResetSelected(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const resetSelectAll = () => setResetSelected(new Set(RESET_TARGETS.map(t => t.id)));
  const resetDeselectAll = () => setResetSelected(new Set());

  const handleResetConfirm1 = () => {
    if (!resetEmail.trim() || resetSelected.size === 0) return;
    setResetConfirmEmail("");
    setResetModal("confirm1");
  };
  const handleResetConfirm2 = () => setResetModal("confirm2");
  const handleResetExecute = async () => {
    if (resetConfirmEmail.trim().toLowerCase() !== resetEmail.trim().toLowerCase()) return;
    setResetBusy(true);
    try {
      const res = await fetch(`${API}/admin/users/reset`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: resetEmail.trim(), targets: [...resetSelected] }),
      });
      const data = await res.json();
      if (!res.ok) { alert(data.detail || "Error"); setResetBusy(false); return; }
      setResetResult(data.results);
      setResetModal(null);
      setResetEmail("");
      setResetSelected(new Set());
    } catch { alert("Network error"); }
    setResetBusy(false);
  };

  const [systemLevels, setSystemLevels] = useState([]);

  useEffect(() => {
    (async () => {
      const [r1, r4] = await Promise.all([
        fetch(`${API}/admin/settings`, { credentials: "include" }),
        fetch(`${API}/levels`, { credentials: "include" }),
      ]);
      if (r1.ok) setSettings(await r1.json());
      if (r4.ok) setSystemLevels(await r4.json());
    })();
  }, []);

  const save = async () => {
    const response = await fetch(`${API}/admin/settings`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(settings),
    });
    if (response.ok) toast.success(t("common.success", lang));
  };

  const footerText = settings.footer_text || {};

  const [activeSubTab, setActiveSubTab] = useState("general");

  const SUB_TABS = [
    { id: "general", icon: <Settings className="w-3.5 h-3.5" />, label: t("Admin.general_2", lang) },
    { id: "danger",  icon: <Trash2 className="w-3.5 h-3.5" />,   label: t("Admin.danger", lang), danger: true },
  ];

  return (
    <div className="max-w-2xl animate-fade-in-up">

      {/* ── SUB-TAB BAR ──────────────────────────────────────────────── */}
      <div className="flex gap-1.5 p-1 rounded-2xl mb-5 flex-wrap" style={{ background: "var(--surface-1)", border: "1px solid var(--border-color)" }}>
        {SUB_TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveSubTab(tab.id)}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs font-semibold transition-all"
            style={{
              background: activeSubTab === tab.id
                ? tab.danger ? "rgba(239,68,68,0.15)" : "var(--surface-3)"
                : "transparent",
              color: activeSubTab === tab.id
                ? tab.danger ? "#ef4444" : "var(--text-primary)"
                : tab.danger ? "rgba(239,68,68,0.6)" : "var(--text-secondary)",
            }}>
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {activeSubTab === "general" && <div className="space-y-4">

      {/* ── PAYMENT SETTINGS ─────────────────────────────────────────── */}
      <div className="rounded-2xl overflow-hidden border" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
        <div className="px-5 py-3.5 flex items-center gap-3 border-b" style={{ borderColor: "var(--border-color)" }}>
          <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(247,119,55,0.14)", color: "#F77737" }}>
            <Wallet className="w-4 h-4" />
          </div>
          <p className="text-sm font-bold">{t("Admin.payment_settings", lang)}</p>
        </div>

        {/* Exchange rate row */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b" style={{ borderColor: "var(--border-color)" }}>
          <div>
            <p className="text-sm font-medium">{t("admin.exchange_rate", lang)}</p>
            <p className="text-[11px] mt-0.5" style={{ color: "var(--text-secondary)" }}>1 ₪ → iPoints{providerFxRate ? ` · ${t("Admin.ref", lang)}: ${providerFxRate}` : ""}</p>
          </div>
          <div className="w-36 flex-shrink-0">
            <StepperInput
              value={settings.ipoint_exchange_rate || 1}
              onChange={v => setSettings({ ...settings, ipoint_exchange_rate: Math.max(0.01, v) })}
              min={0.01} step={0.5} unit="iP" accent="#F77737"
              data-testid="exchange-rate-input"
            />
          </div>
        </div>

        {/* Min top-up row */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b" style={{ borderColor: "var(--border-color)" }}>
          <p className="text-sm font-medium">{t("Admin.min_top_up_3", lang)}</p>
          <div className="w-36 flex-shrink-0">
            <StepperInput
              value={settings.min_topup_ils ?? 25}
              onChange={v => setSettings({ ...settings, min_topup_ils: Math.max(1, v) })}
              min={1} step={5} unit="₪" accent="#22c55e"
              data-testid="min-topup-input"
            />
          </div>
        </div>

        {/* Max top-up row */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b" style={{ borderColor: "var(--border-color)" }}>
          <p className="text-sm font-medium">{t("Admin.max_top_up", lang)}</p>
          <div className="w-36 flex-shrink-0">
            <StepperInput
              value={settings.max_topup_ils ?? 3600}
              onChange={v => setSettings({ ...settings, max_topup_ils: Math.max(100, v) })}
              min={100} step={100} unit="₪" accent="#22c55e"
            />
          </div>
        </div>

        {/* Bit phone row */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b" style={{ borderColor: "var(--border-color)" }}>
          <p className="text-sm font-medium">{t("Admin.bit_phone", lang)}</p>
          <Input
            type="text"
            value={settings.bit_phone_number || ""}
            onChange={(e) => setSettings({ ...settings, bit_phone_number: e.target.value })}
            placeholder="050-0000000"
            className="w-40 h-9 text-sm"
            style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }}
          />
        </div>

        {/* VIP threshold row */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b" style={{ borderColor: "var(--border-color)" }}>
          <div>
            <p className="text-sm font-medium">💎 {t("Admin.vip_threshold", lang)}</p>
            <p className="text-[11px] mt-0.5" style={{ color: "var(--text-secondary)" }}>{t("Admin.min_total_top_up_to_earn_vip_badge", lang)}</p>
          </div>
          <div className="w-36 flex-shrink-0">
            <StepperInput
              value={settings.vip_threshold_ils ?? 100}
              onChange={v => setSettings({ ...settings, vip_threshold_ils: Math.max(1, v) })}
              min={1} step={50} unit="₪" accent="#8B5CF6"
            />
          </div>
        </div>

        {/* Receipt toggle row */}
        <div className="flex items-center justify-between px-5 py-3.5">
          <p className="text-sm font-medium">{t("Admin.receipts", lang)}</p>
          <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => setSettings(s => ({ ...s, receipt_enabled: !s.receipt_enabled }))}>
            <span className="text-xs font-semibold" style={{ color: settings.receipt_enabled ? "#22c55e" : "var(--text-secondary)" }}>
              {settings.receipt_enabled ? t("Admin.on", lang) : t("Admin.off", lang)}
            </span>
            <div className="w-9 h-5 rounded-full transition-colors relative" style={{ background: settings.receipt_enabled ? "#22c55e" : "var(--surface-3)" }}>
              <div className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all shadow"
                style={{ left: settings.receipt_enabled ? "calc(100% - 1.125rem)" : "0.125rem" }} />
            </div>
          </div>
        </div>
      </div>

      {/* ── BRAND ──────────────────────────────────────────────────────── */}
      <div className="rounded-2xl overflow-hidden border" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
        <div className="px-5 py-3.5 flex items-center gap-3 border-b" style={{ borderColor: "var(--border-color)" }}>
          <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(225,48,108,0.14)", color: "#E1306C" }}>
            <Instagram className="w-4 h-4" />
          </div>
          <p className="text-sm font-bold">{t("Admin.brand", lang)}</p>
        </div>

        {/* Instagram handle row */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b" style={{ borderColor: "var(--border-color)" }}>
          <p className="text-sm font-medium">@ {t("Admin.handle", lang)}</p>
          <Input
            placeholder="ifollowers_israel"
            value={settings.brand_instagram_handle || ""}
            onChange={(e) => setSettings({ ...settings, brand_instagram_handle: e.target.value.replace(/^@+/, "").trim() })}
            className="w-48 h-9 text-sm"
            style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }}
            data-testid="brand-instagram-handle"
          />
        </div>

        {/* Instagram URL row */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b" style={{ borderColor: "var(--border-color)" }}>
          <p className="text-sm font-medium">URL</p>
          <Input
            placeholder="https://instagram.com/…"
            value={settings.brand_instagram_url || ""}
            onChange={(e) => setSettings({ ...settings, brand_instagram_url: e.target.value.trim() })}
            className="w-48 h-9 text-sm"
            style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }}
            data-testid="brand-instagram-url"
          />
        </div>

        {/* Footer text row */}
        <div className="px-5 py-3.5">
          <p className="text-sm font-medium mb-2.5">{t("Admin.footer_text", lang)}</p>
          <div className="grid grid-cols-3 gap-2">
            {[["EN", footerText.en, v => setSettings({ ...settings, footer_text: { ...footerText, en: v } }), "footer-text-en"],
              ["RU", footerText.ru, v => setSettings({ ...settings, footer_text: { ...footerText, ru: v } }), null],
              ["HE", footerText.he, v => setSettings({ ...settings, footer_text: { ...footerText, he: v } }), null]
            ].map(([flag, val, onChange, tid]) => (
              <div key={flag} className="relative">
                <span className="absolute start-2.5 top-1/2 -translate-y-1/2 text-[10px] font-bold" style={{ color: "var(--text-secondary)" }}>{flag}</span>
                <Input value={val || ""} onChange={e => onChange(e.target.value)} placeholder={flag} className="h-9 text-sm ps-8" style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }} data-testid={tid || undefined} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── SAVE ─────────────────────────────────────────────────────── */}
      <Button onClick={save} className="btn-brand w-full h-11" data-testid="save-settings-btn">
        <Save className="w-4 h-4 me-2" />
        {t("Admin.save_settings", lang)}
      </Button>

      {/* ── SYSTEM BANNERS (general tab) ─────────────────────────────── */}
      <SystemBannersCard lang={lang} />

      </div>}{/* end general tab */}

      {/* ══ DANGER TAB ═══════════════════════════════════════════════ */}
      {activeSubTab === "danger" && <div className="space-y-4">

      {/* ── USER RESET ───────────────────────────────────────────────── */}
      <div className="rounded-2xl overflow-hidden border" style={{ background: "var(--surface-1)", borderColor: "rgba(239,68,68,0.20)" }}>
        <div className="px-5 py-4 flex items-center gap-3 border-b" style={{ borderColor: "rgba(239,68,68,0.15)" }}>
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(239,68,68,0.12)", color: "#ef4444" }}>
            <Trash2 className="w-4 h-4" />
          </div>
          <div>
            <p className="text-sm font-bold" style={{ color: "#ef4444" }}>{t("Admin.user_data_reset", lang)}</p>
            <p className="text-[11px]" style={{ color: "var(--text-secondary)" }}>{t("Admin.selectively_wipe_user_data_by_email", lang)}</p>
          </div>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: "var(--text-secondary)" }}>
              {t("Admin.user_email", lang)}
            </label>
            <input
              type="email"
              value={resetEmail}
              onChange={e => setResetEmail(e.target.value)}
              placeholder="user@example.com"
              className="w-full rounded-xl px-3 py-2.5 text-sm"
              style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)", color: "var(--text-primary)" }}
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold" style={{ color: "var(--text-secondary)" }}>{t("Admin.what_to_reset", lang)}</label>
              <div className="flex gap-2">
                <button onClick={resetSelectAll} className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full hover:opacity-80"
                  style={{ background: "rgba(239,68,68,0.12)", color: "#ef4444" }}>
                  {t("Admin.all", lang)}
                </button>
                <button onClick={resetDeselectAll} className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full hover:opacity-80"
                  style={{ background: "var(--surface-2)", color: "var(--text-secondary)" }}>
                  {t("Admin.none_2", lang)}
                </button>
              </div>
            </div>
            {[
              { key: "account", label: { en: "Account", ru: "Аккаунт", he: "חשבון" } },
              { key: "data",    label: { en: "Data",    ru: "Данные",  he: "נתונים" } },
              { key: "other",   label: { en: "Other",   ru: "Прочее",  he: "אחר" } },
            ].map(grp => (
              <div key={grp.key} className="mb-3">
                <p className="text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: "var(--text-secondary)", opacity: 0.6 }}>
                  {L(lang, grp.label.en, grp.label.ru, grp.label.he)}
                </p>
                <div className="grid grid-cols-2 gap-1.5">
                  {RESET_TARGETS.filter(t => t.group === grp.key).map(t => (
                    <label key={t.id} className="flex items-center gap-2 px-3 py-2 rounded-xl cursor-pointer hover:opacity-90 select-none"
                      style={{ background: resetSelected.has(t.id) ? "rgba(239,68,68,0.10)" : "var(--surface-2)", border: `1px solid ${resetSelected.has(t.id) ? "rgba(239,68,68,0.30)" : "var(--border-color)"}` }}>
                      <input type="checkbox" className="hidden" checked={resetSelected.has(t.id)} onChange={() => resetToggle(t.id)} />
                      <div className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0"
                        style={{ background: resetSelected.has(t.id) ? "#ef4444" : "var(--surface-3)", border: resetSelected.has(t.id) ? "none" : "1px solid var(--border-color)" }}>
                        {resetSelected.has(t.id) && <Check className="w-2.5 h-2.5 text-white" />}
                      </div>
                      <span className="text-[12px] font-medium leading-tight">{L(lang, t.label.en, t.label.ru, t.label.he)}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
            <div className="pt-3" style={{ borderTop: "1px solid rgba(239,68,68,0.20)" }}>
              {RESET_TARGETS.filter(tgt => tgt.group === "danger").map(tgt => (
                <label key={tgt.id} className="flex items-center gap-3 px-4 py-3 rounded-xl cursor-pointer hover:opacity-90 select-none"
                  style={{ background: resetSelected.has(tgt.id) ? "rgba(239,68,68,0.18)" : "rgba(239,68,68,0.06)", border: `1px solid ${resetSelected.has(tgt.id) ? "rgba(239,68,68,0.50)" : "rgba(239,68,68,0.20)"}` }}>
                  <input type="checkbox" className="hidden" checked={resetSelected.has(tgt.id)} onChange={() => resetToggle(tgt.id)} />
                  <div className="w-5 h-5 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ background: resetSelected.has(tgt.id) ? "#ef4444" : "rgba(239,68,68,0.15)", border: resetSelected.has(tgt.id) ? "none" : "1px solid rgba(239,68,68,0.35)" }}>
                    {resetSelected.has(tgt.id) ? <Check className="w-3 h-3 text-white" /> : <Trash2 className="w-3 h-3" style={{ color: "#ef4444" }} />}
                  </div>
                  <div className="flex-1">
                    <span className="text-[13px] font-black tracking-wide" style={{ color: "#ef4444" }}>{L(lang, tgt.label.en, tgt.label.ru, tgt.label.he)}</span>
                    <p className="text-[10px] mt-0.5" style={{ color: "rgba(239,68,68,0.7)" }}>
                      {t("Admin.permanently_deletes_the_user_record_from_databas", lang)}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {resetResult && (
            <div className="rounded-xl p-3 text-xs space-y-1" style={{ background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.20)" }}>
              <p className="font-bold" style={{ color: "#22c55e" }}>{t("Admin.reset_completed", lang)}</p>
              {Object.entries(resetResult).map(([k, v]) => (
                <div key={k} className="flex justify-between">
                  <span style={{ color: "var(--text-secondary)" }}>{k}</span>
                  <span className="font-semibold" style={{ color: "var(--text-primary)" }}>{v}</span>
                </div>
              ))}
              <button onClick={() => setResetResult(null)} className="text-[11px] mt-1 underline" style={{ color: "var(--text-secondary)" }}>
                {t("Admin.dismiss", lang)}
              </button>
            </div>
          )}

          <button
            onClick={handleResetConfirm1}
            disabled={!resetEmail.trim() || resetSelected.size === 0}
            className="w-full py-2.5 rounded-xl text-sm font-bold transition hover:opacity-80 disabled:opacity-30 disabled:cursor-not-allowed"
            style={{ background: "rgba(239,68,68,0.15)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.30)" }}>
            <Trash2 className="w-4 h-4 inline me-1.5" />
            {t("Admin.reset_selected_data", lang)}
            {resetSelected.size > 0 && <span className="ms-1.5 opacity-70">({resetSelected.size})</span>}
          </button>
        </div>
      </div>

      {/* ── RESET CONFIRM MODAL 1 ─────────────────────────────────────── */}
      {resetModal === "confirm1" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.7)" }}>
          <div className="rounded-2xl p-6 w-full max-w-md space-y-4" style={{ background: "var(--bg-secondary)", border: "1px solid rgba(239,68,68,0.25)" }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(239,68,68,0.15)", color: "#ef4444" }}>
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div>
                <p className="font-bold" style={{ color: "#ef4444" }}>{t("Admin.confirm_reset", lang)}</p>
                <p className="text-xs" style={{ color: "var(--text-secondary)" }}>{resetEmail}</p>
              </div>
            </div>
            <div className="rounded-xl p-3 space-y-1 text-xs" style={{ background: "var(--surface-2)" }}>
              <p className="font-semibold mb-1.5">{t("Admin.will_be_reset", lang)}</p>
              {[...resetSelected].map(id => {
                const tgt = RESET_TARGETS.find(x => x.id === id);
                return <div key={id} className="flex items-center gap-1.5"><span style={{ color: "#ef4444" }}>•</span>{L(lang, tgt.label.en, tgt.label.ru, tgt.label.he)}</div>;
              })}
            </div>
            <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
              {t("Admin.this_action_is_irreversible_continue", lang)}
            </p>
            <div className="flex gap-3">
              <button onClick={() => setResetModal(null)} className="flex-1 py-2.5 rounded-xl text-sm font-semibold hover:opacity-80"
                style={{ background: "var(--surface-2)", color: "var(--text-primary)" }}>
                {t("Admin.cancel", lang)}
              </button>
              <button onClick={handleResetConfirm2} className="flex-1 py-2.5 rounded-xl text-sm font-bold hover:opacity-80"
                style={{ background: "rgba(239,68,68,0.18)", color: "#ef4444", border: "1px solid rgba(239,68,68,0.35)" }}>
                {t("Admin.yes_continue", lang)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── RESET CONFIRM MODAL 2 ─────────────────────────────────────── */}
      {resetModal === "confirm2" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.7)" }}>
          <div className="rounded-2xl p-6 w-full max-w-md space-y-4" style={{ background: "var(--bg-secondary)", border: "1px solid rgba(239,68,68,0.35)" }}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(239,68,68,0.20)", color: "#ef4444" }}>
                <Trash2 className="w-5 h-5" />
              </div>
              <p className="font-bold text-base" style={{ color: "#ef4444" }}>
                {t("Admin.final_confirmation", lang)}
              </p>
            </div>
            <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
              {t("Admin.type_the_email_to_confirm", lang)}
              <span className="ms-1 font-mono font-bold" style={{ color: "var(--text-primary)" }}>{resetEmail}</span>
            </p>
            <input
              type="email"
              value={resetConfirmEmail}
              onChange={e => setResetConfirmEmail(e.target.value)}
              placeholder={resetEmail}
              className="w-full rounded-xl px-3 py-2.5 text-sm"
              style={{ background: "var(--surface-2)", border: `1px solid ${resetConfirmEmail.toLowerCase() === resetEmail.toLowerCase() && resetConfirmEmail ? "rgba(239,68,68,0.50)" : "var(--border-color)"}`, color: "var(--text-primary)" }}
            />
            <div className="flex gap-3">
              <button onClick={() => setResetModal(null)} className="flex-1 py-2.5 rounded-xl text-sm font-semibold hover:opacity-80"
                style={{ background: "var(--surface-2)", color: "var(--text-primary)" }}>
                {t("Admin.cancel", lang)}
              </button>
              <button
                onClick={handleResetExecute}
                disabled={resetBusy || resetConfirmEmail.trim().toLowerCase() !== resetEmail.trim().toLowerCase()}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold hover:opacity-80 disabled:opacity-30 disabled:cursor-not-allowed"
                style={{ background: "#ef4444", color: "#fff" }}>
                {resetBusy ? "..." : t("Admin.reset_now", lang)}
              </button>
            </div>
          </div>
        </div>
      )}

      </div>}{/* end danger tab */}

    </div>
  );
}

// ── Banner type config ────────────────────────────────────────────────────────
const BANNER_TYPE_CFG = {
  info:    { color: "#7aa2ff", bg: "rgba(64,93,230,0.10)",   border: "rgba(64,93,230,0.25)",   Icon: Info,          label: "Info" },
  warning: { color: "#F77737", bg: "rgba(247,119,55,0.10)",  border: "rgba(247,119,55,0.30)",  Icon: AlertTriangle, label: "Warning" },
  error:   { color: "#E1306C", bg: "rgba(225,48,108,0.10)",  border: "rgba(225,48,108,0.30)",  Icon: AlertCircle,   label: "Error" },
  success: { color: "#22c55e", bg: "rgba(34,197,94,0.10)",   border: "rgba(34,197,94,0.25)",   Icon: Check,         label: "Success" },
  promo:   { color: "#c084fc", bg: "rgba(131,58,180,0.10)",  border: "rgba(131,58,180,0.25)",  Icon: Sparkles,      label: "Promo" },
};

function SystemBannersCard({ lang }) {
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  
  const [banners, setBanners] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ type: "info", msg_en: "", msg_ru: "", msg_he: "", link_url: "", link_label_en: "", dismissible: true });

  const load = useCallback(async () => {
    const r = await fetch(`${API}/admin/banners`, { credentials: "include" });
    if (r.ok) setBanners(await r.json());
  }, [API]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.msg_en.trim() && !form.msg_ru.trim() && !form.msg_he.trim()) {
      toast.error(t("Admin.message_required", lang)); return;
    }
    const r = await fetch(`${API}/admin/banners`, {
      method: "POST", credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        type: form.type,
        message: { en: form.msg_en, ru: form.msg_ru, he: form.msg_he },
        link_url: form.link_url || null,
        link_label: form.link_url ? { en: form.link_label_en || "Learn more", ru: form.link_label_en || "Подробнее", he: form.link_label_en || "למידע נוסף" } : null,
        dismissible: form.dismissible,
      }),
    });
    if (r.ok) {
      toast.success(t("Admin.banner_created", lang));
      setShow(false);
      setForm({ type: "info", msg_en: "", msg_ru: "", msg_he: "", link_url: "", link_label_en: "", dismissible: true });
      load();
    }
  };

  const toggle = async (id) => {
    await fetch(`${API}/admin/banners/${id}/toggle`, { method: "PUT", credentials: "include" });
    load();
  };

  const del = async (id) => {
    await fetch(`${API}/admin/banners/${id}`, { method: "DELETE", credentials: "include" });
    load();
  };

  return (
    <div className="rounded-2xl overflow-hidden border" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
      <div className="p-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl flex items-center justify-center" style={{ background: "rgba(247,119,55,0.1)" }}>
            <Bell className="w-5 h-5 text-[#F77737]" />
          </div>
          <div>
            <h3 className="font-bold text-sm">{t("Admin.system_banners", lang)}</h3>
            <p className="text-[11px]" style={{ color: "var(--text-secondary)" }}>{t("Admin.shown_on_landing_page", lang)} · {banners.filter(b => b.is_active).length} {t("Admin.active_3", lang)}</p>
          </div>
        </div>
        <button onClick={() => setShow(v => !v)}
          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-xl font-semibold transition-all"
          style={{ background: "rgba(247,119,55,0.12)", color: "#F77737", border: "1px solid rgba(247,119,55,0.25)" }}>
          <Plus className="w-3.5 h-3.5" />{t("Admin.new_banner", lang)}
        </button>
      </div>

      {/* Create form */}
      {show && (
        <div className="border-t px-5 pb-5 pt-4 space-y-3 animate-fade-in" style={{ borderColor: "var(--border-color)" }}>
          {/* Type selector */}
          <div className="flex gap-2 flex-wrap">
            {Object.entries(BANNER_TYPE_CFG).map(([key, cfg]) => (
              <button key={key} onClick={() => setForm(f => ({ ...f, type: key }))}
                className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-xl font-semibold transition-all"
                style={{ background: form.type === key ? cfg.bg : "var(--surface-1)", color: form.type === key ? cfg.color : "var(--text-secondary)", border: `1px solid ${form.type === key ? cfg.border : "var(--surface-3)"}` }}>
                <cfg.Icon className="w-3 h-3" />{cfg.label}
              </button>
            ))}
          </div>
          {/* Messages */}
          {[["EN", "msg_en", "English text…"], ["RU", "msg_ru", "Текст на русском…"], ["HE", "msg_he", "טקסט בעברית…"]].map(([flag, key, ph]) => (
            <div key={flag} className="flex items-center gap-2">
              <span className="w-7 text-[11px] font-bold text-center rounded-lg py-0.5 flex-shrink-0" style={{ background: "var(--surface-2)", color: "var(--text-secondary)" }}>{flag}</span>
              <input value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} placeholder={ph}
                className="flex-1 rounded-xl px-3 py-2 text-sm" style={{ background: "var(--surface-2)", border: "1px solid var(--surface-3)", color: "var(--text-primary)" }} />
            </div>
          ))}
          {/* Link */}
          <div className="grid grid-cols-2 gap-2">
            <input value={form.link_url} onChange={e => setForm(f => ({ ...f, link_url: e.target.value }))} placeholder="Link URL (optional)"
              className="rounded-xl px-3 py-2 text-sm" style={{ background: "var(--surface-2)", border: "1px solid var(--surface-3)", color: "var(--text-primary)" }} />
            <input value={form.link_label_en} onChange={e => setForm(f => ({ ...f, link_label_en: e.target.value }))} placeholder="Link label"
              className="rounded-xl px-3 py-2 text-sm" style={{ background: "var(--surface-2)", border: "1px solid var(--surface-3)", color: "var(--text-primary)" }} />
          </div>
          {/* Dismissible toggle */}
          <label className="flex items-center gap-2 cursor-pointer">
            <div onClick={() => setForm(f => ({ ...f, dismissible: !f.dismissible }))}
              className="w-9 h-5 rounded-full transition-colors relative"
              style={{ background: form.dismissible ? "#22c55e" : "var(--surface-3)" }}>
              <div className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all shadow"
                style={{ left: form.dismissible ? "calc(100% - 1.125rem)" : "0.125rem" }} />
            </div>
            <span className="text-xs" style={{ color: "var(--text-secondary)" }}>{t("Admin.users_can_dismiss", lang)}</span>
          </label>
          <div className="flex gap-2">
            <button onClick={() => setShow(false)} className="flex-1 py-2 rounded-xl text-sm font-semibold" style={{ background: "var(--surface-2)" }}>
              {t("Admin.cancel", lang)}
            </button>
            <button onClick={create} className="flex-1 py-2 rounded-xl text-sm font-bold btn-brand">
              {t("Admin.create", lang)}
            </button>
          </div>
        </div>
      )}

      {/* Banner list */}
      {banners.length > 0 && (
        <div className="border-t divide-y" style={{ borderColor: "var(--border-color)" }}>
          {banners.map(b => {
            const cfg = BANNER_TYPE_CFG[b.type] || BANNER_TYPE_CFG.info;
            const msg = b.message?.ru || b.message?.en || b.message?.he || "";
            return (
              <div key={b.banner_id} className="px-5 py-3 flex items-center gap-3">
                <cfg.Icon className="w-4 h-4 flex-shrink-0" style={{ color: cfg.color }} />
                <span className="flex-1 text-sm truncate" style={{ color: b.is_active ? "var(--text-primary)" : "var(--text-secondary)", opacity: b.is_active ? 1 : 0.6 }}>{msg}</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full font-bold flex-shrink-0"
                  style={{ background: b.is_active ? "rgba(34,197,94,0.1)" : "rgba(248,113,113,0.1)", color: b.is_active ? "#22c55e" : "#f87171" }}>
                  {b.is_active ? t("Admin.active_4", lang) : t("Admin.off", lang)}
                </span>
                <button onClick={() => toggle(b.banner_id)} className="w-8 h-8 rounded-xl flex items-center justify-center transition-colors"
                  style={{ background: b.is_active ? "rgba(34,197,94,0.1)" : "rgba(100,100,100,0.1)", color: b.is_active ? "#22c55e" : "var(--text-secondary)" }}>
                  <Power className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => del(b.banner_id)} className="w-8 h-8 rounded-xl flex items-center justify-center hover:bg-red-900/20 hover:text-red-400 transition-colors"
                  style={{ color: "var(--text-secondary)" }}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ===== AI CHATS TAB =====
function AiChatsTab({ lang, onOpenTicket }) {
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetch(`${API}/admin/ai-conversations`, { credentials: "include" })
      .then(r => r.ok ? r.json() : [])
      .then(d => { setTickets(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const filtered = tickets.filter(t =>
    !search || t.user_name?.toLowerCase().includes(search.toLowerCase()) ||
    t.user_email?.toLowerCase().includes(search.toLowerCase()) ||
    t.subject?.toLowerCase().includes(search.toLowerCase())
  );

  function fmtDate(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleString([], { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-black font-outfit" style={{ color: "var(--text-primary)" }}>
          {t("Admin.ai_chats", lang)}
        </h1>
        <p className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>
          {t("Admin.all_ai_conversations_from_the_support_widget", lang)}
        </p>
      </div>

      {/* Search */}
      <div className="mb-4">
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={t("Admin.search_by_user_or_subject", lang)}
          className="w-full h-10 rounded-xl px-3 text-sm"
          style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)", color: "var(--text-primary)" }}
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin h-6 w-6 border-2 border-[#8B5CF6] border-t-transparent rounded-full" /></div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center py-16 gap-3" style={{ color: "var(--text-secondary)" }}>
          <Sparkles className="w-10 h-10 opacity-20" />
          <p className="text-sm">{t("Admin.no_ai_conversations_yet_2", lang)}</p>
        </div>
      ) : (
        <div className="flex gap-4">
          {/* List */}
          <div className="w-72 shrink-0 space-y-2">
            {filtered.map(ticket => {
              const conv = ticket.ai_conversation || [];
              const isSelected = selected?.ticket_id === ticket.ticket_id;
              return (
                <button key={ticket.ticket_id} onClick={() => setSelected(ticket)}
                  className="w-full text-left rounded-2xl p-3 transition-all"
                  style={{
                    background: isSelected ? "rgba(139,92,246,0.1)" : "var(--surface-1)",
                    border: `1px solid ${isSelected ? "rgba(139,92,246,0.3)" : "var(--border-color)"}`,
                  }}>
                  <div className="flex items-start gap-2.5">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0"
                      style={{ background: "linear-gradient(135deg,#8B5CF6,#4F46E5)" }}>
                      {(ticket.user_name || "?")[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold truncate" style={{ color: "var(--text-primary)" }}>{ticket.user_name || ticket.user_email}</p>
                      <p className="text-[10px] truncate mt-0.5" style={{ color: "var(--text-secondary)" }}>{ticket.subject}</p>
                      <div className="flex items-center gap-1.5 mt-1">
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium"
                          style={{ background: "rgba(139,92,246,0.1)", color: "#8B5CF6" }}>
                          {conv.length} {t("Admin.msgs_2", lang)}
                        </span>
                        <span className="text-[10px]" style={{ color: "var(--text-secondary)" }}>
                          {ticket.status === "closed" ? "✅" : ticket.status === "open" || ticket.status === "in_progress" ? "🎧" : ""}
                        </span>
                      </div>
                    </div>
                  </div>
                  <p className="text-[10px] mt-1.5" style={{ color: "var(--text-secondary)" }}>{fmtDate(ticket.created_at)}</p>
                </button>
              );
            })}
          </div>

          {/* Detail */}
          <div className="flex-1 min-w-0">
            {!selected ? (
              <div className="flex flex-col items-center justify-center h-64 rounded-2xl gap-3"
                style={{ background: "var(--surface-1)", border: "1px solid var(--border-color)", color: "var(--text-secondary)" }}>
                <Sparkles className="w-8 h-8 opacity-20" />
                <p className="text-sm">{t("Admin.select_a_conversation", lang)}</p>
              </div>
            ) : (
              <div className="rounded-2xl overflow-hidden" style={{ background: "var(--surface-1)", border: "1px solid var(--border-color)" }}>
                {/* Header */}
                <div className="px-4 py-3 flex items-center gap-3" style={{ borderBottom: "1px solid var(--border-color)" }}>
                  <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white shrink-0"
                    style={{ background: "linear-gradient(135deg,#8B5CF6,#4F46E5)" }}>
                    {(selected.user_name || "?")[0].toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold truncate" style={{ color: "var(--text-primary)" }}>{selected.user_name || selected.user_email}</p>
                    <p className="text-xs truncate" style={{ color: "var(--text-secondary)" }}>{selected.user_email}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs px-2 py-1 rounded-lg font-medium"
                      style={{ background: "rgba(139,92,246,0.1)", color: "#8B5CF6" }}>
                      {(selected.ai_conversation || []).length} {t("Admin.messages_2", lang)}
                    </span>
                    {selected.ticket_id && (
                      <button onClick={() => onOpenTicket?.(selected.ticket_id)}
                        className="text-xs px-2.5 py-1 rounded-lg font-semibold text-white transition-opacity hover:opacity-80"
                        style={{ background: "#405DE6" }}>
                        {t("Admin.open_ticket_2", lang)} →
                      </button>
                    )}
                  </div>
                </div>

                {/* Subject */}
                <div className="px-4 py-2 text-xs" style={{ borderBottom: "1px solid var(--border-color)", color: "var(--text-secondary)" }}>
                  <span className="font-semibold" style={{ color: "var(--text-primary)" }}>{t("Admin.subject_3", lang)} </span>
                  {selected.subject}
                  <span className="ml-3">{fmtDate(selected.created_at)}</span>
                </div>

                {/* AI messages */}
                <div className="p-4 space-y-3 max-h-[500px] overflow-y-auto">
                  {(selected.ai_conversation || []).map((msg, i) => {
                    const isUser = msg.role === "user";
                    return (
                      <div key={i} className={`flex gap-2 ${isUser ? "" : "flex-row-reverse"}`}>
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs shrink-0 ${isUser ? "bg-secondary text-muted-foreground" : ""}`}
                          style={isUser ? {} : { background: "linear-gradient(135deg,#8B5CF6,#4F46E5)" }}>
                          {isUser ? "👤" : <Sparkles className="w-3 h-3 text-white" />}
                        </div>
                        <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-xs leading-relaxed ${
                          isUser
                            ? "rounded-tl-sm bg-secondary text-secondary-foreground"
                            : "rounded-tr-sm text-white"
                        }`}
                          style={isUser ? {} : { background: "linear-gradient(135deg,#8B5CF6,#4F46E5)" }}>
                          {msg.content}
                        </div>
                      </div>
                    );
                  })}
                  {(selected.ai_conversation || []).length === 0 && (
                    <p className="text-center text-xs py-4" style={{ color: "var(--text-secondary)" }}>
                      {t("Admin.no_messages", lang)}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SupportAnalyticsTab({ lang }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/admin/tickets/analytics`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const fmtMin = (min) => {
    if (min === null || min === undefined) return "—";
    if (min < 60) return `${min}м`;
    const h = Math.floor(min / 60), m = min % 60;
    return m > 0 ? `${h}ч ${m}м` : `${h}ч`;
  };

  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-6 h-6 rounded-full border-2 border-[#405DE6] border-t-transparent animate-spin" /></div>;
  if (!data) return <div className="p-8 text-center text-red-400">Ошибка загрузки аналитики</div>;

  const ov = data.overview || {};
  const periods = data.periods || {};
  const kpis = [
    { label: t("Admin.total_tickets", lang), value: ov.total, color: "#405DE6" },
    { label: t("Admin.open_2", lang), value: ov.open, color: "#F77737" },
    { label: t("Admin.closed_3", lang), value: ov.closed, color: "#00C853" },
    { label: t("Admin.unread_3", lang), value: ov.unread, color: "#E1306C" },
    { label: t("Admin.urgent_4", lang), value: ov.urgent_open, color: "#833AB4" },
    { label: t("Admin.csat", lang), value: ov.csat !== null && ov.csat !== undefined ? `${ov.csat}%` : "—", color: "#00C853" },
    { label: t("Admin.avg_frt", lang), value: fmtMin(ov.avg_frt_min), color: "#405DE6" },
    { label: t("Admin.avg_resolution", lang), value: fmtMin(ov.avg_resolution_min), color: "#F77737" },
  ];

  const PRIO_ICONS = { urgent: "🔴", high: "🟠", medium: "🟡", low: "🟢", none: "⚪" };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-bold flex items-center gap-2">
          <BarChart2 className="w-5 h-5 text-[#405DE6]" />
          {t("Admin.support_analytics", lang)}
        </h2>
        <span className="text-xs" style={{ color: "var(--text-secondary)" }}>{t("Admin.last_30_days", lang)}</span>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {kpis.map(k => (
          <div key={k.label} className="rounded-2xl border p-4" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
            <div className="text-xs mb-1" style={{ color: "var(--text-secondary)" }}>{k.label}</div>
            <div className="text-2xl font-bold" style={{ color: k.color }}>{k.value ?? "—"}</div>
          </div>
        ))}
      </div>

      {/* Period breakdown */}
      <div className="rounded-2xl border p-5" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
        <h3 className="text-sm font-bold mb-4">{t("Admin.period_breakdown", lang)}</h3>
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: t("Admin.today", lang), d: periods.today },
            { label: t("Admin.this_week_2", lang), d: periods.week },
            { label: t("Admin.this_month_4", lang), d: periods.month },
          ].map(({ label, d }) => (
            <div key={label} className="text-center">
              <div className="text-xs mb-2 font-medium" style={{ color: "var(--text-secondary)" }}>{label}</div>
              <div className="flex justify-center gap-4">
                <div>
                  <div className="text-lg font-bold text-[#405DE6]">{d?.new ?? 0}</div>
                  <div className="text-[10px]" style={{ color: "var(--text-secondary)" }}>{t("Admin.new_2", lang)}</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-[#00C853]">{d?.closed ?? 0}</div>
                  <div className="text-[10px]" style={{ color: "var(--text-secondary)" }}>{t("Admin.closed_4", lang)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Per-admin leaderboard */}
        <div className="rounded-2xl border p-5" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
          <h3 className="text-sm font-bold mb-4">{t("Admin.admin_activity_30d", lang)}</h3>
          {(data.per_admin || []).length === 0
            ? <p className="text-xs" style={{ color: "var(--text-secondary)" }}>{t("Admin.no_data", lang)}</p>
            : <div className="space-y-3">
                {(data.per_admin || []).map((a, i) => (
                  <div key={a.id} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0" style={{ background: i === 0 ? "rgba(247,119,55,0.2)" : "var(--surface-2)", color: i === 0 ? "#F77737" : "var(--text-secondary)" }}>
                      {i + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium truncate">{a.name}</div>
                      <div className="text-[10px]" style={{ color: "var(--text-secondary)" }}>
                        {a.ticket_count} {t("Admin.tickets_2", lang)}  ·  {a.notes} {t("Admin.notes", lang)}
                      </div>
                    </div>
                    <div className="text-sm font-bold text-[#405DE6] shrink-0">{a.replies}</div>
                  </div>
                ))}
              </div>
          }
        </div>

        {/* Priority distribution */}
        <div className="rounded-2xl border p-5" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
          <h3 className="text-sm font-bold mb-4">{t("Admin.priority_distribution_open", lang)}</h3>
          <div className="space-y-2">
            {["urgent","high","medium","low","none"].map(p => {
              const cnt = (data.priority_dist || {})[p] || 0;
              const total = Object.values(data.priority_dist || {}).reduce((a, b) => a + b, 0) || 1;
              return (
                <div key={p} className="flex items-center gap-2">
                  <span className="text-sm">{PRIO_ICONS[p]}</span>
                  <span className="text-xs w-16 capitalize" style={{ color: "var(--text-secondary)" }}>{p}</span>
                  <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: "var(--surface-2)" }}>
                    <div className="h-full rounded-full" style={{ width: `${(cnt/total*100).toFixed(0)}%`, background: p === "urgent" ? "#E1306C" : p === "high" ? "#F77737" : p === "medium" ? "#FCAF45" : "#00C853" }} />
                  </div>
                  <span className="text-xs font-bold w-6 text-end">{cnt}</span>
                </div>
              );
            })}
          </div>

          {/* Top tags */}
          {(data.top_tags || []).length > 0 && (
            <div className="mt-5">
              <div className="text-xs font-semibold mb-2" style={{ color: "var(--text-secondary)" }}>{t("Admin.top_tags", lang)}</div>
              <div className="flex flex-wrap gap-1.5">
                {(data.top_tags || []).map(t => (
                  <span key={t.tag} className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: "rgba(64,93,230,0.12)", color: "#405DE6" }}>
                    #{t.tag} <span style={{ opacity: 0.7 }}>{t.count}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Weekly chart */}
      <div className="rounded-2xl border p-5" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
        <h3 className="text-sm font-bold mb-4">{t("Admin.weekly_trend", lang)}</h3>
        <div className="flex items-end gap-3">
          {(data.weekly || []).map((w, i) => {
            const maxVal = Math.max(...(data.weekly || []).map(x => Math.max(x.new, x.closed)), 1);
            return (
              <div key={i} className="flex-1 text-center">
                <div className="flex items-end justify-center gap-1 h-20 mb-1">
                  <div className="w-3 rounded-t-sm" style={{ height: `${(w.new / maxVal * 100).toFixed(0)}%`, background: "#405DE6", opacity: 0.8 }} title={`New: ${w.new}`} />
                  <div className="w-3 rounded-t-sm" style={{ height: `${(w.closed / maxVal * 100).toFixed(0)}%`, background: "#00C853", opacity: 0.8 }} title={`Closed: ${w.closed}`} />
                </div>
                <div className="text-[10px]" style={{ color: "var(--text-secondary)" }}>{w.label}</div>
              </div>
            );
          })}
        </div>
        <div className="flex gap-4 mt-2">
          <div className="flex items-center gap-1.5 text-[11px]" style={{ color: "var(--text-secondary)" }}>
            <div className="w-2.5 h-2.5 rounded-sm bg-[#405DE6] opacity-80" />{t("Admin.new_3", lang)}
          </div>
          <div className="flex items-center gap-1.5 text-[11px]" style={{ color: "var(--text-secondary)" }}>
            <div className="w-2.5 h-2.5 rounded-sm bg-[#00C853] opacity-80" />{t("Admin.closed_5", lang)}
          </div>
        </div>
      </div>
    </div>
  );
}

function InstagramLocksTab({ lang }) {
  const [locks, setLocks] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [deletingId, setDeletingId] = useState("");
  const PER_PAGE = 30;

  const fetchLocks = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), per_page: String(PER_PAGE), search });
      if (typeFilter !== "all") params.set("service_type", typeFilter);
      const r = await fetch(`${API}/admin/instagram-locks?${params}`, { credentials: "include" });
      const d = await r.json();
      setLocks(d.locks || []);
      setTotal(d.total || 0);
    } catch {}
    setLoading(false);
  }, [page, search, typeFilter]);

  useEffect(() => { fetchLocks(); }, [fetchLocks]);

  const deleteLock = async (lockId) => {
    setDeletingId(lockId);
    try {
      const r = await fetch(`${API}/admin/instagram-locks/${lockId}`, { method: "DELETE", credentials: "include" });
      if (!r.ok) throw new Error("Failed");
      toast.success(t("Admin.lock_deleted", lang));
      if (expanded === lockId) setExpanded(null);
      fetchLocks();
    } catch {
      toast.error(t("Admin.failed_to_delete", lang));
    }
    setDeletingId("");
  };

  const lockedCount  = locks.filter(l => l.count >= 2).length;
  const likesCount   = locks.filter(l => l.service_type === "likes").length;
  const followCount  = locks.filter(l => l.service_type === "followers").length;
  const blockedAttempts = locks.reduce((s, l) => s + (l.blocked_attempts?.length || 0), 0);

  return (
    <div className="space-y-4">

      {/* Header */}
      <div className="rounded-2xl p-5" style={{ background: "var(--surface-1)", border: "1px solid var(--border-color)" }}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <AdminIcon emoji="🔒" className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-black font-outfit" style={{ color: "var(--text-primary)" }}>
                {t("Admin.instagram_free_locks", lang)}
              </h2>
              <p className="text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>
                {t("Admin.accounts_that_used_both_free_services_likes_foll", lang)}
              </p>
            </div>
          </div>
          <button onClick={fetchLocks}
            className="h-8 px-3 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-all hover:opacity-80"
            style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)", color: "var(--text-secondary)" }}>
            <RefreshCw className="w-3.5 h-3.5" />{t("Admin.refresh", lang)}
          </button>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
          {[
            { label: t("Admin.total_locks", lang), value: total, color: "#E1306C" },
            { label: t("Admin.fully_locked", lang), value: lockedCount, color: "#ef4444" },
            { label: t("Admin.blocked_attempts", lang), value: blockedAttempts, color: "#F77737" },
            { label: t("Admin.likes_followers", lang), value: `${likesCount} / ${followCount}`, color: "#8B5CF6" },
          ].map((s, i) => (
            <div key={i} className="rounded-xl px-3 py-2.5" style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)" }}>
              <div className="text-[10px] uppercase tracking-wider font-semibold mb-0.5" style={{ color: "var(--text-secondary)" }}>{s.label}</div>
              <div className="text-xl font-black font-outfit" style={{ color: s.color }}>{s.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute top-1/2 -translate-y-1/2 start-3 w-4 h-4" style={{ color: "var(--text-secondary)" }} />
          <Input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder={t("Admin.search_instagram", lang)}
            className="h-9 ps-9 text-sm" style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }} />
        </div>
        {/* Type filter pills */}
        {[
          { v: "all",       label: t("Admin.all_types", lang),           color: "#9ca3af" },
          { v: "likes",     label: t("shop.likes", lang),  color: "#E1306C" },
          { v: "followers", label: t("shop.followers", lang), color: "#833AB4" },
        ].map(opt => (
          <button key={opt.v} type="button" onClick={() => { setTypeFilter(opt.v); setPage(1); }}
            className="h-9 px-4 rounded-xl text-xs font-semibold border transition-all"
            style={typeFilter === opt.v
              ? { background: `${opt.color}18`, borderColor: `${opt.color}40`, color: opt.color }
              : { background: "transparent", borderColor: "var(--border-color)", color: "var(--text-secondary)" }}>
            {opt.label}
          </button>
        ))}
        <span className="text-xs ms-1" style={{ color: "var(--text-secondary)" }}>
          {total} {t("Admin.records", lang)}
        </span>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-6 h-6 border-2 rounded-full border-t-transparent animate-spin" style={{ borderColor: "#E1306C", borderTopColor: "transparent" }} />
        </div>
      ) : locks.length === 0 ? (
        <div className="rounded-2xl border p-12 text-center" style={{ borderColor: "var(--border-color)", background: "var(--surface-1)" }}>
          <Lock className="w-10 h-10 mx-auto mb-3 opacity-20" style={{ color: "var(--text-secondary)" }} />
          <p className="font-semibold text-sm" style={{ color: "var(--text-primary)" }}>
            {t("Admin.no_locks_found", lang)}
          </p>
          <p className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>
            {search || typeFilter !== "all"
              ? t("Admin.try_changing_filters", lang)
              : t("Admin.no_free_service_locks_yet", lang)}
          </p>
        </div>
      ) : (
        <div className="rounded-2xl border overflow-hidden" style={{ borderColor: "var(--border-color)" }}>
          {/* Table header */}
          <div className="grid px-4 py-2 text-[10px] uppercase tracking-wider font-semibold"
            style={{ gridTemplateColumns: "1fr 80px 70px 100px 36px", background: "var(--surface-1)", borderBottom: "1px solid var(--border-color)", color: "var(--text-secondary)" }}>
            <span>{t("Admin.instagram_account", lang)}</span>
            <span className="text-center">{t("Admin.service", lang)}</span>
            <span className="text-center">{t("Admin.usage_2", lang)}</span>
            <span className="text-end">{t("Admin.last_activity", lang)}</span>
            <span />
          </div>

          {locks.map((lock, idx) => {
            const isExpanded = expanded === lock.lock_id;
            const isFullLocked = lock.count >= 2;
            const svcColor = lock.service_type === "likes" ? "#E1306C" : "#833AB4";
            const lastAt = lock.updated_at || lock.created_at;

            return (
              <div key={lock.lock_id}>
                {idx > 0 && <div style={{ height: "1px", background: "var(--border-color)" }} />}
                <div
                  className="grid px-4 py-3 cursor-pointer transition-colors hover:bg-white/[0.02]"
                  style={{ gridTemplateColumns: "1fr 80px 70px 100px 36px", background: isExpanded ? "var(--surface-2)" : "transparent" }}
                  onClick={() => setExpanded(isExpanded ? null : lock.lock_id)}>

                  {/* Account */}
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: "rgba(225,48,108,0.1)" }}>
                      <Instagram className="w-3.5 h-3.5" style={{ color: "#E1306C" }} />
                    </div>
                    <div className="min-w-0">
                      <span className="text-sm font-medium truncate block" style={{ color: "var(--text-primary)" }}>
                        {lock.instagram_target}
                      </span>
                      {(lock.blocked_attempts?.length || 0) > 0 && (
                        <span className="text-[10px] font-semibold" style={{ color: "#ef4444" }}>
                          {lock.blocked_attempts.length} {t("Admin.blocked_3", lang)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Service type */}
                  <div className="flex items-center justify-center">
                    <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold"
                      style={{ background: `${svcColor}15`, color: svcColor, border: `1px solid ${svcColor}30` }}>
                      {lock.service_type === "likes" ? t("shop.likes", lang) : t("Admin.followers_2", lang)}
                    </span>
                  </div>

                  {/* Usage bar */}
                  <div className="flex items-center justify-center gap-2">
                    <div className="flex gap-0.5">
                      {[0, 1].map(i => (
                        <div key={i} className="w-3 h-3 rounded-full"
                          style={{ background: i < lock.count ? (isFullLocked ? "#ef4444" : "#22c55e") : "var(--surface-3)" }} />
                      ))}
                    </div>
                    <span className="text-xs font-bold" style={{ color: isFullLocked ? "#ef4444" : "#22c55e" }}>
                      {lock.count}/2
                    </span>
                  </div>

                  {/* Date */}
                  <div className="flex items-center justify-end">
                    <span className="text-[11px]" dir="ltr" style={{ color: "var(--text-secondary)" }}>
                      {lastAt ? formatIsraelDate(lastAt) : "—"}
                    </span>
                  </div>

                  {/* Delete */}
                  <div className="flex items-center justify-end" onClick={e => e.stopPropagation()}>
                    <button onClick={() => deleteLock(lock.lock_id)} disabled={deletingId === lock.lock_id}
                      className="w-7 h-7 rounded-lg flex items-center justify-center transition-all hover:opacity-80 disabled:opacity-40"
                      style={{ color: "#ef4444" }}>
                      {deletingId === lock.lock_id
                        ? <div className="w-3 h-3 rounded-full border-2 border-red-400/30 border-t-red-400 animate-spin" />
                        : <Trash2 className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <div className="px-4 pb-4" style={{ background: "var(--surface-2)", borderTop: "1px solid var(--border-color)" }}>
                    <div className="grid sm:grid-cols-2 gap-3 pt-3">
                      {/* Successful attempts */}
                      {(lock.attempts?.length || 0) > 0 && (
                        <div className="rounded-xl p-3" style={{ background: "var(--surface-1)", border: "1px solid var(--border-color)" }}>
                          <div className="text-[10px] uppercase tracking-wider font-semibold mb-2" style={{ color: "#22c55e" }}>
                            {t("Admin.free_services_used", lang)} ({lock.attempts.length})
                          </div>
                          <div className="space-y-1.5">
                            {lock.attempts.map((a, i) => (
                              <div key={i} className="flex items-center gap-2 text-xs">
                                <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                                  style={{ background: "rgba(34,197,94,0.15)", color: "#22c55e" }}>
                                  <Check className="w-2.5 h-2.5" />
                                </div>
                                <span className="flex-1 truncate" style={{ color: "var(--text-primary)" }}>{a.user_email}</span>
                                <span className="flex-shrink-0 text-[10px]" style={{ color: "var(--text-secondary)" }}>
                                  {a.attempted_at ? formatIsraelDateTime(a.attempted_at) : ""}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {/* Blocked attempts */}
                      {(lock.blocked_attempts?.length || 0) > 0 && (
                        <div className="rounded-xl p-3" style={{ background: "var(--surface-1)", border: "1px solid var(--border-color)" }}>
                          <div className="text-[10px] uppercase tracking-wider font-semibold mb-2" style={{ color: "#ef4444" }}>
                            {t("Admin.blocked_attempts_2", lang)} ({lock.blocked_attempts.length})
                          </div>
                          <div className="space-y-1.5">
                            {lock.blocked_attempts.map((a, i) => (
                              <div key={i} className="flex items-center gap-2 text-xs">
                                <div className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0"
                                  style={{ background: "rgba(239,68,68,0.15)", color: "#ef4444" }}>
                                  <X className="w-2.5 h-2.5" />
                                </div>
                                <span className="flex-1 truncate" style={{ color: "var(--text-primary)" }}>{a.user_email}</span>
                                <span className="flex-shrink-0 text-[10px]" style={{ color: "var(--text-secondary)" }}>
                                  {a.attempted_at ? formatIsraelDateTime(a.attempted_at) : ""}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center justify-between mt-3 pt-3" style={{ borderTop: "1px solid var(--border-color)" }}>
                      <div className="flex items-center gap-3 text-[11px]" style={{ color: "var(--text-secondary)" }}>
                        <span>ID: <span dir="ltr" className="font-mono">{lock.lock_id}</span></span>
                        <span>{t("Admin.created", lang)}: {formatIsraelDate(lock.created_at)}</span>
                      </div>
                      <button onClick={() => deleteLock(lock.lock_id)} disabled={deletingId === lock.lock_id}
                        className="h-8 px-3 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-all hover:opacity-80 disabled:opacity-40"
                        style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.25)", color: "#f87171" }}>
                        {deletingId === lock.lock_id
                          ? <div className="w-3 h-3 rounded-full border-2 border-red-400/30 border-t-red-400 animate-spin" />
                          : <Trash2 className="w-3.5 h-3.5" />}
                        {t("Admin.delete_lock", lang)}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {total > PER_PAGE && (
        <div className="flex items-center justify-center gap-2">
          <button disabled={page === 1} onClick={() => setPage(p => p - 1)}
            className="h-8 w-8 rounded-xl text-sm font-semibold flex items-center justify-center disabled:opacity-30 transition-all hover:opacity-80"
            style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)", color: "var(--text-secondary)" }}>
            ‹
          </button>
          <span className="text-xs px-3 py-1.5 rounded-xl font-semibold" style={{ background: "var(--surface-2)", color: "var(--text-primary)" }}>
            {page} / {Math.ceil(total / PER_PAGE)}
          </span>
          <button disabled={page * PER_PAGE >= total} onClick={() => setPage(p => p + 1)}
            className="h-8 w-8 rounded-xl text-sm font-semibold flex items-center justify-center disabled:opacity-30 transition-all hover:opacity-80"
            style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)", color: "var(--text-secondary)" }}>
            ›
          </button>
        </div>
      )}
    </div>
  );
}

// ── Emails Tab ────────────────────────────────────────────────────────────────
const EMAIL_CATEGORY_CFG = {
  order_completed: { icon: "✅", color: "#22c55e",  label: { en: "Order",    ru: "Заказ",       he: "הזמנה"     } },
  topup_approval:  { icon: "🪙", color: "#F77737",  label: { en: "Top-up",   ru: "Пополнение",  he: "טעינה"     } },
  tasks:           { icon: "🎯", color: "#8B5CF6",  label: { en: "Task",     ru: "Задание",     he: "משימה"     } },
  global_bonus:    { icon: "🎉", color: "#E1306C",  label: { en: "Bonus",    ru: "Бонус",       he: "בונוס"     } },
  support_replies: { icon: "💬", color: "#a855f7",  label: { en: "Support",  ru: "Поддержка",   he: "תמיכה"     } },
  default:         { icon: "📧", color: "#8B5CF6",  label: { en: "Email",    ru: "Письмо",      he: "מייל"      } },
};

function BonusCampaignsSummary({ lang }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);

  const load = () => {
    fetch(`${API}/admin/bonus-campaigns?per_page=30`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setCampaigns(d.campaigns || []); })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openDetail = async (id) => {
    if (expanded === id) { setExpanded(null); setDetail(null); return; }
    setExpanded(id);
    setDetail(null);
    setDetailLoading(true);
    try {
      const r = await fetch(`${API}/admin/bonus-campaigns/${id}`, { credentials: "include" });
      if (r.ok) setDetail(await r.json());
    } finally { setDetailLoading(false); }
  };

  const resend = async (campaignId, failedCount) => {
    if (!failedCount) return;
    setResendBusy(campaignId);
    try {
      const r = await fetch(`${API}/admin/bonus-campaigns/${campaignId}/resend`, { method: "POST", credentials: "include" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail);
      toast.success(L(lang, `Resending to ${d.resent} users…`, `Повторная отправка ${d.resent} пользователям…`, `שולח שוב ל-${d.resent}…`));
      load();
    } catch (e) { toast.error(e.message); }
    finally { setResendBusy(false); }
  };

  const STATUS_CFG = {
    pending: { label: { en: "Queued",   ru: "В очереди", he: "בתור"   }, dot: "#F77737", bg: "rgba(247,119,55,0.12)"  },
    sending: { label: { en: "Sending",  ru: "Отправка",  he: "שולח"   }, dot: "#06B6D4", bg: "rgba(6,182,212,0.12)"   },
    done:    { label: { en: "Delivered",ru: "Доставлено",he: "נשלח"   }, dot: "#22c55e", bg: "rgba(34,197,94,0.12)"   },
  };

  if (loading) return (
    <div className="rounded-2xl border p-6 flex items-center justify-center gap-3" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
      <div className="animate-spin h-5 w-5 border-2 border-[#E1306C] border-t-transparent rounded-full" />
      <span className="text-sm" style={{ color: "var(--text-secondary)" }}>{L(lang,"Loading campaigns…","Загрузка кампаний…","טוען קמפיינים…")}</span>
    </div>
  );
  if (!campaigns.length) return (
    <div className="rounded-2xl border p-10 text-center" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
      <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3" style={{ background: "rgba(225,48,108,0.08)" }}>
        <span className="text-2xl">📧</span>
      </div>
      <p className="font-bold text-sm" style={{ color: "var(--text-primary)" }}>{L(lang,"No email campaigns yet","Кампаний пока нет","אין קמפיינים עדיין")}</p>
      <p className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>{L(lang,"Campaigns will appear here after you send a bonus.","Кампании появятся после отправки бонуса.","קמפיינים יופיעו כאן לאחר שליחת בונוס.")}</p>
    </div>
  );

  const totalSent    = campaigns.reduce((s, c) => s + (c.sent_count    || 0), 0);
  const totalFailed  = campaigns.reduce((s, c) => s + (c.failed_count  || 0), 0);
  const totalSkipped = campaigns.reduce((s, c) => s + (c.skipped_count || 0), 0);

  return (
    <div className="space-y-4 mb-6">
      {/* Header + aggregate stats */}
      <div className="rounded-2xl overflow-hidden border" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
        {/* Title strip */}
        <div className="px-5 py-4 flex items-center gap-3" style={{ background: "linear-gradient(135deg, rgba(225,48,108,0.08) 0%, rgba(139,92,246,0.08) 100%)", borderBottom: "1px solid var(--border-color)" }}>
          <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg,#E1306C,#8B5CF6)" }}>
            <span className="text-base">🎉</span>
          </div>
          <div>
            <h3 className="font-bold text-sm" style={{ color: "var(--text-primary)" }}>{L(lang,"Bonus Email Campaigns","Бонусные email-кампании","קמפיינים של בונוס")}</h3>
            <p className="text-[11px] mt-0.5" style={{ color: "var(--text-secondary)" }}>{campaigns.length} {L(lang,"campaigns total","кампаний всего","קמפיינים סה\"כ")}</p>
          </div>
          <button onClick={load} className="ms-auto p-1.5 rounded-lg hover:opacity-70 transition-opacity" style={{ color: "var(--text-secondary)" }}>
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {/* Aggregate stat pills */}
        <div className="grid grid-cols-3 divide-x" style={{ borderColor: "var(--border-color)" }}>
          {[
            { label: { en:"Total Sent",    ru:"Всего отправлено", he:"נשלח סה\"כ" }, value: totalSent,    color: "#22c55e", bg: "rgba(34,197,94,0.06)"    },
            { label: { en:"Total Failed",  ru:"Ошибок",           he:"נכשל סה\"כ" }, value: totalFailed,  color: "#ef4444", bg: "rgba(239,68,68,0.06)"    },
            { label: { en:"Total Skipped", ru:"Пропущено",        he:"דולג סה\"כ" }, value: totalSkipped, color: "#F77737", bg: "rgba(247,119,55,0.06)"   },
          ].map(s => (
            <div key={s.color} className="px-4 py-3 text-center" style={{ background: s.bg }}>
              <div className="text-xl font-black" style={{ color: s.color }}>{s.value.toLocaleString()}</div>
              <div className="text-[10px] font-semibold uppercase tracking-wide mt-0.5" style={{ color: "var(--text-secondary)" }}>{s.label[lang] || s.label.en}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Campaign list */}
      <div className="space-y-2">
        {campaigns.map((c, idx) => {
          const cfg  = STATUS_CFG[c.status] || STATUS_CFG.done;
          const prog = c.total_recipients > 0 ? Math.round(((c.sent_count + c.failed_count) / c.total_recipients) * 100) : 100;
          const successRate = c.total_recipients > 0 ? Math.round((c.sent_count / c.total_recipients) * 100) : 0;
          const isOpen = expanded === c.campaign_id;
          const dateStr = c.created_at ? new Date(c.created_at).toLocaleString(lang === "he" ? "he-IL" : lang === "ru" ? "ru-RU" : "en-GB", { day:"2-digit", month:"2-digit", year:"2-digit", hour:"2-digit", minute:"2-digit" }) : "";

          return (
            <div key={c.campaign_id} className="rounded-2xl overflow-hidden border transition-all"
              style={{ background: "var(--surface-1)", borderColor: isOpen ? "rgba(225,48,108,0.35)" : "var(--border-color)", boxShadow: isOpen ? "0 0 0 1px rgba(225,48,108,0.15)" : "none" }}>

              {/* Campaign row */}
              <div className="px-4 py-3.5 cursor-pointer select-none" onClick={() => openDetail(c.campaign_id)}>
                <div className="flex items-center gap-3">
                  {/* Index bubble */}
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-xs font-black"
                    style={{ background: "linear-gradient(135deg,#E1306C22,#8B5CF622)", color: "#E1306C" }}>
                    {campaigns.length - idx}
                  </div>

                  {/* Bonus badge */}
                  <div className="px-2.5 py-1 rounded-lg flex items-center gap-1.5" style={{ background: "linear-gradient(135deg,rgba(225,48,108,0.12),rgba(139,92,246,0.12))", border: "1px solid rgba(225,48,108,0.2)" }}>
                    <span className="text-sm">🎉</span>
                    <span className="font-black text-sm" style={{ background: "linear-gradient(90deg,#E1306C,#8B5CF6)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>+{c.bonus_pct}%</span>
                  </div>

                  {/* Status pill */}
                  <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold" style={{ background: cfg.bg, color: cfg.dot }}>
                    <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: cfg.dot, animation: c.status === "sending" ? "pulse 1.5s ease-in-out infinite" : "none" }} />
                    {cfg.label[lang] || cfg.label.en}
                  </div>

                  {/* Date */}
                  <span className="ms-auto text-[11px]" style={{ color: "var(--text-secondary)" }}>{dateStr}</span>
                  <span className="text-xs" style={{ color: "var(--text-secondary)" }}>{isOpen ? "▲" : "▼"}</span>
                </div>

                {/* Mini stats bar */}
                <div className="mt-2.5 flex items-center gap-3">
                  <div className="flex-1">
                    <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "var(--border-color)" }}>
                      <div className="h-full rounded-full transition-all" style={{ width: `${prog}%`, background: c.status === "sending" ? "linear-gradient(90deg,#06B6D4,#8B5CF6)" : successRate > 90 ? "linear-gradient(90deg,#22c55e,#16a34a)" : successRate > 60 ? "linear-gradient(90deg,#F77737,#facc15)" : "linear-gradient(90deg,#ef4444,#E1306C)" }} />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-[11px] font-semibold flex-shrink-0">
                    <span style={{ color: "#22c55e" }}>✓ {c.sent_count}</span>
                    {c.failed_count > 0 && <span style={{ color: "#ef4444" }}>✗ {c.failed_count}</span>}
                    <span style={{ color: "var(--text-secondary)" }}>⊘ {c.skipped_count}</span>
                    <span className="font-bold" style={{ color: "var(--text-secondary)" }}>/ {c.total_recipients}</span>
                  </div>
                </div>
              </div>

              {/* Expanded detail */}
              {isOpen && (
                <div style={{ borderTop: "1px solid var(--border-color)", background: "var(--surface-2)" }}>
                  {detailLoading ? (
                    <div className="flex items-center justify-center gap-2 py-6">
                      <div className="animate-spin h-4 w-4 border-2 border-[#E1306C] border-t-transparent rounded-full" />
                      <span className="text-xs" style={{ color: "var(--text-secondary)" }}>{L(lang,"Loading…","Загрузка…","טוען…")}</span>
                    </div>
                  ) : detail && detail.campaign_id === c.campaign_id ? (
                    <div className="px-4 py-4 space-y-4">
                      {/* Delivery rate ring + stats */}
                      <div className="flex items-center gap-4">
                        {/* Circular progress */}
                        <div className="relative w-16 h-16 flex-shrink-0">
                          <svg viewBox="0 0 48 48" className="w-full h-full -rotate-90">
                            <circle cx="24" cy="24" r="20" fill="none" stroke="var(--border-color)" strokeWidth="5" />
                            <circle cx="24" cy="24" r="20" fill="none"
                              stroke={successRate > 90 ? "#22c55e" : successRate > 60 ? "#F77737" : "#ef4444"}
                              strokeWidth="5"
                              strokeLinecap="round"
                              strokeDasharray={`${2 * Math.PI * 20}`}
                              strokeDashoffset={`${2 * Math.PI * 20 * (1 - successRate / 100)}`}
                            />
                          </svg>
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-xs font-black" style={{ color: successRate > 90 ? "#22c55e" : successRate > 60 ? "#F77737" : "#ef4444" }}>{successRate}%</span>
                          </div>
                        </div>

                        {/* Stat cards */}
                        <div className="flex-1 grid grid-cols-3 gap-2">
                          {[
                            { l: { en:"Sent",    ru:"Отправлено",he:"נשלח"  }, v: detail.sent_count,    color: "#22c55e", bg: "rgba(34,197,94,0.08)"  },
                            { l: { en:"Failed",  ru:"Ошибка",    he:"נכשל"  }, v: detail.failed_count,  color: "#ef4444", bg: "rgba(239,68,68,0.08)"  },
                            { l: { en:"Skipped", ru:"Пропущено", he:"דולג"  }, v: detail.skipped_count, color: "#F77737", bg: "rgba(247,119,55,0.08)" },
                          ].map(s => (
                            <div key={s.color} className="rounded-xl p-2 text-center" style={{ background: s.bg, border: `1px solid ${s.color}22` }}>
                              <div className="text-lg font-black leading-none" style={{ color: s.color }}>{s.v}</div>
                              <div className="text-[10px] font-semibold mt-0.5" style={{ color: "var(--text-secondary)" }}>{s.l[lang] || s.l.en}</div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Timeline */}
                      <div className="rounded-xl px-3 py-2.5 space-y-1.5" style={{ background: "var(--surface-1)", border: "1px solid var(--border-color)" }}>
                        {[
                          { icon: "📅", label: { en:"Created", ru:"Создано",    he:"נוצר"     }, ts: detail.created_at  },
                          { icon: "🚀", label: { en:"Started", ru:"Начато",     he:"החל"      }, ts: detail.sent_at     },
                          { icon: "✅", label: { en:"Finished",ru:"Завершено",  he:"הסתיים"   }, ts: detail.finished_at },
                        ].filter(e => e.ts).map(e => (
                          <div key={e.icon} className="flex items-center gap-2 text-[11px]">
                            <span>{e.icon}</span>
                            <span style={{ color: "var(--text-secondary)" }}>{e.label[lang] || e.label.en}:</span>
                            <span className="font-semibold" style={{ color: "var(--text-primary)" }}>{new Date(e.ts).toLocaleString()}</span>
                          </div>
                        ))}
                      </div>

                      {/* Recipient table */}
                      {detail.recipients?.length > 0 && (
                        <div>
                          <p className="text-[11px] font-semibold mb-1.5" style={{ color: "var(--text-secondary)" }}>
                            {L(lang,"Recipients","Получатели","נמענים")} ({Math.min(detail.recipients.length, 100)}{detail.recipients.length > 100 ? "+" : ""})
                          </p>
                          <div className="rounded-xl overflow-hidden" style={{ border: "1px solid var(--border-color)", maxHeight: 200, overflowY: "auto" }}>
                            {detail.recipients.slice(0, 100).map((r, i) => (
                              <div key={i} className="flex items-center gap-2 px-3 py-1.5 text-xs"
                                style={{ borderTop: i > 0 ? "1px solid var(--border-color)" : "none", background: i % 2 === 0 ? "var(--surface-2)" : "var(--surface-1)" }}>
                                <span className="flex-shrink-0">{r.lang === "en" ? "🇺🇸" : r.lang === "ru" ? "🇷🇺" : "🇮🇱"}</span>
                                <span className="flex-1 truncate" style={{ color: "var(--text-primary)" }}>{r.email}</span>
                                <span className="font-semibold flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded-md"
                                  style={{ background: r.status === "sent" ? "rgba(34,197,94,0.12)" : r.status === "failed" ? "rgba(239,68,68,0.12)" : "var(--border-color)", color: r.status === "sent" ? "#22c55e" : r.status === "failed" ? "#ef4444" : "var(--text-secondary)" }}>
                                  {r.status}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Resend button */}
                      {detail.failed_count > 0 && (
                        <button onClick={() => resend(detail.campaign_id, detail.failed_count)}
                          disabled={resendBusy === detail.campaign_id || detail.status === "sending"}
                          className="w-full px-4 py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-2 disabled:opacity-40 transition-all hover:opacity-80"
                          style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.25)", color: "#f87171" }}>
                          {resendBusy === detail.campaign_id
                            ? <div className="animate-spin h-3.5 w-3.5 border-2 border-current border-t-transparent rounded-full" />
                            : <span>↩️</span>}
                          {L(lang, `Resend to ${detail.failed_count} failed`, `Повторить для ${detail.failed_count} ошибок`, `שלח שוב ל-${detail.failed_count} שנכשלו`)}
                        </button>
                      )}
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EmailsTab({ lang }) {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [langFilter, setLangFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [preview, setPreview] = useState(null);
  const [htmlBody, setHtmlBody] = useState(null);
  const [htmlLoading, setHtmlLoading] = useState(false);
  const PER_PAGE = 50;

  const fetch_ = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, per_page: PER_PAGE });
      if (categoryFilter) params.set("category_filter", categoryFilter);
      if (langFilter)     params.set("lang_filter", langFilter);
      if (statusFilter)   params.set("status_filter", statusFilter);
      if (search)         params.set("search", search);
      const res = await fetch(`${API}/admin/email-logs?${params}`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs || []);
        setTotal(data.total || 0);
      }
    } finally { setLoading(false); }
  }, [page, categoryFilter, langFilter, statusFilter, search]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const LANG_FLAGS = { en: "🇺🇸", ru: "🇷🇺", he: "🇮🇱" };
  const TABLE_GRID = "minmax(180px,1fr) minmax(220px,2fr) 100px 60px 100px 120px";

  return (
    <div className="space-y-5">
      {/* Bonus campaigns grouped summary */}
      <BonusCampaignsSummary lang={lang} />

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold" style={{ color: "var(--text-primary)" }}>
            {t("Admin.sent_emails", lang)}
          </h2>
          <p className="text-sm mt-0.5" style={{ color: "var(--text-secondary)" }}>
            {total.toLocaleString()} {t("Admin.total_2", lang)}
          </p>
        </div>
        <button onClick={fetch_} className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-all hover:opacity-80" style={{ background: "var(--surface-2)", color: "var(--text-secondary)" }}>
          <RefreshCw className="w-4 h-4" /> {t("Admin.refresh", lang)}
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "var(--text-secondary)" }} />
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder={L(lang, "Search by email or subject…", "Поиск по email или теме…", "חיפוש לפי מייל או נושא…")}
            className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm outline-none transition-all"
            style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)", color: "var(--text-primary)" }}
          />
        </div>
        <select value={categoryFilter} onChange={e => { setCategoryFilter(e.target.value); setPage(1); }}
          className="px-3 py-2.5 rounded-xl text-sm outline-none cursor-pointer"
          style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)", color: "var(--text-primary)" }}>
          <option value="">{L(lang, "All categories", "Все категории", "כל הקטגוריות")}</option>
          <option value="topup_approval">{L(lang, "Top-up", "Пополнение", "טעינה")}</option>
          <option value="order_completed">{L(lang, "Order", "Заказ", "הזמנה")}</option>
          <option value="tasks">{L(lang, "Tasks", "Задания", "משימות")}</option>
          <option value="global_bonus">{L(lang, "Bonus", "Бонус", "בונוס")}</option>
          <option value="support_replies">{L(lang, "Support", "Поддержка", "תמיכה")}</option>
        </select>
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          className="px-3 py-2.5 rounded-xl text-sm outline-none cursor-pointer"
          style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)", color: "var(--text-primary)" }}>
          <option value="">{L(lang, "All statuses", "Все статусы", "כל הסטטוסים")}</option>
          <option value="sent">{L(lang, "Sent", "Отправлено", "נשלח")}</option>
          <option value="failed">{L(lang, "Failed", "Ошибка", "נכשל")}</option>
        </select>
        <select value={langFilter} onChange={e => { setLangFilter(e.target.value); setPage(1); }}
          className="px-3 py-2.5 rounded-xl text-sm outline-none cursor-pointer"
          style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)", color: "var(--text-primary)" }}>
          <option value="">{t("Admin.all_languages", lang)}</option>
          <option value="en">🇺🇸 English</option>
          <option value="ru">🇷🇺 Русский</option>
          <option value="he">🇮🇱 עברית</option>
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin h-7 w-7 border-2 border-[#06B6D4] border-t-transparent rounded-full" /></div>
      ) : logs.length === 0 ? (
        <div className="text-center py-20">
          <div className="text-5xl mb-4">📭</div>
          <p className="text-base font-medium" style={{ color: "var(--text-secondary)" }}>{t("Admin.no_emails_found", lang)}</p>
        </div>
      ) : (
        <div className="rounded-2xl overflow-hidden border" style={{ borderColor: "var(--border-color)" }}>
          {/* Table header */}
          <div className="grid gap-3 px-4 py-3 text-xs font-semibold uppercase tracking-wider"
            style={{ background: "var(--surface-2)", color: "var(--text-secondary)", gridTemplateColumns: TABLE_GRID }}>
            <span>{t("Admin.recipient", lang)}</span>
            <span>{t("Admin.subject_2", lang)}</span>
            <span>{L(lang, "Category", "Категория", "קטגוריה")}</span>
            <span>{t("Admin.lang", lang)}</span>
            <span>{t("order.status", lang)}</span>
            <span>{t("Admin.sent_at", lang)}</span>
          </div>

          {/* Rows */}
          {logs.map((log, i) => {
            const cfg = EMAIL_CATEGORY_CFG[log.category] || EMAIL_CATEGORY_CFG.default;
            return (
              <div key={i}
                className="grid gap-3 px-4 py-3.5 items-center cursor-pointer transition-colors hover:opacity-80"
                style={{
                  gridTemplateColumns: TABLE_GRID,
                  background: i % 2 === 0 ? "var(--bg-secondary)" : "var(--surface-1)",
                  borderTop: "1px solid var(--border-color)",
                }}
                onClick={async () => {
                  setPreview(log);
                  setHtmlBody(null);
                  if (!log.email_id) return;
                  setHtmlLoading(true);
                  try {
                    const r = await fetch(`${API}/admin/email-logs/${log.email_id}`, { credentials: "include" });
                    if (r.ok) { const d = await r.json(); setHtmlBody(d.html_body || null); }
                  } catch {}
                  finally { setHtmlLoading(false); }
                }}
              >
                {/* Recipient */}
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate" style={{ color: "var(--text-primary)" }}>{log.to_email}</p>
                  {log.user_name && <p className="text-xs truncate mt-0.5" style={{ color: "var(--text-secondary)" }}>{log.user_name}</p>}
                </div>

                {/* Subject */}
                <p className="text-sm truncate" style={{ color: "var(--text-secondary)" }}>{log.subject}</p>

                {/* Category badge */}
                <div>
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold"
                    style={{ background: `${cfg.color}18`, color: cfg.color }}>
                    <span>{cfg.icon}</span>
                    {cfg.label[lang] || cfg.label.en}
                  </span>
                </div>

                {/* Lang flag */}
                <span className="text-lg">{LANG_FLAGS[log.lang] || "🌐"}</span>

                {/* Delivery status */}
                <div>
                  {log.success ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold" style={{ background: "#22c55e18", color: "#22c55e" }}>
                      <CheckCircle2 className="w-3 h-3" /> {t("Admin.sent_2", lang)}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold" style={{ background: "#ef444418", color: "#ef4444" }}>
                      <XCircle className="w-3 h-3" /> {t("Admin.failed", lang)}
                    </span>
                  )}
                </div>

                {/* Date */}
                <p className="text-xs" style={{ color: "var(--text-secondary)" }}>
                  {log.sent_at ? new Date(log.sent_at).toLocaleString("he-IL", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}
                </p>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {total > PER_PAGE && (
        <div className="flex items-center justify-center gap-2">
          <button disabled={page === 1} onClick={() => setPage(p => p - 1)}
            className="px-4 py-2 rounded-xl text-sm font-medium disabled:opacity-40 transition-all"
            style={{ background: "var(--surface-2)", color: "var(--text-primary)" }}>←</button>
          <span className="px-4 py-2 text-sm font-medium rounded-xl" style={{ background: "var(--surface-2)", color: "var(--text-secondary)" }}>
            {page} / {Math.ceil(total / PER_PAGE)}
          </span>
          <button disabled={page * PER_PAGE >= total} onClick={() => setPage(p => p + 1)}
            className="px-4 py-2 rounded-xl text-sm font-medium disabled:opacity-40 transition-all"
            style={{ background: "var(--surface-2)", color: "var(--text-primary)" }}>→</button>
        </div>
      )}

      {/* Preview modal */}
      {preview && (() => {
        const cfg = EMAIL_CATEGORY_CFG[preview.category] || EMAIL_CATEGORY_CFG.default;
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-3" style={{ background: "rgba(0,0,0,0.75)" }}
            onClick={() => { setPreview(null); setHtmlBody(null); }}>
            <div className="rounded-2xl border w-full flex flex-col" onClick={e => e.stopPropagation()}
              style={{ background: "var(--bg-secondary)", borderColor: "var(--border-color)", maxWidth: "100%", height: "calc(100vh - 24px)" }}>

              {/* Header */}
              <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-4 flex-shrink-0"
                style={{ borderBottom: "1px solid var(--border-color)" }}>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium mb-0.5" style={{ color: "var(--text-secondary)" }}>
                    {preview.to_email}
                    {preview.user_name && <span className="ml-2 font-semibold" style={{ color: "var(--text-primary)" }}>{preview.user_name}</span>}
                  </p>
                  <h3 className="text-sm font-bold leading-snug" style={{ color: "var(--text-primary)" }}>{preview.subject}</h3>
                  <p className="text-[11px] mt-1" style={{ color: "var(--text-secondary)" }}>
                    {preview.sent_at ? new Date(preview.sent_at).toLocaleString() : ""}
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2.5">
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold"
                      style={{ background: `${cfg.color}18`, color: cfg.color }}>
                      {cfg.icon} {cfg.label[lang] || cfg.label.en}
                    </span>
                    {LANG_FLAGS[preview.lang] && (
                      <span className="px-2 py-1 rounded-lg text-xs font-semibold" style={{ background: "var(--surface-2)", color: "var(--text-secondary)" }}>
                        {LANG_FLAGS[preview.lang]}
                      </span>
                    )}
                    {preview.success ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold" style={{ background: "#22c55e18", color: "#22c55e" }}>
                        <CheckCircle2 className="w-3 h-3" /> {t("Admin.sent_2", lang)}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold" style={{ background: "#ef444418", color: "#ef4444" }}>
                        <XCircle className="w-3 h-3" /> {t("Admin.failed", lang)}
                      </span>
                    )}
                  </div>
                </div>
                <button onClick={() => { setPreview(null); setHtmlBody(null); }}
                  className="p-1.5 rounded-lg transition-colors hover:bg-white/10 flex-shrink-0" style={{ color: "var(--text-secondary)" }}>
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Email HTML body */}
              <div className="flex-1 overflow-hidden rounded-b-2xl" style={{ minHeight: 0 }}>
                {htmlLoading ? (
                  <div className="flex items-center justify-center py-16">
                    <div className="animate-spin h-6 w-6 border-2 border-[#8B5CF6] border-t-transparent rounded-full" />
                  </div>
                ) : htmlBody ? (
                  <iframe
                    srcDoc={htmlBody}
                    sandbox="allow-same-origin"
                    title="email-preview"
                    style={{ width: "100%", height: "100%", border: "none", background: "#fff", borderRadius: "0 0 16px 16px" }}
                  />
                ) : (
                  <div className="px-5 py-4">
                    {preview.resend_id && (
                      <div className="rounded-xl p-3 text-xs" style={{ background: "var(--surface-2)", color: "var(--text-secondary)" }}>
                        Resend ID: <span className="font-mono" style={{ color: "var(--text-primary)" }}>{preview.resend_id}</span>
                      </div>
                    )}
                    <p className="text-xs mt-3 text-center" style={{ color: "var(--text-secondary)" }}>
                      {L(lang, "No preview available", "Превью недоступно", "אין תצוגה מקדימה")}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

/* ══════════════════════════════════════════════
   PayoutsTab — commission tracking & payouts
   ══════════════════════════════════════════════ */
function PayoutsTab({ lang }) {
  const { token } = useAuth();
  const [summary, setSummary]   = useState(null);
  const [payouts, setPayouts]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [deleting, setDeleting] = useState("");
  const [form, setForm]         = useState({ amount: "", note: "", payout_date: new Date().toISOString().slice(0, 10), is_supplement: false });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/payouts`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error();
      const data = await r.json();
      setSummary(data.summary);
      setPayouts(data.payouts || []);
    } catch { toast.error(t("Admin.failed_to_load_payouts", lang)); }
    finally { setLoading(false); }
  }, [token, lang]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) return toast.error(t("Admin.enter_a_valid_amount", lang));
    setSaving(true);
    try {
      const r = await fetch(`${API}/admin/payouts`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ amount: amt, note: form.note || undefined, payout_date: form.payout_date || undefined, is_bonus: form.is_supplement }),
      });
      if (!r.ok) throw new Error();
      toast.success(t("Admin.payout_recorded", lang));
      setForm(f => ({ ...f, amount: "", note: "", is_supplement: false }));
      load();
    } catch { toast.error(t("Admin.failed_to_save", lang)); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("Admin.delete_this_payout", lang))) return;
    setDeleting(id);
    try {
      const r = await fetch(`${API}/admin/payouts/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error();
      toast.success(t("Admin.deleted", lang));
      load();
    } catch { toast.error(t("Admin.failed_to_delete", lang)); }
    finally { setDeleting(""); }
  };

  const fmt = (n) => `₪${Number(n || 0).toLocaleString("en-IL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const pct  = summary ? Math.min(100, Math.round((summary.total_paid / Math.max(summary.total_commission, 0.01)) * 100)) : 0;

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="rounded-2xl p-5" style={{ background: "var(--surface-1)", border: "1px solid var(--border-color)" }}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <AdminIcon emoji="💰" className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-black font-outfit gradient-text">{t("Admin.payouts", lang)}</h2>
              <p className="text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>{t("Admin.commission_tracking_payout_history", lang)}</p>
            </div>
          </div>
          <button onClick={load} className="p-2 rounded-xl transition-colors hover:bg-white/5">
            <RefreshCw className="w-4 h-4" style={{ color: "var(--text-secondary)" }} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin h-8 w-8 border-2 border-green-500 border-t-transparent rounded-full" />
        </div>
      ) : (
        <>
          {/* ── Summary Cards ── */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            {[
              { emoji:"💵", label:t("Admin.total_commission", lang), value:fmt(summary?.total_commission), sub:t("Admin.25_of_approved_top_ups", lang), color:"#22c55e", bg:"rgba(34,197,94,0.09)" },
              { emoji:"💸", label:t("Admin.total_paid_out", lang), value:fmt(summary?.total_paid), sub:`${payouts.filter(p=>!p.is_bonus).length} ${t("Admin.payout_s_recorded", lang)}`, color:"#3b82f6", bg:"rgba(59,130,246,0.09)" },
              { emoji:"⬆️", label:t("Admin.total_supplements", lang), value:fmt(summary?.total_bonuses), sub:`${payouts.filter(p=>p.is_bonus).length} ${t("Admin.payout_s_recorded", lang)}`, color:"#a78bfa", bg:"rgba(167,139,250,0.09)" },
              { emoji:"🏦", label:t("Admin.remaining_balance", lang), value:fmt(summary?.remaining), sub:null, color:"#f59e0b", bg:"rgba(245,158,11,0.09)", progress:true },
            ].map(card => (
              <div key={card.label} className="rounded-2xl p-5" style={{ background: "var(--surface-1)", border: "1px solid var(--border-color)" }}>
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <AdminIcon emoji={card.emoji} className="w-5 h-5" />
                    <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-secondary)" }}>{card.label}</span>
                  </div>
                  <div className="text-[2rem] font-black font-outfit leading-none" style={{ color: card.color, fontVariantNumeric: "tabular-nums" }}>{card.value}</div>
                  {card.sub && <div className="text-xs mt-1.5" style={{ color: "var(--text-secondary)" }}>{card.sub}</div>}
                  {card.progress && (
                    <div className="mt-3">
                      <div className="flex justify-between text-xs mb-1.5" style={{ color: "var(--text-secondary)" }}>
                        <span>{t("Admin.paid", lang)}</span>
                        <span className="font-bold" style={{ color: card.color }}>{pct}%</span>
                      </div>
                      <div className="h-2 rounded-full overflow-hidden" style={{ background: "var(--surface-2)" }}>
                        <div className="h-2 rounded-full transition-all" style={{ width: `${pct}%`, background: card.color }} />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* ── Add Payout Form ── */}
          <div className="rounded-2xl border p-6" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
            <h3 className="text-base font-bold mb-4 flex items-center gap-2">
              <Plus className="w-4 h-4 text-green-400" />
              {t("Admin.record_payout", lang)}
            </h3>
            <form onSubmit={handleAdd} className="space-y-3">
              {/* Type toggle */}
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: "var(--text-secondary)" }}>{t("Admin.payout_type", lang)}</label>
                <div className="flex gap-2">
                  {[
                    { val: false, label: t("Admin.payout_type_regular", lang), color: "#3b82f6" },
                    { val: true,  label: t("Admin.payout_type_supplement", lang), color: "#a78bfa" },
                  ].map(opt => (
                    <button key={String(opt.val)} type="button"
                      onClick={() => setForm(f => ({ ...f, is_supplement: opt.val }))}
                      className="flex-1 h-9 rounded-xl text-sm font-semibold transition-all border"
                      style={{
                        background: form.is_supplement === opt.val ? `${opt.color}18` : "var(--surface-2)",
                        borderColor: form.is_supplement === opt.val ? `${opt.color}55` : "var(--border-color)",
                        color: form.is_supplement === opt.val ? opt.color : "var(--text-secondary)",
                      }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: "var(--text-secondary)" }}>
                    {t("Admin.amount_2", lang)} *
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0.01"
                    placeholder="0.00"
                    value={form.amount}
                    onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: "var(--text-secondary)" }}>
                    {t("order.date", lang)}
                  </label>
                  <div className="relative">
                    <Calendar className="absolute start-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none" style={{ color: "#8B5CF6", opacity: 0.7 }} />
                    <input
                      type="date"
                      value={form.payout_date}
                      onChange={e => setForm(f => ({ ...f, payout_date: e.target.value }))}
                      className="w-full h-9 rounded-xl text-sm ps-9 pe-3 outline-none border"
                      style={{ background: "var(--surface-2)", borderColor: "var(--border-color)", color: "var(--text-primary)" }}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: "var(--text-secondary)" }}>
                    {t("Admin.note_optional", lang)}
                  </label>
                  <Input
                    placeholder={t("Admin.e_g_bank_transfer", lang)}
                    value={form.note}
                    onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
                  />
                </div>
                <Button
                  type="submit"
                  disabled={saving}
                  className="h-10 font-semibold"
                  style={{ background: form.is_supplement ? "#a78bfa" : "#22c55e", color: "white", border: "none" }}
                >
                  {saving ? <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" /> : (
                    <><Check className="w-4 h-4 mr-1" />{t("admin.save", lang)}</>
                  )}
                </Button>
              </div>
            </form>
          </div>

          {/* ── Payout History ── */}
          <div className="rounded-2xl overflow-hidden border" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
            <div className="px-6 py-4 border-b" style={{ borderColor: "var(--surface-2)" }}>
              <h3 className="text-base font-bold flex items-center gap-2">
                <History className="w-4 h-4" style={{ color: "var(--text-secondary)" }} />
                {t("Admin.payout_history", lang)}
                <span className="ml-auto text-xs font-normal px-2 py-0.5 rounded-full" style={{ background: "var(--surface-2)", color: "var(--text-secondary)" }}>
                  {payouts.length}
                </span>
              </h3>
            </div>

            {payouts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: "var(--surface-2)" }}>
                  <Coins className="w-6 h-6" style={{ color: "var(--text-secondary)" }} />
                </div>
                <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
                  {t("Admin.no_payouts_recorded_yet", lang)}
                </p>
              </div>
            ) : (
              <div className="divide-y" style={{ borderColor: "var(--surface-2)" }}>
                <div className="grid grid-cols-[80px_1fr_160px_1fr_48px] gap-4 px-6 py-2.5 text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-secondary)", background: "var(--surface-1)" }}>
                  <span>{t("Admin.payout_type", lang)}</span>
                  <span>{t("Admin.amount", lang)}</span>
                  <span>{t("order.date", lang)}</span>
                  <span>{t("Admin.note_2", lang)}</span>
                  <span />
                </div>
                {payouts.map((p) => {
                  const isSupplement = !!p.is_bonus;
                  return (
                    <div key={p.payout_id} className="grid grid-cols-[80px_1fr_160px_1fr_48px] gap-4 px-6 py-3.5 items-center hover:bg-white/[0.02] transition-colors">
                      <span className="text-[11px] font-bold px-2 py-0.5 rounded-full text-center"
                        style={{ background: isSupplement ? "rgba(167,139,250,0.12)" : "rgba(59,130,246,0.12)", color: isSupplement ? "#a78bfa" : "#3b82f6" }}>
                        {isSupplement ? t("Admin.payout_type_supplement", lang) : t("Admin.payout_type_regular", lang)}
                      </span>
                      <div className="font-bold text-base" style={{ color: isSupplement ? "#a78bfa" : "#22c55e" }}>{fmt(p.amount)}</div>
                      <div className="text-sm" style={{ color: "var(--text-secondary)" }}>
                        {p.payout_date ? new Date(p.payout_date + "T00:00:00").toLocaleDateString(lang === "he" ? "he-IL" : lang === "ru" ? "ru-RU" : "en-US", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                      </div>
                      <div className="text-sm truncate" style={{ color: "var(--text-secondary)" }}>{p.note || "—"}</div>
                      <button
                        onClick={() => handleDelete(p.payout_id)}
                        disabled={deleting === p.payout_id}
                        className="p-1.5 rounded-lg transition-colors hover:bg-red-500/10 text-red-400 disabled:opacity-40"
                      >
                        {deleting === p.payout_id
                          ? <div className="animate-spin h-3.5 w-3.5 border-2 border-red-400 border-t-transparent rounded-full" />
                          : <Trash2 className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ALL_ADMIN_TABS metadata for permission UI
// ─────────────────────────────────────────────────────────────────────────────
const TAB_META = [
  { id: "dashboard",         label: "Dashboard",          emoji: "📊", section: "Overview",    color: "#E1306C" },
  { id: "analytics",         label: "Analytics",          emoji: "📈", section: "Overview",    color: "#00C853" },
  { id: "seo",               label: "SEO",                emoji: "🌐", section: "Overview",    color: "#8B5CF6" },
  { id: "improvements",      label: "Improvements",       emoji: "💡", section: "Overview",    color: "#FACC15" },
  { id: "orders",            label: "Orders",             emoji: "📦", section: "Operations",  color: "#E1306C" },
  { id: "products",          label: "Products",           emoji: "🛍️", section: "Operations",  color: "#F77737" },
  { id: "topups",            label: "Top Ups",            emoji: "💳", section: "Operations",  color: "#00C853" },
  { id: "promos",            label: "Promo Codes",        emoji: "🏷️", section: "Operations",  color: "#833AB4" },
  { id: "payouts",           label: "Payouts",            emoji: "💰", section: "Operations",  color: "#22c55e" },
  { id: "tasks",             label: "Tasks",              emoji: "🎯", section: "Workspace",   color: "#F77737" },
  { id: "users",             label: "Users",              emoji: "👥", section: "Workspace",   color: "#833AB4" },
  { id: "messages",          label: "Messages",           emoji: "💬", section: "Workspace",   color: "#405DE6" },
  { id: "ig-locks",          label: "Instagram Locks",   emoji: "🔒", section: "Workspace",   color: "#E1306C" },
  { id: "emails",            label: "Emails",             emoji: "📧", section: "Workspace",   color: "#06B6D4" },
  { id: "tickets",           label: "Support Tickets",   emoji: "🎧", section: "Support",     color: "#405DE6" },
  { id: "support-analytics", label: "Support Analytics", emoji: "📉", section: "Support",     color: "#405DE6" },
  { id: "ai-chats",          label: "AI Chats",           emoji: "🤖", section: "Support",     color: "#8B5CF6" },
];
const TAB_SECTIONS_ORDER = ["Overview", "Operations", "Workspace", "Support"];

// ─────────────────────────────────────────────────────────────────────────────
// PermissionCheckboxGrid — reusable grid of tabs with checkboxes
// ─────────────────────────────────────────────────────────────────────────────
function PermissionCheckboxGrid({ selected, onChange }) {
  const grouped = TAB_SECTIONS_ORDER.map(sec => ({
    section: sec,
    tabs: TAB_META.filter(t => t.section === sec),
  }));

  const toggleAll = (tabIds, on) => {
    let next = new Set(selected);
    tabIds.forEach(id => on ? next.add(id) : next.delete(id));
    onChange([...next]);
  };

  return (
    <div className="space-y-3">
      {grouped.map(({ section, tabs }) => {
        const allOn = tabs.every(t => selected.includes(t.id));
        return (
          <div key={section}>
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-[10px] font-black uppercase tracking-widest" style={{ color: "var(--text-secondary)" }}>{section}</span>
              <div className="flex-1 h-px" style={{ background: "var(--border-color)" }} />
              <button type="button" onClick={() => toggleAll(tabs.map(t => t.id), !allOn)}
                className="text-[10px] font-semibold px-2 py-0.5 rounded-md transition-all"
                style={{ color: allOn ? "#f87171" : "#10b981", background: allOn ? "rgba(248,113,113,0.1)" : "rgba(16,185,129,0.1)" }}>
                {allOn ? "Clear all" : "Select all"}
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
              {tabs.map(tab => {
                const on = selected.includes(tab.id);
                return (
                  <button type="button" key={tab.id}
                    onClick={() => {
                      const next = on ? selected.filter(x => x !== tab.id) : [...selected, tab.id];
                      onChange(next);
                    }}
                    className="flex items-center gap-2 px-2.5 py-2 rounded-xl text-xs font-semibold transition-all text-left"
                    style={{
                      background: on ? `${tab.color}18` : "var(--surface-2)",
                      border: `1px solid ${on ? `${tab.color}40` : "var(--border-color)"}`,
                      color: on ? tab.color : "var(--text-secondary)",
                    }}>
                    <span className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0 text-[11px]"
                      style={{ background: on ? `${tab.color}22` : "var(--surface-1)", border: `1px solid ${on ? `${tab.color}50` : "var(--border-color)"}` }}>
                      {on ? "✓" : ""}
                    </span>
                    <span className="mr-0.5">{tab.emoji}</span>
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

const ROLE_COLORS = ["#6366f1","#8B5CF6","#a855f7","#E1306C","#F77737","#10b981","#0ea5e9","#f97316","#ec4899","#14b8a6"];

// ─────────────────────────────────────────────────────────────────────────────
// RolesTab — super_admin only
// ─────────────────────────────────────────────────────────────────────────────
function RolesTab({ lang }) {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editRole, setEditRole] = useState(null);   // null | "new" | role object
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState("");
  const [form, setForm] = useState({ name: "", color: "#6366f1", description: "", permissions: [] });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/roles`, { credentials: "include" });
      if (r.ok) setRoles(await r.json());
    } catch {}
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setForm({ name: "", color: "#6366f1", description: "", permissions: [] });
    setEditRole("new");
  };
  const openEdit = (role) => {
    setForm({ name: role.name, color: role.color, description: role.description || "", permissions: role.permissions || [] });
    setEditRole(role);
  };
  const closeEditor = () => setEditRole(null);

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    setSaving(true);
    try {
      const isNew = editRole === "new";
      const url = isNew ? `${API}/admin/roles` : `${API}/admin/roles/${editRole.id}`;
      const method = isNew ? "POST" : "PUT";
      const r = await fetch(url, {
        method, credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Failed");
      toast.success(isNew ? "Role created" : "Role updated");
      closeEditor();
      load();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const handleDelete = async (role) => {
    if (!window.confirm(`Delete role "${role.name}"? Users assigned to this role will lose their permissions.`)) return;
    setDeleting(role.id);
    try {
      const r = await fetch(`${API}/admin/roles/${role.id}`, { method: "DELETE", credentials: "include" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Failed");
      toast.success("Role deleted");
      load();
    } catch (e) { toast.error(e.message); }
    setDeleting("");
  };

  return (
    <div className="space-y-4 animate-fade-in-up">
      {/* Header */}
      <div className="rounded-2xl p-4 border flex items-center justify-between gap-3"
        style={{ borderColor: "var(--border-color)", background: "var(--surface-1)" }}>
        <div>
          <h2 className="text-base font-bold flex items-center gap-2" style={{ color: "var(--text-primary)" }}>
            <Shield className="w-4 h-4 text-[#8B5CF6]" /> Roles &amp; Access
          </h2>
          <p className="text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>
            Create roles with specific tab permissions, then assign them to admin users.
          </p>
        </div>
        <button onClick={openNew}
          className="flex items-center gap-1.5 h-8 px-3 rounded-xl text-xs font-bold transition-all hover:opacity-90"
          style={{ background: "rgba(139,92,246,0.15)", border: "1px solid rgba(139,92,246,0.35)", color: "#a78bfa" }}>
          <Plus className="w-3.5 h-3.5" /> New Role
        </button>
      </div>

      {/* Role list */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin h-6 w-6 border-2 border-[#8B5CF6] border-t-transparent rounded-full" />
        </div>
      ) : roles.length === 0 ? (
        <div className="rounded-2xl border p-10 text-center" style={{ borderColor: "var(--border-color)", background: "var(--surface-1)" }}>
          <Shield className="w-10 h-10 mx-auto mb-3 opacity-20" style={{ color: "#8B5CF6" }} />
          <p className="text-sm font-semibold" style={{ color: "var(--text-secondary)" }}>No roles yet</p>
          <p className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>Create your first role to restrict admin access.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {roles.map(role => (
            <div key={role.id} className="rounded-2xl border p-4" style={{ borderColor: "var(--border-color)", background: "var(--surface-1)" }}>
              <div className="flex items-start gap-3">
                {/* Color swatch */}
                <div className="w-9 h-9 rounded-xl flex-shrink-0 flex items-center justify-center mt-0.5"
                  style={{ background: `${role.color}20`, border: `2px solid ${role.color}40` }}>
                  <Shield className="w-4 h-4" style={{ color: role.color }} />
                </div>
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-sm" style={{ color: "var(--text-primary)" }}>{role.name}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
                      style={{ background: `${role.color}15`, color: role.color }}>
                      {(role.permissions || []).length} tabs
                    </span>
                    {role.user_count > 0 && (
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
                        style={{ background: "rgba(139,92,246,0.12)", color: "#a78bfa" }}>
                        {role.user_count} user{role.user_count !== 1 ? "s" : ""}
                      </span>
                    )}
                  </div>
                  {role.description && (
                    <p className="text-xs mt-0.5 truncate" style={{ color: "var(--text-secondary)" }}>{role.description}</p>
                  )}
                  {/* Permission pills */}
                  <div className="flex flex-wrap gap-1 mt-2">
                    {(role.permissions || []).map(perm => {
                      const meta = TAB_META.find(t => t.id === perm);
                      return (
                        <span key={perm} className="text-[10px] px-1.5 py-0.5 rounded-md font-semibold"
                          style={{ background: `${meta?.color || "#666"}15`, color: meta?.color || "#aaa", border: `1px solid ${meta?.color || "#666"}25` }}>
                          {meta?.emoji} {meta?.label || perm}
                        </span>
                      );
                    })}
                    {(role.permissions || []).length === 0 && (
                      <span className="text-[10px] italic" style={{ color: "var(--text-secondary)" }}>No permissions</span>
                    )}
                  </div>
                </div>
                {/* Actions */}
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button onClick={() => openEdit(role)}
                    className="h-7 px-2.5 rounded-lg text-xs font-semibold flex items-center gap-1 transition-all hover:opacity-80"
                    style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)", color: "var(--text-secondary)" }}>
                    <PenSquare className="w-3 h-3" /> Edit
                  </button>
                  <button onClick={() => handleDelete(role)} disabled={deleting === role.id}
                    className="h-7 w-7 rounded-lg flex items-center justify-center transition-all hover:bg-red-500/10 disabled:opacity-40"
                    style={{ border: "1px solid var(--border-color)", color: "#f87171" }}>
                    {deleting === role.id
                      ? <div className="animate-spin h-3 w-3 border-2 border-red-400 border-t-transparent rounded-full" />
                      : <Trash2 className="w-3 h-3" />}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit modal */}
      {editRole !== null && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto"
          style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)" }}
          onClick={(e) => { if (e.target === e.currentTarget) closeEditor(); }}>
          <div className="w-full max-w-lg rounded-2xl shadow-2xl my-auto flex flex-col"
            style={{ background: "var(--bg-secondary)", border: "1px solid var(--border-color)", maxHeight: "calc(100vh - 2rem)" }}>
            {/* Modal header */}
            <div className="px-5 py-4 flex items-center justify-between border-b flex-shrink-0" style={{ borderColor: "var(--border-color)" }}>
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${form.color}20` }}>
                  <Shield className="w-3.5 h-3.5" style={{ color: form.color }} />
                </div>
                <span className="font-bold text-sm" style={{ color: "var(--text-primary)" }}>
                  {editRole === "new" ? "New Role" : `Edit: ${editRole.name}`}
                </span>
              </div>
              <button onClick={closeEditor} className="w-7 h-7 rounded-lg flex items-center justify-center transition-all hover:bg-white/5"
                style={{ color: "var(--text-secondary)" }}>
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal body */}
            <div className="p-5 space-y-4 overflow-y-auto flex-1">
              {/* Name + Color */}
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: "var(--text-secondary)" }}>Role Name</label>
                  <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. CRM Manager"
                    className="w-full h-9 px-3 rounded-xl text-sm outline-none"
                    style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)", color: "var(--text-primary)" }} />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: "var(--text-secondary)" }}>Color</label>
                  <div className="flex gap-1 flex-wrap w-36">
                    {ROLE_COLORS.map(c => (
                      <button type="button" key={c} onClick={() => setForm(f => ({ ...f, color: c }))}
                        className="w-6 h-6 rounded-lg flex-shrink-0 transition-all"
                        style={{ background: c, outline: form.color === c ? `2px solid ${c}` : "none", outlineOffset: 2, opacity: form.color === c ? 1 : 0.5 }} />
                    ))}
                  </div>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: "var(--text-secondary)" }}>Description (optional)</label>
                <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="What this role is for…"
                  className="w-full h-9 px-3 rounded-xl text-sm outline-none"
                  style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)", color: "var(--text-primary)" }} />
              </div>

              {/* Permissions */}
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: "var(--text-secondary)" }}>
                  Permissions — {form.permissions.length} tab{form.permissions.length !== 1 ? "s" : ""} selected
                </label>
                <PermissionCheckboxGrid selected={form.permissions} onChange={perms => setForm(f => ({ ...f, permissions: perms }))} />
              </div>
            </div>

            {/* Modal footer */}
            <div className="px-5 py-4 flex gap-2 border-t flex-shrink-0" style={{ borderColor: "var(--border-color)" }}>
              <button onClick={handleSave} disabled={saving}
                className="flex-1 h-9 rounded-xl text-sm font-bold transition-all hover:opacity-90 flex items-center justify-center gap-1.5 disabled:opacity-50"
                style={{ background: `${form.color}20`, border: `1px solid ${form.color}40`, color: form.color }}>
                {saving ? <div className="animate-spin h-3.5 w-3.5 border-2 border-current border-t-transparent rounded-full" /> : <Save className="w-3.5 h-3.5" />}
                {saving ? "Saving…" : "Save Role"}
              </button>
              <button onClick={closeEditor}
                className="h-9 px-4 rounded-xl text-sm font-semibold transition-all hover:opacity-80"
                style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)", color: "var(--text-secondary)" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// UserPermissionsModal — assign a role or custom permissions to an admin user
// ─────────────────────────────────────────────────────────────────────────────
function UserPermissionsModal({ userId, userName, onClose, onSaved }) {
  const [info, setInfo]       = useState(null);
  const [roles, setRoles]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode]       = useState("role"); // "role" | "custom" | "clear"
  const [selectedRole, setSelectedRole] = useState("");
  const [customPerms, setCustomPerms]   = useState([]);
  const [saving, setSaving]   = useState(false);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/admin/users/${userId}/permissions`, { credentials: "include" }).then(r => r.ok ? r.json() : null),
      fetch(`${API}/admin/roles`, { credentials: "include" }).then(r => r.ok ? r.json() : []),
    ]).then(([pInfo, rList]) => {
      setRoles(rList || []);
      if (pInfo) {
        setInfo(pInfo);
        if (pInfo.admin_role_id) {
          setMode("role");
          setSelectedRole(pInfo.admin_role_id);
        } else if (pInfo.admin_permissions !== null && pInfo.admin_permissions !== undefined) {
          setMode("custom");
          setCustomPerms(pInfo.admin_permissions || []);
        } else {
          setMode("clear");
        }
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [userId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      let body;
      if (mode === "role") {
        if (!selectedRole) return toast.error("Select a role");
        body = { mode: "role", role_id: selectedRole };
      } else if (mode === "custom") {
        body = { mode: "custom", permissions: customPerms };
      } else {
        body = { mode: "clear" };
      }
      const r = await fetch(`${API}/admin/users/${userId}/set-permissions`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Failed");
      toast.success("Permissions updated");
      onSaved?.();
      onClose();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto"
      style={{ background: "rgba(0,0,0,0.7)", backdropFilter: "blur(6px)" }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-lg rounded-2xl shadow-2xl my-auto flex flex-col"
        style={{ background: "var(--bg-secondary)", border: "1px solid var(--border-color)", maxHeight: "calc(100vh - 2rem)" }}>

        {/* Header */}
        <div className="px-5 py-4 flex items-center justify-between border-b flex-shrink-0" style={{ borderColor: "var(--border-color)" }}>
          <div>
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" style={{ color: "#8B5CF6" }} />
              <span className="font-bold text-sm" style={{ color: "var(--text-primary)" }}>Manage Permissions</span>
            </div>
            <p className="text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>{userName}</p>
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-white/5 transition-all" style={{ color: "var(--text-secondary)" }}>
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin h-6 w-6 border-2 border-[#8B5CF6] border-t-transparent rounded-full" />
          </div>
        ) : (
          <>
            <div className="p-5 space-y-4 overflow-y-auto flex-1">
              {/* Mode selector */}
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: "var(--text-secondary)" }}>Access Mode</label>
                <div className="grid grid-cols-3 gap-1.5">
                  {[
                    { id: "role",   label: "Assign Role",   desc: "Inherits role perms",   icon: <Shield className="w-3.5 h-3.5" />,    color: "#8B5CF6" },
                    { id: "custom", label: "Custom",        desc: "Hand-pick tabs",         icon: <Settings className="w-3.5 h-3.5" />,  color: "#F77737" },
                    { id: "clear",  label: "No Access",     desc: "Remove all permissions", icon: <Lock className="w-3.5 h-3.5" />,      color: "#f87171" },
                  ].map(m => (
                    <button key={m.id} type="button" onClick={() => setMode(m.id)}
                      className="flex flex-col items-center gap-1 p-2.5 rounded-xl transition-all"
                      style={{
                        background: mode === m.id ? `${m.color}15` : "var(--surface-2)",
                        border: `1px solid ${mode === m.id ? `${m.color}40` : "var(--border-color)"}`,
                        color: mode === m.id ? m.color : "var(--text-secondary)",
                      }}>
                      {m.icon}
                      <span className="text-[11px] font-bold">{m.label}</span>
                      <span className="text-[9px] opacity-70">{m.desc}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Role selector */}
              {mode === "role" && (
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: "var(--text-secondary)" }}>Select Role</label>
                  {roles.length === 0 ? (
                    <div className="text-sm text-center py-6" style={{ color: "var(--text-secondary)" }}>
                      No roles created yet. Go to <strong>Roles &amp; Access</strong> to create one.
                    </div>
                  ) : (
                    <div className="space-y-1.5">
                      {roles.map(role => {
                        const on = selectedRole === role.id;
                        return (
                          <button key={role.id} type="button" onClick={() => setSelectedRole(role.id)}
                            className="w-full flex items-center gap-3 p-3 rounded-xl transition-all text-left"
                            style={{
                              background: on ? `${role.color}12` : "var(--surface-2)",
                              border: `1px solid ${on ? `${role.color}40` : "var(--border-color)"}`,
                            }}>
                            <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: `${role.color}20` }}>
                              <Shield className="w-3.5 h-3.5" style={{ color: role.color }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-semibold" style={{ color: on ? "var(--text-primary)" : "var(--text-secondary)" }}>{role.name}</span>
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: `${role.color}15`, color: role.color }}>
                                  {(role.permissions || []).length} tabs
                                </span>
                              </div>
                              {role.description && <p className="text-[11px] truncate mt-0.5" style={{ color: "var(--text-secondary)" }}>{role.description}</p>}
                            </div>
                            {on && <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: role.color }} />}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Custom permissions */}
              {mode === "custom" && (
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: "var(--text-secondary)" }}>
                    Select Tabs — {customPerms.length} selected
                  </label>
                  <PermissionCheckboxGrid selected={customPerms} onChange={setCustomPerms} />
                </div>
              )}

              {/* Clear info */}
              {mode === "clear" && (
                <div className="rounded-xl p-3 text-sm" style={{ background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.2)", color: "#f87171" }}>
                  This admin will lose all tab access. They can still log in but won't see any admin sections until permissions are re-assigned.
                </div>
              )}

              {/* Current effective permissions preview */}
              {info?.effective_permissions && info.effective_permissions.length > 0 && (
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: "var(--text-secondary)" }}>Current Access</label>
                  <div className="flex flex-wrap gap-1">
                    {info.effective_permissions.map(perm => {
                      const meta = TAB_META.find(t => t.id === perm);
                      return (
                        <span key={perm} className="text-[10px] px-1.5 py-0.5 rounded-md font-semibold"
                          style={{ background: `${meta?.color || "#666"}15`, color: meta?.color || "#aaa", border: `1px solid ${meta?.color || "#666"}25` }}>
                          {meta?.emoji} {meta?.label || perm}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-5 py-4 flex gap-2 border-t flex-shrink-0" style={{ borderColor: "var(--border-color)" }}>
              <button onClick={handleSave} disabled={saving || (mode === "role" && !selectedRole)}
                className="flex-1 h-9 rounded-xl text-sm font-bold transition-all hover:opacity-90 flex items-center justify-center gap-1.5 disabled:opacity-50"
                style={{ background: "rgba(139,92,246,0.15)", border: "1px solid rgba(139,92,246,0.35)", color: "#a78bfa" }}>
                {saving ? <div className="animate-spin h-3.5 w-3.5 border-2 border-current border-t-transparent rounded-full" /> : <Save className="w-3.5 h-3.5" />}
                {saving ? "Saving…" : "Save Permissions"}
              </button>
              <button onClick={onClose}
                className="h-9 px-4 rounded-xl text-sm font-semibold hover:opacity-80 transition-all"
                style={{ background: "var(--surface-2)", border: "1px solid var(--border-color)", color: "var(--text-secondary)" }}>
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function OnboardingAnalyticsTab({ lang: _lang }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/admin/onboarding/analytics`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-6 h-6 rounded-full border-2 border-[#8B5CF6] border-t-transparent animate-spin" /></div>;
  if (!data) return <div className="p-8 text-center text-red-400">Ошибка загрузки</div>;

  const total = data.total || 0;
  const completed = data.completed || 0;
  const skipped = data.skipped || 0;
  const notAnswered = data.not_answered || 0;

  const Bar = ({ label, count, max, color }) => (
    <div className="flex items-center gap-3 text-sm">
      <div className="w-28 truncate flex-shrink-0" style={{ color: "var(--text-secondary)" }}>{label}</div>
      <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: "var(--border-color)" }}>
        <div className="h-2 rounded-full transition-all" style={{ width: max ? `${Math.round((count / max) * 100)}%` : "0%", background: color || "#8B5CF6" }} />
      </div>
      <div className="w-10 text-right font-bold flex-shrink-0" style={{ color: "var(--text-primary)" }}>{count}</div>
    </div>
  );

  const Section = ({ title, items, color }) => {
    const max = items[0]?.count || 0;
    return items.length ? (
      <div className="rounded-2xl p-4 border space-y-3" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
        <div className="font-semibold text-sm">{title}</div>
        {items.map(it => <Bar key={it.value} label={it.value} count={it.count} max={max} color={color} />)}
      </div>
    ) : null;
  };

  return (
    <div className="p-6 space-y-5 max-w-3xl mx-auto">
      <div className="text-xl font-black">📋 Аналитика анкет</div>

      {/* KPI row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Всего пользователей", value: total + notAnswered, color: "#405DE6" },
          { label: "Заполнено", value: completed, color: "#22c55e" },
          { label: "Пропущено", value: skipped, color: "#facc15" },
          { label: "Не открывали", value: notAnswered, color: "#9ca3af" },
        ].map(k => (
          <div key={k.label} className="rounded-2xl p-4 border text-center" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
            <div className="text-2xl font-black" style={{ color: k.color }}>{k.value}</div>
            <div className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Completion bar */}
      <div className="rounded-2xl p-4 border" style={{ background: "var(--surface-1)", borderColor: "var(--border-color)" }}>
        <div className="font-semibold text-sm mb-3">Процент завершения</div>
        <div className="h-3 rounded-full overflow-hidden flex" style={{ background: "var(--border-color)" }}>
          <div style={{ width: total ? `${Math.round((completed / (total + notAnswered)) * 100)}%` : "0%", background: "#22c55e" }} />
          <div style={{ width: total ? `${Math.round((skipped / (total + notAnswered)) * 100)}%` : "0%", background: "#facc15" }} />
        </div>
        <div className="flex gap-4 mt-2 text-xs" style={{ color: "var(--text-secondary)" }}>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500 inline-block" /> Заполнено {total ? Math.round((completed / (total + notAnswered)) * 100) : 0}%</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-yellow-400 inline-block" /> Пропущено {total ? Math.round((skipped / (total + notAnswered)) * 100) : 0}%</span>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Section title="Откуда узнали" items={data.heard_about_us || []} color="#405DE6" />
        <Section title="Платформы" items={data.platforms || []} color="#E1306C" />
        <Section title="Возраст" items={data.age_category || []} color="#F77737" />
        <Section title="Основной интерес" items={data.main_interest || []} color="#8B5CF6" />
      </div>
    </div>
  );
}
